import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faBuilding,
  faBullseye,
  faCalendar,
  faChartLine,
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faClock,
  faCopy,
  faDumbbell,
  faEnvelope,
  faFire,
  faGear,
  faClipboardList,
  faLeaf,
  faList,
  faLock,
  faLocationDot,
  faMoon,
  faPen,
  faPlus,
  faRepeat,
  faRotateRight,
  faStar,
  faStopwatch,
  faSun,
  faTimes,
  faTrash,
  faTriangleExclamation,
  faUser,
  faUsers,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { supabase } from "../supabaseClient";
import { useAuth } from "../AuthContext";
import logo from "../assets/logo.png";
import {
  getDefaultWorkoutGroupAccess,
  hasWorkoutGroupEntitlement,
  hasWorkoutGroupTier,
  loadWorkoutGroupAccess,
  loadWorkoutGroupOwnerAccessForClub,
} from "../workoutGroupAccess";
import { uploadFileToR2 } from "../services/r2Upload";
import {
  analyticsGold,
  WorkoutGroupAnalyticsAthleteSelector,
  WorkoutGroupAnalyticsIcon,
  WorkoutGroupAnalyticsMetricCard,
  WorkoutGroupAnalyticsModalFrame,
  WorkoutGroupAnalyticsTeamStatusCard,
  WorkoutGroupExerciseHeaderIcon,
  WorkoutGroupNutritionTile,
} from "./WorkoutGroupAnalyticsShared";

const noImage = "https://wcislnchclarifyudagt.supabase.co/storage/v1/object/public/images/app-Images/no-image.jpg";

const TEAM_ENTITLEMENT_KEYS = [
  "workout_groups.dashboard.web",
  "workout_groups.branding",
  "workout_groups.multiple_coaches",
  "workout_groups.season_management",
  "workout_groups.club_analysis",
  "workout_groups.calendar_sessions",
];

const CLUB_STAFF_ROLE_OPTIONS = [
  { value: "coach", label: "Coach" },
  { value: "lead_coach", label: "Lead coach" },
];

const CLUB_STAFF_ROLE_LABELS = {
  owner: "Club owner",
  admin: "Admin",
  lead_coach: "Lead coach",
  coach: "Coach",
  analyst: "Analyst",
};

const formatClubStaffRole = (role = "coach") => CLUB_STAFF_ROLE_LABELS[role] || "Coach";

const DEFAULT_COLORS = {
  primary_color: "#0C69C8",
  secondary_color: "#000000",
  accent_color: "#D5A419",
};
const CLUB_BRAND_GRADIENT_SECONDARY = "#000000";

const WORKOUT_GROUPS_MEDIA_FOLDER = "workout-groups";

const WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY = {
  ownOnly: "own_only",
  group: "group",
};

const DEFAULT_GROUP_NUTRITION_TARGETS = {
  calories: 2500,
  protein: 150,
  carbs: 300,
  fat: 70,
};

const EMPTY_NUTRITION_PLAN_FORM = {
  title: "",
  groupId: "",
  coachId: "",
  scheduledDays: [],
  mealSection: "Breakfast",
  foodName: "",
  brand: "",
  calories: "",
  protein: "",
  carbs: "",
  fat: "",
  fiber: "",
  sugar: "",
  sodium: "",
  portion: "100",
  unit: "g",
  items: [],
  selectedAthleteIds: [],
};

const NUTRITION_PLAN_MEAL_ORDER = ["Breakfast", "Lunch", "Dinner", "Snacks"];

const NUTRITION_MEAL_META = {
  Breakfast: { color: "#D97706", bg: "#FFF7ED", border: "#FED7AA", icon: faSun },
  Lunch: { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0", icon: faLeaf },
  Dinner: { color: "#E03131", bg: "#FFF5F5", border: "#FFC9C9", icon: faMoon },
  Snacks: { color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", icon: faBolt },
};

const normalizePositiveTarget = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.round(numericValue) : fallback;
};

const getGroupNutritionTargets = (group = {}) => ({
  calories: normalizePositiveTarget(group?.daily_calorie_target, DEFAULT_GROUP_NUTRITION_TARGETS.calories),
  protein: normalizePositiveTarget(group?.daily_protein_target_g, DEFAULT_GROUP_NUTRITION_TARGETS.protein),
  carbs: normalizePositiveTarget(group?.daily_carbs_target_g, DEFAULT_GROUP_NUTRITION_TARGETS.carbs),
  fat: normalizePositiveTarget(group?.daily_fat_target_g, DEFAULT_GROUP_NUTRITION_TARGETS.fat),
});

const formatGroupNutritionTargets = (group = {}) => {
  const targets = getGroupNutritionTargets(group);
  return `${targets.calories.toLocaleString()} kcal · ${targets.protein}g protein · ${targets.carbs}g carbs · ${targets.fat}g fat`;
};

const getWorkoutGroupMemberAnalyticsVisibility = (group = {}) => (
  group?.member_analytics_visibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
    ? WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
    : WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly
);

const WORKOUT_GROUP_SELECT_BASE = "id, name, coach_id, created_by, club_id, season_id, join_code, member_analytics_visibility, weekly_xp_target, weekly_session_target, daily_calorie_target";
const WORKOUT_GROUP_SELECT_WITH_MACROS = `${WORKOUT_GROUP_SELECT_BASE}, daily_protein_target_g, daily_carbs_target_g, daily_fat_target_g`;
const WORKOUT_GROUP_COACH_RELATION = ", coach:profiles!workout_groups_coach_id_fkey(id,username,name,surname,profile_img)";
const WORKOUT_GROUP_RELATION_SUFFIX = `${WORKOUT_GROUP_COACH_RELATION}, workout_clubs(id,name), workout_club_seasons(id,name,status,starts_on,ends_on)`;
const DEMO_WORKOUT_GROUP_MEMBERS_SELECT = "demo_workout_group_members(id,demo_profile_id,group_id,squad_role,position,jersey_number,counts_as_athlete,metadata,demo_profiles(id,name,surname,username,profile_img,level,metadata))";
const DEMO_NUTRITION_ASSIGNMENTS_SELECT = "demo_nutrition_plan_assignments(*,demo_profiles(id,name,surname,username,profile_img,level,metadata),demo_nutrition_plan_progress(*),demo_nutrition_plan_log_events(*))";
const WORKOUT_GROUP_MEMBER_SUFFIX = `, allowed_workout_names, allowed_workout_pairs${WORKOUT_GROUP_COACH_RELATION}, workout_group_members(athlete_id, profiles(id, name, surname, username, profile_img, push_token)), ${DEMO_WORKOUT_GROUP_MEMBERS_SELECT}`;

const WORKOUT_GROUP_SELECTS = {
  relation: [
    `${WORKOUT_GROUP_SELECT_WITH_MACROS}${WORKOUT_GROUP_RELATION_SUFFIX}`,
    `${WORKOUT_GROUP_SELECT_BASE}${WORKOUT_GROUP_RELATION_SUFFIX}`,
  ],
  picker: [
    WORKOUT_GROUP_SELECT_WITH_MACROS,
    WORKOUT_GROUP_SELECT_BASE,
  ],
  clubMembers: [
    `${WORKOUT_GROUP_SELECT_WITH_MACROS}${WORKOUT_GROUP_MEMBER_SUFFIX}`,
    `${WORKOUT_GROUP_SELECT_BASE}${WORKOUT_GROUP_MEMBER_SUFFIX}`,
  ],
};

const isMissingWorkoutGroupColumnError = (error) => {
  const text = [error?.code, error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return error?.code === "42703" ||
    (text.includes("workout_groups") && text.includes("schema cache")) ||
    (text.includes("workout_groups") && text.includes("column") && text.includes("does not exist")) ||
    (text.includes("workout_groups") && text.includes("could not find"));
};

const selectWorkoutGroupDataWithFallback = async (buildQuery, selects, fallbackValue, label) => {
  let lastError = null;
  for (const select of selects) {
    const { data, error } = await buildQuery(select);
    if (!error) return data ?? fallbackValue;
    lastError = error;
    if (!isMissingWorkoutGroupColumnError(error)) {
      console.error(`${label} error:`, error);
      return fallbackValue;
    }
    console.warn(`${label} retrying without optional workout group columns:`, error);
  }
  if (lastError) console.error(`${label} failed after fallbacks:`, lastError);
  return fallbackValue;
};

const fetchPagedSupabaseRows = async (buildQuery, { pageSize = 1000, maxRows = 10000 } = {}) => {
  const rows = [];
  for (let from = 0; from < maxRows; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) return { data: rows, error };
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }
  return { data: rows, error: null };
};

const hexToRgb = (hex) => {
  const normalized = String(hex || "").replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const isDarkColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  return (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) < 140;
};

const isLightColor = (hex) => !isDarkColor(hex);

const alpha = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${amount})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${amount})`;
};

const base = {
  bg: "var(--club-page)",
  card: "var(--club-card)",
  text: "var(--club-text)",
  textDark: "var(--club-text)",
  muted: "var(--club-muted)",
  mutedLight: "var(--club-muted)",
  border: "var(--club-border)",
  borderLight: "var(--club-border)",
  blue: "#0C69C8",
  headerBg: "var(--club-header)",
  bgAlt: "var(--club-surface-alt)",
  danger: "#DC2626",
  success: "#16A34A",
};

const card = { background: base.card, border: `1px solid ${base.border}`, borderRadius: 16, padding: 18 };
const btn = { border: `1px solid ${base.border}`, background: "var(--club-card)", color: base.text, borderRadius: 10, padding: "9px 12px", fontWeight: 900, cursor: "pointer" };
const nutritionDarkInput = { width: "100%", boxSizing: "border-box", minWidth: 0, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 10, padding: "9px 10px", fontWeight: 850 };

const NutritionFormField = ({ label: fieldLabel, children }) => (
  <label style={{ minWidth: 0, display: "grid", gap: 5 }}>
    <span style={{ color: "rgba(255,255,255,0.72)", fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>
      {fieldLabel}
    </span>
    {children}
  </label>
);

const getNutritionMealMeta = (mealSection) => (
  NUTRITION_MEAL_META[formatMealSection(mealSection)] || { color: base.blue, bg: "#EFF6FF", border: "#BFDBFE", icon: faUtensils }
);

const formatNutritionMetricNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";
  const rounded = Math.round(numericValue * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toLocaleString() : rounded.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const NutritionPlanMealRow = ({ item, includeExtras = false, actions = null }) => {
  const mealLabel = formatMealSection(item?.meal_section || "Breakfast");
  const meta = getNutritionMealMeta(mealLabel);
  const macros = [
    { key: "calories", value: formatNutritionMetricNumber(item?.calories), label: "kcal" },
    { key: "protein", value: `${formatNutritionMetricNumber(item?.protein_g)}g`, label: "protein" },
    { key: "carbs", value: `${formatNutritionMetricNumber(item?.carbs_g)}g`, label: "carbs" },
    { key: "fat", value: `${formatNutritionMetricNumber(item?.fat_g)}g`, label: "fat" },
  ];
  const extraMacros = [
    { key: "fiber", value: `${formatNutritionMetricNumber(item?.fiber_g)}g`, label: "fiber" },
    { key: "sugar", value: `${formatNutritionMetricNumber(item?.sugar_g)}g`, label: "sugar" },
    { key: "sodium", value: `${formatNutritionMetricNumber(item?.sodium_mg)}mg`, label: "sodium" },
  ];
  return (
    <div className={`club-nutrition-meal-row${includeExtras ? " club-nutrition-meal-row-expanded" : ""}`}>
      <span className="club-nutrition-meal-accent" style={{ background: meta.color }} aria-hidden="true" />
      <span className={`club-nutrition-meal-icon club-nutrition-meal-icon--${mealLabel.toLowerCase()}`} style={{ color: meta.color, borderColor: meta.border, background: meta.bg }}>
        <FontAwesomeIcon icon={meta.icon} aria-hidden="true" />
      </span>
      <div className="club-nutrition-meal-copy">
        <div className="club-nutrition-meal-category">{mealLabel}</div>
        <div className="club-nutrition-meal-name">{item?.food_name || "Food"}</div>
      </div>
      <div className="club-nutrition-meal-macros" aria-label={`${mealLabel} nutrition`}>
        {macros.map((macro) => (
          <div key={macro.key} className="club-nutrition-meal-macro">
            <span className="club-nutrition-meal-macro-value">{macro.value}</span>
            <span className="club-nutrition-meal-macro-label">{macro.label}</span>
          </div>
        ))}
      </div>
      {includeExtras && (
        <div className="club-nutrition-meal-extra-macros" aria-label={`${mealLabel} additional nutrition`}>
          {extraMacros.map((macro) => (
            <div key={macro.key} className="club-nutrition-meal-extra-macro">
              <span className="club-nutrition-meal-extra-value">{macro.value}</span>
              <span className="club-nutrition-meal-extra-label">{macro.label}</span>
            </div>
          ))}
        </div>
      )}
      {actions && <div className="club-nutrition-meal-actions">{actions}</div>}
    </div>
  );
};

const getInitials = (name = "Athlete") => {
  const words = String(name || "Athlete").trim().split(/\s+/).filter(Boolean);
  return (words[0]?.[0] || "A") + (words[1]?.[0] || "");
};

const AthleteAvatar = ({ src, name, size = 28, shape = "rounded" }) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const hasImage = src && src !== noImage && !imageFailed;
  const borderRadius = shape === "circle" ? "50%" : Math.max(8, Math.round(size / 3));
  if (hasImage) {
    return <img src={src} alt="" onError={() => setImageFailed(true)} style={{ width: size, height: size, borderRadius, objectFit: "cover", flexShrink: 0, border: `1px solid ${base.borderLight}` }} loading="lazy" />;
  }
  return (
    <span aria-hidden="true" title={name || "Athlete"} style={{ width: size, height: size, borderRadius, display: "grid", placeItems: "center", flexShrink: 0, color: base.textDark, background: "#EFF6FF", border: `1px solid ${base.borderLight}`, fontSize: Math.max(10, Math.round(size * 0.34)), fontWeight: 950 }}>
      {getInitials(name)}
    </span>
  );
};

const ASSIGNED_WORKOUT_DAYS = [
  { index: 0, short: "Mon", label: "Monday" },
  { index: 1, short: "Tue", label: "Tuesday" },
  { index: 2, short: "Wed", label: "Wednesday" },
  { index: 3, short: "Thu", label: "Thursday" },
  { index: 4, short: "Fri", label: "Friday" },
  { index: 5, short: "Sat", label: "Saturday" },
  { index: 6, short: "Sun", label: "Sunday" },
];

const normalizeAssignedWorkoutDays = (value) => (
  Array.isArray(value)
    ? Array.from(new Set(value.map((day) => Number(day)).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))).sort((a, b) => a - b)
    : []
);

const formatAssignedWorkoutDays = (pair) => {
  const days = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days);
  if (!days.length) return "Any training day";
  return days.map((day) => ASSIGNED_WORKOUT_DAYS.find((item) => item.index === day)?.short).filter(Boolean).join(", ");
};

const getAllowedWorkoutNames = (group) => {
  const names = Array.isArray(group?.allowed_workout_names) ? group.allowed_workout_names : [];
  const pairNames = Array.isArray(group?.allowed_workout_pairs)
    ? group.allowed_workout_pairs.map((pair) => pair?.name)
    : [];
  return [...names, ...pairNames]
    .filter(Boolean)
    .map((name) => String(name).trim().toLowerCase())
    .filter(Boolean);
};

const normalizeWorkoutName = (name = "") => String(name || "").trim().toLowerCase();

const getAllowedWorkoutPairs = (group) => {
  const seen = new Set();
  const pairs = [];
  const pushPair = (name, code = null, workoutId = null, scheduledDays = []) => {
    const trimmed = String(name || "").trim();
    const key = normalizeWorkoutName(trimmed);
    if (!key || seen.has(key)) return;
    seen.add(key);
    pairs.push({ name: trimmed, code: code || null, workout_id: workoutId || null, scheduled_days: normalizeAssignedWorkoutDays(scheduledDays) });
  };
  (Array.isArray(group?.allowed_workout_pairs) ? group.allowed_workout_pairs : []).forEach((pair) => pushPair(pair?.name, pair?.code, pair?.workout_id || pair?.id, pair?.scheduled_days || pair?.days));
  (Array.isArray(group?.allowed_workout_names) ? group.allowed_workout_names : []).forEach((name) => pushPair(name));
  return pairs;
};

const sessionWorkoutName = (session) => {
  const customWorkout = Array.isArray(session?.user_workouts) ? session.user_workouts[0] : session?.user_workouts;
  const presetWorkout = Array.isArray(session?.workouts) ? session.workouts[0] : session?.workouts;
  return String(session?.workout_name || customWorkout?.name || presetWorkout?.name || "Unnamed workout").trim().toLowerCase();
};

const sessionCountsForGroup = (session, group) => {
  const allowedNames = getAllowedWorkoutNames(group);
  const allowedPairs = Array.isArray(group?.allowed_workout_pairs) ? group.allowed_workout_pairs : [];
  if (!allowedNames.length && !allowedPairs.length) return false;
  const planName = sessionWorkoutName(session);
  if (planName && allowedNames.includes(planName)) return true;
  const customWorkout = Array.isArray(session?.user_workouts) ? session.user_workouts[0] : session?.user_workouts;
  const presetWorkout = Array.isArray(session?.workouts) ? session.workouts[0] : session?.workouts;
  const sessionIds = new Set([
    session?.workout_plan_id,
    customWorkout?.id,
    customWorkout?.source_workout_plan_id,
    session?.preset_plan_id,
    presetWorkout?.id,
  ].map((value) => String(value || "").trim()).filter(Boolean));
  const sessionCodes = new Set([
    customWorkout?.share_code,
    customWorkout?.code,
    session?.workout_code,
  ].map((value) => String(value || "").trim()).filter(Boolean));
  return allowedPairs.some((pair) => {
    const pairWorkoutId = String(pair?.workout_id || pair?.id || "").trim();
    const pairCode = String(pair?.code || "").trim();
    return (!!pairWorkoutId && sessionIds.has(pairWorkoutId)) || (!!pairCode && sessionCodes.has(pairCode));
  });
};

const getGroupAttentionStatus = (xp, xpTarget, sessions, sessionTarget) => {
  const target = Math.max(1, Number(sessionTarget || 0));
  const count = Number(sessions || 0);
  const ratio = count / target;
  if (ratio >= 1.2) return { label: "Ahead of Plan", tone: "warning" };
  if (ratio >= 1) return { label: "On Plan", tone: "success" };
  if (ratio >= 0.75) return { label: "Building Momentum", tone: "warning" };
  return { label: "Needs Activity", tone: "danger" };
};

const ANALYTICS_RANGE_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "last_7", label: "7D" },
  { value: "last_14", label: "14D" },
  { value: "last_30", label: "30D" },
  { value: "last_90", label: "90D" },
  { value: "last_all", label: "All" },
  { value: "custom", label: "Custom" },
];

const EXTENDED_ANALYTICS_RANGE_VALUES = new Set(["date", "last_14", "last_28", "last_30", "last_90", "last_365", "custom"]);
const NUTRITION_ATHLETE_CARD_PREVIEW_LIMIT = 6;
const NUTRITION_ASSIGNED_PLAN_PAGE_SIZE = 2;

const BRAND_TOGGLE_ITEMS = [
  { key: "theme_workout_groups", label: "Allow colors in Workout Groups" },
  { key: "theme_workouts", label: "Use colors in my workouts" },
  { key: "theme_nutrition", label: "Use colors in my Calorie Tracker" },
  { key: "theme_sessions", label: "Use colors in my Workout Sessions" },
];

const FEEDBACK_EMOJI_OPTIONS = {
  rating: [
    { value: 1, emoji: "😖", label: "Hated" },
    { value: 2, emoji: "😕", label: "Meh" },
    { value: 3, emoji: "🙂", label: "Good" },
    { value: 4, emoji: "😄", label: "Loved" },
    { value: 5, emoji: "🤩", label: "Amazing" },
  ],
  difficulty: [
    { value: 1, emoji: "😌", label: "Easy" },
    { value: 2, emoji: "🙂", label: "Light" },
    { value: 3, emoji: "😅", label: "Moderate" },
    { value: 4, emoji: "😤", label: "Hard" },
    { value: 5, emoji: "🥵", label: "Brutal" },
  ],
  focus: [
    { value: 1, emoji: "❌", label: "Missed" },
    { value: 2, emoji: "👎", label: "Poor" },
    { value: 3, emoji: "🤷", label: "Unsure" },
    { value: 4, emoji: "👍", label: "Good" },
    { value: 5, emoji: "🎯", label: "Perfect" },
  ],
};

const MOOD_OPTIONS = [
  { value: "energized", emoji: "⚡", label: "Energized" },
  { value: "strong", emoji: "💪", label: "Strong" },
  { value: "relaxed", emoji: "😌", label: "Relaxed" },
  { value: "stressed", emoji: "😤", label: "Stressed" },
  { value: "tired", emoji: "😴", label: "Tired" },
];

const FeedbackEmojiRow = ({ label: rowLabel, value, options }) => (
  <div>
    <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 6 }}>{rowLabel}</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 5 }}>
      {options.map((option) => {
        const selected = Number(value) === option.value;
        return (
          <div key={option.value} style={{ minWidth: 0, textAlign: "center", padding: "8px 5px", borderRadius: 12, background: selected ? base.blue : "#FFFFFF", border: `1px solid ${selected ? base.blue : base.border}`, opacity: selected ? 1 : 0.45 }}>
            <div style={{ fontSize: 18, lineHeight: 1.05 }}>{option.emoji}</div>
            <div style={{ color: selected ? "#FFFFFF" : base.text, fontSize: 10, fontWeight: 900, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{option.label}</div>
          </div>
        );
      })}
    </div>
  </div>
);

const TrainingTrendTooltip = ({ active, payload, label: tooltipLabel, metricLabel = "Value", metricColor = base.blue }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;
  return (
    <div style={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, color: "#fff", padding: "9px 11px", boxShadow: "0 14px 34px rgba(0,0,0,0.28)" }}>
      <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, fontWeight: 900, marginBottom: 6 }}>{tooltipLabel}</div>
      <div style={{ color: metricColor, fontSize: 12, fontWeight: 950 }}>{metricLabel}: {Number(value || 0).toLocaleString()}</div>
    </div>
  );
};

const MacroBar = ({ label: macroLabel, value, max, color, unit = "g" }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "58px 1fr 52px", gap: 8, alignItems: "center" }}>
      <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 }}>
        {macroLabel}
      </span>
      <div style={{ height: 6, background: "rgba(0,0,0,0.08)", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.6s cubic-bezier(0.34,1.56,0.64,1)" }} />
      </div>
      <span style={{ color: base.textDark, fontSize: 11, fontWeight: 900, textAlign: "right" }}>
        {Math.round(value).toLocaleString()}{unit}
      </span>
    </div>
  );
};

const InsightStatCard = ({ label: statLabel, value, sub, accent, icon }) => (
  <div style={{ border: `1px solid ${alpha(accent, 0.34)}`, background: "rgba(255,255,255,0.07)", borderRadius: 16, padding: 14, position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", inset: "auto 0 0 auto", width: 72, height: 72, borderRadius: "50%", background: alpha(accent, 0.18), transform: "translate(25%,25%)" }} />
    <div style={{ width: 30, height: 30, borderRadius: 10, background: alpha(accent, 0.3), color: "#FFFFFF", display: "grid", placeItems: "center", marginBottom: 10, position: "relative", zIndex: 1, boxShadow: `0 0 0 1px ${alpha(accent, 0.42)} inset` }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 14 }} />
    </div>
    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5, position: "relative", zIndex: 1 }}>{statLabel}</div>
    <div style={{ color: "#fff", fontSize: 25, fontWeight: 950, marginTop: 4, lineHeight: 1, position: "relative", zIndex: 1 }}>{value}</div>
    <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 10, fontWeight: 850, marginTop: 4, position: "relative", zIndex: 1 }}>{sub}</div>
  </div>
);

const formatMealSection = (value) => {
  const normalized = String(value || "other").replace(/[_-]+/g, " ").trim();
  if (!normalized) return "Other";
  return normalized.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
};

const nutritionMealRank = (value) => {
  const index = NUTRITION_PLAN_MEAL_ORDER.indexOf(formatMealSection(value));
  return index >= 0 ? index : NUTRITION_PLAN_MEAL_ORDER.length;
};

const sortNutritionPlanItems = (items = []) => (
  [...items].sort((a, b) => (
    nutritionMealRank(a.meal_section) - nutritionMealRank(b.meal_section)
    || Number(a.day_index || 0) - Number(b.day_index || 0)
    || Number(a.sort_order || 0) - Number(b.sort_order || 0)
    || String(a.food_name || "").localeCompare(String(b.food_name || ""))
  ))
);

const normalizeNutritionPlanDays = (value) => normalizeAssignedWorkoutDays(value);

const formatNutritionPlanDays = (value, fallback = "Any day") => {
  const days = normalizeNutritionPlanDays(value);
  if (!days.length) return fallback;
  return days.map((day) => ASSIGNED_WORKOUT_DAYS.find((item) => item.index === day)?.short).filter(Boolean).join(", ");
};

const isActiveNutritionAssignment = (assignment = {}) => (
  !assignment.status || assignment.status === "active" || assignment.status === "completed"
);

const displayProfileName = (profile = {}, fallback = "Athlete") => (
  [profile?.name, profile?.surname].filter(Boolean).join(" ").trim()
  || profile?.username
  || fallback
);

const firstRelationRow = (value) => Array.isArray(value) ? value[0] : value;

const mapDemoWorkoutMemberRow = (row = {}) => {
  const profile = firstRelationRow(row.demo_profiles) || {};
  const id = row.demo_profile_id || profile.id;
  return {
    ...row,
    id,
    athlete_id: id,
    demo_profile_id: id,
    is_demo_profile: true,
    profiles: {
      id,
      name: profile.name || "",
      surname: profile.surname || "",
      username: profile.username || "",
      profile_img: profile.profile_img || "",
      level: profile.level || 1,
      metadata: profile.metadata || {},
    },
  };
};

const isCountingWorkoutAthlete = (member = {}) => (
  member.counts_as_athlete !== false &&
  String(member.squad_role || "Athlete").toLowerCase() === "athlete"
);

const getCombinedWorkoutMembers = (group = {}) => {
  const sourceGroup = group || {};
  return [
    ...(Array.isArray(sourceGroup.workout_group_members) ? sourceGroup.workout_group_members : []),
    ...(Array.isArray(sourceGroup.demo_workout_group_members) ? sourceGroup.demo_workout_group_members.map(mapDemoWorkoutMemberRow) : []),
  ];
};

const mapDemoProfileToCalendarProfile = (profile = {}, fallbackId = "") => ({
  id: profile.id || fallbackId,
  name: profile.name || "",
  surname: profile.surname || "",
  username: profile.username || "",
  profile_img: profile.profile_img || "",
  level: profile.level || 1,
  metadata: profile.metadata || {},
});

const getCountingWorkoutMemberIds = (group = {}) => (
  getCombinedWorkoutMembers(group)
    .filter(isCountingWorkoutAthlete)
    .map((member) => member.athlete_id || member.demo_profile_id || member.profiles?.id)
    .filter(Boolean)
);

const mapDemoFoodLogRow = (row = {}) => ({ ...row, user_id: row.demo_profile_id });

const normalizeDemoWorkoutSession = (row = {}) => {
  const progressRows = Array.isArray(row.demo_exercise_progress) ? row.demo_exercise_progress : [];
  return {
    ...row,
    profile_id: row.demo_profile_id,
    user_workout_feedback: row.demo_user_workout_feedback || [],
    exercise_progress: progressRows,
    workout_exercise_summary: progressRows.map((progress) => ({
      ...progress,
      total_reps: Number(progress.total_reps ?? progress.reps ?? 0),
      avg_weight: Number(progress.avg_weight ?? progress.weight ?? 0),
      total_xp: Number(progress.total_xp ?? progress.xp_gained ?? 0),
      total_volume: Number(progress.total_volume ?? progress.volume ?? 0) || (Number(progress.weight || 0) * Number(progress.reps || 0)),
      exercises: progress.exercises,
    })),
  };
};

const mapDemoNutritionAssignment = (assignment = {}) => {
  const profile = firstRelationRow(assignment.demo_profiles) || {};
  const id = assignment.demo_profile_id || profile.id;
  return {
    ...assignment,
    athlete_id: id,
    demo_profile_id: id,
    is_demo_assignment: true,
    profiles: {
      id,
      name: profile.name || "",
      surname: profile.surname || "",
      username: profile.username || "",
      profile_img: profile.profile_img || "",
      level: profile.level || 1,
      metadata: profile.metadata || {},
    },
    nutrition_plan_progress: assignment.demo_nutrition_plan_progress || [],
    nutrition_plan_log_events: (assignment.demo_nutrition_plan_log_events || []).map((event) => ({
      ...event,
      athlete_id: event.demo_profile_id,
      food_log_ids: event.demo_food_log_ids || [],
      is_demo_event: true,
    })),
  };
};

const mergeDemoNutritionAssignments = (plan = {}) => ({
  ...plan,
  nutrition_plan_assignments: [
    ...(plan.nutrition_plan_assignments || []),
    ...(plan.demo_nutrition_plan_assignments || []).map(mapDemoNutritionAssignment),
  ],
});

const normalizeLoggedFoodName = (value) => String(value || "").replace(/\s+/g, " ").trim();

const buildFoodItemRows = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    const name = normalizeLoggedFoodName(row.food_name);
    if (!name) return;
    const key = name.toLowerCase();
    const current = map.get(key) || { name, count: 0, calories: 0, protein: 0, carbs: 0, fat: 0, athletes: new Set(), meals: new Map(), examples: new Set() };
    current.count += 1;
    current.calories += Number(row.calories || 0);
    current.protein += Number(row.protein_g || 0);
    current.carbs += Number(row.carbs_g || 0);
    current.fat += Number(row.fat_g || 0);
    if (row.user_id) current.athletes.add(row.user_id);
    const meal = formatMealSection(row.meal_section);
    current.meals.set(meal, (current.meals.get(meal) || 0) + 1);
    current.examples.add(name);
    map.set(key, current);
  });
  return Array.from(map.values()).map((item) => {
    const topMeal = Array.from(item.meals.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "Meals";
    return {
      ...item,
      athletes: item.athletes.size,
      topMeal,
      examples: Array.from(item.examples),
      avgCalories: item.count ? Math.round(item.calories / item.count) : 0,
    };
  }).sort((a, b) => b.count - a.count || b.calories - a.calories || a.name.localeCompare(b.name));
};

const getNutritionTargetStatus = (value, target, lower = 0.85, upper = 1.15) => {
  const numericValue = Number(value || 0);
  const numericTarget = Number(target || 0);
  if (!Number.isFinite(numericTarget) || numericTarget <= 0) return { label: "Track", color: base.muted, bg: base.bgAlt, pct: 0 };
  const ratio = numericValue / numericTarget;
  if (ratio < lower) return { label: "Low", color: "#D97706", bg: "#FFF7ED", pct: Math.min(100, Math.round(ratio * 100)) };
  if (ratio > upper) return { label: "High", color: base.danger, bg: "#FEF2F2", pct: Math.min(140, Math.round(ratio * 100)) };
  return { label: "On target", color: base.success, bg: "#F0FDF4", pct: Math.min(100, Math.round(ratio * 100)) };
};

const buildDashboardNutritionStory = (rows = [], members = [], targets = {}, expectedRangeDays = 1) => {
  const memberMap = new Map((members || []).filter((member) => member?.id).map((member) => [
    member.id,
    displayProfileName(member, "Athlete"),
  ]));
  const memberIds = new Set(memberMap.keys());
  const expectedDays = Math.max(1, memberIds.size * Math.max(1, Number(expectedRangeDays || 1)));
  const athleteMap = new Map(Array.from(memberMap.entries()).map(([id, name]) => [id, { id, name, dates: new Set(), calories: 0, items: 0 }]));
  const mealMap = new Map(NUTRITION_PLAN_MEAL_ORDER.map((meal) => [meal, { meal, count: 0, calories: 0, athletes: new Set() }]));
  const foodMap = new Map();
  const loggedKeys = new Set();
  const totals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0, sugar_g: 0, sodium_mg: 0, items: 0 };

  (rows || []).forEach((row) => {
    if (memberIds.size && !memberIds.has(row.user_id)) return;
    const date = String(row.log_date || "").slice(0, 10);
    const key = `${row.user_id || "athlete"}:${date}`;
    if (date) loggedKeys.add(key);
    const meal = formatMealSection(row.meal_section);
    totals.calories += Number(row.calories || 0);
    totals.protein_g += Number(row.protein_g || 0);
    totals.carbs_g += Number(row.carbs_g || 0);
    totals.fat_g += Number(row.fat_g || 0);
    totals.fiber_g += Number(row.fiber_g || 0);
    totals.sugar_g += Number(row.sugar_g || 0);
    totals.sodium_mg += Number(row.sodium_mg || 0);
    totals.items += 1;

    const athlete = athleteMap.get(row.user_id) || { id: row.user_id, name: memberMap.get(row.user_id) || "Athlete", dates: new Set(), calories: 0, items: 0 };
    if (date) athlete.dates.add(date);
    athlete.calories += Number(row.calories || 0);
    athlete.items += 1;
    athleteMap.set(row.user_id, athlete);

    const mealBucket = mealMap.get(meal) || { meal, count: 0, calories: 0, athletes: new Set() };
    mealBucket.count += 1;
    mealBucket.calories += Number(row.calories || 0);
    if (row.user_id) mealBucket.athletes.add(row.user_id);
    mealMap.set(meal, mealBucket);

    const foodName = normalizeLoggedFoodName(row.food_name);
    if (foodName) {
      const foodKey = foodName.toLowerCase();
      const food = foodMap.get(foodKey) || { name: foodName, count: 0, calories: 0, meals: new Map(), athletes: new Set() };
      food.count += 1;
      food.calories += Number(row.calories || 0);
      food.meals.set(meal, (food.meals.get(meal) || 0) + 1);
      if (row.user_id) food.athletes.add(row.user_id);
      foodMap.set(foodKey, food);
    }
  });

  const loggedDays = loggedKeys.size;
  const avg = (key) => loggedDays ? totals[key] / loggedDays : 0;
  const consistency = expectedDays ? (loggedDays / expectedDays) * 100 : 0;
  const macroRows = [
    { key: "calories", label: "Calories", icon: faFire, value: avg("calories"), target: targets.calories, unit: "kcal", color: analyticsGold },
    { key: "protein_g", label: "Protein", icon: faDumbbell, value: avg("protein_g"), target: targets.protein, unit: "g", color: base.success },
    { key: "carbs_g", label: "Carbs", icon: faBolt, value: avg("carbs_g"), target: targets.carbs, unit: "g", color: base.blue },
    { key: "fat_g", label: "Fat", icon: faUtensils, value: avg("fat_g"), target: targets.fat, unit: "g", color: "#7C3AED" },
  ].map((row) => ({ ...row, status: getNutritionTargetStatus(row.value, row.target) }));
  const mealRows = Array.from(mealMap.values()).map((meal) => ({
    ...meal,
    meta: getNutritionMealMeta(meal.meal),
    athletes: meal.athletes.size,
    avgCalories: meal.count ? meal.calories / meal.count : 0,
  }));
  const topFoods = Array.from(foodMap.values()).map((food) => ({
    ...food,
    athletes: food.athletes.size,
    avgCalories: food.count ? food.calories / food.count : 0,
    topMeal: Array.from(food.meals.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "Meals",
  })).sort((a, b) => b.count - a.count || b.calories - a.calories).slice(0, 5);
  const athleteRows = Array.from(athleteMap.values()).map((athlete) => {
    const logged = athlete.dates.size;
    const status = logged <= 0
      ? { label: "No logs", color: base.danger, bg: "#FEF2F2" }
      : logged < Math.max(2, Math.ceil(Number(expectedRangeDays || 1) * 0.35))
      ? { label: "Needs logs", color: "#D97706", bg: "#FFF7ED" }
      : { label: "Logging", color: base.success, bg: "#F0FDF4" };
    return { ...athlete, loggedDays: logged, avgCalories: logged ? athlete.calories / logged : 0, status };
  }).sort((a, b) => a.loggedDays - b.loggedDays || a.name.localeCompare(b.name));
  return { expectedDays, loggedDays, consistency, totals, macroRows, mealRows, topFoods, athleteRows };
};

const dateOnlyTime = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const toDateOnly = (value) => {
  if (!value) return null;
  const raw = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  const date = new Date(`${raw}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNutritionDueDateLabel = (value) => {
  const date = toDateOnly(value);
  if (!date) return String(value || "");
  return date.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" });
};

const getMondayFirstDayIndex = (date) => (date.getDay() + 6) % 7;

const getNutritionPlanDueDates = ({ plan = {}, assignment = {}, window = {} }) => {
  const scheduledDays = normalizeNutritionPlanDays(plan.scheduled_days);
  const today = toDateOnly(new Date()) || new Date();
  const start = toDateOnly(window.start) || toDateOnly(plan.start_date || today) || today;
  const end = toDateOnly(window.end) || today;
  if (start.getTime() > end.getTime()) return [];
  if (!scheduledDays.length) return [dateKey(end)];
  const dueDates = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    if (scheduledDays.includes(getMondayFirstDayIndex(cursor))) dueDates.push(dateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dueDates;
};

const inclusiveDays = (start, end) => {
  if (!start || !end) return 1;
  return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1);
};

const targetWeeksForWindow = (window, fallbackDays = 7) => {
  const days = Number(window?.days);
  if (Number.isFinite(days) && days > 0) return Math.max(1, Math.ceil(days / 7));
  if (window?.start && window?.end) return Math.max(1, Math.ceil(inclusiveDays(window.start, window.end) / 7));
  if (window?.start) return Math.max(1, Math.ceil(inclusiveDays(window.start, new Date()) / 7));
  return Math.max(1, Math.ceil(fallbackDays / 7));
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds || 0)));
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;
  return minutes > 0 ? `${minutes}m ${remaining}s` : `${remaining}s`;
};

const formatGroupVolume = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "0 kg";
  if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k kg`;
  return `${Math.round(numericValue).toLocaleString()} kg`;
};

const compactGroupNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";
  if (Math.abs(numericValue) >= 1000000) return `${(numericValue / 1000000).toFixed(1)}m`;
  if (Math.abs(numericValue) >= 1000) return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k`;
  return Math.round(numericValue).toLocaleString();
};

const getWorkoutSessionDateKey = (session = {}) => {
  if (session?.local_date) return String(session.local_date).slice(0, 10);
  return session?.started_at ? localDateInput(new Date(session.started_at)) : localDateInput(new Date());
};

const getWorkoutSessionTotals = (session = {}) => {
  const exerciseRows = Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : [];
  const exerciseReps = exerciseRows.reduce((sum, row) => sum + Number(row?.total_reps || row?.reps || 0), 0);
  const exerciseVolume = exerciseRows.reduce((sum, row) => {
    const reps = Number(row?.total_reps || row?.reps || 0);
    const savedVolume = Number(row?.total_volume ?? row?.volume);
    if (Number.isFinite(savedVolume)) return sum + savedVolume;
    return sum + (Number(row?.avg_weight || row?.weight || 0) * reps);
  }, 0);
  const exerciseSets = exerciseRows.reduce((sum, row) => {
    if (String(row?.logged_as || "set").toLowerCase() === "cardio") return sum;
    if (row?.set === false) return sum;
    return sum + 1;
  }, 0);
  const workoutContext = Array.isArray(session.workout_context) ? session.workout_context[0] : session.workout_context;
  const sessionSets = Number(session.total_sets || 0);
  const sessionReps = Number(session.total_reps || 0);
  const sessionVolume = Number(session.total_volume || 0);
  return {
    totalXp: Number(session.total_xp || 0),
    totalSets: sessionSets > 0 ? sessionSets : exerciseSets,
    totalReps: sessionReps > 0 ? sessionReps : exerciseReps,
    totalVolume: sessionVolume > 0 ? sessionVolume : exerciseVolume,
    durationSeconds: Number(session.duration_seconds || 0),
    totalRestSeconds: Number(session.total_rest_seconds ?? workoutContext?.rest_timer_usage ?? 0),
    effort: Number(session.session_effort || 0),
  };
};

const parseWorkoutGroupLocalDateKey = (key = "") => {
  const [year, month, day] = String(key || "").slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? date : null;
};

const startOfWorkoutGroupDay = (date = new Date()) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const startOfWorkoutGroupWeek = (date = new Date()) => {
  const next = startOfWorkoutGroupDay(date);
  const day = next.getDay();
  next.setDate(next.getDate() - (day === 0 ? 6 : day - 1));
  return next;
};

const addWorkoutGroupDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatWorkoutGroupTrendDate = (date) => date?.toLocaleDateString("en-IE", { month: "short", day: "numeric" }) || "";

const makeWorkoutGroupTrendBucket = (key, rangeDays) => {
  const date = parseWorkoutGroupLocalDateKey(key);
  return {
    key,
    label: date
      ? Number(rangeDays || 7) <= 7
        ? date.toLocaleDateString("en-IE", { weekday: "short" })
        : formatWorkoutGroupTrendDate(date)
      : key,
    sessions: 0,
    totalXp: 0,
    totalSets: 0,
    totalReps: 0,
    totalVolume: 0,
  };
};

const getWorkoutGroupTrendBucketKey = (date, rangeDays = 7) =>
  Number(rangeDays || 7) > 31
    ? localDateInput(startOfWorkoutGroupWeek(date))
    : localDateInput(startOfWorkoutGroupDay(date));

const addWorkoutGroupSessionToTrendBucket = (bucket, session) => {
  const totals = getWorkoutSessionTotals(session);
  bucket.sessions += 1;
  bucket.totalXp += totals.totalXp;
  bucket.totalSets += totals.totalSets;
  bucket.totalReps += totals.totalReps;
  bucket.totalVolume += totals.totalVolume;
};

const buildWorkoutGroupTrendRows = (sessions = [], rangeDays = 7) => {
  const numericRange = Number(rangeDays || 7);
  const today = startOfWorkoutGroupDay(new Date());
  const byKey = new Map();

  if (rangeDays === 0) {
    const datedSessions = (sessions || [])
      .map((session) => ({ session, date: parseWorkoutGroupLocalDateKey(getWorkoutSessionDateKey(session)) }))
      .filter((item) => item.date);
    if (!datedSessions.length) return [];
    const sortedDates = datedSessions.map((item) => item.date).sort((a, b) => a.getTime() - b.getTime());
    const spanDays = Math.max(1, Math.round((sortedDates[sortedDates.length - 1].getTime() - sortedDates[0].getTime()) / 86400000) + 1);
    const bucketRange = spanDays > 31 ? 90 : Math.max(7, spanDays);
    datedSessions.forEach(({ session, date }) => {
      const key = getWorkoutGroupTrendBucketKey(date, bucketRange);
      if (!byKey.has(key)) byKey.set(key, makeWorkoutGroupTrendBucket(key, bucketRange));
      addWorkoutGroupSessionToTrendBucket(byKey.get(key), session);
    });
    return Array.from(byKey.values()).sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }

  const days = Math.max(numericRange || 7, 1);
  const start = addWorkoutGroupDays(today, -(days - 1));
  const weekly = days > 31;
  const cursorStart = weekly ? startOfWorkoutGroupWeek(start) : startOfWorkoutGroupDay(start);
  const cursorEnd = weekly ? startOfWorkoutGroupWeek(today) : today;
  const buckets = [];
  for (let cursor = cursorStart; cursor.getTime() <= cursorEnd.getTime(); cursor = addWorkoutGroupDays(cursor, weekly ? 7 : 1)) {
    const key = localDateInput(cursor);
    const row = makeWorkoutGroupTrendBucket(key, days);
    buckets.push(row);
    byKey.set(key, row);
  }
  (sessions || []).forEach((session) => {
    const date = parseWorkoutGroupLocalDateKey(getWorkoutSessionDateKey(session));
    if (!date || date.getTime() < start.getTime() || date.getTime() > today.getTime()) return;
    const bucket = byKey.get(getWorkoutGroupTrendBucketKey(date, days));
    if (bucket) addWorkoutGroupSessionToTrendBucket(bucket, session);
  });
  return buckets;
};

const normalizeWorkoutGroupAnalyticsKey = (value = "") => String(value || "").trim().toLowerCase();

const getWorkoutGroupSessionWorkoutMeta = (session = {}) => {
  const customWorkout = Array.isArray(session.user_workouts) ? session.user_workouts[0] : session.user_workouts;
  const presetWorkout = Array.isArray(session.workouts) ? session.workouts[0] : session.workouts;
  const name = customWorkout?.name || presetWorkout?.name || session.workout_name || "Unnamed workout";
  const key = session.workout_plan_id
    ? `custom:${session.workout_plan_id}`
    : session.preset_plan_id
      ? `preset:${session.preset_plan_id}`
      : `name:${normalizeWorkoutGroupAnalyticsKey(name) || "unknown"}`;
  return { key, label: name };
};

const getWorkoutGroupExerciseMeta = (row = {}, fallbackIndex = 0) => {
  const exercise = Array.isArray(row.exercises) ? row.exercises[0] : row.exercises;
  const name = exercise?.name || `Exercise ${fallbackIndex + 1}`;
  return {
    id: String(exercise?.id || row.exercise_id || name).trim(),
    name,
    image_url: exercise?.image_url || null,
    gif_url: exercise?.gif_url || null,
  };
};

const buildWorkoutGroupWorkoutTargets = (sessions = []) => {
  const workoutMap = new Map();
  const ensureWorkout = (session) => {
    const meta = getWorkoutGroupSessionWorkoutMeta(session);
    if (!workoutMap.has(meta.key)) {
      workoutMap.set(meta.key, {
        key: meta.key,
        label: meta.label,
        total_sets: 0,
        total_reps: 0,
        total_volume: 0,
        total_xp: 0,
        sessions_count: 0,
        sessionIds: new Set(),
        trendSessions: [],
        exerciseMap: new Map(),
      });
    }
    return workoutMap.get(meta.key);
  };

  (sessions || []).forEach((session) => {
    const sessionId = String(session?.id || "").trim();
    const workout = ensureWorkout(session);
    const totals = getWorkoutSessionTotals(session);
    workout.total_sets += totals.totalSets;
    workout.total_reps += totals.totalReps;
    workout.total_volume += totals.totalVolume;
    workout.total_xp += totals.totalXp;
    workout.trendSessions.push(session);
    if (sessionId) workout.sessionIds.add(sessionId);
    workout.sessions_count = workout.sessionIds.size || workout.trendSessions.length;

    (Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : []).forEach((row, index) => {
      const meta = getWorkoutGroupExerciseMeta(row, index);
      const exerciseKey = meta.id || `${workout.key}:${meta.name}`;
      if (!workout.exerciseMap.has(exerciseKey)) {
        workout.exerciseMap.set(exerciseKey, {
          id: exerciseKey,
          name: meta.name,
          image_url: meta.image_url,
          gif_url: meta.gif_url,
          total_sets: 0,
          total_reps: 0,
          total_volume: 0,
          total_xp: 0,
          sessions_count: 0,
          sessionIds: new Set(),
          trendSessions: [],
        });
      }
      const exercise = workout.exerciseMap.get(exerciseKey);
      const reps = Number(row.total_reps || 0);
      const avgWeight = Number(row.avg_weight || 0);
      const sets = Number(row.total_sets || 0);
      const volume = Number(row.total_volume || 0) || (avgWeight * reps);
      const xp = Number(row.total_xp || 0);
      exercise.total_sets += sets;
      exercise.total_reps += reps;
      exercise.total_volume += volume;
      exercise.total_xp += xp;
      if (sessionId) exercise.sessionIds.add(sessionId);
      exercise.sessions_count = exercise.sessionIds.size || exercise.sessions_count + 1;
      exercise.trendSessions.push({
        ...session,
        total_sets: sets,
        total_reps: reps,
        total_volume: volume,
        total_xp: xp,
        workout_exercise_summary: [],
      });
    });
  });

  return Array.from(workoutMap.values())
    .map((workout) => ({
      ...workout,
      exercises: Array.from(workout.exerciseMap.values())
        .map((exercise) => ({ ...exercise, sessionIds: undefined }))
        .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps) || String(a.name).localeCompare(String(b.name))),
      sessionIds: undefined,
      exerciseMap: undefined,
    }))
    .filter((workout) => workout.exercises.length > 0 || workout.total_reps > 0)
    .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps) || String(a.label).localeCompare(String(b.label)));
};

const getWGTrendMetricUnit = (metric, value) => {
  const count = Math.round(Number(value || 0));
  if (metric === "sessions") return count === 1 ? "session" : "sessions";
  if (metric === "totalReps") return count === 1 ? "rep" : "reps";
  if (metric === "totalSets") return count === 1 ? "set" : "sets";
  if (metric === "totalVolume") return "volume";
  return "";
};

const formatWGTrendRangeLabel = (rows = []) => {
  const first = parseWorkoutGroupLocalDateKey(rows[0]?.key);
  const last = parseWorkoutGroupLocalDateKey(rows[rows.length - 1]?.key);
  const firstLabel = formatWorkoutGroupTrendDate(first);
  const lastLabel = formatWorkoutGroupTrendDate(last);
  if (!firstLabel && !lastLabel) return "";
  if (!lastLabel || firstLabel === lastLabel) return firstLabel;
  return `${firstLabel} - ${lastLabel}`;
};

const WGTrendBars = ({ rows = [], metric = "sessions", color = "#0C69C8", height = 126 }) => {
  const values = rows.map((row) => Number(row?.[metric] || 0));
  const max = Math.max(...values, 1);
  const hasValues = values.some((value) => value > 0);
  const useLineTrend = rows.length > 7;
  if (useLineTrend) {
    const chartHeight = Math.max(72, height - 44);
    const chartWidth = 320;
    const padX = 10;
    const padY = 10;
    const usableWidth = chartWidth - padX * 2;
    const usableHeight = chartHeight - padY * 2;
    const points = values.map((value, index) => ({
      value,
      x: values.length <= 1 ? chartWidth / 2 : padX + (index / (values.length - 1)) * usableWidth,
      y: chartHeight - padY - (value / max) * usableHeight,
    }));
    const path = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(" ");
    const peak = points.reduce((best, point) => point.value > best.value ? point : best, { x: 0, y: 0, value: 0 });
    const latest = points[points.length - 1] || null;
    const metricUnit = getWGTrendMetricUnit(metric, peak.value);
    return (
      <div role="img" aria-label={`${metric} trend for ${formatWGTrendRangeLabel(rows) || "selected range"}`} style={{ minHeight: height, display: "grid", alignContent: "center", gap: 7, paddingTop: 10 }}>
        {!hasValues ? (
          <div style={{ width: "100%", alignSelf: "center", textAlign: "center", color: "#64748B", fontSize: 12, fontWeight: 850 }}>No trend breakdown available</div>
        ) : (
          <>
            <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none" aria-hidden="true">
              <path d={`M ${padX} ${chartHeight - padY} L ${chartWidth - padX} ${chartHeight - padY}`} stroke="#DDE7F6" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d={path} stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" vectorEffect="non-scaling-stroke" />
              {peak.value > 0 && <circle cx={peak.x} cy={peak.y} r="4.5" fill="#FFFFFF" stroke={color} strokeWidth="3" vectorEffect="non-scaling-stroke" />}
              {latest?.value > 0 && <circle cx={latest.x} cy={latest.y} r="4" fill={color} />}
            </svg>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", color: "#64748B", fontSize: 10, fontWeight: 900 }}>
              <span>{formatWGTrendRangeLabel(rows)}</span>
              <span style={{ color }}>{`Peak ${compactGroupNumber(peak.value)}${metricUnit ? ` ${metricUnit}` : ""}`}</span>
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <div style={{ minHeight: height, display: "flex", alignItems: "flex-end", gap: 7, paddingTop: 12 }}>
      {!hasValues ? (
        <div style={{ width: "100%", alignSelf: "center", textAlign: "center", color: "#64748B", fontSize: 12, fontWeight: 850 }}>No trend breakdown available</div>
      ) : rows.map((row, index) => {
        const value = Number(row?.[metric] || 0);
        const barHeight = value > 0 ? Math.max(12, Math.round((value / max) * (height - 52))) : 5;
        return (
          <div key={row.key || index} style={{ flex: 1, minWidth: 0, display: "grid", justifyItems: "center", alignItems: "end", gap: 5 }}>
            <div style={{ color: value > 0 ? "#0D275B" : "#94A3B8", fontSize: 10, fontWeight: 950, minHeight: 12, width: "100%", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {compactGroupNumber(value)}
            </div>
            <div style={{ width: "66%", maxWidth: 34, height: barHeight, borderRadius: 10, background: value > 0 ? color : "#E8EEF8" }} />
            <div style={{ color: "#64748B", fontSize: 10, fontWeight: 900, width: "100%", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const localDateInput = (date = new Date()) => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const formatDateFilterLabel = (value) => {
  const date = toDateOnly(value);
  return date ? date.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Selected date";
};

const localTimeInput = (date = new Date()) => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(11, 16);
};

const buildLocalDateTime = (date, time) => {
  if (!date || !time) return null;
  const value = new Date(`${date}T${time}:00`);
  return Number.isNaN(value.getTime()) ? null : value;
};

const formatCalendarRange = (startsAt, endsAt) => {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return "Time not set";
  const dateText = start.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" });
  const startText = start.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" });
  if (!endsAt) return `${dateText} · ${startText}`;
  const end = new Date(endsAt);
  const endText = Number.isNaN(end.getTime()) ? "" : ` - ${end.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}`;
  return `${dateText} · ${startText}${endText}`;
};

const formatCalendarDateTimeParts = (startsAt, endsAt) => {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return { date: "Date not set", time: "Time not set" };
  const date = start.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" });
  const startTime = start.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" });
  const end = endsAt ? new Date(endsAt) : null;
  const endTime = end && !Number.isNaN(end.getTime())
    ? end.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })
    : "";
  return { date, time: endTime ? `${startTime}-${endTime}` : startTime };
};

const isExpoPushToken = (token) => /^Expo(nent)?PushToken\[[^\]]+\]$/.test(String(token || ""));

const createDefaultCalendarForm = (groupId = "") => {
  const start = new Date();
  start.setHours(Math.max(7, start.getHours() + 1), 0, 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    groupId,
    title: "Group training",
    date: localDateInput(start),
    startTime: localTimeInput(start),
    hasEndTime: true,
    endTime: localTimeInput(end),
    location: "",
    notes: "",
    inviteAll: true,
    selectedAthleteIds: [],
    coachIds: [],
  };
};

const uniqueIds = (values = []) => Array.from(new Set(values.filter(Boolean).map((value) => String(value))));

const calendarProfileName = (profile = {}) => (
  [profile.name, profile.surname].filter(Boolean).join(" ") || profile.username || "Athlete"
);

const CLUB_CALENDAR_FILTERS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" },
  { value: "cancelled", label: "Cancelled" },
];

const calendarDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return localDateInput(date);
};

const buildCalendarDayChips = (sessions = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const counts = sessions.reduce((map, session) => {
    if (session?.status === "cancelled") return map;
    const key = calendarDateKey(session.starts_at);
    if (!key) return map;
    map.set(key, (map.get(key) || 0) + 1);
    return map;
  }, new Map());
  return Array.from({ length: 14 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index);
    const key = localDateInput(date);
    return {
      key,
      day: index === 0 ? "Today" : date.toLocaleDateString("en-IE", { weekday: "short" }),
      date: date.toLocaleDateString("en-IE", { day: "numeric" }),
      label: date.toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long" }),
      count: counts.get(key) || 0,
    };
  });
};

const getCalendarAttendanceSummary = (session = {}) => {
  const athletes = Array.isArray(session.athletes) ? session.athletes : [];
  return {
    invited: athletes.length,
    accepted: athletes.filter((row) => row.status === "accepted").length,
    declined: athletes.filter((row) => row.status === "declined").length,
    pending: athletes.filter((row) => !row.status || row.status === "pending").length,
  };
};

const groupCalendarSessionsByDate = (sessions = []) => {
  const map = new Map();
  sessions.forEach((session) => {
    const start = new Date(session.starts_at);
    const key = Number.isNaN(start.getTime()) ? "unknown" : localDateInput(start);
    const title = Number.isNaN(start.getTime())
      ? "Time not set"
      : start.toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long" });
    const current = map.get(key) || { key, title, sessions: [] };
    current.sessions.push(session);
    map.set(key, current);
  });
  return Array.from(map.values());
};

const pctDelta = (current, previous) => {
  const a = Number(current || 0);
  const b = Number(previous || 0);
  if (!b && !a) return "0%";
  if (!b) return "+100%";
  const value = Math.round(((a - b) / b) * 100);
  return `${value > 0 ? "+" : ""}${value}%`;
};

const compareTone = (current, previous) => {
  if (Number(current || 0) > Number(previous || 0)) return base.success;
  if (Number(current || 0) < Number(previous || 0)) return base.danger;
  return base.muted;
};

const pill = (tone = "neutral") => {
  const styles = {
    success: { background: "#DCFCE7", borderColor: "#86EFAC", color: "#166534" },
    warning: { background: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" },
    danger: { background: "#FEE2E2", borderColor: "#FCA5A5", color: "#991B1B" },
    info: { background: "#E6F0FF", borderColor: "#BFDBFE", color: "#0C69C8" },
    neutral: { background: "#F1F5F9", borderColor: "#CBD5E1", color: "#475569" },
  };
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    border: "1px solid",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
    ...styles[tone],
  };
};

const challengePlaceLabel = (place) => {
  const n = Number(place || 0);
  if (!n) return "-";
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
};

const ClubManageLabel = ({ children }) => (
  <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7, margin: "16px 0 8px" }}>
    {children}
  </div>
);

const ClubManageOption = ({ icon, title, subtitle, onClick, muted = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      minHeight: 66,
      border: `1px solid ${muted ? "#CBD5E1" : base.border}`,
      background: muted ? "#F1F5F9" : "#FFFFFF",
      color: muted ? "#94A3B8" : base.text,
      borderRadius: 16,
      padding: 12,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 12,
      textAlign: "left",
    }}
  >
    <span style={{ width: 40, height: 40, borderRadius: 14, background: muted ? "#E2E8F0" : "#E6F0FF", color: muted ? "#94A3B8" : base.blue, display: "grid", placeItems: "center", flexShrink: 0 }}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <span style={{ minWidth: 0, flex: 1 }}>
      <span style={{ display: "block", fontSize: 14, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
      <span style={{ display: "block", color: muted ? "#94A3B8" : base.muted, fontSize: 12, fontWeight: 820, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</span>
    </span>
  </button>
);

const ClubManageTileButton = ({ icon, title, subtitle, onClick, muted = false, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: 78,
      border: `1px solid ${danger ? "#FECACA" : muted ? "#CBD5E1" : base.border}`,
      background: danger ? "#FEF2F2" : muted ? "#F1F5F9" : "#FFFFFF",
      color: danger ? "#991B1B" : muted ? "#94A3B8" : base.text,
      borderRadius: 16,
      padding: 12,
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
      gap: 5,
      textAlign: "center",
    }}
  >
    <FontAwesomeIcon icon={icon} />
    <span style={{ fontSize: 13, fontWeight: 950 }}>{title}</span>
    {subtitle && <span style={{ color: danger ? "#B91C1C" : muted ? "#94A3B8" : base.muted, fontSize: 11, fontWeight: 800 }}>{subtitle}</span>}
  </button>
);

export default function WorkoutClubDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState(null);
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signInError, setSignInError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [access, setAccess] = useState(getDefaultWorkoutGroupAccess());
  const [currentProfile, setCurrentProfile] = useState(null);
  const [clubOwnerAccessById, setClubOwnerAccessById] = useState({});
  const [clubs, setClubs] = useState([]);
  const [activeClubId, setActiveClubId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [calendarSessions, setCalendarSessions] = useState([]);
  const [calendarForm, setCalendarForm] = useState(() => createDefaultCalendarForm());
  const [calendarEditingSession, setCalendarEditingSession] = useState(null);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarCoachesRefreshing, setCalendarCoachesRefreshing] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [calendarFormOpen, setCalendarFormOpen] = useState(false);
  const [calendarFilter, setCalendarFilter] = useState("upcoming");
  const [calendarSelectedDateKey, setCalendarSelectedDateKey] = useState(null);
  const [calendarSelectedSessionId, setCalendarSelectedSessionId] = useState(null);
  const [joinCodeShare, setJoinCodeShare] = useState(null);
  const [nutritionRows, setNutritionRows] = useState([]);
  const [nutritionPlans, setNutritionPlans] = useState([]);
  const [nutritionPlanForm, setNutritionPlanForm] = useState(EMPTY_NUTRITION_PLAN_FORM);
  const [editingNutritionPlanId, setEditingNutritionPlanId] = useState(null);
  const [editingNutritionPlanItemIndex, setEditingNutritionPlanItemIndex] = useState(null);
  const [nutritionPlanAthleteSearch, setNutritionPlanAthleteSearch] = useState("");
  const [nutritionPlanSaving, setNutritionPlanSaving] = useState(false);
  const [nutritionCollapsed, setNutritionCollapsed] = useState(false);
  const [nutritionPlanEditorOpen, setNutritionPlanEditorOpen] = useState(false);
  const [nutritionAthleteCardsExpanded, setNutritionAthleteCardsExpanded] = useState(false);
  const [nutritionPlanPage, setNutritionPlanPage] = useState(0);
  const [nutritionPlanAthletesExpandedById, setNutritionPlanAthletesExpandedById] = useState({});
  const [nutritionGroupFilter, setNutritionGroupFilter] = useState("all");
  const [nutritionAthleteFilter, setNutritionAthleteFilter] = useState("all");
  const [nutritionRangeId, setNutritionRangeId] = useState("last_7");
  const [nutritionCustomDays, setNutritionCustomDays] = useState("30");
  const [nutritionSelectedDate, setNutritionSelectedDate] = useState(() => localDateInput(new Date()));
  const [activeSeasonId, setActiveSeasonId] = useState("last_7");
  const [analyticsSelectedDate, setAnalyticsSelectedDate] = useState(() => localDateInput(new Date()));
  const [compareSeasonAId, setCompareSeasonAId] = useState("");
  const [compareSeasonBId, setCompareSeasonBId] = useState("");
  const [compareGroupAId, setCompareGroupAId] = useState("all");
  const [compareGroupBId, setCompareGroupBId] = useState("all");
  const [compareAthleteAId, setCompareAthleteAId] = useState("all");
  const [compareAthleteBId, setCompareAthleteBId] = useState("all");
  const [compareAthleteId, setCompareAthleteId] = useState("all");
  const [compareDrilldownGroupId, setCompareDrilldownGroupId] = useState("all");
  const [compareMetric, setCompareMetric] = useState("xp");
  const [clubGroupChartMetric, setClubGroupChartMetric] = useState("xp");
  const [trainingTrendMetric, setTrainingTrendMetric] = useState("xp");
  const [customDays, setCustomDays] = useState("90");
  const [clubForm, setClubForm] = useState({ name: "", sport_id: "", logo_url: "", ...DEFAULT_COLORS, theme_workout_groups: false, theme_workouts: false, theme_nutrition: false, theme_sessions: false });
  const [clubLogoUrlInput, setClubLogoUrlInput] = useState("");
  const [showClubLogoUrlInput, setShowClubLogoUrlInput] = useState(false);
  const [clubLogoUploading, setClubLogoUploading] = useState(false);
  const [coachUsername, setCoachUsername] = useState("");
  const [coachRole, setCoachRole] = useState("coach");
  const [coachSaving, setCoachSaving] = useState(false);
  const [staffRoleSavingId, setStaffRoleSavingId] = useState(null);
  const [seasonForm, setSeasonForm] = useState({ name: "", sport_id: "", starts_on: "", ends_on: "", status: "active" });
  const [attachGroupId, setAttachGroupId] = useState("");
  const [editingSeason, setEditingSeason] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const realtimeRefreshTimerRef = useRef(null);
  const [toolView, setToolView] = useState("brand");
  const [athleteModal, setAthleteModal] = useState(null);
  const [athleteNoteText, setAthleteNoteText] = useState("");
  const [athleteNoteSaving, setAthleteNoteSaving] = useState(false);
  const [groupAnalyticsModalOpen, setGroupAnalyticsModalOpen] = useState(false);
  const [groupAnalyticsAthleteId, setGroupAnalyticsAthleteId] = useState("all");
  const [showAllGroupAnalyticsAthletes, setShowAllGroupAnalyticsAthletes] = useState(false);
  const [groupTrendMetric, setGroupTrendMetric] = useState("sessions");
  const [groupWorkoutTrendMetric, setGroupWorkoutTrendMetric] = useState("totalReps");
  const [selectedGroupWorkoutKey, setSelectedGroupWorkoutKey] = useState("");
  const [selectedGroupExerciseId, setSelectedGroupExerciseId] = useState("");
  const [athleteTrendMetric, setAthleteTrendMetric] = useState("totalVolume");
  const [expandedAthleteSessionId, setExpandedAthleteSessionId] = useState(null);
  const [groupManageModal, setGroupManageModal] = useState(null);
  const [groupEditModal, setGroupEditModal] = useState(null);
  const [groupEditValue, setGroupEditValue] = useState("");
  const [groupNutritionDraft, setGroupNutritionDraft] = useState({ calories: "", protein: "", carbs: "", fat: "" });
  const [groupEditSaving, setGroupEditSaving] = useState(false);
  const [groupVisibilitySaving, setGroupVisibilitySaving] = useState(false);
  const [groupCoachSaving, setGroupCoachSaving] = useState(false);
  const [allowedWorkoutsModalGroup, setAllowedWorkoutsModalGroup] = useState(null);
  const [allowedWorkoutSearch, setAllowedWorkoutSearch] = useState("");
  const [coachWorkouts, setCoachWorkouts] = useState([]);
  const [coachWorkoutsLoading, setCoachWorkoutsLoading] = useState(false);
  const [allowedWorkoutsSaving, setAllowedWorkoutsSaving] = useState(false);
  const [savingClub, setSavingClub] = useState(false);
  const [deletingClub, setDeletingClub] = useState(false);
  const activeClubStorageKey = profileId ? `snows-proam-workout-club-dashboard-active:${profileId}` : null;
  const clubLogoFileInputRef = useRef(null);
  const calendarCoachSelectionTouchedRef = useRef(false);
  const nutritionDefaultedClubRef = useRef("");
  const focusedDashboardGroupIdRef = useRef(null);

  const activeClub = useMemo(() => activeClubId === "__new__" ? null : (clubs.find((club) => club.id === activeClubId) || clubs[0] || null), [clubs, activeClubId]);
  const selectedGroupId = searchParams.get("group_id") || "";
  const requestedClubId = searchParams.get("club_id") || "";
  const hasTeamClubAccess = TEAM_ENTITLEMENT_KEYS.some((key) => hasWorkoutGroupEntitlement(access, key)) || hasWorkoutGroupTier(access, "team_club");
  const isActiveClubStaff = !!(activeClub?.id && clubs.some((club) => club.id === activeClub.id));
  const hasAccess = hasTeamClubAccess || clubs.length > 0;
  const hasSeasonManagementAccess = hasWorkoutGroupEntitlement(access, "workout_groups.season_management") || hasWorkoutGroupTier(access, "team_club") || isActiveClubStaff;
  const activeClubOwnerAccess = activeClub?.owner_profile_id === profileId ? access : clubOwnerAccessById[activeClub?.owner_profile_id];
  const activeFeatureAccess = activeClubOwnerAccess || access;
  const hasNutritionAccess = hasWorkoutGroupTier(activeFeatureAccess, "pro_coach") || hasWorkoutGroupEntitlement(activeFeatureAccess, "workout_groups.club_analysis");
  const canUseClubCalendar = hasWorkoutGroupTier(activeFeatureAccess, "pro_coach") || hasWorkoutGroupTier(activeFeatureAccess, "team_club") || hasWorkoutGroupEntitlement(activeFeatureAccess, "workout_groups.calendar_sessions");
  const canUseWorkoutShareCodes = hasWorkoutGroupTier(activeFeatureAccess, "coach_starter") || hasWorkoutGroupEntitlement(activeFeatureAccess, "workout_groups.workout_share_codes");
  const canUseClubBranding = hasWorkoutGroupTier(activeFeatureAccess, "team_club") || hasWorkoutGroupEntitlement(activeFeatureAccess, "workout_groups.branding");
  const brandToggleAccess = useMemo(() => ({
    theme_workout_groups: {
      allowed: canUseClubBranding,
      message: "Club branding is included with Club Coaching.",
    },
    theme_workouts: {
      allowed: canUseClubBranding,
      message: "Workout branding is included with Club Coaching.",
    },
    theme_nutrition: {
      allowed: canUseClubBranding && hasNutritionAccess,
      message: hasNutritionAccess ? "Calorie Tracker branding is included with Club Coaching." : "Calorie Tracker branding needs Club Analysis access.",
    },
    theme_sessions: {
      allowed: canUseClubBranding && canUseClubCalendar,
      message: canUseClubCalendar ? "Workout Sessions branding is included with Club Coaching." : "Workout Sessions branding needs Calendar Sessions access.",
    },
  }), [canUseClubBranding, hasNutritionAccess, canUseClubCalendar]);
  const isBrandToggleAllowed = useCallback((key) => brandToggleAccess[key]?.allowed === true, [brandToggleAccess]);
  const canUseClubExtendedRanges = !!(
    hasWorkoutGroupEntitlement(activeClubOwnerAccess, "workout_groups.range_2w_4w") ||
    hasWorkoutGroupTier(activeClubOwnerAccess, "coach_starter")
  );
  const canManageSelectedClub = !!activeClub?.id;
  const canDeleteActiveClub = !!(activeClub?.id && activeClub.owner_profile_id === profileId);
  const canManageClubStaff = canDeleteActiveClub;
  const hasExistingClub = clubs.length > 0;
  const canCreateNewClub = hasTeamClubAccess && !hasExistingClub;
  const canSaveClubBrand = activeClub?.id ? canManageSelectedClub : canCreateNewClub;
  const clubBrandSaveReason = !canSaveClubBrand
    ? hasExistingClub
      ? "Choose an existing club to edit its branding."
      : "Club Coaching is required to create or edit club branding."
    : !clubForm.name.trim()
    ? "Enter a club name before saving."
    : "";
  const clubBrandSaveDisabled = savingClub || clubLogoUploading || !!clubBrandSaveReason;
  const activeSeason = useMemo(() => seasons.find((season) => season.id === activeSeasonId) || null, [seasons, activeSeasonId]);
  const activeNutritionSeason = useMemo(() => seasons.find((season) => season.id === nutritionRangeId) || null, [seasons, nutritionRangeId]);
  const selectedGroupAttachedHere = !!selectedGroup?.id && selectedGroup.club_id === activeClub?.id;
  const profileReturnUrl = `/profile?tab=workouts${selectedGroupId ? `&group_id=${encodeURIComponent(selectedGroupId)}` : ""}`;
  const notify = useCallback((message, tone = "info") => {
    setNotice({ id: Date.now(), message, tone });
  }, []);
  const handleInlineSignIn = useCallback(async (event) => {
    event.preventDefault();
    setSignInError("");
    if (!signInEmail.trim() || !signInPassword.trim()) {
      setSignInError("Enter your email and password.");
      return;
    }
    setSigningIn(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: signInEmail.trim(),
      password: signInPassword,
    });
    setSigningIn(false);
    if (error) {
      setSignInError(error.message || "Could not sign in.");
      return;
    }
  }, [signInEmail, signInPassword]);
  const selectAnalyticsRange = useCallback((value) => {
    if (EXTENDED_ANALYTICS_RANGE_VALUES.has(value) && !canUseClubExtendedRanges) {
      notify("This analytics range is not available for this club dashboard. The club owner needs Coach Starter or higher.", "warning");
      return;
    }
    setActiveSeasonId(value);
  }, [canUseClubExtendedRanges, notify]);
  const selectNutritionRange = useCallback((value) => {
    if (EXTENDED_ANALYTICS_RANGE_VALUES.has(value) && !canUseClubExtendedRanges) {
      notify("This nutrition range is not available for this club dashboard. The club owner needs Coach Starter or higher.", "warning");
      return;
    }
    setNutritionRangeId(value);
  }, [canUseClubExtendedRanges, notify]);
  const handleClubBrandToggle = useCallback((key, checked) => {
    if (checked && !isBrandToggleAllowed(key)) {
      setClubForm((prev) => ({ ...prev, [key]: false }));
      notify(brandToggleAccess[key]?.message || "This branding option is not included in the current plan.", "warning");
      return;
    }
    setClubForm((prev) => ({ ...prev, [key]: checked }));
  }, [brandToggleAccess, isBrandToggleAllowed, notify]);
  const buildClubPayload = (form, club, logoValue) => ({
    name: String(form?.name || "").trim(),
    sport_id: String(form?.sport_id || "").trim() || null,
    logo_url: String(logoValue ?? form?.logo_url ?? "").trim() || null,
    primary_color: form?.primary_color || DEFAULT_COLORS.primary_color,
    secondary_color: form?.secondary_color || DEFAULT_COLORS.secondary_color,
    accent_color: form?.accent_color || DEFAULT_COLORS.accent_color,
    brand_settings: {
      ...(club?.brand_settings || {}),
      theme_workout_groups: form?.theme_workout_groups === true && isBrandToggleAllowed("theme_workout_groups"),
      theme_workouts: form?.theme_workouts === true && isBrandToggleAllowed("theme_workouts"),
      theme_nutrition: form?.theme_nutrition === true && isBrandToggleAllowed("theme_nutrition"),
      theme_sessions: form?.theme_sessions === true && isBrandToggleAllowed("theme_sessions"),
    },
  });

  useEffect(() => {
    if (!activeClubOwnerAccess) return;
    if (EXTENDED_ANALYTICS_RANGE_VALUES.has(activeSeasonId) && !canUseClubExtendedRanges) setActiveSeasonId("last_7");
    if (EXTENDED_ANALYTICS_RANGE_VALUES.has(nutritionRangeId) && !canUseClubExtendedRanges) setNutritionRangeId("last_7");
  }, [activeSeasonId, nutritionRangeId, activeClubOwnerAccess, canUseClubExtendedRanges]);

  useEffect(() => {
    setClubForm((prev) => {
      const next = { ...prev };
      let changed = false;
      BRAND_TOGGLE_ITEMS.forEach(({ key }) => {
        if (next[key] === true && !isBrandToggleAllowed(key)) {
          next[key] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [canUseClubBranding, hasNutritionAccess, canUseClubCalendar, isBrandToggleAllowed]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 3600);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const load = async () => {
      if (authLoading) return;
      setLoading(true);
      const userId = user?.id || null;
      setProfileId(userId || null);
      if (!userId) {
        setCurrentProfile(null);
        setLoading(false);
        return;
      }

      try {
        setAccess(await loadWorkoutGroupAccess(userId));
      } catch (err) {
        console.error("loadWorkoutGroupAccess error:", err);
        setAccess(getDefaultWorkoutGroupAccess());
      }

      const [{ data: profileRow }, { data: ownedClubs }, { data: coachedRows }] = await Promise.all([
        supabase.from("profiles").select("id, username, name, surname, profile_img").eq("id", userId).maybeSingle(),
        supabase.from("workout_clubs").select("*").eq("owner_profile_id", userId).order("created_at", { ascending: false }),
        supabase
          .from("workout_club_coaches")
          .select("role,status,workout_clubs(*)")
          .eq("profile_id", userId)
          .eq("status", "active"),
      ]);
      setCurrentProfile(profileRow || null);
      const clubRows = Array.from(
        [
          ...(ownedClubs || []).map((club) => ({ ...club, __isClubOwner: true, __isClubStaff: true })),
          ...(coachedRows || [])
            .map((row) => {
              const club = Array.isArray(row.workout_clubs) ? row.workout_clubs[0] : row.workout_clubs;
              return club?.id ? { ...club, __isClubStaff: true, __clubRole: row.role || "coach" } : null;
            })
            .filter(Boolean),
        ].reduce((map, club) => {
          const existing = map.get(club.id) || {};
          map.set(club.id, {
            ...existing,
            ...club,
            __isClubOwner: existing.__isClubOwner || club.__isClubOwner,
            __isClubStaff: existing.__isClubStaff || club.__isClubStaff,
            __clubRole: existing.__clubRole || club.__clubRole,
          });
          return map;
        }, new Map()).values()
      );
      setClubs(clubRows || []);
      const savedActiveClubId = activeClubStorageKey ? window.localStorage.getItem(activeClubStorageKey) : null;
      setActiveClubId((current) => {
        if (current === "__new__") return current;
        if (requestedClubId && clubRows.some((club) => club.id === requestedClubId)) return requestedClubId;
        if (clubRows.some((club) => club.id === current)) return current;
        if (savedActiveClubId && clubRows.some((club) => club.id === savedActiveClubId)) return savedActiveClubId;
        return clubRows?.[0]?.id || null;
      });
      const ownerClubs = (clubRows || []).filter((club) => club.owner_profile_id && club.owner_profile_id !== userId);
      if (ownerClubs.length) {
        const ownerAccessEntries = await Promise.all(ownerClubs.map(async (club) => {
          try {
            return [club.owner_profile_id, await loadWorkoutGroupOwnerAccessForClub(club.id)];
          } catch (err) {
            console.error("loadWorkoutGroupOwnerAccessForClub error:", err);
            return [club.owner_profile_id, getDefaultWorkoutGroupAccess()];
          }
        }));
        setClubOwnerAccessById(Object.fromEntries(ownerAccessEntries));
      } else {
        setClubOwnerAccessById({});
      }
      setLoading(false);
    };
    load();
  }, [activeClubStorageKey, authLoading, requestedClubId, user?.id]);

  useEffect(() => {
    const loadSelectedGroup = async () => {
      if (!profileId || !selectedGroupId) {
        setSelectedGroup(null);
        return;
      }
      try {
        const data = await selectWorkoutGroupDataWithFallback(
          (select) => supabase
            .from("workout_groups")
            .select(select)
            .eq("id", selectedGroupId)
            .maybeSingle(),
          WORKOUT_GROUP_SELECTS.relation,
          null,
          "load selected workout group"
        );
        setSelectedGroup(data || null);
        if (data?.club_id) setActiveClubId(data.club_id);
        setAttachGroupId(data?.id || "");
      } catch (error) {
        notify(error.message || "Could not load group.", "error");
        setSelectedGroup(null);
      }
    };
    loadSelectedGroup();
  }, [activeClubStorageKey, profileId, selectedGroupId, notify]);

  useEffect(() => {
    if (activeClub) {
      const nextForm = {
        name: activeClub.name || "",
        sport_id: activeClub.sport_id || "",
        logo_url: activeClub.logo_url || "",
        primary_color: activeClub.primary_color || DEFAULT_COLORS.primary_color,
        secondary_color: activeClub.secondary_color || DEFAULT_COLORS.secondary_color,
        accent_color: activeClub.accent_color || DEFAULT_COLORS.accent_color,
        theme_workout_groups: activeClub.brand_settings?.theme_workout_groups === true,
        theme_workouts: activeClub.brand_settings?.theme_workouts === true,
        theme_nutrition: activeClub.brand_settings?.theme_nutrition === true,
        theme_sessions: activeClub.brand_settings?.theme_sessions === true,
      };
      setClubForm(nextForm);
      setClubLogoUrlInput("");
      setShowClubLogoUrlInput(false);
    } else {
      setClubLogoUrlInput("");
      setShowClubLogoUrlInput(false);
    }
  }, [activeClub]);

  const loadClubData = useCallback(async () => {
    if (!profileId || !hasAccess) return;
    if (!activeClub?.id) {
      setGroups([]);
      setCoaches([]);
      setSeasons([]);
      setSessions([]);
      setCalendarSessions([]);
      setNutritionRows([]);
      setNutritionPlans([]);
      return;
    }
    const [groupRows, ownGroupRows, coachResult, seasonResult] = await Promise.all([
      selectWorkoutGroupDataWithFallback(
        (select) => supabase
          .from("workout_groups")
          .select(select)
          .eq("club_id", activeClub.id)
          .order("created_at", { ascending: false }),
        WORKOUT_GROUP_SELECTS.clubMembers,
        [],
        "load club workout groups"
      ),
      selectWorkoutGroupDataWithFallback(
        (select) => supabase
          .from("workout_groups")
          .select(select)
          .or(`coach_id.eq.${profileId},created_by.eq.${profileId}`)
          .order("created_at", { ascending: false }),
        WORKOUT_GROUP_SELECTS.picker,
        [],
        "load coach workout groups"
      ),
      supabase
        .from("workout_club_coaches")
        .select("id, profile_id, role, status, profiles(id, username, name, surname, profile_img)")
        .eq("club_id", activeClub.id),
      supabase
        .from("workout_club_seasons")
        .select("*")
        .eq("club_id", activeClub.id)
        .order("created_at", { ascending: false }),
    ]);
    const coachRows = coachResult.error ? [] : (coachResult.data || []);
    const seasonRows = seasonResult.error ? [] : (seasonResult.data || []);
    if (coachResult.error) console.error("load club coaches error:", coachResult.error);
    if (seasonResult.error) console.error("load club seasons error:", seasonResult.error);
    const mergedPickerGroups = Array.from(
      [...(groupRows || []), ...(ownGroupRows || [])]
        .reduce((map, group) => map.set(group.id, { ...(map.get(group.id) || {}), ...group }), new Map())
        .values()
    );
    setGroups(groupRows || []);
    setAllGroups(mergedPickerGroups);
    setCoaches(coachRows || []);
    setSeasons(seasonRows || []);
    const groupIds = (groupRows || []).map((group) => group.id).filter(Boolean);
    if (groupIds.length) {
      const now = new Date();
      now.setDate(now.getDate() - 30);
      const { data: calendarRows, error: calendarErrorResult } = await supabase
        .from("workout_group_calendar_sessions")
        .select("id, group_id, club_id, created_by, title, starts_at, ends_at, location, notes, status, metadata, created_at, updated_at")
        .in("group_id", groupIds)
        .gte("starts_at", now.toISOString())
        .order("starts_at", { ascending: true })
        .limit(120);
      if (calendarErrorResult) {
        console.error("load calendar sessions error:", calendarErrorResult);
        setCalendarSessions([]);
      } else {
        const sessionIds = (calendarRows || []).map((row) => row.id).filter(Boolean);
        const calendarDemoProfileIds = Array.from(new Set((calendarRows || []).flatMap((row) => (
          Array.isArray(row?.metadata?.demo_athlete_ids) ? row.metadata.demo_athlete_ids : []
        )).filter(Boolean)));
        const [{ data: calendarAthletes }, { data: calendarCoaches }] = sessionIds.length
          ? await Promise.all([
            supabase
              .from("workout_group_calendar_session_athletes")
              .select("id, session_id, athlete_id, status, responded_at, profiles(id, name, surname, username, profile_img, push_token)")
              .in("session_id", sessionIds),
            supabase
              .from("workout_group_calendar_session_coaches")
              .select("id, session_id, coach_id, role, profiles(id, name, surname, username, profile_img)")
              .in("session_id", sessionIds),
          ])
          : [{ data: [] }, { data: [] }];
        const { data: calendarDemoProfiles, error: calendarDemoProfileError } = calendarDemoProfileIds.length
          ? await supabase
            .from("demo_profiles")
            .select("id,name,surname,username,profile_img,level,metadata")
            .in("id", calendarDemoProfileIds)
          : { data: [], error: null };
        if (calendarDemoProfileError) console.error("load calendar demo profiles error:", calendarDemoProfileError);
        const calendarDemoProfilesById = new Map((calendarDemoProfiles || []).map((profile) => [profile.id, profile]));
        setCalendarSessions((calendarRows || []).map((row) => {
          const group = (groupRows || []).find((item) => item.id === row.group_id) || null;
          const demoIds = Array.isArray(row?.metadata?.demo_athlete_ids) ? row.metadata.demo_athlete_ids.filter(Boolean) : [];
          const combinedMembers = getCombinedWorkoutMembers(group);
          const demoAthletes = demoIds.map((demoId) => {
            const member = combinedMembers.find((candidate) => (candidate.demo_profile_id || candidate.athlete_id || candidate.profiles?.id) === demoId);
            const demoProfile = member?.profiles || mapDemoProfileToCalendarProfile(calendarDemoProfilesById.get(demoId), demoId);
            return {
              id: `demo-${row.id}-${demoId}`,
              session_id: row.id,
              athlete_id: demoId,
              demo_profile_id: demoId,
              is_demo_profile: true,
              status: "pending",
              profiles: demoProfile,
            };
          });
          return {
            ...row,
            group,
            athletes: [...((calendarAthletes || []).filter((item) => item.session_id === row.id)), ...demoAthletes],
            coaches: (calendarCoaches || []).filter((item) => item.session_id === row.id),
          };
        }));
      }
    } else {
      setCalendarSessions([]);
    }
    setActiveSeasonId((current) => {
      const presetIds = new Set(ANALYTICS_RANGE_OPTIONS.map((option) => option.value));
      if (presetIds.has(current) || seasonRows?.some((season) => season.id === current)) return current;
      return seasonRows?.find((season) => season.status === "active")?.id || seasonRows?.[0]?.id || "last_7";
    });
    const defaultCompareAId = seasonRows?.find((season) => season.status === "active")?.id || seasonRows?.[0]?.id || "";
    const defaultCompareBId = seasonRows?.find((season) => season.id !== defaultCompareAId)?.id || defaultCompareAId;
    setCompareSeasonAId((current) => current && seasonRows?.some((season) => season.id === current) ? current : defaultCompareAId);
    setCompareSeasonBId((current) => {
      if (!current || !seasonRows?.some((season) => season.id === current)) return defaultCompareBId;
      if (current === defaultCompareAId && defaultCompareBId && defaultCompareBId !== current) return defaultCompareBId;
      return current;
    });
    setCompareDrilldownGroupId((current) => current && (groupRows || []).some((group) => group.id === current) ? current : "all");

    const athleteIds = Array.from(new Set((groupRows || []).flatMap((group) => (
      getCombinedWorkoutMembers(group)
        .filter((member) => !member.is_demo_profile)
        .map((member) => member.athlete_id || member.profiles?.id)
    )).filter(Boolean)));
    const demoAthleteIds = Array.from(new Set((groupRows || []).flatMap((group) => (
      getCombinedWorkoutMembers(group)
        .filter((member) => member.is_demo_profile)
        .map((member) => member.demo_profile_id || member.athlete_id || member.profiles?.id)
    )).filter(Boolean)));
    if (!athleteIds.length && !demoAthleteIds.length) {
      setSessions([]);
      setNutritionRows([]);
      return;
    }
    const start = new Date();
    start.setDate(start.getDate() - 1095);
    const [
      { data: sessionRows, error: sessionError },
      { data: demoSessionRows, error: demoSessionError },
      { data: foodRows, error: foodError },
      { data: demoFoodRows, error: demoFoodError },
    ] = await Promise.all([
      athleteIds.length
        ? fetchPagedSupabaseRows(() => supabase
          .from("workout_sessions")
          .select("id, profile_id, total_xp, total_sets, total_reps, total_volume, total_rest_seconds, session_effort, local_date, started_at, ended_at, duration_seconds, workout_plan_id, preset_plan_id, user_workouts(id,name,share_code,source_workout_plan_id), workouts(id,name), workout_context(rest_timer_usage, created_at), user_workout_feedback(rating,difficulty,focus_rating,mood,notes,created_at), workout_exercise_summary(total_reps,avg_weight,total_xp,exercises(id,name,image_url,gif_url))")
          .in("profile_id", athleteIds)
          .gte("started_at", start.toISOString())
          .order("started_at", { ascending: false }))
        : Promise.resolve({ data: [], error: null }),
      demoAthleteIds.length
        ? fetchPagedSupabaseRows(() => supabase
          .from("demo_workout_sessions")
          .select("id, demo_profile_id, group_id, total_xp, total_sets, total_reps, total_volume, total_rest_seconds, session_effort, local_date, started_at, ended_at, duration_seconds, workout_plan_id, user_workouts(id,name,share_code,source_workout_plan_id), workout_context, demo_user_workout_feedback(rating,difficulty,focus_rating,mood,notes,created_at), demo_exercise_progress(reps,weight,xp_gained,exercises(id,name,image_url,gif_url))")
          .in("demo_profile_id", demoAthleteIds)
          .gte("started_at", start.toISOString())
          .order("started_at", { ascending: false }))
        : Promise.resolve({ data: [], error: null }),
      athleteIds.length
        ? fetchPagedSupabaseRows(() => supabase
          .from("food_log")
          .select("user_id, log_date, meal_section, food_name, calories, protein_g, carbs_g, fat_g, fiber_g, sugar_g, sodium_mg")
          .in("user_id", athleteIds)
          .gte("log_date", start.toISOString().slice(0, 10))
          .order("log_date", { ascending: false }))
        : Promise.resolve({ data: [], error: null }),
      demoAthleteIds.length
        ? fetchPagedSupabaseRows(() => supabase
          .from("demo_food_log")
          .select("demo_profile_id, log_date, meal_section, food_name, calories, protein_g, carbs_g, fat_g, fiber_g, sugar_g, sodium_mg")
          .in("demo_profile_id", demoAthleteIds)
          .gte("log_date", start.toISOString().slice(0, 10))
          .order("log_date", { ascending: false }))
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (sessionError) console.error("load workout sessions error:", sessionError);
    if (demoSessionError) console.error("load demo workout sessions error:", demoSessionError);
    if (foodError) console.error("load food logs error:", foodError);
    if (demoFoodError) console.error("load demo food logs error:", demoFoodError);
    setSessions([...(sessionRows || []), ...(demoSessionRows || []).map(normalizeDemoWorkoutSession)]);
    setNutritionRows([...(foodRows || []), ...(demoFoodRows || []).map(mapDemoFoodLogRow)]);
    const { data: planRows, error: planError } = await supabase
      .from("nutrition_plans")
      .select(`*, nutrition_plan_items(*), nutrition_plan_assignments(*, profiles(id,name,surname,username,profile_img), nutrition_plan_progress(*), nutrition_plan_log_events(*)), ${DEMO_NUTRITION_ASSIGNMENTS_SELECT}, workout_groups(id,name)`)
      .eq("club_id", activeClub.id)
      .order("created_at", { ascending: false });
    if (planError) {
      console.error("load nutrition plans error:", planError);
      setNutritionPlans([]);
    } else {
      const ownerIds = uniqueIds((planRows || []).map((plan) => plan.owner_id).filter(Boolean));
      let ownerProfilesById = new Map();
      if (ownerIds.length) {
        const { data: ownerProfiles, error: ownerProfileError } = await supabase
          .from("profiles")
          .select("id,name,surname,username,profile_img")
          .in("id", ownerIds);
        if (ownerProfileError) console.error("load nutrition plan owners error:", ownerProfileError);
        ownerProfilesById = new Map((ownerProfiles || []).map((profile) => [profile.id, profile]));
      }
      setNutritionPlans((planRows || []).map((plan) => mergeDemoNutritionAssignments({
        ...plan,
        owner_profile: ownerProfilesById.get(plan.owner_id) || null,
      })));
    }
  }, [activeClub?.id, hasAccess, profileId]);

  const calendarCoachOptions = useMemo(() => {
    const rows = (coaches || [])
      .filter((coach) => coach?.profile_id && (!coach.status || coach.status === "active"))
      .map((coach) => ({
        id: coach.profile_id,
        role: coach.role || "coach",
        profile: coach.profiles || {},
      }));
    if (profileId && !rows.some((row) => row.id === profileId)) {
      rows.unshift({
        id: profileId,
        role: "coach",
        profile: currentProfile || { username: user?.email || "You" },
      });
    }
    return Array.from(rows.reduce((map, row) => map.set(row.id, row), new Map()).values());
  }, [coaches, currentProfile, profileId, user?.email]);

  const activeClubCoachOptions = useMemo(() => {
    const rows = [...(coaches || [])];
    if (
      activeClub?.owner_profile_id
      && currentProfile?.id === activeClub.owner_profile_id
      && !rows.some((coach) => coach?.profile_id === activeClub.owner_profile_id)
    ) {
      rows.unshift({
        profile_id: activeClub.owner_profile_id,
        role: "owner",
        status: "active",
        profiles: currentProfile,
      });
    }
    return Array.from(rows
      .filter((coach) => coach?.profile_id && (!coach.status || coach.status === "active"))
      .reduce((map, coach) => {
        if (!map.has(coach.profile_id)) {
          map.set(coach.profile_id, {
            id: coach.profile_id,
            role: coach.role || "coach",
            profile: coach.profiles || {},
          });
        }
        return map;
      }, new Map())
      .values())
      .sort((a, b) => displayProfileName(a.profile, "Coach").localeCompare(displayProfileName(b.profile, "Coach"), undefined, { sensitivity: "base" }))
  }, [activeClub?.owner_profile_id, coaches, currentProfile]);

  const nutritionCoachOptions = useMemo(() => {
    const rows = [...activeClubCoachOptions];
    if (profileId && !rows.some((coach) => coach.id === profileId)) {
      rows.unshift({
        id: profileId,
        role: "coach",
        profile: currentProfile || { username: user?.email || "You" },
      });
    }
    return Array.from(rows.reduce((map, coach) => map.set(coach.id, coach), new Map()).values())
      .sort((a, b) => displayProfileName(a.profile, "Coach").localeCompare(displayProfileName(b.profile, "Coach"), undefined, { sensitivity: "base" }));
  }, [activeClubCoachOptions, currentProfile, profileId, user?.email]);

  const refreshCalendarCoaches = useCallback(async () => {
    if (calendarCoachesRefreshing) return;
    setCalendarCoachesRefreshing(true);
    try {
      await loadClubData();
    } finally {
      setCalendarCoachesRefreshing(false);
    }
  }, [calendarCoachesRefreshing, loadClubData]);

  const selectedCalendarGroup = useMemo(() => (
    groups.find((group) => group.id === calendarForm.groupId) || null
  ), [calendarForm.groupId, groups]);

  const getPreferredCalendarCoachIds = useCallback((group = selectedCalendarGroup) => {
    const coachOptionIds = new Set(calendarCoachOptions.map((coach) => String(coach.id)));
    const hasCoachOptions = coachOptionIds.size > 0;
    const leadCoachId = group?.coach_id && (!hasCoachOptions || coachOptionIds.has(String(group.coach_id)))
      ? String(group.coach_id)
      : "";
    const currentCoachId = profileId && (!hasCoachOptions || coachOptionIds.has(String(profileId)))
      ? String(profileId)
      : "";
    const fallbackCoachId = calendarCoachOptions[0]?.id ? String(calendarCoachOptions[0].id) : "";
    const preferredCoachId = leadCoachId || currentCoachId || fallbackCoachId;
    return preferredCoachId ? [preferredCoachId] : [];
  }, [calendarCoachOptions, profileId, selectedCalendarGroup]);

  const calendarGroupAthleteOptions = useMemo(() => {
    const rows = getCombinedWorkoutMembers(selectedCalendarGroup);
    const byId = new Map();
    rows.forEach((member) => {
      const id = member.is_demo_profile
        ? (member.demo_profile_id || member.athlete_id || member.profiles?.id)
        : (member.athlete_id || member.profiles?.id);
      if (!id) return;
      byId.set(id, { id, isDemo: !!member.is_demo_profile, demoProfileId: member.demo_profile_id || null, profile: member.profiles || {} });
    });
    return Array.from(byId.values());
  }, [selectedCalendarGroup]);

  const calendarSelectedAthleteIds = useMemo(() => {
    const athleteIds = calendarGroupAthleteOptions.map((athlete) => athlete.id);
    if (calendarForm.inviteAll) return athleteIds;
    const validAthleteIds = new Set(athleteIds);
    return uniqueIds(calendarForm.selectedAthleteIds).filter((id) => validAthleteIds.has(id));
  }, [calendarForm.inviteAll, calendarForm.selectedAthleteIds, calendarGroupAthleteOptions]);

  const calendarSelectedAthleteRows = useMemo(() => {
    const selectedIds = new Set(calendarSelectedAthleteIds);
    return calendarGroupAthleteOptions.filter((athlete) => selectedIds.has(athlete.id));
  }, [calendarGroupAthleteOptions, calendarSelectedAthleteIds]);

  const calendarSelectedCoachIds = useMemo(() => {
    const validCoachIds = new Set(calendarCoachOptions.map((coach) => coach.id));
    return uniqueIds(calendarForm.coachIds).filter((id) => validCoachIds.has(id));
  }, [calendarCoachOptions, calendarForm.coachIds]);

  const calendarStartsAt = useMemo(() => buildLocalDateTime(calendarForm.date, calendarForm.startTime), [calendarForm.date, calendarForm.startTime]);
  const calendarEndsAt = useMemo(() => (
    calendarForm.hasEndTime ? buildLocalDateTime(calendarForm.date, calendarForm.endTime) : null
  ), [calendarForm.date, calendarForm.endTime, calendarForm.hasEndTime]);

  const calendarFormError = useMemo(() => {
    if (!activeClub?.id) return "Create or choose a club before scheduling sessions.";
    if (!canUseClubCalendar) return "Calendar sessions are included with Pro Coach and Club Coaching plans.";
    if (!selectedCalendarGroup?.id) return "Choose a workout group for this session.";
    if (!calendarForm.title.trim()) return "Enter a session title.";
    if (!calendarStartsAt) return "Choose a valid date and start time.";
    if (calendarForm.hasEndTime && !calendarEndsAt) return "Choose a valid end time.";
    if (calendarEndsAt && calendarEndsAt <= calendarStartsAt) return "End time must be after the start time.";
    if (!calendarSelectedAthleteIds.length) return "Invite at least one athlete.";
    if (!calendarSelectedCoachIds.length) return "Assign at least one coach.";
    return "";
  }, [activeClub?.id, calendarEndsAt, calendarForm.hasEndTime, calendarForm.title, calendarSelectedAthleteIds.length, calendarSelectedCoachIds.length, calendarStartsAt, canUseClubCalendar, selectedCalendarGroup?.id]);

  const calendarGuidanceMessage = useMemo(() => {
    if (!activeClub?.id) return "Create or choose a club before creating sessions.";
    if (!groups.length) return "Attach a workout group before creating sessions.";
    if (!calendarForm.groupId) return "Choose a workout group before creating sessions.";
    if (!calendarGroupAthleteOptions.length) return "Add athletes to this group before creating sessions.";
    if (!calendarSelectedCoachIds.length) return "Assign at least one coach before creating sessions.";
    return "";
  }, [activeClub?.id, calendarForm.groupId, calendarGroupAthleteOptions.length, calendarSelectedCoachIds.length, groups.length]);

  const calendarNowTime = Date.now();
  const calendarSessionBuckets = useMemo(() => {
    const buckets = { upcoming: [], past: [], cancelled: [] };
    calendarSessions.forEach((session) => {
      const startTime = new Date(session.starts_at).getTime();
      if (session.status === "cancelled") {
        buckets.cancelled.push(session);
      } else if (!Number.isNaN(startTime) && startTime < calendarNowTime) {
        buckets.past.push(session);
      } else {
        buckets.upcoming.push(session);
      }
    });
    buckets.upcoming.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
    buckets.past.sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime());
    buckets.cancelled.sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime());
    return buckets;
  }, [calendarNowTime, calendarSessions]);
  const calendarSessionCounts = useMemo(() => ({
    upcoming: calendarSessionBuckets.upcoming.length,
    past: calendarSessionBuckets.past.length,
    cancelled: calendarSessionBuckets.cancelled.length,
  }), [calendarSessionBuckets]);
  const calendarUpcomingDayChips = useMemo(() => buildCalendarDayChips(calendarSessionBuckets.upcoming), [calendarSessionBuckets.upcoming]);
  const displayedCalendarSessions = useMemo(() => {
    const rows = calendarSessionBuckets[calendarFilter] || [];
    if (calendarFilter !== "upcoming" || !calendarSelectedDateKey) return rows;
    return rows.filter((session) => calendarDateKey(session.starts_at) === calendarSelectedDateKey);
  }, [calendarFilter, calendarSelectedDateKey, calendarSessionBuckets]);
  const groupedCalendarSessions = useMemo(() => groupCalendarSessionsByDate(displayedCalendarSessions), [displayedCalendarSessions]);
  const selectedCalendarSession = useMemo(() => (
    calendarSessions.find((session) => session.id === calendarSelectedSessionId) || null
  ), [calendarSelectedSessionId, calendarSessions]);

  useEffect(() => {
    loadClubData();
  }, [loadClubData]);

  useEffect(() => {
    const requestedTool = searchParams.get("return_tab");
    if (["brand", "staff", "seasons", "groups", "sessions"].includes(requestedTool)) {
      setToolView(requestedTool);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!groups.length) return;
    setCalendarForm((prev) => {
      if (prev.groupId && groups.some((group) => group.id === prev.groupId)) return prev;
      const requestedGroup = selectedGroupId && groups.some((group) => group.id === selectedGroupId) ? selectedGroupId : groups[0].id;
      const group = groups.find((item) => item.id === requestedGroup);
      const athleteIds = uniqueIds((group?.workout_group_members || []).map((member) => member.athlete_id || member.profiles?.id));
      return { ...prev, groupId: requestedGroup, selectedAthleteIds: athleteIds };
    });
  }, [groups, selectedGroupId]);

  useEffect(() => {
    calendarCoachSelectionTouchedRef.current = false;
  }, [activeClub?.id]);

  useEffect(() => {
    if (!calendarCoachOptions.length || calendarCoachSelectionTouchedRef.current) return;
    setCalendarForm((prev) => {
      if ((prev.coachIds || []).length) return prev;
      const coachIds = getPreferredCalendarCoachIds(selectedCalendarGroup);
      return coachIds.length ? { ...prev, coachIds } : prev;
    });
  }, [calendarCoachOptions, getPreferredCalendarCoachIds, selectedCalendarGroup]);

  useEffect(() => {
    if (calendarSelectedSessionId && !calendarSessions.some((session) => session.id === calendarSelectedSessionId)) {
      setCalendarSelectedSessionId(null);
    }
  }, [calendarSelectedSessionId, calendarSessions]);

  useEffect(() => {
    const firstVisibleSessionId = displayedCalendarSessions[0]?.id || null;
    if (calendarSelectedSessionId) {
      if (!displayedCalendarSessions.some((session) => session.id === calendarSelectedSessionId)) {
        setCalendarSelectedSessionId(firstVisibleSessionId);
      }
      return;
    }
    if (firstVisibleSessionId) setCalendarSelectedSessionId(firstVisibleSessionId);
  }, [calendarSelectedSessionId, displayedCalendarSessions]);

  const groupPickerValue = selectedGroup?.id || attachGroupId || "";
  const selectDashboardGroup = useCallback(async (groupId) => {
    setAttachGroupId(groupId || "");
    if (!groupId) {
      setSelectedGroup(null);
      return;
    }

    const localGroup = [...allGroups, ...groups].find((group) => group.id === groupId);
    if (localGroup) {
      setSelectedGroup((prev) => ({
        ...(prev?.id === groupId ? prev : {}),
        ...localGroup,
        club_id: localGroup.club_id || null,
        season_id: localGroup.season_id || null,
      }));
      return;
    }

    try {
      const data = await selectWorkoutGroupDataWithFallback(
        (select) => supabase
          .from("workout_groups")
          .select(select)
          .eq("id", groupId)
          .maybeSingle(),
        WORKOUT_GROUP_SELECTS.relation,
        null,
        "select dashboard workout group"
      );
      setSelectedGroup(data || null);
    } catch (error) {
      notify(error.message || "Could not load workout group.", "error");
      setSelectedGroup(null);
    }
  }, [allGroups, groups, notify]);

  const athletes = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => {
      getCombinedWorkoutMembers(group).filter(isCountingWorkoutAthlete).forEach((member) => {
        if (member.profiles?.id) map.set(member.profiles.id, member.profiles);
      });
    });
    return Array.from(map.values());
  }, [groups]);

  const brand = {
    primary: activeClub?.primary_color || base.blue,
    secondary: CLUB_BRAND_GRADIENT_SECONDARY,
    accent: activeClub?.accent_color || DEFAULT_COLORS.accent_color,
  };
  const brandSurface = {
    primary: isLightColor(brand.primary) ? "#0F5132" : brand.primary,
    secondary: isLightColor(brand.secondary) ? "#111827" : brand.secondary,
    accent: isLightColor(brand.accent) ? "#111827" : brand.accent,
  };
  const clubFormSurface = {
    primary: isLightColor(clubForm.primary_color) ? "#0F5132" : (clubForm.primary_color || DEFAULT_COLORS.primary_color),
    secondary: CLUB_BRAND_GRADIENT_SECONDARY,
    accent: isLightColor(clubForm.accent_color) ? "#111827" : (clubForm.accent_color || DEFAULT_COLORS.accent_color),
  };
  const darkPanelHighlightText = "#FFFFFF";

  const analyticsWindow = useMemo(() => {
    if (activeSeason) {
      return {
        label: `${activeSeason.name}: ${activeSeason.starts_on || "open"} to ${activeSeason.ends_on || "open"}`,
        season: activeSeason,
        start: activeSeason.starts_on ? new Date(`${activeSeason.starts_on}T00:00:00`) : null,
        end: activeSeason.ends_on ? new Date(`${activeSeason.ends_on}T23:59:59.999`) : null,
      };
    }
    if (activeSeasonId === "last_all") return { label: "All club data", season: null, start: null, end: null, days: null };
    if (activeSeasonId === "date") {
      const selectedDate = analyticsSelectedDate || localDateInput(new Date());
      return {
        label: formatDateFilterLabel(selectedDate),
        season: null,
        start: new Date(`${selectedDate}T00:00:00`),
        end: new Date(`${selectedDate}T23:59:59.999`),
        days: 1,
      };
    }
    const presetDays = activeSeasonId === "custom" ? Number(customDays) : Number((activeSeasonId || "last_7").replace("last_", ""));
    const days = Number.isFinite(presetDays) && presetDays > 0 ? Math.min(Math.round(presetDays), 1095) : 7;
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    return { label: `Last ${days} days`, season: null, start, end: null, days };
  }, [activeSeason, activeSeasonId, analyticsSelectedDate, customDays]);

  const renderAnalyticsReviewWindow = (ariaLabel = "Analytics review window") => {
    const season = activeSeasonId === "date"
      ? null
      : analyticsWindow.season || firstRelationRow(dashboardGroup?.workout_club_seasons) || activeSeason || null;
    const windowLabel = season
      ? `${season.name || "Season"}: ${season.starts_on || "open"} to ${season.ends_on || "open"}`
      : analyticsWindow.label;
    return (
      <section style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: brandSurface.primary, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Review window</div>
          <h3 style={{ margin: "5px 0 0", color: base.text, fontSize: 18, lineHeight: 1.25, fontWeight: 950 }}>{windowLabel}</h3>
        </div>
        <div aria-label={ariaLabel} style={{ display: "flex", gap: 7, flexWrap: "wrap", borderTop: `1px solid ${base.border}`, paddingTop: 12 }}>
          {ANALYTICS_RANGE_OPTIONS.map((option) => {
            const active = activeSeasonId === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => selectAnalyticsRange(option.value)}
                style={{
                  minHeight: 36,
                  border: `1px solid ${active ? brandSurface.primary : base.border}`,
                  background: active ? brandSurface.primary : "#F8FAFF",
                  color: active ? "#fff" : base.text,
                  borderRadius: 999,
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                {option.label}
              </button>
            );
          })}
          {activeSeasonId === "custom" && (
            <input aria-label="Custom analytics days" type="number" min="1" max="1095" value={customDays} onChange={(event) => setCustomDays(event.target.value)} style={{ width: 94, minHeight: 40, border: `1px solid ${base.border}`, background: "#F8FAFF", color: base.text, borderRadius: 12, padding: "8px 10px", fontWeight: 900 }} />
          )}
          {activeSeasonId === "date" && (
            <input aria-label="Analytics review date" type="date" value={analyticsSelectedDate} onChange={(event) => setAnalyticsSelectedDate(event.target.value || localDateInput(new Date()))} style={{ minHeight: 40, border: `1px solid ${base.border}`, background: "#F8FAFF", color: base.text, borderRadius: 12, padding: "8px 10px", fontWeight: 900 }} />
          )}
        </div>
      </section>
    );
  };

  const inWindow = useCallback((row, season = activeSeason) => {
    const time = new Date(row.started_at).getTime();
    if (season) {
      if (season.starts_on && time < new Date(`${season.starts_on}T00:00:00`).getTime()) return false;
      if (season.ends_on && time > new Date(`${season.ends_on}T23:59:59.999`).getTime()) return false;
      return true;
    }
    if (analyticsWindow.start && time < analyticsWindow.start.getTime()) return false;
    if (analyticsWindow.end && time > analyticsWindow.end.getTime()) return false;
    return true;
  }, [activeSeason, analyticsWindow]);

  const activeSessions = useMemo(() => sessions.filter((row) => inWindow(row)), [sessions, inWindow]);
  const avg = (values) => {
    const nums = values.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  };

  const groupAnalytics = useMemo(() => {
    return groups.map((group) => {
      const athleteIds = new Set(getCountingWorkoutMemberIds(group));
      const groupSessions = activeSessions.filter((row) => athleteIds.has(row.profile_id) && sessionCountsForGroup(row, group));
      const xp = groupSessions.reduce((sum, row) => sum + Number(row.total_xp || 0), 0);
      const activeAthletes = new Set(groupSessions.map((row) => row.profile_id)).size;
      const targetXp = Number(group.weekly_xp_target || 0) * (activeSeason ? Math.max(1, Math.ceil((new Date(activeSeason.ends_on || Date.now()).getTime() - new Date(activeSeason.starts_on || Date.now()).getTime()) / (7 * 86400000))) : 4);
      const feedbackRows = groupSessions.flatMap((row) => row.user_workout_feedback || []);
      const restSeconds = groupSessions.reduce((sum, row) => sum + Number(row.workout_context?.[0]?.rest_timer_usage || 0), 0);
      return {
        ...group,
        athleteCount: athleteIds.size,
        activeAthletes,
        sessions: groupSessions.length,
        sessionIds: groupSessions.map((row) => row.id),
        xp,
        targetXp,
        progress: targetXp ? Math.min(100, Math.round((xp / targetXp) * 100)) : 0,
        avgRating: avg(feedbackRows.map((row) => row.rating)),
        avgDifficulty: avg(feedbackRows.map((row) => row.difficulty)),
        avgFocus: avg(feedbackRows.map((row) => row.focus_rating)),
        restMinutes: Math.round(restSeconds / 60),
      };
    }).sort((a, b) => b.xp - a.xp);
  }, [groups, activeSessions, activeSeason]);

  const countedSessionIds = useMemo(() => new Set(groupAnalytics.flatMap((group) => group.sessionIds || [])), [groupAnalytics]);
  const countedSessions = useMemo(() => activeSessions.filter((row) => countedSessionIds.has(row.id)), [activeSessions, countedSessionIds]);

  const seasonAnalytics = useMemo(() => {
    return seasons.map((season) => {
      const seasonGroups = groups.filter((group) => group.season_id === season.id);
      const rowIds = new Set();
      seasonGroups.forEach((group) => {
        const athleteIds = new Set(getCountingWorkoutMemberIds(group));
        sessions
          .filter((row) => athleteIds.has(row.profile_id) && inWindow(row, season) && sessionCountsForGroup(row, group))
          .forEach((row) => rowIds.add(row.id));
      });
      const rows = sessions.filter((row) => rowIds.has(row.id));
      return {
        ...season,
        sessions: rows.length,
        xp: rows.reduce((sum, row) => sum + Number(row.total_xp || 0), 0),
        activeAthletes: new Set(rows.map((row) => row.profile_id)).size,
        avgRating: avg(rows.flatMap((row) => row.user_workout_feedback || []).map((row) => row.rating)),
      };
    }).sort((a, b) => new Date(b.starts_on || b.created_at || 0) - new Date(a.starts_on || a.created_at || 0));
  }, [seasons, sessions, groups, inWindow]);

  const athleteAnalytics = useMemo(() => {
    return athletes.map((athlete) => {
      const rows = countedSessions.filter((row) => row.profile_id === athlete.id);
      const feedbackRows = rows.flatMap((row) => row.user_workout_feedback || []);
      const xp = rows.reduce((sum, row) => sum + Number(row.total_xp || 0), 0);
      const restSeconds = rows.reduce((sum, row) => sum + Number(row.workout_context?.[0]?.rest_timer_usage || 0), 0);
      const groupNames = groups
        .filter((group) => getCountingWorkoutMemberIds(group).includes(athlete.id))
        .map((group) => group.name)
        .filter(Boolean);
      const avgFocus = avg(feedbackRows.map((row) => row.focus_rating));
      const avgRating = avg(feedbackRows.map((row) => row.rating));
      const avgDifficulty = avg(feedbackRows.map((row) => row.difficulty));
      const lastSession = rows.map((row) => new Date(row.started_at).getTime()).filter(Number.isFinite).sort((a, b) => b - a)[0] || null;
      const focusScore = rows.length === 0 ? 100 : Math.max(0, Math.round((5 - (avgFocus || 0)) * 20)) + Math.max(0, 3 - rows.length) * 10;
      const attentionLabel = rows.length === 0 ? "No workout sessions" : avgFocus && avgFocus < 3 ? "Low focus feedback" : rows.length < 3 ? "Low activity" : "Monitor";
      return {
        id: athlete.id,
        name: [athlete.name, athlete.surname].filter(Boolean).join(" ") || athlete.username || "Athlete",
        username: athlete.username,
        profile_img: athlete.profile_img,
        groups: groupNames.join(", "),
        sessions: rows.length,
        xp,
        avgFocus,
        avgRating,
        avgDifficulty,
        restMinutes: Math.round(restSeconds / 60),
        lastSession,
        focusScore,
        attentionLabel,
      };
    });
  }, [athletes, countedSessions, groups]);

  const topPerformers = useMemo(() => [...athleteAnalytics].sort((a, b) => b.xp - a.xp || b.sessions - a.sessions).slice(0, 8), [athleteAnalytics]);
  const activeAthleteCount = athleteAnalytics.filter((athlete) => athlete.sessions > 0).length;
  const teamAvgFocus = avg(athleteAnalytics.map((athlete) => athlete.avgFocus));
  const teamAvgRating = avg(athleteAnalytics.map((athlete) => athlete.avgRating));
  const teamRestMinutes = athleteAnalytics.reduce((sum, athlete) => sum + athlete.restMinutes, 0);
  const dashboardGroup = useMemo(() => {
    if (!groups.length) return null;
    if (selectedGroup?.id) return groups.find((group) => group.id === selectedGroup.id) || groups[0];
    return groups[0];
  }, [groups, selectedGroup?.id]);

  const dashboardGroupData = useMemo(() => {
    if (!dashboardGroup?.id) return null;
    const members = getCombinedWorkoutMembers(dashboardGroup)
      .filter(isCountingWorkoutAthlete)
      .map((member) => member.profiles || member.profile || null)
      .filter((profile) => profile?.id);
    const memberIds = new Set(members.map((member) => member.id));
    const rows = activeSessions.filter((row) => memberIds.has(row.profile_id) && sessionCountsForGroup(row, dashboardGroup));
    const weeksFactor = targetWeeksForWindow(analyticsWindow);
    const sessionTarget = Number(dashboardGroup.weekly_session_target || 3) * weeksFactor;
    const groupTarget = Number(dashboardGroup.weekly_xp_target || 1000) * weeksFactor;
    const groupNutritionTargets = getGroupNutritionTargets(dashboardGroup);
    const calorieDailyTarget = groupNutritionTargets.calories;
    const memberNutritionRows = nutritionRows.filter((row) => {
      if (!memberIds.has(row.user_id)) return false;
      const time = dateOnlyTime(row.log_date);
      if (!time) return false;
      if (activeSeason) {
        if (activeSeason.starts_on && time < dateOnlyTime(activeSeason.starts_on)) return false;
        if (activeSeason.ends_on && time > dateOnlyTime(activeSeason.ends_on)) return false;
        return true;
      }
      if (analyticsWindow.start && time < dateOnlyTime(analyticsWindow.start.toISOString().slice(0, 10))) return false;
      if (analyticsWindow.end && time > dateOnlyTime(analyticsWindow.end.toISOString().slice(0, 10))) return false;
      return true;
    });
    const dashboardNutritionDays = (() => {
      const today = new Date();
      if (activeSeason) {
        const times = memberNutritionRows.map((row) => dateOnlyTime(row.log_date)).filter(Boolean);
        const start = activeSeason.starts_on ? new Date(`${activeSeason.starts_on}T00:00:00`) : new Date(times.length ? Math.min(...times) : today.getTime());
        const end = activeSeason.ends_on ? new Date(`${activeSeason.ends_on}T00:00:00`) : today;
        return inclusiveDays(start, end);
      }
      if (analyticsWindow.days) return analyticsWindow.days;
      const times = memberNutritionRows.map((row) => dateOnlyTime(row.log_date)).filter(Boolean);
      if (!times.length) return 1;
      return inclusiveDays(new Date(Math.min(...times)), today);
    })();
    const calorieTarget = calorieDailyTarget * dashboardNutritionDays;
    const nutritionStory = buildDashboardNutritionStory(memberNutritionRows, members, groupNutritionTargets, dashboardNutritionDays);
    const athleteTarget = members.length ? Math.round(groupTarget / members.length) : groupTarget;
    const athleteRows = members.map((member) => {
      const memberSessions = rows.filter((row) => row.profile_id === member.id);
      const memberCalories = memberNutritionRows.filter((row) => row.user_id === member.id).reduce((sum, row) => sum + Number(row.calories || 0), 0);
      const xp = memberSessions.reduce((sum, row) => sum + Number(row.total_xp || 0), 0);
      const sessionsCount = memberSessions.length;
      return {
        id: member.id,
        name: [member.name, member.surname].filter(Boolean).join(" ") || member.username || "Athlete",
        username: member.username,
        profile_img: member.profile_img,
        xp,
        sessions: sessionsCount,
        calories: memberCalories,
        caloriePct: calorieTarget ? Math.min(100, Math.round((memberCalories / calorieTarget) * 100)) : 0,
        status: getGroupAttentionStatus(xp, athleteTarget, sessionsCount, sessionTarget),
      };
    }).sort((a, b) => b.xp - a.xp || a.name.localeCompare(b.name));
    const totalGroupXp = rows.reduce((sum, row) => sum + Number(row.total_xp || 0), 0);
    const totalReps = rows.reduce((sum, row) => sum + Number(row.total_reps || 0), 0);
    const totalVolume = rows.reduce((sum, row) => sum + Number(row.total_volume || 0), 0);
    const totalWorkoutSeconds = rows.reduce((sum, row) => sum + Math.max(0, Number(row.duration_seconds || 0)), 0);
    const totalRestSeconds = rows.reduce((sum, row) => {
      const duration = Math.max(0, Number(row.duration_seconds || 0));
      const rest = Math.max(0, Number(row.total_rest_seconds || row.workout_context?.[0]?.rest_timer_usage || 0));
      return sum + (duration > 0 ? Math.min(rest, duration) : rest);
    }, 0);
    const activeWorkoutSeconds = Math.max(0, totalWorkoutSeconds - totalRestSeconds);
    const restPercent = totalWorkoutSeconds > 0 ? Math.min(100, Math.round((totalRestSeconds / totalWorkoutSeconds) * 100)) : 0;
    const completion = groupTarget ? Math.min(100, Math.round((totalGroupXp / groupTarget) * 100)) : 0;
    const needAttentionCount = athleteRows.filter((row) => row.sessions < Math.ceil(sessionTarget * 0.75)).length;
    const onPlanCount = athleteRows.filter((row) => row.sessions >= sessionTarget).length;
    const topAthlete = athleteRows.slice().sort((a, b) => (b.sessions - a.sessions) || (b.xp - a.xp) || a.name.localeCompare(b.name))[0] || null;
    return {
      members,
      athleteRows,
      totalGroupXp,
      totalSessions: rows.length,
      totalWorkoutSeconds,
      activeWorkoutSeconds,
      totalRestSeconds,
      restPercent,
      activeAthletes: new Set(rows.map((row) => row.profile_id)).size,
      avgXp: members.length ? Math.round(totalGroupXp / members.length) : totalGroupXp,
      sessionTarget,
      groupTarget,
      athleteTarget,
      calorieTarget,
      nutritionTargets: groupNutritionTargets,
      nutritionRows: memberNutritionRows,
      nutritionStory,
      dashboardNutritionDays,
      totalReps,
      totalVolume,
      completion,
      needAttentionCount,
      onPlanCount,
      topAthlete,
    };
  }, [activeSeason, activeSessions, analyticsWindow, dashboardGroup, nutritionRows]);

  useEffect(() => {
    if (!selectedGroupId || dashboardGroup?.id !== selectedGroupId || !dashboardGroupData) return;
    if (focusedDashboardGroupIdRef.current === selectedGroupId) return;
    focusedDashboardGroupIdRef.current = selectedGroupId;
    window.requestAnimationFrame(() => {
      document.getElementById("club-workout-group-dashboard")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [dashboardGroup?.id, dashboardGroupData, selectedGroupId]);

  const groupChartData = useMemo(() => groupAnalytics.map((group) => ({
    name: group.name,
    xp: group.xp,
    sessions: group.sessions,
    active: group.activeAthletes,
  })), [groupAnalytics]);
  const clubGroupChartMetrics = useMemo(() => ([
    { key: "xp", label: "XP", color: brandSurface.primary },
    { key: "sessions", label: "Sessions", color: analyticsGold },
    { key: "active", label: "Active athletes", color: "#38BDF8" },
  ]), [brandSurface.primary]);
  const activeClubGroupChartMetric = clubGroupChartMetrics.find((metric) => metric.key === clubGroupChartMetric) || clubGroupChartMetrics[0];

  const focusLabelForScore = (score) => {
    const value = Number(score || 0);
    if (!value) return "No focus feedback";
    if (value >= 4.5) return "Locked in";
    if (value >= 4) return "Strong focus";
    if (value >= 3.3) return "Steady";
    if (value >= 2.6) return "Variable";
    return "Needs follow-up";
  };
  const focusColorForScore = (score) => {
    if (!Number(score || 0)) return "rgba(255,255,255,0.45)";
    return "#38BDF8";
  };

  const trendData = useMemo(() => {
    const buckets = new Map();
    countedSessions.forEach((row) => {
      const day = new Date(row.started_at).toISOString().slice(0, 10);
      const current = buckets.get(day) || { date: day.slice(5), xp: 0, sessions: 0 };
      current.xp += Number(row.total_xp || 0);
      current.sessions += 1;
      buckets.set(day, current);
    });
    return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
  }, [countedSessions]);
  const trainingTrendMetrics = useMemo(() => ([
    { key: "xp", label: "XP", color: brandSurface.primary },
    { key: "sessions", label: "Sessions", color: analyticsGold },
  ]), [brandSurface.primary]);
  const activeTrainingTrendMetric = trainingTrendMetrics.find((metric) => metric.key === trainingTrendMetric) || trainingTrendMetrics[0];

  const nutritionWindow = useMemo(() => {
    if (activeNutritionSeason) {
      return {
        label: `${activeNutritionSeason.name}: ${activeNutritionSeason.starts_on || "open"} to ${activeNutritionSeason.ends_on || "open"}`,
        start: activeNutritionSeason.starts_on ? new Date(`${activeNutritionSeason.starts_on}T00:00:00`) : null,
        end: activeNutritionSeason.ends_on ? new Date(`${activeNutritionSeason.ends_on}T23:59:59.999`) : null,
      };
    }
    if (nutritionRangeId === "last_all") return { label: "All nutrition", start: null, end: null, days: null };
    if (nutritionRangeId === "date") {
      const selectedDate = nutritionSelectedDate || localDateInput(new Date());
      return {
        label: formatDateFilterLabel(selectedDate),
        start: new Date(`${selectedDate}T00:00:00`),
        end: new Date(`${selectedDate}T23:59:59.999`),
        days: 1,
      };
    }
    const presetDays = nutritionRangeId === "custom" ? Number(nutritionCustomDays) : Number((nutritionRangeId || "last_7").replace("last_", ""));
    const days = Number.isFinite(presetDays) && presetDays > 0 ? Math.min(Math.round(presetDays), 1095) : 7;
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    return { label: `Last ${days} days`, start, end: null, days };
  }, [activeNutritionSeason, nutritionCustomDays, nutritionRangeId, nutritionSelectedDate]);

  const inNutritionWindow = useCallback((row) => {
    const time = dateOnlyTime(row.log_date);
    if (!time) return false;
    if (nutritionWindow.start && time < dateOnlyTime(nutritionWindow.start.toISOString().slice(0, 10))) return false;
    if (nutritionWindow.end && time > dateOnlyTime(nutritionWindow.end.toISOString().slice(0, 10))) return false;
    return true;
  }, [nutritionWindow]);

  const nutritionWindowDays = useMemo(() => {
    const today = new Date();
    if (activeNutritionSeason) {
      const times = nutritionRows.map((row) => dateOnlyTime(row.log_date)).filter(Boolean);
      const start = activeNutritionSeason.starts_on ? new Date(`${activeNutritionSeason.starts_on}T00:00:00`) : new Date(times.length ? Math.min(...times) : today.getTime());
      const end = activeNutritionSeason.ends_on ? new Date(`${activeNutritionSeason.ends_on}T00:00:00`) : today;
      return inclusiveDays(start, end);
    }
    if (nutritionWindow.days) return nutritionWindow.days;
    const times = nutritionRows.map((row) => dateOnlyTime(row.log_date)).filter(Boolean);
    if (!times.length) return 1;
    return inclusiveDays(new Date(Math.min(...times)), today);
  }, [activeNutritionSeason, nutritionRows, nutritionWindow.days]);

  const selectedNutritionGroup = useMemo(() => groups.find((group) => group.id === nutritionGroupFilter) || null, [groups, nutritionGroupFilter]);

  useEffect(() => {
    const clubKey = activeClubId || "__none__";
    if (!groups.length) {
      nutritionDefaultedClubRef.current = "";
      if (nutritionGroupFilter !== "all") setNutritionGroupFilter("all");
      return;
    }

    const currentGroupExists = nutritionGroupFilter !== "all" && groups.some((group) => group.id === nutritionGroupFilter);
    const shouldDefaultForClub = nutritionDefaultedClubRef.current !== clubKey;
    if (currentGroupExists && !shouldDefaultForClub) return;
    if (nutritionGroupFilter === "all" && !shouldDefaultForClub) return;

    const requestedGroup = selectedGroupId && groups.some((group) => group.id === selectedGroupId)
      ? selectedGroupId
      : groups[0].id;
    nutritionDefaultedClubRef.current = clubKey;
    setNutritionGroupFilter(requestedGroup);
    setNutritionAthleteFilter("all");
  }, [activeClubId, groups, nutritionGroupFilter, selectedGroupId]);

  const nutritionOverview = useMemo(() => {
    const selectedGroups = selectedNutritionGroup ? [selectedNutritionGroup] : groups;
    const selectedGroupIds = new Set(selectedGroups.map((group) => group.id));
    const sum = (items, key) => items.reduce((total, row) => total + Number(row[key] || 0), 0);
    return athletes.map((athlete) => {
      const memberGroups = groups.filter((group) => {
        if (selectedGroupIds.size && !selectedGroupIds.has(group.id)) return false;
        return getCountingWorkoutMemberIds(group).includes(athlete.id);
      });
      if (!memberGroups.length) return null;
      const rows = nutritionRows.filter((row) => row.user_id === athlete.id && inNutritionWindow(row));
      const dailyTargets = memberGroups.map((group) => Number(group.daily_calorie_target || 2500)).filter((target) => Number.isFinite(target) && target > 0);
      const target = dailyTargets.length ? Math.max(...dailyTargets) : 2500;
      const calories = sum(rows, "calories");
      const mealMap = new Map();
      rows.forEach((row) => {
        const key = formatMealSection(row.meal_section);
        const current = mealMap.get(key) || { section: key, calories: 0, foods: [] };
        current.calories += Number(row.calories || 0);
        if (row.food_name && current.foods.length < 2) current.foods.push(row.food_name);
        mealMap.set(key, current);
      });
      const targetRange = target * nutritionWindowDays;
      return {
        id: athlete.id,
        name: [athlete.name, athlete.surname].filter(Boolean).join(" ") || athlete.username || "Athlete",
        profile_img: athlete.profile_img,
        groups: memberGroups.map((group) => group.name),
        target,
        targetRange,
        calories,
        protein: sum(rows, "protein_g"),
        carbs: sum(rows, "carbs_g"),
        fat: sum(rows, "fat_g"),
        fiber: sum(rows, "fiber_g"),
        sugar: sum(rows, "sugar_g"),
        sodium: sum(rows, "sodium_mg"),
        daysLogged: new Set(rows.map((row) => row.log_date)).size,
        lastLog: rows.map((row) => row.log_date).sort().reverse()[0] || null,
        pct: targetRange ? Math.min(140, Math.round((calories / targetRange) * 100)) : 0,
        meals: Array.from(mealMap.values()).sort((a, b) => b.calories - a.calories),
        popularFoods: buildFoodItemRows(rows).slice(0, 6),
      };
    }).filter(Boolean).sort((a, b) => a.pct - b.pct || b.calories - a.calories || a.name.localeCompare(b.name));
  }, [athletes, groups, inNutritionWindow, nutritionRows, nutritionWindowDays, selectedNutritionGroup]);

  const nutritionAthleteOptions = useMemo(() => nutritionOverview
    .map((row) => ({ id: row.id, name: row.name, groups: row.groups.join(", ") }))
    .sort((a, b) => a.name.localeCompare(b.name)), [nutritionOverview]);

  useEffect(() => {
    if (nutritionAthleteFilter !== "all" && !nutritionAthleteOptions.some((athlete) => athlete.id === nutritionAthleteFilter)) {
      setNutritionAthleteFilter("all");
    }
  }, [nutritionAthleteFilter, nutritionAthleteOptions]);

  const displayedNutritionOverview = useMemo(() => {
    if (nutritionAthleteFilter === "all") return nutritionOverview;
    return nutritionOverview.filter((row) => row.id === nutritionAthleteFilter);
  }, [nutritionAthleteFilter, nutritionOverview]);

  useEffect(() => {
    setNutritionAthleteCardsExpanded(false);
  }, [nutritionAthleteFilter, nutritionGroupFilter, nutritionRangeId]);

  const visibleNutritionOverview = useMemo(() => {
    if (nutritionAthleteFilter !== "all" || nutritionAthleteCardsExpanded) return displayedNutritionOverview;
    return displayedNutritionOverview.slice(0, NUTRITION_ATHLETE_CARD_PREVIEW_LIMIT);
  }, [displayedNutritionOverview, nutritionAthleteCardsExpanded, nutritionAthleteFilter]);

  const hiddenNutritionAthleteCount = Math.max(0, displayedNutritionOverview.length - visibleNutritionOverview.length);

  const nutritionSummary = useMemo(() => {
    const totalCalories = displayedNutritionOverview.reduce((sum, row) => sum + row.calories, 0);
    const onTarget = displayedNutritionOverview.filter((row) => row.pct >= 90).length;
    const needsAttention = displayedNutritionOverview.filter((row) => row.daysLogged === 0 || row.pct < 70).length;
    const totalProtein = displayedNutritionOverview.reduce((sum, row) => sum + row.protein, 0);
    const totalCarbs = displayedNutritionOverview.reduce((sum, row) => sum + row.carbs, 0);
    const totalFat = displayedNutritionOverview.reduce((sum, row) => sum + row.fat, 0);
    const totalFiber = displayedNutritionOverview.reduce((sum, row) => sum + row.fiber, 0);
    const totalSugar = displayedNutritionOverview.reduce((sum, row) => sum + row.sugar, 0);
    const totalSodium = displayedNutritionOverview.reduce((sum, row) => sum + row.sodium, 0);
    return { totalCalories, onTarget, needsAttention, totalProtein, totalCarbs, totalFat, totalFiber, totalSugar, totalSodium };
  }, [displayedNutritionOverview]);

  const nutritionSummaryItems = useMemo(() => ([
    { label: "Total Calories", value: nutritionSummary.totalCalories.toLocaleString(), unit: "kcal", color: "#F59E0B", icon: faFire },
    { label: "On Target", value: `${nutritionSummary.onTarget}/${displayedNutritionOverview.length}`, unit: "athletes 90%+", color: "#16A34A", icon: faCircleCheck },
    { label: "Needs Attention", value: nutritionSummary.needsAttention.toLocaleString(), unit: "athletes below 70%", color: "#DC2626", icon: faTriangleExclamation },
    { label: "Total Protein", value: `${Math.round(nutritionSummary.totalProtein / 1000).toLocaleString()}k`, unit: "grams", color: "#2563EB", icon: faDumbbell },
    { label: "Total Carbs", value: `${Math.round(nutritionSummary.totalCarbs / 1000).toLocaleString()}k`, unit: "grams", color: "#D97706", icon: faBolt },
    { label: "Total Fat", value: `${Math.round(nutritionSummary.totalFat / 1000).toLocaleString()}k`, unit: "grams", color: "#7C3AED", icon: faUtensils },
    { label: "Total Fiber", value: Math.round(nutritionSummary.totalFiber).toLocaleString(), unit: "grams", color: "#0D9488", icon: faClipboardList },
    { label: "Total Sugar", value: Math.round(nutritionSummary.totalSugar).toLocaleString(), unit: "grams", color: "#EC4899", icon: faClipboardList },
    { label: "Total Sodium", value: Math.round(nutritionSummary.totalSodium).toLocaleString(), unit: "mg", color: "#64748B", icon: faClipboardList },
  ]), [displayedNutritionOverview.length, nutritionSummary]);

  const nutritionPlanAdoption = useMemo(() => {
    const eventInWindow = (event) => inNutritionWindow({ log_date: event?.log_date });
    return (nutritionPlans || []).map((plan) => {
      const items = sortNutritionPlanItems(plan.nutrition_plan_items || []);
      const group = groups.find((row) => row.id === plan.group_id) || plan.workout_groups || null;
      const athletes = getCombinedWorkoutMembers(group)
        .filter(isCountingWorkoutAthlete)
        .map((member) => ({
          id: member.athlete_id || member.profiles?.id,
          name: displayProfileName(member.profiles, "Athlete"),
          profile_img: member.profiles?.profile_img || null,
        }))
        .filter((athlete) => athlete.id);
      const activeAssignments = (plan.nutrition_plan_assignments || []).filter(isActiveNutritionAssignment);
      const activeAssignmentsByAthlete = new Map(activeAssignments.map((assignment) => [assignment.athlete_id, assignment]));
      const planDueDates = getNutritionPlanDueDates({ plan, window: nutritionWindow });
      const athleteRows = athletes.map((athlete) => {
        const assignment = activeAssignmentsByAthlete.get(athlete.id) || null;
        const events = (assignment?.nutrition_plan_log_events || []).filter(eventInWindow);
        const itemTarget = Math.max(1, items.length || 1);
        const dueDates = assignment ? planDueDates : [];
        const dueDateSet = new Set(dueDates);
        const eventItemsByDate = events.reduce((map, event) => {
          const key = String(event.log_date || "").slice(0, 10);
          if (!key) return map;
          map.set(key, (map.get(key) || 0) + Math.max(1, Number(event.item_count || 0)));
          return map;
        }, new Map());
        const relevantDateKeys = dueDateSet.size
          ? dueDates
          : Array.from(eventItemsByDate.keys()).sort();
        const expectedPlanDays = Math.max(1, relevantDateKeys.length || 1);
        const startedPlanDays = relevantDateKeys.filter((key) => Number(eventItemsByDate.get(key) || 0) > 0).length;
        const completedPlanDays = relevantDateKeys.filter((key) => Number(eventItemsByDate.get(key) || 0) >= itemTarget).length;
        const loggedItems = relevantDateKeys.reduce((total, key) => total + Math.min(itemTarget, Number(eventItemsByDate.get(key) || 0)), 0);
        const expectedItems = expectedPlanDays * itemTarget;
        const completed = completedPlanDays >= expectedPlanDays;
        const dueDayRows = relevantDateKeys.map((key) => {
          const loggedItemCount = Number(eventItemsByDate.get(key) || 0);
          return {
            key,
            label: formatNutritionDueDateLabel(key),
            loggedItemCount,
            status: loggedItemCount >= itemTarget ? "complete" : loggedItemCount > 0 ? "partial" : "missed",
          };
        });
        const missedDueDates = dueDayRows.filter((day) => day.status !== "complete");
        return {
          ...athlete,
          assignment,
          hasPlan: !!assignment,
          loggedItems,
          expectedItems,
          expectedPlanDays,
          startedPlanDays,
          completedPlanDays,
          dueDayRows,
          missedDueDates,
          statusLabel: !assignment
            ? "No plan"
            : completed
            ? `${completedPlanDays}/${expectedPlanDays} days`
            : loggedItems > 0
            ? `${completedPlanDays}/${expectedPlanDays} days`
            : `0/${expectedPlanDays} days`,
          statusTone: !assignment ? "missing" : completed ? "complete" : loggedItems > 0 ? "logged" : "idle",
        };
      });
      const assignedCount = athleteRows.filter((athlete) => athlete.hasPlan).length;
      const loggedCount = athleteRows.reduce((total, athlete) => total + Number(athlete.startedPlanDays || 0), 0);
      const completedCount = athleteRows.reduce((total, athlete) => total + Number(athlete.completedPlanDays || 0), 0);
      const expectedPlanDayCount = athleteRows.reduce((total, athlete) => total + (athlete.hasPlan ? Number(athlete.expectedPlanDays || 0) : 0), 0);
      const calories = items.reduce((total, item) => total + Number(item.calories || 0), 0);
      return {
        plan,
        group,
        items,
        athletes: athleteRows,
        assignedCount,
        totalAthletes: athletes.length || activeAssignments.length,
        loggedCount,
        completedCount,
        expectedPlanDayCount,
        expectedDueDays: Math.max(1, planDueDates.length || 1),
        calories,
        missingAthletes: athleteRows.filter((athlete) => !athlete.hasPlan),
      };
    }).filter((adoption) => nutritionGroupFilter === "all" || adoption.plan.group_id === nutritionGroupFilter || adoption.group?.id === nutritionGroupFilter);
  }, [groups, inNutritionWindow, nutritionGroupFilter, nutritionPlans, nutritionWindow]);

  const nutritionPlanPageCount = Math.max(1, Math.ceil(nutritionPlanAdoption.length / NUTRITION_ASSIGNED_PLAN_PAGE_SIZE));
  const boundedNutritionPlanPage = Math.min(nutritionPlanPage, nutritionPlanPageCount - 1);
  const pagedNutritionPlanAdoption = nutritionPlanAdoption.slice(
    boundedNutritionPlanPage * NUTRITION_ASSIGNED_PLAN_PAGE_SIZE,
    (boundedNutritionPlanPage + 1) * NUTRITION_ASSIGNED_PLAN_PAGE_SIZE
  );
  const nutritionPlanPageStart = nutritionPlanAdoption.length
    ? boundedNutritionPlanPage * NUTRITION_ASSIGNED_PLAN_PAGE_SIZE + 1
    : 0;
  const nutritionPlanPageEnd = Math.min(nutritionPlanAdoption.length, (boundedNutritionPlanPage + 1) * NUTRITION_ASSIGNED_PLAN_PAGE_SIZE);

  useEffect(() => {
    setNutritionPlanPage(0);
    setNutritionPlanAthletesExpandedById({});
  }, [nutritionGroupFilter]);

  useEffect(() => {
    if (nutritionPlanPage > nutritionPlanPageCount - 1) {
      setNutritionPlanPage(Math.max(0, nutritionPlanPageCount - 1));
    }
  }, [nutritionPlanPage, nutritionPlanPageCount]);

  const comparisonOptions = useMemo(() => [
    { value: "xp", label: "XP" },
    { value: "sessions", label: "Sessions" },
    { value: "calories", label: "Calories" },
    { value: "caloriePct", label: "Calorie Target" },
    { value: "activeAthletes", label: "Active Athletes" },
  ], []);

  const comparisonAthleteOptions = useMemo(() => athletes.map((athlete) => ({
    id: athlete.id,
    name: [athlete.name, athlete.surname].filter(Boolean).join(" ") || athlete.username || "Athlete",
  })).sort((a, b) => a.name.localeCompare(b.name)), [athletes]);

  const seasonComparisonLab = useMemo(() => {
    const displayWorkoutName = (name) => {
      const text = String(name || "Unnamed workout").trim();
      if (!text) return "Unnamed workout";
      return text.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    };
    const dayName = (value) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleDateString("en-IE", { weekday: "long" });
    };
    const nutritionInSeason = (row, season) => {
      const time = dateOnlyTime(row.log_date);
      if (!time) return false;
      if (season?.starts_on && time < dateOnlyTime(season.starts_on)) return false;
      if (season?.ends_on && time > dateOnlyTime(season.ends_on)) return false;
      return true;
    };
    const buildPanel = (seasonId, groupId, athleteId = "all") => {
      const season = seasons.find((item) => item.id === seasonId) || seasons[0] || null;
      const selectedGroups = groupId && groupId !== "all"
        ? groups.filter((group) => group.id === groupId)
        : groups.filter((group) => !season?.id || group.season_id === season.id);
      const usableGroups = selectedGroups.length ? selectedGroups : groups;
      const memberMap = new Map();
      usableGroups.forEach((group) => getCombinedWorkoutMembers(group).filter(isCountingWorkoutAthlete).forEach((member) => {
        const profile = member.profiles || member.profile;
        if (profile?.id) memberMap.set(profile.id, profile);
      }));
      if (athleteId && athleteId !== "all") {
        Array.from(memberMap.keys()).forEach((profileId) => {
          if (profileId !== athleteId) memberMap.delete(profileId);
        });
      }
      const athleteIds = new Set(memberMap.keys());
      const rowIds = new Set();
      usableGroups.forEach((group) => {
        const groupAthletes = new Set(getCountingWorkoutMemberIds(group));
        sessions
          .filter((row) => groupAthletes.has(row.profile_id) && (!season || inWindow(row, season)) && sessionCountsForGroup(row, group))
          .forEach((row) => rowIds.add(row.id));
      });
      const rows = sessions.filter((row) => rowIds.has(row.id) && athleteIds.has(row.profile_id));
      const foods = nutritionRows.filter((row) => athleteIds.has(row.user_id) && nutritionInSeason(row, season));
      const seasonDays = season ? inclusiveDays(
        season.starts_on ? new Date(`${season.starts_on}T00:00:00`) : new Date(),
        season.ends_on ? new Date(`${season.ends_on}T00:00:00`) : new Date(),
      ) : 1;

      const workoutMap = new Map();
      rows.forEach((row) => {
        const key = sessionWorkoutName(row);
        const current = workoutMap.get(key) || { name: displayWorkoutName(key), sessions: 0, xp: 0, athletes: new Set() };
        current.sessions += 1;
        current.xp += Number(row.total_xp || 0);
        current.athletes.add(row.profile_id);
        workoutMap.set(key, current);
      });
      const workouts = Array.from(workoutMap.values()).map((item) => ({ ...item, athletes: item.athletes.size, avgXp: item.sessions ? Math.round(item.xp / item.sessions) : 0 }));

      const groupRows = usableGroups.map((group) => {
        const groupAthletes = new Set(getCountingWorkoutMemberIds(group));
        const groupSessions = rows.filter((row) => groupAthletes.has(row.profile_id));
        const groupFoods = foods.filter((row) => groupAthletes.has(row.user_id));
        return {
          id: group.id,
          name: group.name,
          xp: groupSessions.reduce((sum, row) => sum + Number(row.total_xp || 0), 0),
          sessions: groupSessions.length,
          calories: groupFoods.reduce((sum, row) => sum + Number(row.calories || 0), 0),
          activeAthletes: new Set(groupSessions.map((row) => row.profile_id)).size,
        };
      }).sort((a, b) => b[compareMetric] - a[compareMetric]);

      const athleteRows = Array.from(memberMap.values()).map((profile) => {
        const athleteSessions = rows.filter((row) => row.profile_id === profile.id);
        const athleteFoods = foods.filter((row) => row.user_id === profile.id);
        const athleteGroups = usableGroups.filter((group) => getCountingWorkoutMemberIds(group).includes(profile.id));
        const target = Math.max(...athleteGroups.map((group) => Number(group.daily_calorie_target || 2500)).filter(Boolean), 2500) * seasonDays;
        const calories = athleteFoods.reduce((sum, row) => sum + Number(row.calories || 0), 0);
        return {
          id: profile.id,
          name: [profile.name, profile.surname].filter(Boolean).join(" ") || profile.username || "Athlete",
          profile_img: profile.profile_img,
          groups: athleteGroups.map((group) => group.name).join(", "),
          xp: athleteSessions.reduce((sum, row) => sum + Number(row.total_xp || 0), 0),
          sessions: athleteSessions.length,
          calories,
          caloriePct: target ? Math.round((calories / target) * 100) : 0,
          activeAthletes: athleteSessions.length ? 1 : 0,
        };
      }).sort((a, b) => b[compareMetric] - a[compareMetric]);

      const trainingDays = Array.from(rows.reduce((map, row) => {
        const key = dayName(row.started_at);
        const current = map.get(key) || { name: key, sessions: 0, xp: 0 };
        current.sessions += 1;
        current.xp += Number(row.total_xp || 0);
        map.set(key, current);
        return map;
      }, new Map()).values()).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);

      return {
        season,
        groups: usableGroups,
        xp: rows.reduce((sum, row) => sum + Number(row.total_xp || 0), 0),
        sessions: rows.length,
        calories: foods.reduce((sum, row) => sum + Number(row.calories || 0), 0),
        activeAthletes: new Set(rows.map((row) => row.profile_id)).size,
        caloriePct: athleteRows.length ? Math.round(athleteRows.reduce((sum, row) => sum + row.caloriePct, 0) / athleteRows.length) : 0,
        topWorkouts: [...workouts].sort((a, b) => b.xp - a.xp || b.sessions - a.sessions).slice(0, 5),
        lowestWorkouts: [...workouts].filter((item) => item.sessions > 0).sort((a, b) => a.avgXp - b.avgXp || a.sessions - b.sessions).slice(0, 5),
        groupRows,
        athleteRows,
        trainingDays,
        bestDay: trainingDays[0] || null,
      };
    };
    const buildDrilldown = (seasonId, groupId, athleteId) => {
      const season = seasons.find((item) => item.id === seasonId) || seasons[0] || null;
      const selectedGroups = groupId && groupId !== "all" ? groups.filter((group) => group.id === groupId) : groups.filter((group) => !season?.id || group.season_id === season.id);
      const usableGroups = selectedGroups.length ? selectedGroups : groups;
      const athleteSet = new Set();
      const profileMap = new Map();
      usableGroups.forEach((group) => getCombinedWorkoutMembers(group).filter(isCountingWorkoutAthlete).forEach((member) => {
        const profile = member.profiles || member.profile;
        if (member.athlete_id) athleteSet.add(member.athlete_id);
        if (profile?.id) profileMap.set(profile.id, profile);
      }));
      const filteredAthletes = athleteId && athleteId !== "all" ? new Set([athleteId]) : athleteSet;
      const rows = sessions.filter((row) => filteredAthletes.has(row.profile_id) && (!season || inWindow(row, season)));
      const foods = nutritionRows.filter((row) => filteredAthletes.has(row.user_id) && (!season || nutritionInSeason(row, season)));
      const workoutMap = new Map();
      rows.forEach((row) => {
        const key = sessionWorkoutName(row);
        const current = workoutMap.get(key) || { name: displayWorkoutName(key), sessions: 0, xp: 0, athletes: new Set() };
        current.sessions += 1;
        current.xp += Number(row.total_xp || 0);
        current.athletes.add(row.profile_id);
        workoutMap.set(key, current);
      });
      const workouts = Array.from(workoutMap.values()).map((item) => ({ ...item, athletes: item.athletes.size, avgXp: item.sessions ? Math.round(item.xp / item.sessions) : 0 }));
      const days = Array.from(rows.reduce((map, row) => {
        const key = dayName(row.started_at);
        const current = map.get(key) || { name: key, sessions: 0, xp: 0 };
        current.sessions += 1;
        current.xp += Number(row.total_xp || 0);
        map.set(key, current);
        return map;
      }, new Map()).values()).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);
      const selectedProfile = athleteId && athleteId !== "all" ? profileMap.get(athleteId) : null;
      return {
        label: selectedProfile ? ([selectedProfile.name, selectedProfile.surname].filter(Boolean).join(" ") || selectedProfile.username || "Athlete") : (groupId !== "all" ? usableGroups[0]?.name : "All teams"),
        rows,
        foods,
        workoutsBySessions: [...workouts].sort((a, b) => b.sessions - a.sessions || b.xp - a.xp).slice(0, 6),
        workoutsByXp: [...workouts].sort((a, b) => b.xp - a.xp || b.sessions - a.sessions).slice(0, 6),
        days,
        calories: foods.reduce((sum, row) => sum + Number(row.calories || 0), 0),
        protein: foods.reduce((sum, row) => sum + Number(row.protein_g || 0), 0),
      };
    };
    const a = buildPanel(compareSeasonAId, compareGroupAId, compareAthleteAId);
    const b = buildPanel(compareSeasonBId, compareGroupBId, compareAthleteBId);
    const drilldown = buildDrilldown(compareSeasonAId, compareDrilldownGroupId, compareAthleteId);
    const metricRows = [
      { key: "xp", label: "XP", icon: faBolt, tone: "#2563EB", suffix: "" },
      { key: "sessions", label: "Sessions", icon: faDumbbell, tone: "#16A34A", suffix: "" },
      { key: "calories", label: "Calories", icon: faUtensils, tone: "#F59E0B", suffix: "" },
      { key: "caloriePct", label: "Calorie Target", icon: faCircleCheck, tone: "#0D9488", suffix: "%" },
      { key: "activeAthletes", label: "Active Athletes", icon: faUsers, tone: "#7C3AED", suffix: "" },
    ].map((metric) => {
      const left = Number(a[metric.key] || 0);
      const right = Number(b[metric.key] || 0);
      const max = Math.max(left, right, 1);
      return {
        ...metric,
        left,
        right,
        max,
        delta: left - right,
        winner: left === right ? "Even" : left > right ? "Season A" : "Season B",
      };
    });
    const athleteDelta = a.athleteRows.map((athlete) => {
      const other = b.athleteRows.find((row) => row.id === athlete.id) || {};
      return {
        ...athlete,
        previous: Number(other[compareMetric] || 0),
        delta: Number(athlete[compareMetric] || 0) - Number(other[compareMetric] || 0),
      };
    }).sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta)).slice(0, 8);
    const workoutComparisonMap = new Map();
    const addWorkoutComparisonRow = (workout, side) => {
      const key = String(workout?.name || "No workouts").toLowerCase();
      const current = workoutComparisonMap.get(key) || { name: workout?.name || "No workouts", a: { xp: 0, sessions: 0 }, b: { xp: 0, sessions: 0 } };
      current[side] = {
        xp: Number(workout?.xp || 0),
        sessions: Number(workout?.sessions || 0),
      };
      workoutComparisonMap.set(key, current);
    };
    a.topWorkouts.forEach((workout) => addWorkoutComparisonRow(workout, "a"));
    b.topWorkouts.forEach((workout) => addWorkoutComparisonRow(workout, "b"));
    const workoutComparison = Array.from(workoutComparisonMap.values())
      .map((row) => ({ ...row, deltaXp: row.a.xp - row.b.xp, peakXp: Math.max(row.a.xp, row.b.xp, 1) }))
      .sort((x, y) => Math.max(y.a.xp, y.b.xp) - Math.max(x.a.xp, x.b.xp))
      .slice(0, 6);
    const teamComparisonMap = new Map();
    const addTeamComparisonRow = (group, side) => {
      const key = group?.id || group?.name || "No teams";
      const current = teamComparisonMap.get(key) || { id: key, name: group?.name || "No teams", a: 0, b: 0 };
      current[side] = Number(group?.[compareMetric] || 0);
      teamComparisonMap.set(key, current);
    };
    a.groupRows.forEach((group) => addTeamComparisonRow(group, "a"));
    b.groupRows.forEach((group) => addTeamComparisonRow(group, "b"));
    const teamComparison = Array.from(teamComparisonMap.values())
      .map((row) => ({ ...row, delta: row.a - row.b, peak: Math.max(row.a, row.b, 1) }))
      .sort((x, y) => Math.max(y.a, y.b) - Math.max(x.a, x.b))
      .slice(0, 8);
    const activeMetric = metricRows.find((metric) => metric.key === compareMetric) || metricRows[0];
    return { a, b, athleteDelta, drilldown, metrics: metricRows, activeMetric, workoutComparison, teamComparison };
  }, [compareAthleteAId, compareAthleteBId, compareAthleteId, compareDrilldownGroupId, compareGroupAId, compareGroupBId, compareMetric, compareSeasonAId, compareSeasonBId, groups, inWindow, nutritionRows, seasons, sessions]);

  const reloadClubs = useCallback(async (nextActiveId = activeClubId) => {
    const { data } = await supabase.from("workout_clubs").select("*").order("created_at", { ascending: false });
    setClubs(data || []);
    if (nextActiveId) {
      setActiveClubId(nextActiveId);
      if (activeClubStorageKey && nextActiveId !== "__new__") window.localStorage.setItem(activeClubStorageKey, nextActiveId);
      return;
    }
    const savedActiveClubId = activeClubStorageKey ? window.localStorage.getItem(activeClubStorageKey) : null;
    if (savedActiveClubId && data?.some((club) => club.id === savedActiveClubId)) setActiveClubId(savedActiveClubId);
    else setActiveClubId(data?.[0]?.id || null);
  }, [activeClubId, activeClubStorageKey]);

  useEffect(() => {
    if (!profileId || !hasAccess) return undefined;
    const refreshDashboard = () => {
      if (realtimeRefreshTimerRef.current) clearTimeout(realtimeRefreshTimerRef.current);
      realtimeRefreshTimerRef.current = setTimeout(async () => {
        await reloadClubs(activeClubId);
        await loadClubData();
      }, 300);
    };

    const channel = supabase
      .channel(`workout-club-dashboard-web-${profileId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_clubs" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_club_coaches" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_club_seasons" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_groups" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_group_members" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_group_calendar_sessions" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_group_calendar_session_athletes" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_group_calendar_session_coaches" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_sessions" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_context" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "workout_exercise_summary" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "user_workout_feedback" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "food_log" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "nutrition_plans" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "nutrition_plan_items" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "nutrition_plan_assignments" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "nutrition_plan_progress" }, refreshDashboard)
      .on("postgres_changes", { event: "*", schema: "public", table: "nutrition_plan_log_events" }, refreshDashboard)
      .subscribe();

    return () => {
      if (realtimeRefreshTimerRef.current) clearTimeout(realtimeRefreshTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [activeClubId, hasAccess, loadClubData, profileId, reloadClubs]);

  const saveClub = async ({ silent = false } = {}) => {
    if (!profileId) return;
    if (savingClub) return;
    if (activeClub?.id ? !canManageSelectedClub : !canCreateNewClub) {
      if (!silent) notify(activeClub?.id ? "You cannot edit this club." : "You already have a club. Switch clubs if you are part of another club.", "warning");
      return;
    }
    setSavingClub(true);
    try {
      const payload = buildClubPayload(clubForm, activeClub, clubLogoUrlInput.trim() || clubForm.logo_url);
      if (!payload.name) { notify("Club name is required.", "warning"); return; }
      const query = activeClub?.id
        ? supabase.from("workout_clubs").update(payload).eq("id", activeClub.id).select("*").single()
        : supabase.from("workout_clubs").insert({ ...payload, owner_profile_id: profileId }).select("*").single();
      const { data, error } = await query;
      if (error) { notify(error.message || "Could not save club.", "error"); return; }
      if (!activeClub?.id) {
        const { error: coachError } = await supabase.from("workout_club_coaches").insert({ club_id: data.id, profile_id: profileId, role: "owner", status: "active" });
        if (coachError) { notify(coachError.message || "Could not assign the club owner.", "error"); return; }
      }
      await reloadClubs(data.id);
      await loadClubData();
      setClubForm((prev) => ({ ...prev, logo_url: data?.logo_url || "" }));
      setClubLogoUrlInput("");
      setShowClubLogoUrlInput(false);
      if (!silent) notify(activeClub?.id ? "Club settings saved." : "Club created.", "success");
    } finally {
      setSavingClub(false);
    }
  };

  const deleteActiveClub = async () => {
    if (!activeClub?.id || !profileId) return;
    if (!canDeleteActiveClub) {
      notify("Only the club owner can delete this club.", "warning");
      return;
    }
    setConfirmAction({
      title: "Delete club?",
      message: `Delete ${activeClub.name}? Coaches, seasons, branding, and dashboard access will be removed. ${groups.length} workout group${groups.length === 1 ? "" : "s"} will stay intact but will no longer belong to this club.`,
      confirmLabel: deletingClub ? "Deleting..." : "Delete Club",
      tone: "danger",
      onConfirm: async () => {
        setDeletingClub(true);
        const deletedClubId = activeClub.id;
        try {
          const { error } = await supabase
            .from("workout_clubs")
            .delete()
            .eq("id", deletedClubId)
            .eq("owner_profile_id", profileId);
          if (error) {
            notify(error.message || "Could not delete club.", "error");
            return;
          }
          if (activeClubStorageKey) window.localStorage.removeItem(activeClubStorageKey);
          setActiveClubId(null);
          setClubs((prev) => prev.filter((club) => club.id !== deletedClubId));
          setGroups([]);
          setCoaches([]);
          setSeasons([]);
          setSessions([]);
          setCalendarSessions([]);
          setNutritionRows([]);
          setAllGroups((prev) => prev.map((group) => group.club_id === deletedClubId ? { ...group, club_id: null, season_id: null } : group));
          setSelectedGroup((prev) => prev?.club_id === deletedClubId ? { ...prev, club_id: null, season_id: null, workout_clubs: null, workout_club_seasons: null } : prev);
          setClubForm({ name: "", sport_id: "", logo_url: "", ...DEFAULT_COLORS, theme_workout_groups: false, theme_workouts: false, theme_nutrition: false, theme_sessions: false });
          setClubLogoUrlInput("");
          setShowClubLogoUrlInput(false);
          await reloadClubs(null);
          notify("Club deleted. Workout groups were kept.", "success");
        } finally {
          setDeletingClub(false);
        }
      },
    });
  };

  const visibleClubLogoUrl = showClubLogoUrlInput ? clubLogoUrlInput : clubForm.logo_url;
  const cancelClubLogoUrlInput = () => {
    if (!showClubLogoUrlInput) return;
    if (clubLogoUrlInput.trim()) {
      setClubForm((prev) => ({ ...prev, logo_url: clubLogoUrlInput.trim() }));
    }
    setClubLogoUrlInput("");
    setShowClubLogoUrlInput(false);
  };

  const uploadClubLogoFile = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      notify("Choose an image file for the club logo.", "warning");
      return;
    }
    if (!canSaveClubBrand) {
      notify("Club Coaching is required to upload a club logo.", "warning");
      return;
    }
    setClubLogoUploading(true);
    try {
      const rawExtension = (file.name.split(".").pop() || file.type.split("/").pop() || "jpg").toLowerCase();
      const extension = rawExtension.replace(/[^a-z0-9]/g, "") || "jpg";
      const clubKey = String(activeClub?.id || clubForm.name || "draft-club")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "draft-club";
      const filename = `${profileId || "profile"}-${clubKey}-logo-${Date.now()}.${extension}`.replace(/[/\\]+/g, "-");
      const publicUrl = await uploadFileToR2({
        bucket: "media",
        folder: WORKOUT_GROUPS_MEDIA_FOLDER,
        filename,
        file,
      });
      setClubForm((prev) => ({ ...prev, logo_url: publicUrl }));
      setClubLogoUrlInput("");
      setShowClubLogoUrlInput(false);
      notify(activeClub?.id ? "Club logo uploaded." : "Club logo uploaded. Save the club to apply it.", "success");
    } catch (error) {
      console.error("uploadClubLogoFile error:", error);
      notify(error?.message || "Could not upload club logo.", "error");
    } finally {
      setClubLogoUploading(false);
    }
  };

  const addCoach = async (event) => {
    event?.preventDefault?.();
    if (!activeClub?.id || !coachUsername.trim() || coachSaving) return;
    if (!canManageClubStaff) {
      notify("Only the club owner can add staff or change staff roles.", "warning");
      return;
    }
    const username = coachUsername.trim().replace(/^@/, "");
    setCoachSaving(true);
    try {
      const { data: coach, error: coachLookupError } = await supabase
        .from("profiles")
        .select("id,username")
        .eq("username", username)
        .maybeSingle();
      if (coachLookupError) {
        notify(coachLookupError.message || "Could not look up that coach.", "error");
        return;
      }
      if (!coach?.id) {
        notify(`No ProAm account found for @${username}.`, "warning");
        return;
      }
      const requestedRole = CLUB_STAFF_ROLE_OPTIONS.some((option) => option.value === coachRole) ? coachRole : "coach";
      const { error } = await supabase.rpc("set_workout_club_staff_role", {
        p_club_id: activeClub.id,
        p_profile_id: coach.id,
        p_role: requestedRole,
      });
      if (error) {
        notify(error.message || "Could not add this coach.", "error");
        return;
      }
      setCoachUsername("");
      setCoachRole("coach");
      await loadClubData();
      notify(`@${username} added as ${formatClubStaffRole(requestedRole).toLowerCase()}.`, "success");
    } finally {
      setCoachSaving(false);
    }
  };

  const updateCoachRole = async (coach, requestedRole) => {
    if (!activeClub?.id || !coach?.profile_id || coach.role === requestedRole || staffRoleSavingId) return;
    if (!canManageClubStaff) {
      notify("Only the club owner can change staff roles.", "warning");
      return;
    }
    if (!CLUB_STAFF_ROLE_OPTIONS.some((option) => option.value === requestedRole)) return;
    setStaffRoleSavingId(coach.profile_id);
    try {
      const { error } = await supabase.rpc("set_workout_club_staff_role", {
        p_club_id: activeClub.id,
        p_profile_id: coach.profile_id,
        p_role: requestedRole,
      });
      if (error) {
        notify(error.message || "Could not update this coach’s role.", "error");
        return;
      }
      await loadClubData();
      notify(`@${coach.profiles?.username || "coach"} is now ${formatClubStaffRole(requestedRole).toLowerCase()}.`, "success");
    } finally {
      setStaffRoleSavingId(null);
    }
  };

  const removeCoach = async (coach) => {
    if (!coach?.id) return;
    if (!canManageClubStaff) {
      notify("Only the club owner can remove staff.", "warning");
      return;
    }
    setConfirmAction({
      title: "Remove coach?",
      message: `@${coach.profiles?.username || "coach"} will lose access to this club dashboard.`,
      confirmLabel: "Remove Coach",
      tone: "danger",
      onConfirm: async () => {
        const { error } = await supabase.from("workout_club_coaches").delete().eq("id", coach.id);
        if (error) { notify("Could not remove coach.", "error"); return; }
        await loadClubData();
        notify("Coach removed.", "success");
      },
    });
  };

  const saveSeason = async () => {
    if (!activeClub?.id || !seasonForm.name.trim()) return;
    if (!hasSeasonManagementAccess) {
      notify("Season management is included with Club Coaching.", "warning");
      return;
    }
    if (seasonForm.starts_on && seasonForm.ends_on && new Date(seasonForm.ends_on) <= new Date(seasonForm.starts_on)) {
      notify("Season end date must be after the start date.", "warning");
      return;
    }
    const { error } = await supabase.from("workout_club_seasons").insert({
      club_id: activeClub.id,
      name: seasonForm.name.trim(),
      sport_id: seasonForm.sport_id.trim() || activeClub.sport_id || null,
      starts_on: seasonForm.starts_on || null,
      ends_on: seasonForm.ends_on || null,
      status: seasonForm.status || "active",
    });
    if (error) { notify(error.message || "Could not create season.", "error"); return; }
    setSeasonForm({ name: "", sport_id: "", starts_on: "", ends_on: "", status: "active" });
    await loadClubData();
    notify("Season created.", "success");
  };

  const updateSeason = async (season, patch) => {
    if (!hasSeasonManagementAccess) {
      notify("Season management is included with Club Coaching.", "warning");
      return;
    }
    const { error } = await supabase.from("workout_club_seasons").update(patch).eq("id", season.id);
    if (error) { notify("Could not update season.", "error"); return; }
    await loadClubData();
    notify("Season updated.", "success");
  };

  const editSeasonDates = async (season) => {
    setEditingSeason({
      id: season.id,
      name: season.name || "",
      starts_on: season.starts_on || "",
      ends_on: season.ends_on || "",
    });
  };

  const saveSeasonDates = async (season) => {
    if (!editingSeason || editingSeason.id !== season.id) return;
    const name = editingSeason.name.trim();
    const starts_on = editingSeason.starts_on;
    const ends_on = editingSeason.ends_on;
    if (!name) {
      notify("Season name is required.", "warning");
      return;
    }
    if (starts_on && ends_on && new Date(ends_on) <= new Date(starts_on)) {
      notify("Season end date must be after the start date.", "warning");
      return;
    }
    await updateSeason(season, { name, starts_on: starts_on || null, ends_on: ends_on || null });
    setEditingSeason(null);
  };

  const deleteSeason = async (season) => {
    if (!season?.id) return;
    if (!hasSeasonManagementAccess) {
      notify("Season management is included with Club Coaching.", "warning");
      return;
    }
    const assignedGroups = groups.filter((group) => group.season_id === season.id).length;
    setConfirmAction({
      title: "Delete season?",
      message: assignedGroups
        ? `Delete ${season.name}? ${assignedGroups} workout group${assignedGroups === 1 ? "" : "s"} will be left in the club with no season.`
        : `Delete ${season.name}? This cannot be undone.`,
      confirmLabel: "Delete Season",
      tone: "danger",
      onConfirm: async () => {
        const { error } = await supabase.from("workout_club_seasons").delete().eq("id", season.id);
        if (error) { notify(error.message || "Could not delete season.", "error"); return; }
        setEditingSeason(null);
        await loadClubData();
        notify("Season deleted.", "success");
      },
    });
  };

  const attachGroup = async (seasonId = null, groupId = attachGroupId) => {
    if (!activeClub?.id || !groupId) return;
    const { error } = await supabase.from("workout_groups").update({ club_id: activeClub.id, season_id: seasonId }).eq("id", groupId);
    if (error) { notify("Could not update group.", "error"); return; }
    setAttachGroupId(groupId);
    const localGroup = [...allGroups, ...groups].find((group) => group.id === groupId);
    setSelectedGroup((prev) => ({
      ...(prev?.id === groupId ? prev : {}),
      ...(localGroup || {}),
      id: groupId,
      name: localGroup?.name || prev?.name || "Selected group",
      club_id: activeClub.id,
      season_id: seasonId,
    }));
    await loadClubData();
    notify(seasonId ? "Group season updated." : "Group added to club.", "success");
  };

  const openGroupEditModal = (config) => {
    setGroupEditModal(config);
    setGroupEditValue(config.initialValue || "");
    if (config.type !== "nutrition_targets") setGroupNutritionDraft({ calories: "", protein: "", carbs: "", fat: "" });
  };

  const mergeGroupLocal = (groupId, patch) => {
    setGroups((prev) => prev.map((item) => item.id === groupId ? { ...item, ...patch } : item));
    setAllGroups((prev) => prev.map((item) => item.id === groupId ? { ...item, ...patch } : item));
    setSelectedGroup((prev) => prev?.id === groupId ? { ...prev, ...patch } : prev);
    setGroupManageModal((prev) => prev?.id === groupId ? { ...prev, ...patch } : prev);
  };

  const copyGroupInviteCode = async (code, message = "Invite code copied.") => {
    const value = String(code || "").trim();
    if (!value) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(value);
      notify(message, "success");
    } catch {
      window.prompt("Copy this invite code", value);
      notify("Invite code ready to copy.", "info");
    }
  };

  const openGroupJoinCodeModal = (group, code) => {
    const athleteLimit = activeFeatureAccess?.limits?.athletes_per_group;
    const athleteCount = getCountingWorkoutMemberIds(group).length;
    const isAtAthleteLimit = typeof athleteLimit === "number" && athleteCount >= athleteLimit;
    setJoinCodeShare({
      code,
      groupName: group?.name || "Workout group",
      warning: isAtAthleteLimit
        ? `This group is at your current plan limit of ${athleteLimit} athlete${athleteLimit === 1 ? "" : "s"}. You can copy the code, but athletes will not be able to join until you upgrade.`
        : "",
    });
  };

  const getGroupJoinCode = async (group) => {
    if (!group?.id) return;
    if (group.join_code) {
      openGroupJoinCodeModal(group, group.join_code);
      return;
    }
    const code = generateWorkoutShareCode();
    const { data, error } = await supabase
      .from("workout_groups")
      .update({ join_code: code })
      .eq("id", group.id)
      .select("id,join_code")
      .single();
    if (error || !data?.join_code) {
      notify("Could not create invite code.", "error");
      return;
    }
    mergeGroupLocal(group.id, { join_code: data.join_code });
    openGroupJoinCodeModal({ ...group, join_code: data.join_code }, data.join_code);
    notify("Invite code generated.", "success");
  };

  const saveGroupMemberAnalyticsVisibility = async (group, nextVisibility) => {
    if (!group?.id || groupVisibilitySaving) return;
    const normalizedVisibility = nextVisibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
      ? WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group
      : WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly;
    if (getWorkoutGroupMemberAnalyticsVisibility(group) === normalizedVisibility) return;
    setGroupVisibilitySaving(true);
    try {
      const { error } = await supabase
        .from("workout_groups")
        .update({ member_analytics_visibility: normalizedVisibility })
        .eq("id", group.id);
      if (error) throw error;
      mergeGroupLocal(group.id, { member_analytics_visibility: normalizedVisibility });
      notify(normalizedVisibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group ? "Athletes can see everyone." : "Athletes can see only themselves.", "success");
    } catch (error) {
      console.error("saveGroupMemberAnalyticsVisibility error:", error);
      notify("Could not update member visibility.", "error");
    } finally {
      setGroupVisibilitySaving(false);
    }
  };

  const saveGroupLeadCoach = async (group, nextCoachId) => {
    if (!group?.id || groupCoachSaving) return;
    const coachOption = activeClubCoachOptions.find((coach) => coach.id === nextCoachId);
    if (!coachOption?.id) {
      notify("Choose an active club staff member for this group.", "warning");
      return;
    }
    if (group.coach_id === coachOption.id) return;
    setGroupCoachSaving(true);
    try {
      const { data, error } = await supabase
        .from("workout_groups")
        .update({ coach_id: coachOption.id })
        .eq("id", group.id)
        .select(`id,coach_id${WORKOUT_GROUP_COACH_RELATION}`)
        .single();
      if (error) throw error;
      const nextCoach = firstRelationRow(data?.coach) || coachOption.profile || null;
      mergeGroupLocal(group.id, {
        coach_id: data?.coach_id || coachOption.id,
        coach: nextCoach,
      });
      notify(`${displayProfileName(nextCoach, "Lead coach")} assigned to ${group.name || "this group"}.`, "success");
      await loadClubData();
    } catch (error) {
      console.error("saveGroupLeadCoach error:", error);
      notify(error?.message || "Could not assign lead coach.", "error");
    } finally {
      setGroupCoachSaving(false);
    }
  };

  const updateGroupNutritionTargets = (group) => {
    if (!group?.id) return;
    if (!hasNutritionAccess) {
      notify("Nutrition targets are included with Pro Coach and Club Coaching plans.", "warning");
      return;
    }
    const targets = getGroupNutritionTargets(group);
    setGroupNutritionDraft({
      calories: String(targets.calories),
      protein: String(targets.protein),
      carbs: String(targets.carbs),
      fat: String(targets.fat),
    });
    openGroupEditModal({
      type: "nutrition_targets",
      title: "Nutrition Targets",
      label: "Set calories and macros",
      group,
      field: "nutrition_targets",
      helper: "Daily calories, protein, carbs, and fat for this group.",
    });
  };

  const selectedNutritionPlanGroup = useMemo(() => (
    groups.find((group) => group.id === nutritionPlanForm.groupId) || groups[0] || null
  ), [groups, nutritionPlanForm.groupId]);

  const nutritionPlanAthletes = useMemo(() => (
    getCombinedWorkoutMembers(selectedNutritionPlanGroup)
      .filter(isCountingWorkoutAthlete)
      .map((member) => ({
        id: member.athlete_id || member.profiles?.id,
        name: member.profiles?.username || [member.profiles?.name, member.profiles?.surname].filter(Boolean).join(" ") || "Athlete",
        profile_img: member.profiles?.profile_img || null,
        is_demo_profile: !!member.is_demo_profile,
        demo_profile_id: member.demo_profile_id || null,
      }))
      .filter((member) => member.id)
  ), [selectedNutritionPlanGroup]);

  const filteredNutritionPlanAthletes = useMemo(() => {
    const query = nutritionPlanAthleteSearch.trim().toLowerCase();
    if (!query) return nutritionPlanAthletes;
    return nutritionPlanAthletes.filter((athlete) => String(athlete.name || "").toLowerCase().includes(query));
  }, [nutritionPlanAthleteSearch, nutritionPlanAthletes]);

  const selectedNutritionPlanAthleteCount = useMemo(() => {
    const selected = new Set(nutritionPlanForm.selectedAthleteIds);
    return nutritionPlanAthletes.filter((athlete) => selected.has(athlete.id)).length;
  }, [nutritionPlanAthletes, nutritionPlanForm.selectedAthleteIds]);

  const getPreferredNutritionCoachId = useCallback((group = selectedNutritionPlanGroup, currentCoachId = "") => {
    const validCoachIds = new Set(nutritionCoachOptions.map((coach) => coach.id));
    if (currentCoachId && validCoachIds.has(String(currentCoachId))) return String(currentCoachId);
    if (group?.coach_id && validCoachIds.has(String(group.coach_id))) return String(group.coach_id);
    if (profileId && validCoachIds.has(String(profileId))) return String(profileId);
    return nutritionCoachOptions[0]?.id || "";
  }, [nutritionCoachOptions, profileId, selectedNutritionPlanGroup]);

  const selectedNutritionCoachOption = useMemo(() => (
    nutritionCoachOptions.find((coach) => coach.id === nutritionPlanForm.coachId) || null
  ), [nutritionCoachOptions, nutritionPlanForm.coachId]);

  const selectFilteredNutritionPlanAthletes = useCallback(() => {
    if (!filteredNutritionPlanAthletes.length) return;
    setNutritionPlanForm((prev) => ({
      ...prev,
      selectedAthleteIds: uniqueIds([...prev.selectedAthleteIds, ...filteredNutritionPlanAthletes.map((athlete) => athlete.id)]),
    }));
  }, [filteredNutritionPlanAthletes]);

  const clearFilteredNutritionPlanAthletes = useCallback(() => {
    if (!filteredNutritionPlanAthletes.length) return;
    const filteredIds = new Set(filteredNutritionPlanAthletes.map((athlete) => athlete.id));
    setNutritionPlanForm((prev) => ({
      ...prev,
      selectedAthleteIds: prev.selectedAthleteIds.filter((id) => !filteredIds.has(id)),
    }));
  }, [filteredNutritionPlanAthletes]);

  useEffect(() => {
    if (!groups.length) return;
    setNutritionPlanForm((prev) => {
      const groupId = prev.groupId && groups.some((group) => group.id === prev.groupId) ? prev.groupId : groups[0].id;
      const group = groups.find((row) => row.id === groupId) || groups[0];
      const athleteIds = getCountingWorkoutMemberIds(group);
      const selectedAthleteIds = prev.groupId === groupId && prev.selectedAthleteIds.length ? prev.selectedAthleteIds.filter((id) => athleteIds.includes(id)) : athleteIds;
      return { ...prev, groupId, coachId: getPreferredNutritionCoachId(group, prev.coachId), selectedAthleteIds };
    });
  }, [getPreferredNutritionCoachId, groups]);

  const addNutritionPlanItem = () => {
    const foodName = nutritionPlanForm.foodName.trim();
    if (!foodName) {
      notify("Add a food name before adding it to the plan.", "warning");
      return;
    }
    const item = {
      meal_section: nutritionPlanForm.mealSection,
      food_name: foodName,
      brand: nutritionPlanForm.brand.trim() || null,
      image_url: null,
      calories: normalizePositiveTarget(nutritionPlanForm.calories, 0),
      protein_g: normalizePositiveTarget(nutritionPlanForm.protein, 0),
      carbs_g: normalizePositiveTarget(nutritionPlanForm.carbs, 0),
      fat_g: normalizePositiveTarget(nutritionPlanForm.fat, 0),
      fiber_g: normalizePositiveTarget(nutritionPlanForm.fiber, 0),
      sugar_g: normalizePositiveTarget(nutritionPlanForm.sugar, 0),
      sodium_mg: normalizePositiveTarget(nutritionPlanForm.sodium, 0),
      portion: normalizePositiveTarget(nutritionPlanForm.portion, 100),
      unit: nutritionPlanForm.unit.trim() || "g",
    };
    setNutritionPlanForm((prev) => ({
      ...prev,
      items: [...prev.items, item],
      foodName: "",
      brand: "",
      calories: "",
      protein: "",
      carbs: "",
      fat: "",
      fiber: "",
      sugar: "",
      sodium: "",
      portion: "100",
      unit: "g",
    }));
  };

  const removeNutritionPlanItem = (index) => {
    setNutritionPlanForm((prev) => ({ ...prev, items: prev.items.filter((_, itemIndex) => itemIndex !== index) }));
    setEditingNutritionPlanItemIndex((current) => (current === index ? null : current));
  };

  const updateNutritionPlanItem = (index, patch) => {
    setNutritionPlanForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const resetNutritionPlanEditor = (groupId = selectedNutritionPlanGroup?.id || groups[0]?.id || "") => {
    const group = groups.find((row) => row.id === groupId) || groups[0] || null;
    const athleteIds = getCountingWorkoutMemberIds(group);
    setNutritionPlanForm({
      ...EMPTY_NUTRITION_PLAN_FORM,
      groupId: group?.id || "",
      coachId: getPreferredNutritionCoachId(group),
      scheduledDays: [],
      selectedAthleteIds: athleteIds,
    });
    setEditingNutritionPlanId(null);
    setEditingNutritionPlanItemIndex(null);
    setNutritionPlanAthleteSearch("");
    setNutritionPlanEditorOpen(false);
  };

  const startEditNutritionPlan = (plan) => {
    if (!plan?.id) return;
    const groupId = plan.group_id || plan.workout_groups?.id || groups[0]?.id || "";
    const activeAssignments = (plan.nutrition_plan_assignments || []).filter(isActiveNutritionAssignment);
    setNutritionPlanForm({
      ...EMPTY_NUTRITION_PLAN_FORM,
      title: plan.title || "",
      groupId,
      coachId: getPreferredNutritionCoachId(groups.find((row) => row.id === groupId), plan.owner_id || profileId),
      scheduledDays: normalizeNutritionPlanDays(plan.scheduled_days),
      items: sortNutritionPlanItems(plan.nutrition_plan_items || []).map((item) => ({
        meal_section: formatMealSection(item.meal_section || "Breakfast"),
        food_name: item.food_name || "",
        brand: item.brand || "",
        image_url: item.image_url || null,
        calories: Number(item.calories || 0),
        protein_g: Number(item.protein_g || 0),
        carbs_g: Number(item.carbs_g || 0),
        fat_g: Number(item.fat_g || 0),
        fiber_g: Number(item.fiber_g || 0),
        sugar_g: Number(item.sugar_g || 0),
        sodium_mg: Number(item.sodium_mg || 0),
        portion: Number(item.portion || 100),
        unit: item.unit || "g",
      })),
      selectedAthleteIds: uniqueIds(activeAssignments.map((assignment) => assignment.athlete_id).filter(Boolean)),
    });
    setEditingNutritionPlanId(plan.id);
    setEditingNutritionPlanItemIndex(null);
    setNutritionPlanAthleteSearch("");
    setNutritionPlanEditorOpen(true);
  };

  const saveNutritionPlan = async () => {
    if (!hasNutritionAccess) {
      notify("Nutrition plans are included with Pro Coach and Club Coaching.", "warning");
      return;
    }
    if (!selectedNutritionPlanGroup) {
      notify("Choose a workout group before creating a nutrition plan.", "warning");
      return;
    }
    if (!nutritionPlanForm.title.trim()) {
      notify("Enter a plan title.", "warning");
      return;
    }
    if (!nutritionPlanForm.items.length) {
      notify("Add at least one meal item before assigning the plan.", "warning");
      return;
    }
    if (!nutritionPlanForm.selectedAthleteIds.length) {
      notify("Select at least one athlete.", "warning");
      return;
    }
    const selectedCoachId = getPreferredNutritionCoachId(selectedNutritionPlanGroup, nutritionPlanForm.coachId);
    if (!selectedCoachId) {
      notify("Choose a coach for this nutrition plan.", "warning");
      return;
    }
    setNutritionPlanSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      let planId = editingNutritionPlanId;
      if (planId) {
        const { error: planError } = await supabase
          .from("nutrition_plans")
          .update({
            owner_id: selectedCoachId,
            group_id: selectedNutritionPlanGroup.id,
            club_id: activeClub?.id || selectedNutritionPlanGroup.club_id || null,
            title: nutritionPlanForm.title.trim(),
            scheduled_days: normalizeNutritionPlanDays(nutritionPlanForm.scheduledDays),
            plan_type: "group",
            visibility: "group",
            status: "published",
          })
          .eq("id", planId);
        if (planError) throw planError;
        const { error: deleteItemsError } = await supabase
          .from("nutrition_plan_items")
          .delete()
          .eq("plan_id", planId);
        if (deleteItemsError) throw deleteItemsError;
      } else {
        const { data: plan, error: planError } = await supabase
          .from("nutrition_plans")
          .insert({
            owner_id: selectedCoachId,
            group_id: selectedNutritionPlanGroup.id,
            club_id: activeClub?.id || selectedNutritionPlanGroup.club_id || null,
            title: nutritionPlanForm.title.trim(),
            scheduled_days: normalizeNutritionPlanDays(nutritionPlanForm.scheduledDays),
            plan_type: "group",
            visibility: "group",
            status: "published",
            start_date: today,
          })
          .select("id")
          .single();
        if (planError || !plan?.id) throw planError || new Error("Plan was not created.");
        planId = plan.id;
      }
      const { error: itemError } = await supabase.from("nutrition_plan_items").insert(
        nutritionPlanForm.items.map((item, index) => ({
          plan_id: planId,
          day_index: 0,
          sort_order: index,
          meal_section: item.meal_section || "Breakfast",
          food_name: String(item.food_name || "Food").trim(),
          brand: item.brand ? String(item.brand).trim() : null,
          image_url: item.image_url || null,
          calories: normalizePositiveTarget(item.calories, 0),
          protein_g: normalizePositiveTarget(item.protein_g, 0),
          carbs_g: normalizePositiveTarget(item.carbs_g, 0),
          fat_g: normalizePositiveTarget(item.fat_g, 0),
          fiber_g: normalizePositiveTarget(item.fiber_g, 0),
          sugar_g: normalizePositiveTarget(item.sugar_g, 0),
          sodium_mg: normalizePositiveTarget(item.sodium_mg, 0),
          portion: normalizePositiveTarget(item.portion, 100),
          unit: item.unit || "g",
        }))
      );
      if (itemError) throw itemError;
      const selectedAthleteIds = uniqueIds(nutritionPlanForm.selectedAthleteIds);
      const selectedAthleteRows = nutritionPlanAthletes.filter((athlete) => selectedAthleteIds.includes(athlete.id));
      const realAthleteIds = selectedAthleteRows.filter((athlete) => !athlete.is_demo_profile).map((athlete) => athlete.id);
      const demoAthleteIds = selectedAthleteRows.filter((athlete) => athlete.is_demo_profile).map((athlete) => athlete.demo_profile_id || athlete.id);
      const existingPlan = nutritionPlans.find((plan) => plan.id === planId) || null;
      const activeAssignments = (existingPlan?.nutrition_plan_assignments || []).filter(isActiveNutritionAssignment);
      const selectedAthleteSet = new Set(selectedAthleteIds);
      const assignmentsToPause = activeAssignments.filter((assignment) => !assignment.is_demo_assignment && assignment.athlete_id && !selectedAthleteSet.has(assignment.athlete_id));
      const demoAssignmentsToPause = activeAssignments.filter((assignment) => assignment.is_demo_assignment && assignment.demo_profile_id && !selectedAthleteSet.has(assignment.demo_profile_id));
      if (assignmentsToPause.length) {
        const { error: pauseError } = await supabase
          .from("nutrition_plan_assignments")
          .update({ status: "paused" })
          .in("id", assignmentsToPause.map((assignment) => assignment.id));
        if (pauseError) throw pauseError;
      }
      if (demoAssignmentsToPause.length) {
        const { error: demoPauseError } = await supabase
          .from("demo_nutrition_plan_assignments")
          .update({ status: "paused" })
          .in("id", demoAssignmentsToPause.map((assignment) => assignment.id));
        if (demoPauseError) throw demoPauseError;
      }
      const { error: assignmentError } = realAthleteIds.length
        ? await supabase.from("nutrition_plan_assignments").upsert(
          realAthleteIds.map((athleteId) => ({
            plan_id: planId,
            group_id: selectedNutritionPlanGroup.id,
            athlete_id: athleteId,
            assigned_by: selectedCoachId,
            status: "active",
            start_date: today,
          })),
          { onConflict: "plan_id,athlete_id" }
        )
        : { error: null };
      if (assignmentError) throw assignmentError;
      const { error: demoAssignmentError } = demoAthleteIds.length
        ? await supabase.from("demo_nutrition_plan_assignments").upsert(
          demoAthleteIds.map((athleteId) => ({
            plan_id: planId,
            group_id: selectedNutritionPlanGroup.id,
            demo_profile_id: athleteId,
            assigned_by: selectedCoachId,
            status: "active",
            start_date: today,
          })),
          { onConflict: "plan_id,demo_profile_id" }
        )
        : { error: null };
      if (demoAssignmentError) throw demoAssignmentError;
      resetNutritionPlanEditor(selectedNutritionPlanGroup.id);
      notify(editingNutritionPlanId ? "Nutrition plan updated." : "Nutrition plan assigned.", "success");
      await loadClubData();
    } catch (error) {
      notify(error.message || "Could not save nutrition plan.", "error");
    } finally {
      setNutritionPlanSaving(false);
    }
  };

  const deleteNutritionPlan = (plan) => {
    if (!plan?.id) return;
    setConfirmAction({
      title: "Delete nutrition plan?",
      message: `Delete ${plan.title || "this nutrition plan"}? Athletes will lose this assigned plan, but existing food logs stay in their nutrition history.`,
      confirmLabel: "Delete Plan",
      tone: "danger",
      onConfirm: async () => {
        try {
          const { error } = await supabase.from("nutrition_plans").delete().eq("id", plan.id);
          if (error) throw error;
          if (editingNutritionPlanId === plan.id) resetNutritionPlanEditor(plan.group_id || selectedNutritionPlanGroup?.id);
          notify("Nutrition plan deleted.", "success");
          await loadClubData();
        } catch (error) {
          notify(error.message || "Could not delete nutrition plan.", "error");
        }
      },
    });
  };

  const updateGroupNumber = (group, field, labelText) => {
    if (!group?.id) return;
    openGroupEditModal({
      type: "number",
      title: labelText,
      label: labelText,
      group,
      field,
      initialValue: String(group[field] || ""),
      helper: field === "weekly_xp_target" ? "Weekly XP target for group progress." : "Weekly session target for group progress.",
    });
  };

  const renameGroup = (group) => {
    if (!group?.id) return;
    openGroupEditModal({
      type: "text",
      title: "Edit Group Name",
      label: "Workout group name",
      group,
      field: "name",
      initialValue: group.name || "",
    });
  };

  const updateAllowedWorkouts = (group) => {
    if (!group?.id) return;
    setAllowedWorkoutSearch("");
    setAllowedWorkoutsModalGroup(group);
    loadCoachWorkouts();
  };

  const loadCoachWorkouts = async () => {
    if (!profileId) return;
    setCoachWorkoutsLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_workouts")
        .select("id,name,share_code,created_at")
        .eq("profile_id", profileId)
        .eq("deleted", false)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setCoachWorkouts(data || []);
    } catch (error) {
      console.error("loadCoachWorkouts error:", error);
      setCoachWorkouts([]);
      notify("Could not load workout plans.", "error");
    } finally {
      setCoachWorkoutsLoading(false);
    }
  };

  const generateWorkoutShareCode = (len = 6) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  const saveAllowedWorkoutsForGroup = async (group, pairs, names, message = "Allowed workouts updated.") => {
    if (!group?.id || allowedWorkoutsSaving) return;
    const limit = activeFeatureAccess?.limits?.assigned_workouts_per_group;
    const assignedCount = getAllowedWorkoutPairs({ allowed_workout_pairs: pairs, allowed_workout_names: names }).length;
    if (typeof limit === "number" && assignedCount > limit) {
      notify(`This plan lets you assign up to ${limit} workout${limit === 1 ? "" : "s"} per group. Upgrade to attach more.`, "warning");
      return;
    }
    setAllowedWorkoutsSaving(true);
    try {
      const { data, error } = await supabase
        .from("workout_groups")
        .update({ allowed_workout_pairs: pairs, allowed_workout_names: names })
        .eq("id", group.id)
        .select("id,allowed_workout_pairs,allowed_workout_names")
        .single();
      if (error) throw error;
      const patch = {
        allowed_workout_pairs: data?.allowed_workout_pairs || pairs,
        allowed_workout_names: data?.allowed_workout_names || names,
      };
      mergeGroupLocal(group.id, patch);
      setAllowedWorkoutsModalGroup((prev) => prev?.id === group.id ? { ...prev, ...patch } : prev);
      notify(message, "success");
    } catch (error) {
      console.error("saveAllowedWorkoutsForGroup error:", error);
      notify("Could not update allowed workouts.", "error");
    } finally {
      setAllowedWorkoutsSaving(false);
    }
  };

  const ensureWorkoutShareCode = async (workout) => {
    if (workout?.share_code) return workout.share_code;
    if (!canUseWorkoutShareCodes) return null;
    const code = generateWorkoutShareCode();
    const { error } = await supabase
      .from("user_workouts")
      .update({ share_code: code, code_expiration: null, deleted: false })
      .eq("id", workout.id);
    if (error) throw error;
    setCoachWorkouts((prev) => prev.map((item) => item.id === workout.id ? { ...item, share_code: code } : item));
    return code;
  };

  const addAllowedWorkoutFromPlan = async (workout) => {
    const group = allowedWorkoutsModalGroup;
    if (!group?.id || !workout?.id || !workout?.name || allowedWorkoutsSaving) return;
    const currentPairs = getAllowedWorkoutPairs(group);
    const currentNames = currentPairs.map((pair) => pair.name);
    const workoutKey = normalizeWorkoutName(workout.name);
    if (currentNames.some((name) => normalizeWorkoutName(name) === workoutKey)) {
      notify("Workout already assigned to this group.", "warning");
      return;
    }
    const limit = activeFeatureAccess?.limits?.assigned_workouts_per_group;
    if (typeof limit === "number" && currentPairs.length >= limit) {
      notify(`This plan lets you assign up to ${limit} workout${limit === 1 ? "" : "s"} per group. Upgrade to attach more.`, "warning");
      return;
    }
    setAllowedWorkoutsSaving(true);
    try {
      const code = await ensureWorkoutShareCode(workout);
      const nextPairs = [...currentPairs, { name: workout.name, code: code || null, workout_id: workout.id, scheduled_days: [] }];
      const nextNames = [...currentNames, workout.name];
      setAllowedWorkoutsSaving(false);
      await saveAllowedWorkoutsForGroup(group, nextPairs, nextNames, `${workout.name} added.`);
    } catch (error) {
      console.error("addAllowedWorkoutFromPlan error:", error);
      notify("Could not add workout.", "error");
      setAllowedWorkoutsSaving(false);
    }
  };

  const removeAllowedWorkoutFromGroup = async (workoutName) => {
    const group = allowedWorkoutsModalGroup;
    if (!group?.id || !workoutName || allowedWorkoutsSaving) return;
    const key = normalizeWorkoutName(workoutName);
    const nextPairs = getAllowedWorkoutPairs(group).filter((pair) => normalizeWorkoutName(pair?.name) !== key);
    const nextNames = nextPairs.map((pair) => pair.name);
    await saveAllowedWorkoutsForGroup(group, nextPairs, nextNames, `${workoutName} removed.`);
  };

  const updateAllowedWorkoutSchedule = async (workoutName, dayIndex) => {
    const group = allowedWorkoutsModalGroup;
    if (!group?.id || !workoutName || allowedWorkoutsSaving) return;
    const key = normalizeWorkoutName(workoutName);
    const nextPairs = getAllowedWorkoutPairs(group).map((pair) => {
      if (normalizeWorkoutName(pair?.name) !== key) return pair;
      const days = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days);
      const nextDays = days.includes(dayIndex)
        ? days.filter((day) => day !== dayIndex)
        : [...days, dayIndex].sort((a, b) => a - b);
      return { ...pair, scheduled_days: nextDays };
    });
    await saveAllowedWorkoutsForGroup(group, nextPairs, nextPairs.map((pair) => pair.name), "Workout schedule updated.");
  };

  const saveGroupEditModal = async () => {
    const modal = groupEditModal;
    if (!modal?.group?.id) return;
    let patch = {};
    let selectFields = "";
    let successMessage = "Group updated.";

    if (modal.field === "name") {
      const name = groupEditValue.trim();
      if (!name) {
        notify("Group name is required.", "warning");
        return;
      }
      patch = { name };
      selectFields = "id,name";
      successMessage = "Group renamed.";
    } else if (modal.field === "allowed_workout_names") {
      const names = groupEditValue.split(",").map((item) => item.trim()).filter(Boolean);
      const pairs = names.map((name) => ({ name }));
      patch = { allowed_workout_names: names, allowed_workout_pairs: pairs };
      selectFields = "id,allowed_workout_names,allowed_workout_pairs";
      successMessage = "Allowed workouts updated.";
    } else if (modal.type === "nutrition_targets") {
      const nextTargets = {
        daily_calorie_target: normalizePositiveTarget(groupNutritionDraft.calories, DEFAULT_GROUP_NUTRITION_TARGETS.calories),
        daily_protein_target_g: normalizePositiveTarget(groupNutritionDraft.protein, DEFAULT_GROUP_NUTRITION_TARGETS.protein),
        daily_carbs_target_g: normalizePositiveTarget(groupNutritionDraft.carbs, DEFAULT_GROUP_NUTRITION_TARGETS.carbs),
        daily_fat_target_g: normalizePositiveTarget(groupNutritionDraft.fat, DEFAULT_GROUP_NUTRITION_TARGETS.fat),
      };
      const hasInvalidValue = [groupNutritionDraft.calories, groupNutritionDraft.protein, groupNutritionDraft.carbs, groupNutritionDraft.fat]
        .some((value) => !Number.isFinite(Number(value)) || Number(value) <= 0);
      if (hasInvalidValue) {
        notify("Enter targets greater than 0 for calories, protein, carbs, and fat.", "warning");
        return;
      }
      patch = nextTargets;
      selectFields = "id,daily_calorie_target,daily_protein_target_g,daily_carbs_target_g,daily_fat_target_g";
      successMessage = `Nutrition targets saved: ${nextTargets.daily_calorie_target.toLocaleString()} kcal, ${nextTargets.daily_protein_target_g}g protein, ${nextTargets.daily_carbs_target_g}g carbs, ${nextTargets.daily_fat_target_g}g fat.`;
    } else {
      const value = Number.parseInt(String(groupEditValue).replace(/[^\d]/g, ""), 10);
      const minimum = modal.field === "daily_calorie_target" ? 0 : 1;
      if (!Number.isFinite(value) || value < minimum) {
        notify(`${modal.label} must be ${minimum === 0 ? "0 or higher" : "greater than 0"}.`, "warning");
        return;
      }
      patch = { [modal.field]: value };
      selectFields = `id,${modal.field}`;
      successMessage = `${modal.label} updated.`;
    }

    setGroupEditSaving(true);
    const { data, error } = await supabase.from("workout_groups").update(patch).eq("id", modal.group.id).select(selectFields).single();
    setGroupEditSaving(false);
    if (error) {
      notify("Could not update group.", "error");
      return;
    }
    mergeGroupLocal(modal.group.id, data || patch);
    setGroupEditModal(null);
    setGroupEditValue("");
    setGroupNutritionDraft({ calories: "", protein: "", carbs: "", fat: "" });
    notify(successMessage, "success");
  };

  const deleteGroup = async (group) => {
    if (!group?.id) return;
    setConfirmAction({
      title: "Delete workout group?",
      message: "This permanently removes the group and its members. Only do this if you are sure.",
      confirmLabel: "Delete Group",
      tone: "danger",
      onConfirm: async () => {
        const { error: membersError } = await supabase.from("workout_group_members").delete().eq("group_id", group.id);
        if (membersError) { notify("Could not delete group members.", "error"); return; }
        const { error } = await supabase.from("workout_groups").delete().eq("id", group.id);
        if (error) { notify("Could not delete group.", "error"); return; }
        setSelectedGroup(null);
        await loadClubData();
        notify("Workout group deleted.", "success");
      },
    });
  };

  const sendExpoPushMessages = async (messages) => {
    if (!messages.length) return { attempted: 0, failed: 0, errors: [] };
    const { data, error } = await supabase.functions.invoke("send_expo_push", { body: { messages } });
    if (error) {
      return { attempted: messages.length, failed: messages.length, errors: [error.message || String(error)] };
    }
    return {
      attempted: Number(data?.attempted || 0),
      failed: Number(data?.failed || 0),
      errors: Array.isArray(data?.errors) ? data.errors : [],
    };
  };

  const sendCalendarSessionNotifications = async (session, group, recipientIds) => {
    const uniqueRecipientIds = Array.from(new Set((recipientIds || []).filter((id) => id && id !== profileId)));
    if (!session?.id || !group?.id || !uniqueRecipientIds.length) return { attempted: 0, failed: 0, errors: [] };

    const title = String(session.title || "Workout group session").trim();
    const teamName = activeClub?.name || group?.workout_clubs?.name || "Your team";
    const groupName = group?.name || "Workout group";
    const message = truncateNotificationText(`${teamName} - ${groupName}: ${title} scheduled for ${formatCalendarRange(session.starts_at, session.ends_at)}.`);
    const rows = uniqueRecipientIds.map((receiverId) => ({
      sender_id: profileId,
      receiver_id: receiverId,
      type: "Workout Group",
      target_id: session.id,
      message,
      metadata: {
        notification_type: "calendar_session_created",
        session_id: session.id,
        group_id: group.id,
        group_name: groupName,
        team_name: teamName,
        starts_at: session.starts_at,
        ends_at: session.ends_at,
      },
    }));
    const { error } = await supabase.from("notifications").insert(rows);
    if (error) {
      console.error("sendCalendarSessionNotifications insert error:", error);
      return { attempted: 0, failed: 1, errors: [error.message || "notification insert failed"] };
    }

    const { data: recipientProfiles } = await supabase
      .from("profiles")
      .select("id, push_token")
      .in("id", uniqueRecipientIds);
    const pushMessages = Array.from(new Map((recipientProfiles || [])
      .map((profile) => profile.push_token)
      .filter(isExpoPushToken)
      .map((token) => [token, {
        to: token,
        title: "Snows ProAm",
        body: message,
        sound: "default",
        data: {
          screen: "WorkoutGroupCalendar",
          type: "WORKOUT_GROUP_CALENDAR_SESSION_CREATED",
          session_id: session.id,
          group_id: group.id,
          from_profile_id: profileId,
        },
      }])).values());
    return sendExpoPushMessages(pushMessages);
  };

  const openCalendarCreate = () => {
    const groupId = calendarForm.groupId || selectedGroup?.id || groups[0]?.id || "";
    const group = groups.find((item) => item.id === groupId) || selectedGroup || null;
    const nextForm = createDefaultCalendarForm(groupId);
    setCalendarEditingSession(null);
    setCalendarError("");
    calendarCoachSelectionTouchedRef.current = false;
    setCalendarForm({
      ...nextForm,
      date: calendarSelectedDateKey || nextForm.date,
      coachIds: getPreferredCalendarCoachIds(group),
    });
    setCalendarFormOpen(true);
  };

  const openCalendarEdit = (session) => {
    if (!session?.id) return;
    const start = new Date(session.starts_at);
    const end = new Date(session.ends_at || session.starts_at);
    setCalendarEditingSession(session);
    setCalendarSelectedSessionId(session.id);
    setCalendarError("");
    setCalendarFormOpen(true);
    setCalendarForm({
      groupId: session.group_id || session.group?.id || "",
      title: session.title || "",
      date: Number.isNaN(start.getTime()) ? localDateInput() : localDateInput(start),
      startTime: Number.isNaN(start.getTime()) ? "09:00" : localTimeInput(start),
      hasEndTime: !!session.ends_at,
      endTime: Number.isNaN(end.getTime()) ? "10:00" : localTimeInput(end),
      location: session.location || "",
      notes: session.notes || "",
      inviteAll: false,
      selectedAthleteIds: (session.athletes || []).map((athlete) => athlete.athlete_id).filter(Boolean),
      coachIds: (session.coaches || []).map((coach) => coach.coach_id).filter(Boolean),
    });
    calendarCoachSelectionTouchedRef.current = true;
  };

  const clearCalendarEdit = (groupId = calendarForm.groupId) => {
    setCalendarEditingSession(null);
    setCalendarError("");
    calendarCoachSelectionTouchedRef.current = false;
    setCalendarForm(createDefaultCalendarForm(groupId || ""));
    setCalendarFormOpen(false);
  };

  const syncCalendarAssignments = async (sessionId, table, idField, nextIds, extra = () => ({})) => {
    const { data: existingRows, error: existingError } = await supabase
      .from(table)
      .select(idField)
      .eq("session_id", sessionId);
    if (existingError) throw existingError;
    const existingIds = Array.from(new Set((existingRows || []).map((row) => row[idField]).filter(Boolean)));
    const nextUnique = Array.from(new Set((nextIds || []).filter(Boolean)));
    const nextSet = new Set(nextUnique);
    const existingSet = new Set(existingIds);
    const removedIds = existingIds.filter((id) => !nextSet.has(id));
    const addedIds = nextUnique.filter((id) => !existingSet.has(id));
    if (removedIds.length) {
      const { error } = await supabase.from(table).delete().eq("session_id", sessionId).in(idField, removedIds);
      if (error) throw error;
    }
    if (addedIds.length) {
      const { error } = await supabase.from(table).insert(addedIds.map((id) => ({ session_id: sessionId, [idField]: id, ...extra(id) })));
      if (error) throw error;
    }
  };

  const updateCalendarSession = async (session, group, startsAt, endsAt, title, athleteIds, coachIds, metadata = session?.metadata || {}) => {
    const { error } = await supabase
      .from("workout_group_calendar_sessions")
      .update({
        group_id: group.id,
        club_id: activeClub?.id || group.club_id || null,
        title,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt ? endsAt.toISOString() : null,
        location: calendarForm.location.trim() || null,
        notes: calendarForm.notes.trim() || null,
        metadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", session.id);
    if (error) throw error;
    await syncCalendarAssignments(session.id, "workout_group_calendar_session_athletes", "athlete_id", athleteIds, () => ({ status: "pending" }));
    await syncCalendarAssignments(session.id, "workout_group_calendar_session_coaches", "coach_id", coachIds, (coachId) => ({ role: coachId === profileId ? "lead" : "coach" }));
  };

  const setCalendarSessionStatus = (session, status) => {
    if (!session?.id) return;
    const live = status === "scheduled";
    setConfirmAction({
      title: live ? "Make session live again?" : "Cancel session?",
      message: live ? "This restores the session to the club calendar." : "This marks the session as cancelled. You can make it live again later.",
      confirmLabel: live ? "Make Live" : "Cancel Session",
      tone: live ? "default" : "danger",
      onConfirm: async () => {
        const { error } = await supabase
          .from("workout_group_calendar_sessions")
          .update({ status, updated_at: new Date().toISOString() })
          .eq("id", session.id);
        if (error) {
          notify(live ? "Could not restore session." : "Could not cancel session.", "error");
          return;
        }
        await loadClubData();
        setCalendarFilter(live ? "upcoming" : "cancelled");
        setCalendarSelectedDateKey(live ? calendarDateKey(session.starts_at) : null);
        setCalendarSelectedSessionId(session.id);
        notify(live ? "Session is live again." : "Session cancelled.", "success");
      },
    });
  };

  const createCalendarSession = async (event) => {
    event.preventDefault();
    if (calendarSaving) return;
    setCalendarError("");
    if (!activeClub?.id) {
      setCalendarError("Create or choose a club before scheduling sessions.");
      return;
    }
    if (!canUseClubCalendar) {
      setCalendarError("Calendar sessions are included with Pro Coach and Club Coaching plans.");
      return;
    }
    if (calendarFormError) {
      setCalendarError(calendarFormError);
      return;
    }
    const group = selectedCalendarGroup || groups.find((item) => item.id === calendarForm.groupId);
    if (!group?.id) {
      setCalendarError("Choose a workout group for this session.");
      return;
    }
    const startsAt = calendarStartsAt;
    const endsAt = calendarEndsAt;
    if (!startsAt || (calendarForm.hasEndTime && !endsAt) || (endsAt && endsAt <= startsAt)) {
      setCalendarError("Choose a valid start and end time.");
      return;
    }
    const title = calendarForm.title.trim();
    if (!title) {
      setCalendarError("Session title is required.");
      return;
    }
    const athleteIds = calendarSelectedAthleteIds;
    const realAthleteIds = calendarSelectedAthleteRows.filter((athlete) => !athlete.isDemo).map((athlete) => athlete.id);
    const demoAthleteRows = calendarSelectedAthleteRows.filter((athlete) => athlete.isDemo);
    const demoAthleteIds = demoAthleteRows.map((athlete) => athlete.demoProfileId || athlete.id).filter(Boolean);
    const coachIds = calendarSelectedCoachIds;
    if (!coachIds.length) {
      setCalendarError("Assign at least one coach to the session.");
      return;
    }
    setCalendarSaving(true);
    try {
      const sessionDateKey = calendarDateKey(startsAt);
      const nextFilter = startsAt.getTime() < Date.now() ? "past" : "upcoming";
      const nextMetadata = {
        ...(calendarEditingSession?.metadata || {}),
        demo_athlete_ids: demoAthleteIds,
        demo_invited_athlete_count: demoAthleteIds.length,
        invited_athlete_count: athleteIds.length,
      };
      if (calendarEditingSession?.id) {
        await updateCalendarSession(calendarEditingSession, group, startsAt, endsAt, title, realAthleteIds, coachIds, nextMetadata);
        await loadClubData();
        setCalendarFilter(nextFilter);
        setCalendarSelectedDateKey(nextFilter === "upcoming" ? sessionDateKey : null);
        setCalendarSelectedSessionId(calendarEditingSession.id);
        clearCalendarEdit(group.id);
        notify("Session updated.", "success");
        return;
      }
      let sessionId = null;
      if (demoAthleteIds.length) {
        const { data: insertedSession, error } = await supabase
          .from("workout_group_calendar_sessions")
          .insert({
            group_id: group.id,
            club_id: activeClub.id,
            created_by: profileId,
            title,
            starts_at: startsAt.toISOString(),
            ends_at: endsAt ? endsAt.toISOString() : null,
            location: calendarForm.location.trim() || null,
            notes: calendarForm.notes.trim() || null,
            status: "scheduled",
            metadata: {
              ...nextMetadata,
              created_from: "club_dashboard_calendar_demo_metadata",
              assigned_coach_count: coachIds.length,
            },
          })
          .select("id")
          .single();
        if (error) {
          setCalendarError(error.message || "Could not create session.");
          return;
        }
        sessionId = insertedSession.id;
        await syncCalendarAssignments(sessionId, "workout_group_calendar_session_athletes", "athlete_id", realAthleteIds, () => ({ status: "pending" }));
        await syncCalendarAssignments(sessionId, "workout_group_calendar_session_coaches", "coach_id", coachIds, (coachId) => ({ role: coachId === profileId ? "lead" : "coach" }));
      } else {
        const { data, error } = await supabase.rpc("create_workout_group_calendar_session", {
          p_group_id: group.id,
          p_title: title,
          p_starts_at: startsAt.toISOString(),
          p_ends_at: endsAt ? endsAt.toISOString() : null,
          p_location: calendarForm.location.trim() || null,
          p_notes: calendarForm.notes.trim() || null,
          p_athlete_ids: realAthleteIds,
          p_coach_ids: coachIds,
        });
        if (error) {
          setCalendarError(error.message || "Could not create session.");
          return;
        }
        sessionId = data;
      }
      const savedSession = {
        id: sessionId,
        group_id: group.id,
        club_id: activeClub.id,
        title,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt ? endsAt.toISOString() : null,
        location: calendarForm.location.trim() || null,
        notes: calendarForm.notes.trim() || null,
        status: "scheduled",
        metadata: nextMetadata,
      };
      const pushResult = await sendCalendarSessionNotifications(savedSession, group, [...realAthleteIds, ...coachIds]);
      await loadClubData();
      setCalendarFilter(nextFilter);
      setCalendarSelectedDateKey(nextFilter === "upcoming" ? sessionDateKey : null);
      setCalendarSelectedSessionId(sessionId);
      clearCalendarEdit(group.id);
      if (pushResult.failed) {
        notify("Session created, but one or more push alerts failed.", "warning");
      } else if (!pushResult.attempted) {
        notify("Session created. No recipients have push notifications enabled.", "warning");
      } else {
        notify("Session created and athletes notified.", "success");
      }
    } finally {
      setCalendarSaving(false);
    }
  };

  const truncateNotificationText = (value, max = 240) => {
    const text = String(value || "").trim();
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  };

  const sendCoachNoteNotification = async ({ athlete, group, noteRow }) => {
    if (!athlete?.id || !group?.id || !noteRow?.id || athlete.id === profileId) return { attempted: 0, failed: 0, errors: [] };

    const teamName = activeClub?.name || group?.workout_clubs?.name || "Your team";
    const groupName = group?.name || "Workout group";
    const message = truncateNotificationText(`${teamName} - ${groupName}: your coach added a note for you.`);

    const { error } = await supabase.from("notifications").insert({
      sender_id: profileId,
      receiver_id: athlete.id,
      type: "Workout Group",
      target_id: noteRow.id,
      message,
      metadata: {
        notification_type: "coach_note",
        note_id: noteRow.id,
        group_id: group.id,
        group_name: groupName,
        team_name: teamName,
      },
    });

    if (error) {
      console.error("sendCoachNoteNotification insert error:", error);
      return { attempted: 0, failed: 1, errors: [error.message || "notification insert failed"] };
    }

    if (!athlete.push_token?.startsWith("ExponentPushToken[")) return { attempted: 0, failed: 0, errors: [] };

    return sendExpoPushMessages([{
      to: athlete.push_token,
      title: "Snows ProAm",
      body: message,
      sound: "default",
      data: {
        screen: "WorkoutGroups",
        type: "COACH_NOTE",
        note_id: noteRow.id,
        group_id: group.id,
        from_profile_id: profileId,
      },
    }]);
  };

  const sendGroupReminder = (group, reminderType = "training", athleteId = null) => {
    if (reminderType === "calorie" && !hasNutritionAccess) {
      notify("Calorie reminders are included with Pro Coach and Club Coaching plans.", "warning");
      return;
    }
    const members = (group?.workout_group_members || [])
      .map((member) => member.profiles)
      .filter((profile) => profile?.id && (athleteId ? profile.id === athleteId : profile.id !== profileId));
    if (!group?.id || !members.length) {
      notify("No athletes found for this group.", "warning");
      return;
    }
    const isCalorie = reminderType === "calorie";
    const target = Number(group.daily_calorie_target || 2500).toLocaleString();
    const reminderText = isCalorie
      ? `Reminder: log your meals and aim for your ${target} kcal daily group target.`
      : "Quick reminder to get your workout session done this week.";
    const teamName = activeClub?.name || group?.workout_clubs?.name || "Your team";
    const groupName = group?.name || "Workout group";
    const message = `${teamName} - ${groupName}: ${reminderText}`;
    const labelText = isCalorie ? "calorie" : "training";
    const pushTokens = members.map((member) => member.push_token).filter((token) => token?.startsWith("ExponentPushToken["));
    setConfirmAction({
      title: `Send ${labelText} reminder?`,
      message: `Send a ${labelText} reminder to ${members.length} athlete${members.length === 1 ? "" : "s"} in ${group.name}?`,
      confirmLabel: "Send Reminder",
      tone: "info",
      onConfirm: async () => {
        const rows = members.map((member) => ({
          sender_id: profileId,
          receiver_id: member.id,
          type: "Workout Group",
          target_id: null,
          message,
          metadata: {
            group_id: group.id,
            group_name: group.name,
            team_name: teamName,
            reminder_type: isCalorie ? "calorie_target" : "training_target",
            daily_calorie_target: Number(group.daily_calorie_target || 2500),
          },
        }));
        const { error } = await supabase.from("notifications").insert(rows);
        if (error) {
          console.error("sendGroupReminder insert error:", error, { group, rows });
          notify(`Could not send ${labelText} reminders: ${error.message || "notification insert failed"}`, "error");
          return;
        }
        const pushMessages = pushTokens.map((token) => ({
          to: token,
          title: "Snows ProAm",
          body: message,
          sound: "default",
          data: {
            screen: "WorkoutGroups",
            type: isCalorie ? "GROUP_CALORIE_REMINDER" : "GROUP_REMINDER",
            group_id: group.id,
            from_profile_id: profileId,
          },
        }));
        const pushResult = await sendExpoPushMessages(pushMessages);
        console.log("[Workout Club Reminder Debug]", {
          group: group.name,
          reminderType,
          members: members.length,
          notificationRows: rows.length,
          pushTokens: pushTokens.length,
          missingPushTokens: members.length - pushTokens.length,
          pushResult,
        });
        if (!pushResult.attempted) {
          notify(`${labelText[0].toUpperCase()}${labelText.slice(1)} reminder saved. No athletes have push notifications enabled.`, "warning");
          return;
        }
        if (pushResult.failed) {
          console.error("sendGroupReminder push errors:", pushResult.errors);
          notify(`${labelText[0].toUpperCase()}${labelText.slice(1)} reminder saved, but ${pushResult.failed} push alert${pushResult.failed === 1 ? "" : "s"} failed.`, "warning");
          return;
        }
        notify(`${labelText[0].toUpperCase()}${labelText.slice(1)} reminder sent to ${members.length} athlete${members.length === 1 ? "" : "s"}.`, "success");
      },
    });
  };

  const openAthleteNotes = async (athlete) => {
    if (!athlete?.id || !dashboardGroup?.id) return;
    const rows = activeSessions
      .filter((row) => row.profile_id === athlete.id && sessionCountsForGroup(row, dashboardGroup))
      .sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0));
    const { data: notes } = await supabase
      .from("coach_notes")
      .select("id,note,created_at,coach_id,group_id,athlete_id")
      .eq("group_id", dashboardGroup.id)
      .eq("athlete_id", athlete.id)
      .order("created_at", { ascending: false })
      .limit(1);
    const latestNote = notes?.[0] || null;
    setAthleteModal({ athlete, sessions: rows, note: latestNote });
    setAthleteNoteText(latestNote?.note || "");
    setExpandedAthleteSessionId(null);
    setAthleteTrendMetric("totalVolume");
  };

  const saveAthleteNote = async () => {
    if (!athleteModal?.athlete?.id || !dashboardGroup?.id || !profileId) return;
    const note = athleteNoteText.trim();
    if (!note) return;
    setAthleteNoteSaving(true);
    try {
      const { data, error } = await supabase.from("coach_notes").insert({
        group_id: dashboardGroup.id,
        athlete_id: athleteModal.athlete.id,
        coach_id: profileId,
        note,
      }).select("id,note,created_at,coach_id,group_id,athlete_id").single();
      if (error) throw error;
      setAthleteModal((prev) => prev ? { ...prev, note: data } : prev);
      const notificationResult = await sendCoachNoteNotification({
        athlete: athleteModal.athlete,
        group: dashboardGroup,
        noteRow: data,
      });
      if (notificationResult.failed) {
        notify("Athlete note saved, but the notification could not be sent.", "warning");
      } else if (!notificationResult.attempted) {
        notify("Athlete note saved. The athlete will see it in notifications when they open the app.", "success");
      } else {
        notify("Athlete note saved and notification sent.", "success");
      }
    } catch (err) {
      console.error("saveAthleteNote error:", err);
      notify("Could not save athlete note.", "error");
    } finally {
      setAthleteNoteSaving(false);
    }
  };

  const removeGroupMember = async (group, athlete) => {
    if (!group?.id || !athlete?.id) return;
    setConfirmAction({
      title: "Remove athlete?",
      message: `Remove ${athlete.name || "this athlete"} from ${group.name || "this workout group"}?`,
      confirmLabel: "Remove",
      tone: "danger",
      onConfirm: async () => {
        const { error } = await supabase.from("workout_group_members").delete().eq("group_id", group.id).eq("athlete_id", athlete.id);
        if (error) { notify("Could not remove athlete.", "error"); return; }
        setGroups((prev) => prev.map((item) => item.id === group.id ? {
          ...item,
          workout_group_members: (item.workout_group_members || []).filter((member) => member.athlete_id !== athlete.id),
        } : item));
        await loadClubData();
        notify("Athlete removed.", "success");
      },
    });
  };

  const detachGroup = async (groupId) => {
    setConfirmAction({
      title: "Remove workout group from club?",
      message: "The workout group will still exist, but it will no longer appear under this club or season.",
      confirmLabel: "Remove From Club",
      tone: "danger",
      onConfirm: async () => {
        const { error } = await supabase.from("workout_groups").update({ club_id: null, season_id: null }).eq("id", groupId);
        if (error) { notify("Could not remove group from club.", "error"); return; }
        if (selectedGroup?.id === groupId) {
          setSelectedGroup((prev) => prev ? { ...prev, club_id: null, season_id: null } : prev);
        }
        await loadClubData();
        notify("Workout group removed from club.", "success");
      },
    });
  };

  if (loading) {
    return <div style={{ minHeight: "100vh", background: base.bg, color: base.text, display: "grid", placeItems: "center", fontWeight: 800 }}>Loading dashboard...</div>;
  }

  if (!profileId) {
    return (
      <div style={{ minHeight: "100vh", background: base.bg, display: "grid", placeItems: "center", padding: 20 }}>
        <div style={{ ...card, maxWidth: 430, width: "100%", textAlign: "center" }}>
          <h1 style={{ margin: 0, color: base.text }}>Sign in required</h1>
          <p style={{ color: base.muted, lineHeight: 1.6, marginBottom: 18 }}>Sign in with the same ProAm account you use in the mobile app to access this Club Coaching dashboard.</p>
          <form onSubmit={handleInlineSignIn} style={{ display: "grid", gap: 10, textAlign: "left" }}>
            <input
              type="email"
              value={signInEmail}
              onChange={(event) => setSignInEmail(event.target.value)}
              placeholder="Email"
              autoComplete="email"
              style={{ border: `1px solid ${base.border}`, borderRadius: 10, padding: "12px 13px", color: base.text, fontWeight: 700, outline: "none" }}
            />
            <input
              type="password"
              value={signInPassword}
              onChange={(event) => setSignInPassword(event.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              style={{ border: `1px solid ${base.border}`, borderRadius: 10, padding: "12px 13px", color: base.text, fontWeight: 700, outline: "none" }}
            />
            {signInError ? <div style={{ color: base.danger, fontSize: 12, fontWeight: 800 }}>{signInError}</div> : null}
            <button type="submit" disabled={signingIn} style={{ border: 0, background: base.blue, color: "#fff", borderRadius: 10, padding: "12px 16px", fontWeight: 900, cursor: "pointer", opacity: signingIn ? 0.7 : 1 }}>
              {signingIn ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
            <button onClick={() => navigate("/")} style={{ border: `1px solid ${base.border}`, background: base.bgAlt, color: base.text, borderRadius: 10, padding: "11px 14px", fontWeight: 800, cursor: "pointer" }}>Home</button>
            <button onClick={() => navigate("/coaching#pricing")} style={{ border: `1px solid ${base.border}`, background: "#fff", color: base.text, borderRadius: 10, padding: "11px 14px", fontWeight: 800, cursor: "pointer" }}>View Coaching</button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ minHeight: "100vh", background: base.bg, display: "grid", placeItems: "center", padding: 20 }}>
        <div style={{ ...card, maxWidth: 520, textAlign: "center" }}>
          <h1 style={{ margin: 0, color: base.text }}>Club Coaching required</h1>
          <p style={{ color: base.muted, lineHeight: 1.6 }}>This dashboard unlocks with an active Club Coaching subscription. Start the 1-month free trial from the coaching pricing page.</p>
          <button onClick={() => navigate("/coaching#pricing")} style={{ border: 0, background: base.blue, color: "#fff", borderRadius: 10, padding: "12px 16px", fontWeight: 900, cursor: "pointer" }}>View Pricing</button>
        </div>
      </div>
    );
  }

  return (
    <div className="proam-modern-surface proam-club-dashboard" style={{ minHeight: "100vh", background: base.bg, color: base.text }}>
      <header style={{ position: "sticky", top: 0, zIndex: 1000, height: 56, display: "flex", alignItems: "center", padding: "0 clamp(16px, 2vw, 32px)", background: base.headerBg, backdropFilter: "blur(20px) saturate(1.2)", WebkitBackdropFilter: "blur(20px) saturate(1.2)", borderBottom: `1px solid ${base.border}` }}>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: base.bgAlt, border: `1px solid ${base.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 900, color: base.text }}>
              ProAm <span style={{ color: "#0C69C8" }}>Coaching</span>
            </span>
          </button>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button className="proam-action" onClick={() => navigate(profileReturnUrl)} style={{ fontSize: 12, fontWeight: 700, color: base.muted, padding: "5px 10px", borderRadius: 8, border: `1px solid ${base.border}`, background: base.bgAlt, cursor: "pointer" }}><span>Profile</span></button>
          </div>
        </div>
      </header>

      <main className="proam-club-dashboard-main">
        <section style={{ background: `linear-gradient(135deg, ${brandSurface.primary}, ${brandSurface.secondary})`, color: "#fff", padding: 24, border: "1px solid rgba(255,255,255,0.14)", borderRadius: 20, marginBottom: 16, boxShadow: "0 12px 48px rgba(10,29,68,0.14)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 22, alignItems: "stretch" }}>
            <div style={{ display: "grid", gridTemplateColumns: activeClub?.logo_url ? "112px minmax(0, 1fr)" : "1fr", gap: 18, alignItems: "center" }}>
              {activeClub?.logo_url && (
                <div style={{ width: 112, height: 112, borderRadius: 22, background: "#FFFFFF", border: "1px solid rgba(255,255,255,0.34)", overflow: "hidden", display: "grid", placeItems: "center", boxShadow: "0 16px 38px rgba(0,0,0,0.20)" }}>
                  <img src={activeClub.logo_url} alt={`${activeClub.name || "Club"} logo`} style={{ width: "92%", height: "92%", objectFit: "contain", objectPosition: "center" }} />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,.72)", textTransform: "uppercase", marginBottom: 6 }}>Workout Groups Club Dashboard</div>
                <h1 style={{ margin: 0, fontSize: "clamp(24px,4vw,38px)", lineHeight: 1.05 }}>{activeClub?.name || "Club Profile"}</h1>
                <p style={{ margin: "10px 0 0", color: "rgba(255,255,255,.78)", fontWeight: 800, lineHeight: 1.5, maxWidth: 680 }}>
                  Manage club branding, coaches, seasons, group assignment, and team analytics from one web view.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
                  <span style={{ ...pill("info"), background: "rgba(255,255,255,0.16)", borderColor: "rgba(255,255,255,0.28)", color: "#fff" }}>{access.planName || "Club Coaching"}</span>
                </div>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,.7)", textTransform: "uppercase", marginBottom: 10 }}>Workout Group</div>
              <div style={{ marginTop: 12, display: "grid", gap: 8, justifyItems: "center", textAlign: "center" }}>
                {activeClub && (
                  <select aria-label="Choose workout group" value={groupPickerValue} onChange={(event) => selectDashboardGroup(event.target.value)} style={{ width: "100%", maxWidth: 520, border: "1px solid rgba(255,255,255,0.30)", background: "rgba(255,255,255,0.14)", borderRadius: 12, padding: "11px 38px 11px 38px", color: "#fff", fontWeight: 900, outline: "none", textAlign: "center", textAlignLast: "center" }}>
                    <option value="" style={{ color: base.text }}>Choose a group</option>
                    {allGroups.map((group) => <option key={group.id} value={group.id} style={{ color: base.text }}>{group.name}{group.club_id === activeClub?.id ? " (in your club)" : ""}</option>)}
                  </select>
                )}
                {activeClub && selectedGroup?.id && !selectedGroupAttachedHere && <button className="proam-action" onClick={() => attachGroup(null, selectedGroup.id)} style={{ ...btn, width: "100%", maxWidth: 520, justifyContent: "center", textAlign: "center", borderColor: "#fff", background: "#fff", color: brandSurface.primary }}><span>Add Group To Club</span></button>}
                {activeClub && selectedGroupAttachedHere && <button className="proam-action" onClick={() => detachGroup(selectedGroup.id)} style={{ ...btn, width: "100%", maxWidth: 520, justifyContent: "center", textAlign: "center", borderColor: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.12)", color: "#fff" }}><span>Remove Group From Club</span></button>}
                <button className="proam-action" onClick={() => document.getElementById("club-tools")?.scrollIntoView({ behavior: "smooth", block: "start" })} style={{ ...btn, width: "100%", maxWidth: 520, justifyContent: "center", textAlign: "center", background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.35)", color: "#fff" }}><span>Open Tools</span></button>
                <div style={{ width: "100%", maxWidth: 520, color: "rgba(255,255,255,0.66)", fontSize: 12, fontWeight: 800, lineHeight: 1.35, textAlign: "center" }}>Choose a group, then add it to your club or remove it.</div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 16, padding: 14, marginBottom: 16, boxShadow: "0 4px 16px rgba(10,29,68,0.08)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 14, alignItems: "center" }}>
            <div>
              <div style={{ color: base.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.4 }}>Your club</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 7 }}>
                <div style={{ width: 58, height: 58, borderRadius: 15, background: brandSurface.primary, overflow: "hidden", display: "grid", placeItems: "center", color: "#fff", fontWeight: 900, flexShrink: 0 }}>
                  {activeClub?.logo_url ? <img src={activeClub.logo_url} alt="" style={{ width: "92%", height: "92%", objectFit: "contain", objectPosition: "center" }} /> : "C"}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: base.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeClub?.name || "Club profile not saved"}</div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {clubs.length > 0 && (
                <select value={activeClub?.id || ""} onChange={(event) => {
                  const nextId = event.target.value || null;
                  const nextClub = nextId ? clubs.find((club) => club.id === nextId) || null : null;
                  if (nextId && activeClubStorageKey) window.localStorage.setItem(activeClubStorageKey, nextId);
                  if (nextClub) {
                    setClubForm({
                      name: nextClub.name || "",
                      sport_id: nextClub.sport_id || "",
                      logo_url: nextClub.logo_url || "",
                      primary_color: nextClub.primary_color || DEFAULT_COLORS.primary_color,
                      secondary_color: nextClub.secondary_color || DEFAULT_COLORS.secondary_color,
                      accent_color: nextClub.accent_color || DEFAULT_COLORS.accent_color,
                      theme_workout_groups: nextClub.brand_settings?.theme_workout_groups === true,
                      theme_workouts: nextClub.brand_settings?.theme_workouts === true,
                      theme_nutrition: nextClub.brand_settings?.theme_nutrition === true,
                      theme_sessions: nextClub.brand_settings?.theme_sessions === true,
                    });
                  }
                  setActiveClubId(nextId);
                }} aria-label="Switch club" style={{ border: `1px solid ${base.border}`, background: "#fff", color: base.text, borderRadius: 10, padding: "9px 11px", fontWeight: 900, minWidth: 220 }}>
                  {clubs.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
                </select>
              )}
              {canDeleteActiveClub && (
                <button
                  className="proam-action"
                  type="button"
                  onClick={deleteActiveClub}
                  disabled={deletingClub}
                  aria-label={`Delete ${activeClub?.name || "club"}`}
                  style={{ ...btn, borderColor: "#FCA5A5", background: "#FEE2E2", color: "#991B1B", justifyContent: "center", opacity: deletingClub ? 0.65 : 1 }}
                >
                  <span>{deletingClub ? "Deleting..." : "Delete Club"}</span>
                </button>
              )}
            </div>
          </div>
        </section>

        <section id="club-tools" style={{ background: "#070A12", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, marginBottom: 18, boxShadow: "0 26px 80px rgba(10,29,68,0.26)", overflow: "hidden" }}>
          <div style={{ minHeight: 250, padding: 24, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, background: `linear-gradient(90deg, rgba(7,10,18,0.98) 0%, rgba(7,10,18,0.74) 58%, rgba(7,10,18,0.38) 100%), url(${clubForm.logo_url || activeClub?.logo_url || ""}) center/cover` }}>
            <div style={{ maxWidth: 660 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 999, background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.78)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6 }}>Club Admin</div>
              <h2 style={{ margin: "14px 0 8px", color: "#FFFFFF", fontSize: "clamp(34px,5vw,58px)", lineHeight: 0.98, fontWeight: 950, letterSpacing: -1 }}>{clubForm.name || (activeClub?.id ? "Club Profile" : "Create New Club")}</h2>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.70)", fontSize: 15, lineHeight: 1.6, fontWeight: 760 }}>A focused workspace for the club brand, staff, seasons, and workout groups.</p>
            </div>
          </div>

          <div className="club-tools-layout" style={{ display: "grid", gridTemplateColumns: "minmax(148px, 180px) minmax(0, 1fr)", minHeight: 520, background: "#0B0F19" }}>
            <aside style={{ borderRight: "1px solid rgba(255,255,255,0.08)", padding: 14, background: "linear-gradient(180deg,#0E1421,#080B12)" }}>
              {[
                { id: "brand", title: "Brand", meta: clubForm.sport_id || "Identity" },
                { id: "staff", title: "Staff", meta: `${coaches.length} people` },
                { id: "seasons", title: "Seasons", meta: `${seasons.length} blocks` },
                { id: "groups", title: "Club Groups", meta: `${groups.length} groups` },
                { id: "sessions", title: "Sessions", meta: `${calendarSessions.length} upcoming` },
              ].map((item) => {
                const active = toolView === item.id;
                return (
                  <button key={item.id} onClick={() => setToolView(item.id)} style={{ width: "100%", border: `1px solid ${active ? "rgba(255,255,255,0.22)" : "transparent"}`, background: active ? "rgba(255,255,255,0.10)" : "transparent", color: "#fff", borderRadius: 14, padding: "12px 12px", marginBottom: 8, textAlign: "left", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontWeight: 950, fontSize: 14 }}>{item.title}</span>
                      {active && <span style={{ width: 8, height: 8, borderRadius: 99, background: brandSurface.accent }} />}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.50)", fontSize: 11, fontWeight: 850, marginTop: 3 }}>{item.meta}</div>
                  </button>
                );
              })}
            </aside>

            <div style={{ padding: 18, background: "radial-gradient(circle at top right, rgba(12,105,200,0.18), transparent 34%), #0B0F19" }}>
              {toolView === "brand" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 16 }}>
                  <div>
                    <h3 style={{ color: "#fff", margin: 0, fontSize: 24 }}>{activeClub?.id ? "Brand Studio" : "Create New Club"}</h3>
                    <p style={{ color: "rgba(255,255,255,0.58)", margin: "5px 0 18px", fontSize: 13, fontWeight: 760 }}>Edit the club name, sport, logo, colours, and where the brand appears.</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {[["name", "Club name"], ["sport_id", "Sport"]].map(([key, labelText]) => (
                        <label key={key} style={{ display: "grid", gap: 7, color: "rgba(255,255,255,0.60)", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                          {labelText}
                          <input value={clubForm[key]} onChange={(event) => setClubForm((prev) => ({ ...prev, [key]: event.target.value }))} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850, outline: "none" }} />
                        </label>
                      ))}
                    </div>
                    <input
                      ref={clubLogoFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={uploadClubLogoFile}
                      style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                    <div className="club-logo-editor" style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", borderRadius: 16, padding: 12, marginTop: 12 }}>
                      <div style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(12,105,200,0.20)", border: "1px solid rgba(255,255,255,0.14)", overflow: "hidden", display: "grid", placeItems: "center", color: "#93C5FD", fontSize: 22, fontWeight: 950, flexShrink: 0 }}>
                        {visibleClubLogoUrl ? <img src={visibleClubLogoUrl} alt="Club logo preview" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} /> : "C"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#fff", fontSize: 14, fontWeight: 950 }}>Club Logo</div>
                        <div style={{ color: "rgba(255,255,255,0.58)", fontSize: 12, fontWeight: 800, marginTop: 3 }}>
                          {clubForm.logo_url ? "Logo added. Upload a replacement or paste a direct image URL." : "Upload from your device or paste a direct image URL."}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          onClick={() => clubLogoFileInputRef.current?.click()}
                          disabled={clubLogoUploading || !canSaveClubBrand}
                          style={{ border: "1px solid rgba(147,197,253,0.35)", background: "rgba(12,105,200,0.18)", color: "#BFDBFE", borderRadius: 12, padding: "9px 11px", fontSize: 12, fontWeight: 950, cursor: clubLogoUploading || !canSaveClubBrand ? "not-allowed" : "pointer", opacity: clubLogoUploading || !canSaveClubBrand ? 0.62 : 1 }}
                        >
                          {clubLogoUploading ? "Uploading..." : "Upload Image"}
                        </button>
                        {clubForm.logo_url && !showClubLogoUrlInput && (
                          <button type="button" onClick={() => { setClubLogoUrlInput(""); setShowClubLogoUrlInput(true); }} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontSize: 12, fontWeight: 950, cursor: "pointer" }}>
                            Paste URL
                          </button>
                        )}
                      </div>
                    </div>
                    {(!clubForm.logo_url || showClubLogoUrlInput) && (
                      <input
                        value={clubLogoUrlInput}
                        onChange={(event) => setClubLogoUrlInput(event.target.value)}
                        onBlur={() => setTimeout(cancelClubLogoUrlInput, 0)}
                        placeholder="Paste logo image URL"
                        style={{ width: "100%", boxSizing: "border-box", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850, outline: "none", marginTop: 10 }}
                      />
                    )}
                    <div className="club-brand-options" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 14 }}>
                      {[["primary_color", "Primary"], ["secondary_color", "Secondary"], ["accent_color", "Accent"]].map(([key, labelText]) => (
                        <label key={key} style={{ display: "grid", gap: 8, color: "rgba(255,255,255,0.60)", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                          {labelText}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: 9 }}>
                            <input type="color" value={clubForm[key]} onChange={(event) => setClubForm((prev) => ({ ...prev, [key]: event.target.value }))} style={{ width: 38, height: 32, border: 0, padding: 0, background: "transparent" }} />
                            <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>{clubForm[key]}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="club-brand-options" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 14 }}>
                      {BRAND_TOGGLE_ITEMS.map(({ key, label: labelText }) => {
                        const accessInfo = brandToggleAccess[key] || {};
                        const disabled = !accessInfo.allowed;
                        return (
                          <label
                            key={key}
                            onClick={(event) => {
                              if (!disabled) return;
                              event.preventDefault();
                              notify(accessInfo.message || "This branding option is not included in the current plan.", "warning");
                            }}
                            title={disabled ? accessInfo.message : undefined}
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, border: `1px solid ${disabled ? "rgba(253,230,138,0.24)" : "rgba(255,255,255,0.12)"}`, background: disabled ? "rgba(253,230,138,0.08)" : "rgba(255,255,255,0.06)", borderRadius: 14, padding: "13px 14px", color: disabled ? "rgba(255,255,255,0.72)" : "#fff", fontSize: 13, fontWeight: 900, cursor: disabled ? "not-allowed" : "pointer" }}
                          >
                            <span style={{ display: "grid", gap: 4, minWidth: 0 }}>
                              <span>{labelText}</span>
                              {disabled && <span style={{ color: "#FDE68A", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Plan upgrade needed</span>}
                            </span>
                            <input
                              type="checkbox"
                              checked={clubForm[key] === true && !disabled}
                              disabled={disabled}
                              aria-label={labelText}
                              aria-describedby={disabled ? `${key}-locked-note` : undefined}
                              onChange={(event) => handleClubBrandToggle(key, event.target.checked)}
                            />
                            {disabled && <span id={`${key}-locked-note`} style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 }}>{accessInfo.message}</span>}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.58)", fontSize: 12, fontWeight: 800, marginTop: 10 }}>Workout Groups controls whether members can use this club brand on the shared group screen. Members choose their own Workouts, Calorie Tracker, and Workout Sessions colours from group options.</div>
                    <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <button
                        className="club-save-brand-button"
                        type="button"
                        onClick={() => saveClub()}
                        disabled={clubBrandSaveDisabled}
                        style={{ border: 0, background: clubBrandSaveDisabled ? "rgba(255,255,255,0.20)" : "#fff", color: clubBrandSaveDisabled ? "rgba(255,255,255,0.60)" : "#080B12", borderRadius: 14, padding: "12px 16px", fontWeight: 950, cursor: clubBrandSaveDisabled ? "not-allowed" : "pointer", minHeight: 44 }}
                      >
                        {savingClub ? "Saving..." : activeClub?.id ? "Save Changes" : "Create Club"}
                      </button>
                      <div style={{ color: clubBrandSaveReason ? "#FDE68A" : "rgba(255,255,255,0.58)", fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                        {clubBrandSaveReason || (activeClub?.id ? "Use Save Club to apply brand changes." : "Save to create the club and apply this brand.")}
                      </div>
                    </div>
                    {canDeleteActiveClub && (
                      <div style={{ marginTop: 16, border: "1px solid rgba(248,113,113,0.34)", background: "rgba(248,113,113,0.10)", borderRadius: 16, padding: 14 }}>
                        <div style={{ color: "#FCA5A5", fontSize: 12, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Owner danger zone</div>
                        <p style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, fontWeight: 800, lineHeight: 1.5, margin: "6px 0 12px" }}>
                          Delete this club when its branding, staff access, seasons, and dashboard should be removed. Workout groups are kept.
                        </p>
                        <button
                          type="button"
                          onClick={deleteActiveClub}
                          disabled={deletingClub}
                          style={{ border: 0, background: base.danger, color: "#fff", borderRadius: 12, padding: "10px 12px", fontWeight: 950, cursor: deletingClub ? "wait" : "pointer", opacity: deletingClub ? 0.7 : 1 }}
                        >
                          {deletingClub ? "Deleting..." : "Delete Club"}
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ position: "relative", boxSizing: "border-box", borderRadius: 20, overflow: "hidden", border: "1px solid rgba(255,255,255,0.28)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)", background: `linear-gradient(160deg, ${clubFormSurface.primary}, ${clubFormSurface.secondary})`, minHeight: 330, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <div style={{ pointerEvents: "none", position: "absolute", inset: 0, borderRadius: 20, border: "1px solid rgba(255,255,255,0.22)", zIndex: 2 }} />
                    <div style={{ flex: 1, background: visibleClubLogoUrl ? `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.66)), url(${visibleClubLogoUrl}) center/cover` : "transparent" }} />
                    <div style={{ position: "relative", zIndex: 1, padding: 16 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 14, background: clubFormSurface.accent, marginBottom: 10 }} />
                      <div style={{ color: "#fff", fontSize: 23, fontWeight: 950 }}>{clubForm.name || "Club Name"}</div>
                      <div style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, fontWeight: 850, marginTop: 3 }}>{clubForm.sport_id || "Sport"} · branded training group</div>
                    </div>
                  </div>
                </div>
              )}

              {toolView === "staff" && (
                <div>
                  <h3 style={{ color: "#fff", margin: 0, fontSize: 24 }}>Staff Access</h3>
                  <p style={{ color: "rgba(255,255,255,0.58)", margin: "5px 0 18px", fontSize: 13, fontWeight: 760 }}>
                    {canManageClubStaff ? "Add coaches, choose the club’s lead coach, and manage access." : "View the active staff for this club. Only the club owner can change access or roles."}
                  </p>
                  <div style={{ display: "grid", gap: 10 }}>
                    {coaches.length === 0 ? <div style={{ color: "rgba(255,255,255,0.58)", fontWeight: 800, padding: 16, border: "1px solid rgba(255,255,255,0.10)", borderRadius: 16 }}>No coaches yet.</div> : coaches.map((coach) => (
                      <div key={coach.id} className="club-staff-row" style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.055)", borderRadius: 16, padding: 12 }}>
                        {(() => {
                          const isOwner = coach.role === "owner" || coach.profile_id === activeClub?.owner_profile_id;
                          const roleCanBeChanged = CLUB_STAFF_ROLE_OPTIONS.some((option) => option.value === coach.role);
                          const coachName = displayProfileName(coach.profiles, "Coach");
                          const savingThisCoach = staffRoleSavingId === coach.profile_id;
                          return (
                            <>
                              <div style={{ width: 52, height: 52, borderRadius: 16, background: brandSurface.primary, color: "#fff", display: "grid", placeItems: "center", fontWeight: 950, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(255,255,255,0.18)" }}>
                                {coach.profiles?.profile_img ? (
                                  <img src={coach.profiles.profile_img} alt={coachName} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
                                ) : (
                                  (coach.profiles?.username || "?").slice(0, 1).toUpperCase()
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ color: "#fff", fontWeight: 950 }}>{coachName}</div>
                                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 850 }}>@{coach.profiles?.username || "unknown"} · {formatClubStaffRole(coach.role)} · {coach.status}</div>
                              </div>
                              {canManageClubStaff && !isOwner && roleCanBeChanged ? (
                                <div className="club-staff-row-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <label style={{ display: "grid", gap: 4, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 900 }}>
                                    Role
                                    <select
                                      value={coach.role}
                                      disabled={savingThisCoach || !!staffRoleSavingId}
                                      onChange={(event) => updateCoachRole(coach, event.target.value)}
                                      aria-label={`Role for ${coachName}`}
                                      style={{ border: "1px solid rgba(255,255,255,0.16)", background: "#111827", borderRadius: 10, padding: "9px 10px", color: "#fff", fontWeight: 850, minHeight: 40 }}
                                    >
                                      {CLUB_STAFF_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                  </label>
                                  <button type="button" onClick={() => removeCoach(coach)} disabled={savingThisCoach} aria-label={`Remove ${coachName}`} style={{ border: "1px solid rgba(248,113,113,0.28)", background: "rgba(248,113,113,0.10)", color: "#FCA5A5", borderRadius: 12, padding: "10px", minHeight: 40, fontWeight: 900, cursor: savingThisCoach ? "wait" : "pointer", opacity: savingThisCoach ? 0.62 : 1 }}>Remove</button>
                                </div>
                              ) : (
                                <div style={{ color: "rgba(255,255,255,0.50)", fontSize: 11, fontWeight: 850 }}>{isOwner ? "Owner account" : formatClubStaffRole(coach.role)}</div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                  {canManageClubStaff && (
                    <form className="club-staff-add-form" onSubmit={addCoach} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(150px, 0.45fr) auto", gap: 10, marginTop: 16, alignItems: "end" }}>
                      <label htmlFor="club-coach-username" className="club-staff-field" style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 11, fontWeight: 900 }}>
                        Coach username
                        <input id="club-coach-username" value={coachUsername} onChange={(event) => setCoachUsername(event.target.value)} placeholder="@username" autoComplete="username" disabled={coachSaving} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850, outline: "none", minHeight: 46 }} />
                      </label>
                      <label htmlFor="club-coach-role" className="club-staff-field" style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 11, fontWeight: 900 }}>
                        Staff role
                        <select id="club-coach-role" value={coachRole} onChange={(event) => setCoachRole(event.target.value)} aria-describedby="club-coach-role-help" disabled={coachSaving} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.12)", background: "#111827", borderRadius: 14, padding: "13px 12px", color: "#fff", fontWeight: 850, outline: "none", minHeight: 46 }}>
                          {CLUB_STAFF_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                      <button type="submit" disabled={!activeClub?.id || !coachUsername.trim() || coachSaving} style={{ border: 0, background: brandSurface.primary, color: "#fff", borderRadius: 14, padding: "0 18px", minHeight: 46, fontWeight: 950, cursor: coachSaving ? "wait" : "pointer", opacity: coachSaving ? 0.68 : 1 }}>{coachSaving ? "Adding..." : "Add Coach"}</button>
                    </form>
                  )}
                  {canManageClubStaff && <div id="club-coach-role-help" style={{ color: "rgba(255,255,255,0.48)", fontSize: 11, fontWeight: 800, marginTop: 8 }}>There can be one lead coach per club. Choosing a new lead coach automatically returns the previous lead to Coach.</div>}
                </div>
              )}

              {toolView === "seasons" && (
                <div>
                  <h3 style={{ color: "#fff", margin: 0, fontSize: 24 }}>Season Board</h3>
                  <p style={{ color: "rgba(255,255,255,0.58)", margin: "5px 0 18px", fontSize: 13, fontWeight: 760 }}>Create seasons and manage their dates and status.</p>
                  <div style={{ display: "grid", gap: 10 }}>
                    {seasons.map((season) => (
                      <div key={season.id} style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.055)", borderRadius: 16, padding: 13 }}>
                        <div className="club-season-fields" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px 92px 92px", gap: 10, alignItems: "center" }}>
                          <div><div style={{ color: "#fff", fontWeight: 950 }}>{season.name}</div><div style={{ color: "rgba(255,255,255,0.56)", fontSize: 12, fontWeight: 800 }}>{season.starts_on || "No start"} to {season.ends_on || "No end"}</div></div>
                          <select aria-label={`Status for ${season.name}`} value={season.status} onChange={(event) => updateSeason(season, { status: event.target.value })} style={{ border: "1px solid rgba(255,255,255,0.14)", background: "#111827", color: "#fff", borderRadius: 12, padding: "9px 10px", fontWeight: 900 }}>{["draft", "active", "completed", "archived"].map((status) => <option key={status} value={status}>{status}</option>)}</select>
                          <button onClick={() => editSeasonDates(season)} style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 12, padding: "9px 10px", fontWeight: 900, cursor: "pointer" }}>Edit</button>
                          <button onClick={() => deleteSeason(season)} style={{ border: "1px solid rgba(248,113,113,0.36)", background: "rgba(248,113,113,0.12)", color: "#FCA5A5", borderRadius: 12, padding: "9px 10px", fontWeight: 900, cursor: "pointer" }}>Delete</button>
                        </div>
                        {editingSeason?.id === season.id && (
                          <div className="club-season-fields" style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto auto", gap: 8 }}>
                            <label className="club-season-field">Season name<input value={editingSeason.name} onChange={(event) => setEditingSeason((prev) => ({ ...prev, name: event.target.value }))} placeholder="Season name" style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: "10px 10px", color: "#fff", fontWeight: 850 }} /></label>
                            <label className="club-season-field">Start date<input type="date" value={editingSeason.starts_on} onChange={(event) => setEditingSeason((prev) => ({ ...prev, starts_on: event.target.value }))} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: "10px 10px", color: "#fff", fontWeight: 850 }} /></label>
                            <label className="club-season-field">End date<input type="date" value={editingSeason.ends_on} onChange={(event) => setEditingSeason((prev) => ({ ...prev, ends_on: event.target.value }))} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: "10px 10px", color: "#fff", fontWeight: 850 }} /></label>
                            <button onClick={() => setEditingSeason(null)} style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 12, padding: "9px 10px", fontWeight: 900, cursor: "pointer" }}>Cancel</button>
                            <button onClick={() => saveSeasonDates(season)} style={{ border: 0, background: "#fff", color: "#080B12", borderRadius: 12, padding: "9px 12px", fontWeight: 950, cursor: "pointer" }}>Save</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="club-season-fields" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr auto", gap: 8, marginTop: 14 }}>
                    <label className="club-season-field">Season name<input value={seasonForm.name} onChange={(event) => setSeasonForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Season name" style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850 }} /></label>
                    <label className="club-season-field">Start date<input type="date" value={seasonForm.starts_on} onChange={(event) => setSeasonForm((prev) => ({ ...prev, starts_on: event.target.value }))} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850 }} /></label>
                    <label className="club-season-field">End date<input type="date" value={seasonForm.ends_on} onChange={(event) => setSeasonForm((prev) => ({ ...prev, ends_on: event.target.value }))} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850 }} /></label>
                    <button onClick={saveSeason} disabled={!activeClub?.id || !seasonForm.name.trim()} style={{ border: 0, background: "#fff", color: "#080B12", borderRadius: 14, padding: "0 18px", fontWeight: 950, cursor: "pointer", whiteSpace: "nowrap" }}>Create</button>
                  </div>
                </div>
              )}

              {toolView === "groups" && (
                <div>
                  <h3 style={{ color: "#fff", margin: 0, fontSize: 24 }}>Club Workout Groups</h3>
                  <p style={{ color: "rgba(255,255,255,0.58)", margin: "5px 0 18px", fontSize: 13, fontWeight: 760 }}>Choose a group, then add it to your club or remove it from your club.</p>
                  <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                    <select aria-label="Choose workout group" value={groupPickerValue} onChange={(event) => selectDashboardGroup(event.target.value)} style={{ flex: 1, minWidth: 0, border: "1px solid rgba(255,255,255,0.12)", background: "#111827", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850 }}>
                      <option value="">Choose group</option>
                      {allGroups.map((group) => <option key={group.id} value={group.id}>{group.name}{group.club_id === activeClub?.id ? " (in your club)" : ""}</option>)}
                    </select>
                    {selectedGroupAttachedHere
                      ? <button onClick={() => detachGroup(groupPickerValue)} disabled={!activeClub?.id || !groupPickerValue} style={{ border: "1px solid rgba(248,113,113,0.28)", background: "rgba(248,113,113,0.10)", color: "#FCA5A5", borderRadius: 14, padding: "0 18px", fontWeight: 950, cursor: "pointer" }}>Remove From Club</button>
                      : <button onClick={() => attachGroup(null, groupPickerValue)} disabled={!activeClub?.id || !groupPickerValue} style={{ border: 0, background: brandSurface.primary, color: "#fff", borderRadius: 14, padding: "0 18px", fontWeight: 950, cursor: "pointer" }}>Add To Club</button>}
                  </div>
                  {selectedGroupAttachedHere && (
                    <label style={{ display: "grid", gap: 7, marginBottom: 14, color: "rgba(255,255,255,0.60)", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                      Season for this group
                      <select value={selectedGroup?.season_id || ""} onChange={(event) => attachGroup(event.target.value || null, selectedGroup.id)} style={{ width: "100%", border: "1px solid rgba(255,255,255,0.12)", background: "#111827", borderRadius: 14, padding: "13px 13px", color: "#fff", fontWeight: 850 }}>
                        <option value="">No season</option>
                        {seasons.map((season) => <option key={season.id} value={season.id}>{season.name}</option>)}
                      </select>
                    </label>
                  )}
                  <div style={{ display: "grid", gap: 10 }}>
                    {groups.length === 0 ? <div style={{ color: "rgba(255,255,255,0.58)", fontWeight: 800, padding: 16, border: "1px solid rgba(255,255,255,0.10)", borderRadius: 16 }}>No workout groups are in this club yet.</div> : groups.map((group) => {
                      const leadCoach = firstRelationRow(group.coach) || null;
                      const leadCoachName = displayProfileName(leadCoach, "Unassigned coach");
                      return (
                        <div key={group.id} className="club-group-row" style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.055)", borderRadius: 16, padding: 12 }}>
                          <div style={{ width: 52, height: 52, borderRadius: 16, background: "#FFFFFF", color: brandSurface.primary, display: "grid", placeItems: "center", fontWeight: 950, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(255,255,255,0.20)" }}>
                            {activeClub?.logo_url ? (
                              <img src={activeClub.logo_url} alt={`${activeClub.name || "Club"} logo`} loading="lazy" style={{ width: "92%", height: "92%", objectFit: "contain", objectPosition: "center" }} />
                            ) : (
                              (group.name || "G").slice(0, 1).toUpperCase()
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: "#fff", fontWeight: 950 }}>{group.name}</div>
                            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 850 }}>{getCountingWorkoutMemberIds(group).length} athletes · {group.weekly_session_target || 3} sessions target{hasNutritionAccess ? ` · ${Number(group.daily_calorie_target || 2500).toLocaleString()} kcal/day` : ""}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7, color: "rgba(255,255,255,0.80)", fontSize: 11, fontWeight: 900 }}>
                              {leadCoach?.profile_img ? (
                                <img src={leadCoach.profile_img} alt="" loading="lazy" style={{ width: 22, height: 22, borderRadius: 999, objectFit: "cover", border: "1px solid rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.10)" }} />
                              ) : (
                                <span style={{ width: 22, height: 22, borderRadius: 999, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.10)" }}><FontAwesomeIcon icon={faUser} style={{ fontSize: 9 }} /></span>
                              )}
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Lead coach: {leadCoachName}</span>
                            </div>
                          </div>
                          <button onClick={() => setGroupManageModal(group)} style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.09)", color: "#fff", borderRadius: 12, padding: "8px 10px", fontWeight: 900, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}><FontAwesomeIcon icon={faGear} style={{ fontSize: 11 }} />Manage Group</button>
                          <button onClick={() => detachGroup(group.id)} style={{ border: "1px solid rgba(255,255,255,0.40)", background: "#FFFFFF", color: base.danger, borderRadius: 12, padding: "8px 10px", fontWeight: 900, cursor: "pointer" }}>Remove</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

	              {toolView === "sessions" && (
	                <div>
	                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
	                    <div>
	                      <div style={{ color: "rgba(255,255,255,0.58)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Schedule</div>
	                      <h3 style={{ color: "#fff", margin: "4px 0 0", fontSize: 24 }}>Session controls</h3>
	                      <p style={{ color: "rgba(255,255,255,0.58)", margin: "5px 0 0", fontSize: 13, fontWeight: 760 }}>
	                        Manage training dates, athlete responses, coaches, and alerts.
	                      </p>
	                    </div>
	                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
	                      <button type="button" onClick={loadClubData} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontSize: 12, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
	                        <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 11 }} />
	                        Refresh
	                      </button>
	                      <button type="button" onClick={openCalendarCreate} style={{ border: 0, background: "#fff", color: brandSurface.primary, borderRadius: 12, padding: "9px 12px", fontSize: 12, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
	                        <FontAwesomeIcon icon={canUseClubCalendar ? faPlus : faLock} style={{ fontSize: 11 }} />
	                        Create Session
	                      </button>
	                    </div>
	                  </div>

	                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 14 }}>
	                    {CLUB_CALENDAR_FILTERS.map((filter) => {
	                      const active = calendarFilter === filter.value;
	                      return (
	                        <button
	                          key={filter.value}
	                          type="button"
	                          onClick={() => {
	                            setCalendarFilter(filter.value);
	                            if (filter.value !== "upcoming") setCalendarSelectedDateKey(null);
	                          }}
	                          style={{ minHeight: 74, border: `1px solid ${active ? alpha(brandSurface.accent, 0.62) : "rgba(255,255,255,0.12)"}`, background: active ? alpha(brandSurface.accent, 0.18) : "rgba(255,255,255,0.055)", color: "#fff", borderRadius: 16, padding: 12, textAlign: "left", cursor: "pointer" }}
	                        >
                          <div style={{ color: "#fff", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>{filter.label}</div>
	                          <div style={{ fontSize: 24, fontWeight: 950, marginTop: 5 }}>{calendarSessionCounts[filter.value] || 0}</div>
	                        </button>
	                      );
	                    })}
	                  </div>

	                  {calendarFilter === "upcoming" && (
	                    <div style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.055)", borderRadius: 18, padding: 12, marginBottom: 14 }}>
	                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
	                        <div>
	                          <div style={{ color: "rgba(255,255,255,0.56)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Date selector</div>
	                          <div style={{ color: "#fff", fontSize: 13, fontWeight: 950 }}>Next 14 days</div>
	                        </div>
	                        {calendarSelectedDateKey && (
	                          <button type="button" onClick={() => setCalendarSelectedDateKey(null)} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 999, padding: "7px 9px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>
	                            All days
	                          </button>
	                        )}
	                      </div>
	                      <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "minmax(70px, 1fr)", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
	                        {calendarUpcomingDayChips.map((day) => {
	                          const active = calendarSelectedDateKey === day.key;
	                          return (
	                            <button key={day.key} type="button" aria-label={`${day.label}, ${day.count} sessions`} onClick={() => setCalendarSelectedDateKey(active ? null : day.key)} style={{ border: `1px solid ${active ? alpha(brandSurface.accent, 0.70) : "rgba(255,255,255,0.14)"}`, background: active ? alpha(brandSurface.accent, 0.20) : "rgba(255,255,255,0.065)", color: "#fff", borderRadius: 14, padding: "9px 8px", minHeight: 70, cursor: "pointer", textAlign: "center" }}>
	                              <div style={{ color: active ? brandSurface.accent : "rgba(255,255,255,0.70)", fontSize: 11, fontWeight: 950 }}>{day.day}</div>
	                              <div style={{ color: "#fff", fontSize: 18, fontWeight: 950, marginTop: 2 }}>{day.date}</div>
	                              <div style={{ color: "rgba(255,255,255,0.58)", fontSize: 10, fontWeight: 900, marginTop: 2 }}>{day.count}</div>
	                            </button>
	                          );
	                        })}
	                      </div>
	                    </div>
	                  )}

	                  {!canUseClubCalendar && (
	                    <div style={{ border: "1px solid rgba(253,230,138,0.38)", background: "rgba(253,230,138,0.12)", color: "#FDE68A", borderRadius: 14, padding: 12, fontSize: 12, fontWeight: 850, lineHeight: 1.45, marginBottom: 14 }}>
	                      Calendar sessions are included with Pro Coach and Club Coaching plans.
	                    </div>
	                  )}

	                  <div style={{ display: "grid", gridTemplateColumns: calendarFormOpen ? "repeat(auto-fit, minmax(min(360px, 100%), 1fr))" : "1fr", gap: 14 }}>
	                    {calendarFormOpen && (
	                    <form onSubmit={createCalendarSession} style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.055)", borderRadius: 18, padding: 14, display: "grid", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9, color: "#fff", fontWeight: 950 }}>
                        <span style={{ width: 34, height: 34, borderRadius: 12, display: "grid", placeItems: "center", background: alpha(brandSurface.accent, 0.22), border: `1px solid ${alpha(brandSurface.accent, 0.34)}` }}>
                          <FontAwesomeIcon icon={faPlus} />
                        </span>
                        {calendarEditingSession ? "Edit Session" : "Create Session"}
                      </div>
                      <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                        Workout group *
                        <select value={calendarForm.groupId} onChange={(event) => {
                          const nextGroupId = event.target.value;
                          const group = groups.find((item) => item.id === nextGroupId);
                          const athleteIds = uniqueIds((group?.workout_group_members || []).map((member) => member.athlete_id || member.profiles?.id));
                          setCalendarForm((prev) => ({
                            ...prev,
                            groupId: nextGroupId,
                            selectedAthleteIds: athleteIds,
                            inviteAll: true,
                            coachIds: calendarCoachSelectionTouchedRef.current
                              ? prev.coachIds
                              : getPreferredCalendarCoachIds(group),
                          }));
                          setCalendarError("");
                        }} style={{ width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "#111827", color: "#fff", borderRadius: 12, padding: "11px 12px", fontWeight: 900 }}>
                          <option value="">Choose group</option>
                          {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                        </select>
                      </label>
                      <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                        Session title *
                        <input autoFocus value={calendarForm.title} onChange={(event) => { setCalendarForm((prev) => ({ ...prev, title: event.target.value })); setCalendarError(""); }} placeholder="e.g., Group training" style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 12px", fontWeight: 850 }} />
                      </label>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                        <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                          Date *
                          <input type="date" value={calendarForm.date} onChange={(event) => { setCalendarForm((prev) => ({ ...prev, date: event.target.value })); setCalendarError(""); }} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 10px", fontWeight: 850 }} />
                        </label>
                        <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                          Start time *
                          <input type="time" value={calendarForm.startTime} onChange={(event) => { setCalendarForm((prev) => ({ ...prev, startTime: event.target.value })); setCalendarError(""); }} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 10px", fontWeight: 850 }} />
                        </label>
                        <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                          End time
                          <input type="time" value={calendarForm.endTime} disabled={!calendarForm.hasEndTime} onChange={(event) => { setCalendarForm((prev) => ({ ...prev, endTime: event.target.value, hasEndTime: true })); setCalendarError(""); }} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 10px", fontWeight: 850, opacity: calendarForm.hasEndTime ? 1 : 0.52 }} />
                        </label>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.72)", fontSize: 12, fontWeight: 850 }}>
                        <input type="checkbox" checked={calendarForm.hasEndTime} onChange={(event) => { setCalendarForm((prev) => ({ ...prev, hasEndTime: event.target.checked })); setCalendarError(""); }} style={{ width: 16, height: 16, accentColor: brandSurface.accent }} />
                        Set an end time
                      </label>
                      <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                        Location
                        <input value={calendarForm.location} onChange={(event) => setCalendarForm((prev) => ({ ...prev, location: event.target.value }))} placeholder="Court, gym, field, or online link" style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 12px", fontWeight: 850 }} />
                      </label>
                      <label style={{ display: "grid", gap: 6, color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>
                        Notes
                        <textarea value={calendarForm.notes} onChange={(event) => setCalendarForm((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Details athletes and coaches need before the session" style={{ minHeight: 78, resize: "vertical", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: 12, padding: "11px 12px", fontWeight: 850, lineHeight: 1.45 }} />
                      </label>
                      <div style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", borderRadius: 14, padding: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ color: "#fff", fontSize: 14, fontWeight: 950 }}>Invited athletes</div>
                            <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 12, fontWeight: 800, marginTop: 3 }}>{calendarSelectedAthleteIds.length} selected</div>
                          </div>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "rgba(255,255,255,0.72)", fontSize: 12, fontWeight: 900 }}>
                            <input type="checkbox" checked={calendarForm.inviteAll} onChange={(event) => {
                              const nextInviteAll = event.target.checked;
                              setCalendarForm((prev) => ({
                                ...prev,
                                inviteAll: nextInviteAll,
                                selectedAthleteIds: nextInviteAll ? calendarGroupAthleteOptions.map((athlete) => athlete.id) : calendarSelectedAthleteIds,
                              }));
                              setCalendarError("");
                            }} style={{ width: 16, height: 16, accentColor: brandSurface.accent }} />
                            Invite all
                          </label>
                        </div>
                        {calendarForm.inviteAll ? (
                          <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                            All current group athletes will be invited. New athletes added later are not automatically added to this session.
                          </div>
                        ) : calendarGroupAthleteOptions.length === 0 ? (
                          <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 12, fontWeight: 800 }}>No athletes are in this group yet.</div>
                        ) : (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, maxHeight: 220, overflowY: "auto" }}>
                            {calendarGroupAthleteOptions.map((athlete) => {
                              const selected = calendarSelectedAthleteIds.includes(athlete.id);
                              const profile = athlete.profile || {};
                              return (
                                <label key={athlete.id} style={{ minWidth: 0, border: `1px solid ${selected ? alpha(brandSurface.accent, 0.55) : "rgba(255,255,255,0.14)"}`, background: selected ? alpha(brandSurface.accent, 0.16) : "rgba(255,255,255,0.06)", borderRadius: 13, padding: 9, display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(event) => {
                                      const nextChecked = event.target.checked;
                                      setCalendarForm((prev) => ({
                                        ...prev,
                                        selectedAthleteIds: nextChecked
                                          ? uniqueIds([...(prev.selectedAthleteIds || []), athlete.id])
                                          : uniqueIds(prev.selectedAthleteIds).filter((id) => id !== athlete.id),
                                      }));
                                      setCalendarError("");
                                    }}
                                    style={{ width: 16, height: 16, flexShrink: 0, accentColor: brandSurface.accent }}
                                  />
                                  <img src={profile.profile_img || noImage} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(255,255,255,0.18)" }} loading="lazy" />
                                  <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {calendarProfileName(profile)}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", borderRadius: 14, padding: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ color: "#fff", fontSize: 14, fontWeight: 950 }}>Assigned coaches</div>
                            <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 12, fontWeight: 800, marginTop: 3 }}>{calendarSelectedCoachIds.length} selected</div>
                          </div>
                          <button type="button" onClick={refreshCalendarCoaches} disabled={calendarCoachesRefreshing} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 12, padding: "8px 10px", fontSize: 11, fontWeight: 950, cursor: calendarCoachesRefreshing ? "wait" : "pointer" }}>
                            {calendarCoachesRefreshing ? "Loading..." : "Refresh Coaches"}
                          </button>
                        </div>
                        {calendarCoachOptions.length === 0 ? (
                          <div style={{ border: "1px dashed rgba(255,255,255,0.18)", borderRadius: 12, padding: 11, color: "rgba(255,255,255,0.64)", fontSize: 12, fontWeight: 850 }}>
                            Add a coach in Staff before creating sessions.
                          </div>
                        ) : (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                            {calendarCoachOptions.map((coach) => {
                              const profile = coach.profile || {};
                              const coachName = [profile.name, profile.surname].filter(Boolean).join(" ") || profile.username || (coach.id === profileId ? "You" : "Coach");
                              const checked = calendarSelectedCoachIds.includes(coach.id);
                              return (
                                <label key={coach.id} style={{ minWidth: 0, border: `1px solid ${checked ? alpha(brandSurface.accent, 0.55) : "rgba(255,255,255,0.14)"}`, background: checked ? alpha(brandSurface.accent, 0.16) : "rgba(255,255,255,0.06)", borderRadius: 13, padding: 9, display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) => {
                                      const nextChecked = event.target.checked;
                                      calendarCoachSelectionTouchedRef.current = true;
                                      setCalendarForm((prev) => ({
                                        ...prev,
                                        coachIds: nextChecked
                                          ? uniqueIds([...(prev.coachIds || []), coach.id])
                                          : uniqueIds(prev.coachIds).filter((id) => id !== coach.id),
                                      }));
                                      setCalendarError("");
                                    }}
                                    style={{ width: 16, height: 16, flexShrink: 0, accentColor: brandSurface.accent }}
                                  />
                                  <img src={profile.profile_img || noImage} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(255,255,255,0.18)" }} loading="lazy" />
                                  <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {coach.id === profileId ? `${coachName} (you)` : coachName}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {calendarGuidanceMessage && <div style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.68)", borderRadius: 12, padding: 10, fontSize: 12, fontWeight: 850 }}>{calendarGuidanceMessage}</div>}
                      {(calendarError || (calendarFormError && !calendarGuidanceMessage)) && <div style={{ border: "1px solid rgba(248,113,113,0.34)", background: "rgba(248,113,113,0.10)", color: "#FCA5A5", borderRadius: 12, padding: 10, fontSize: 12, fontWeight: 850 }}>{calendarError || calendarFormError}</div>}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {calendarEditingSession && (
                          <button type="button" onClick={() => clearCalendarEdit(calendarForm.groupId)} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "#fff", borderRadius: 13, padding: "12px 14px", fontWeight: 950, cursor: "pointer", flex: "1 1 130px" }}>
                            Cancel Edit
                          </button>
                        )}
                        <button type="submit" disabled={calendarSaving || !!calendarFormError} style={{ border: 0, background: !calendarFormError ? "#fff" : "rgba(255,255,255,0.18)", color: !calendarFormError ? "#080B12" : "rgba(255,255,255,0.62)", borderRadius: 13, padding: "12px 14px", fontWeight: 950, cursor: !calendarFormError && !calendarSaving ? "pointer" : "not-allowed", flex: "1 1 160px" }}>
                          {calendarSaving ? (calendarEditingSession ? "Saving..." : "Creating...") : (calendarEditingSession ? "Save Session" : "Create Session")}
                        </button>
                      </div>
	                    </form>
	                    )}

	                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14 }}>
	                      <div style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.055)", borderRadius: 18, padding: 14 }}>
	                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
	                          <div>
	                            <div style={{ color: "rgba(255,255,255,0.56)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Sessions</div>
	                            <h4 style={{ margin: "2px 0 0", color: "#fff", fontSize: 18 }}>{CLUB_CALENDAR_FILTERS.find((filter) => filter.value === calendarFilter)?.label || "Sessions"}</h4>
	                          </div>
	                          <span style={{ color: "rgba(255,255,255,0.68)", fontSize: 12, fontWeight: 900 }}>{displayedCalendarSessions.length} shown</span>
	                        </div>
	                        {displayedCalendarSessions.length === 0 ? (
	                          <div style={{ border: "1px dashed rgba(255,255,255,0.18)", borderRadius: 16, padding: 18, color: "rgba(255,255,255,0.62)", fontWeight: 850, lineHeight: 1.45 }}>
	                            No {calendarFilter} sessions in this view.
	                          </div>
	                        ) : (
	                          <div style={{ display: "grid", gap: 12 }}>
	                            {groupedCalendarSessions.map((dayGroup) => (
	                              <div key={dayGroup.key} style={{ display: "grid", gap: 8 }}>
	                                <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{dayGroup.title}</div>
	                                {dayGroup.sessions.map((session) => {
	                                  const attendance = getCalendarAttendanceSummary(session);
	                                  const selected = selectedCalendarSession?.id === session.id;
	                                  const sessionTime = formatCalendarDateTimeParts(session.starts_at, session.ends_at);
	                                  return (
	                                    <button
	                                      key={session.id}
	                                      type="button"
	                                      onClick={() => setCalendarSelectedSessionId(session.id)}
	                                      style={{ width: "100%", textAlign: "left", border: `1px solid ${selected ? alpha(brandSurface.accent, 0.66) : base.border}`, background: "#fff", borderRadius: 15, padding: 12, color: base.text, cursor: "pointer", boxShadow: selected ? `0 0 0 2px ${alpha(brandSurface.accent, 0.14)}` : "0 8px 20px rgba(15,23,42,0.16)" }}
	                                    >
	                                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
	                                        <div style={{ minWidth: 0 }}>
	                                          <div style={{ color: base.text, fontSize: 15, fontWeight: 950, lineHeight: 1.25, overflowWrap: "anywhere" }}>{session.title}</div>
	                                          <div style={{ color: base.muted, fontSize: 11, fontWeight: 850, marginTop: 3, overflowWrap: "anywhere" }}>{session.group?.name || "Workout group"}</div>
	                                        </div>
	                                        <span style={pill(session.status === "cancelled" ? "danger" : "success")}>{session.status || "scheduled"}</span>
	                                      </div>
	                                      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "center", marginTop: 12 }}>
	                                        <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
	                                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${base.border}`, background: base.bgAlt, color: base.text, borderRadius: 10, padding: "7px 9px", fontSize: 11, fontWeight: 950 }}>
	                                            <FontAwesomeIcon icon={faCalendar} style={{ color: brandSurface.accent }} />
	                                            {sessionTime.date}
	                                          </span>
	                                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${base.border}`, background: base.bgAlt, color: base.text, borderRadius: 10, padding: "7px 9px", fontSize: 11, fontWeight: 950 }}>
	                                            <FontAwesomeIcon icon={faClock} style={{ color: brandSurface.accent }} />
	                                            {sessionTime.time}
	                                          </span>
	                                          {session.location && (
	                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: base.muted, fontSize: 11, fontWeight: 850, minWidth: 0, overflowWrap: "anywhere" }}>
	                                              <FontAwesomeIcon icon={faLocationDot} style={{ color: base.muted }} />
	                                              {session.location}
	                                            </span>
	                                          )}
	                                        </div>
	                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(58px, 1fr))", gap: 6, minWidth: 210 }}>
	                                          {[
	                                            ["Available", attendance.accepted, "#16A34A", "#ECFDF3", "#BBF7D0"],
	                                            ["Pending", attendance.pending, "#92400E", "#FFFBEB", "#FDE68A"],
	                                            ["Unavailable", attendance.declined, "#991B1B", "#FEF2F2", "#FECACA"],
	                                          ].map(([label, value, color, bg, border]) => (
	                                            <span key={label} style={{ border: `1px solid ${border}`, background: bg, color, borderRadius: 10, padding: "6px 7px", textAlign: "center", lineHeight: 1.05 }}>
	                                              <span style={{ display: "block", fontSize: 15, fontWeight: 950 }}>{Number(value || 0).toLocaleString()}</span>
	                                              <span style={{ display: "block", fontSize: 9, fontWeight: 950, textTransform: "uppercase", marginTop: 3 }}>{label}</span>
	                                            </span>
	                                          ))}
	                                        </div>
	                                      </div>
	                                    </button>
	                                  );
	                                })}
	                              </div>
	                            ))}
	                          </div>
	                        )}
	                      </div>

	                      <div style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.055)", borderRadius: 18, padding: 14 }}>
	                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
	                          <div>
	                            <div style={{ color: "rgba(255,255,255,0.56)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Session details</div>
	                            <h4 style={{ margin: "2px 0 0", color: "#fff", fontSize: 18 }}>{selectedCalendarSession ? selectedCalendarSession.title : "Choose a session"}</h4>
	                          </div>
	                          <FontAwesomeIcon icon={faCalendar} style={{ color: brandSurface.accent }} />
	                        </div>
	                        {!selectedCalendarSession ? (
	                          <div style={{ border: "1px dashed rgba(255,255,255,0.18)", borderRadius: 16, padding: 18, color: "rgba(255,255,255,0.62)", fontWeight: 850, lineHeight: 1.45 }}>
	                            Choose a session to view attendance, details, and coach actions.
	                          </div>
	                        ) : (() => {
	                          const attendance = getCalendarAttendanceSummary(selectedCalendarSession);
	                          const assignedCoaches = selectedCalendarSession.coaches || [];
	                          const invitedAthletes = selectedCalendarSession.athletes || [];
	                          const sessionTime = formatCalendarDateTimeParts(selectedCalendarSession.starts_at, selectedCalendarSession.ends_at);
	                          return (
	                            <div style={{ display: "grid", gap: 12 }}>
	                              <div style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", borderRadius: 16, padding: 12 }}>
	                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
	                                  <div style={{ minWidth: 0 }}>
	                                    <div style={{ color: "#fff", fontSize: 13, fontWeight: 950, overflowWrap: "anywhere" }}>{selectedCalendarSession.group?.name || "Workout group"}</div>
	                                    {selectedCalendarSession.notes && <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>{selectedCalendarSession.notes}</div>}
	                                  </div>
	                                  <span style={{ ...pill(selectedCalendarSession.status === "cancelled" ? "danger" : "success") }}>{selectedCalendarSession.status || "scheduled"}</span>
	                                </div>
	                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 8, marginTop: 12 }}>
	                                  <div style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)", borderRadius: 13, padding: 10 }}>
	                                    <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Date</div>
	                                    <div style={{ color: "#fff", fontSize: 16, fontWeight: 950, marginTop: 4 }}>{sessionTime.date}</div>
	                                  </div>
	                                  <div style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)", borderRadius: 13, padding: 10 }}>
	                                    <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Time</div>
	                                    <div style={{ color: "#fff", fontSize: 16, fontWeight: 950, marginTop: 4 }}>{sessionTime.time}</div>
	                                  </div>
	                                  {selectedCalendarSession.location && (
	                                    <div style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)", borderRadius: 13, padding: 10 }}>
	                                      <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Location</div>
	                                      <div style={{ color: "#fff", fontSize: 14, fontWeight: 900, marginTop: 4, overflowWrap: "anywhere" }}>{selectedCalendarSession.location}</div>
	                                    </div>
	                                  )}
	                                </div>
	                              </div>
	                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(92px, 1fr))", gap: 8 }}>
	                                {[
	                                  ["Invited", attendance.invited, "#475569", "#F1F5F9", "#CBD5E1"],
	                                  ["Available", attendance.accepted, "#166534", "#ECFDF3", "#BBF7D0"],
	                                  ["Pending", attendance.pending, "#92400E", "#FFFBEB", "#FDE68A"],
	                                  ["Unavailable", attendance.declined, "#991B1B", "#FEF2F2", "#FECACA"],
	                                ].map(([label, value, color, bg, border]) => (
	                                  <div key={label} style={{ border: `1px solid ${border}`, background: bg, color, borderRadius: 13, padding: 10 }}>
	                                    <div style={{ fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
	                                    <div style={{ fontSize: 22, fontWeight: 950, marginTop: 3 }}>{Number(value || 0).toLocaleString()}</div>
	                                  </div>
	                                ))}
	                              </div>
	                              {assignedCoaches.length > 0 && (
	                                <div>
	                                  <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Assigned coaches</div>
	                                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
	                                    {assignedCoaches.map((coach) => {
	                                      const profile = coach.profiles || {};
	                                      const coachName = [profile.name, profile.surname].filter(Boolean).join(" ") || profile.username || "Coach";
	                                      return (
	                                        <span key={`${selectedCalendarSession.id}-${coach.coach_id}`} style={{ maxWidth: "100%", display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.08)", borderRadius: 999, padding: "5px 10px 5px 5px", color: "#fff", fontSize: 12, fontWeight: 900 }}>
	                                          <img src={profile.profile_img || noImage} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover", flexShrink: 0 }} loading="lazy" />
	                                          <span style={{ overflowWrap: "anywhere" }}>{coach.coach_id === profileId ? `${coachName} (you)` : coachName}</span>
	                                        </span>
	                                      );
	                                    })}
	                                  </div>
	                                </div>
	                              )}
	                              {invitedAthletes.length > 0 && (
	                                <div>
	                                  <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Invited athletes</div>
	                                  <div style={{ display: "grid", gap: 7 }}>
	                                    {invitedAthletes.map((athlete) => {
	                                      const profile = athlete.profiles || {};
	                                      const athleteName = calendarProfileName(profile);
	                                      const statusTone = athlete.status === "accepted" ? "success" : athlete.status === "declined" ? "danger" : "warning";
	                                      return (
	                                        <div key={`${selectedCalendarSession.id}-${athlete.athlete_id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", borderRadius: 12, padding: 9 }}>
	                                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
	                                            <img src={profile.profile_img || noImage} alt="" style={{ width: 42, height: 42, borderRadius: 13, objectFit: "cover", flexShrink: 0 }} loading="lazy" />
	                                            <div style={{ color: "#fff", fontSize: 12, fontWeight: 900, overflowWrap: "anywhere" }}>{athleteName}</div>
	                                          </div>
	                                          <span style={pill(statusTone)}>{athlete.status || "pending"}</span>
	                                        </div>
	                                      );
	                                    })}
	                                  </div>
	                                </div>
	                              )}
	                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
	                                <button type="button" onClick={() => openCalendarEdit(selectedCalendarSession)} style={{ ...btn, padding: "8px 10px", borderRadius: 11, fontSize: 12 }}>
	                                  Edit
	                                </button>
	                                {selectedCalendarSession.status === "cancelled" ? (
	                                  <button type="button" onClick={() => setCalendarSessionStatus(selectedCalendarSession, "scheduled")} style={{ ...btn, padding: "8px 10px", borderRadius: 11, fontSize: 12, borderColor: base.success, background: base.success, color: "#fff" }}>
	                                    Make Live
	                                  </button>
	                                ) : (
	                                  <button type="button" onClick={() => setCalendarSessionStatus(selectedCalendarSession, "cancelled")} style={{ ...btn, padding: "8px 10px", borderRadius: 11, fontSize: 12, borderColor: "rgba(220,38,38,0.22)", color: base.danger }}>
	                                    Cancel
	                                  </button>
	                                )}
	                              </div>
	                            </div>
	                          );
	                        })()}
	                      </div>
	                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

	        {dashboardGroup && dashboardGroupData && (
	          <section id="club-workout-group-dashboard" style={{ background: `linear-gradient(145deg, ${brandSurface.primary} 0%, ${brandSurface.secondary} 100%)`, border: `1px solid ${brandSurface.accent}66`, borderRadius: 24, padding: 20, marginBottom: 18, boxShadow: `0 22px 70px ${brandSurface.primary}22`, color: "#fff" }}>
	            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
	              <div style={{ minWidth: 0, flex: "1 1 320px", display: "flex", alignItems: "center", gap: 12 }}>
	                <div style={{ width: 68, height: 68, borderRadius: 18, overflow: "hidden", border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center", flexShrink: 0 }}>
	                  {activeClub?.logo_url ? <img src={activeClub.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} loading="lazy" /> : <span style={{ color: "#fff", fontWeight: 950 }}>{(dashboardGroup.name || "G").slice(0, 1).toUpperCase()}</span>}
	                </div>
	                <div style={{ minWidth: 0 }}>
	                  <div style={{ color: "rgba(255,255,255,0.66)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Workout Group Dashboard</div>
	                  <h2 style={{ margin: "5px 0 5px", fontSize: "clamp(23px,2.5vw,34px)", lineHeight: 1.05, overflowWrap: "anywhere" }}>{dashboardGroup.name}</h2>
	                  {(() => {
	                    const leadCoach = firstRelationRow(dashboardGroup.coach) || activeClubCoachOptions.find((coach) => coach.id === dashboardGroup.coach_id)?.profile || null;
	                    return (
	                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.84)", fontSize: 12, fontWeight: 900, marginTop: 4, maxWidth: "100%" }}>
	                        {leadCoach?.profile_img ? (
	                          <img src={leadCoach.profile_img} alt="" loading="lazy" style={{ width: 32, height: 32, borderRadius: 999, objectFit: "cover", border: "1px solid rgba(255,255,255,0.24)" }} />
	                        ) : (
	                          <span style={{ width: 32, height: 32, borderRadius: 999, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.12)" }}><FontAwesomeIcon icon={faUser} style={{ fontSize: 12 }} /></span>
	                        )}
	                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Lead coach: {displayProfileName(leadCoach, "Unassigned")}</span>
	                      </div>
	                    );
	                  })()}
	                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center", flex: "1 1 520px" }}>
                <select aria-label="Workout group dashboard range" value={activeSeasonId} onChange={(event) => selectAnalyticsRange(event.target.value)} style={{ minHeight: 40, border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 10, padding: "8px 10px", fontWeight: 900, fontSize: 12, outline: "none" }}>
                  {ANALYTICS_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value} style={{ color: base.text }}>{option.label}</option>)}
                  {seasons.length > 0 && (
                    <optgroup label="Seasons">
                      {seasons.map((season) => <option key={season.id} value={season.id} style={{ color: base.text }}>{season.name}</option>)}
                    </optgroup>
                  )}
                </select>
                {activeSeasonId === "custom" && (
                  <input
                    aria-label="Custom dashboard range days"
                    type="number"
                    min="1"
                    max="1095"
                    value={customDays}
                    onChange={(event) => setCustomDays(event.target.value)}
                    style={{ width: 92, minHeight: 40, border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 10, padding: "8px 10px", fontWeight: 900, fontSize: 12, outline: "none" }}
                  />
                )}
                {activeSeasonId === "date" && (
                  <input
                    aria-label="Workout group dashboard review date"
                    type="date"
                    value={analyticsSelectedDate}
                    onChange={(event) => setAnalyticsSelectedDate(event.target.value || localDateInput(new Date()))}
                    style={{ minHeight: 40, border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 10, padding: "8px 10px", fontWeight: 900, fontSize: 12, outline: "none" }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setGroupManageModal(dashboardGroup)}
                  style={{ border: "1px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 12, padding: "10px 13px", fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <FontAwesomeIcon icon={faGear} style={{ color: "#fff", fontSize: 12 }} />
                  Manage Group
                </button>
              </div>
            </div>

            {(() => {
              const memberCount = dashboardGroupData.members.length;
              const totalSessionTarget = dashboardGroupData.sessionTarget * memberCount;
              const sessionCompletion = totalSessionTarget > 0
                ? Math.min(100, Math.round((dashboardGroupData.totalSessions / totalSessionTarget) * 100))
                : 0;
              const currentCycleHeadline = memberCount === 0
                ? "No athletes yet"
                : dashboardGroupData.needAttentionCount > 0
                  ? `${dashboardGroupData.needAttentionCount} athlete${dashboardGroupData.needAttentionCount === 1 ? " needs" : "s need"} attention`
                  : "No attention needed";
              const currentCycleTone = memberCount === 0
                ? { bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.22)", text: "#FFFFFF", icon: faUsers }
                : dashboardGroupData.needAttentionCount > 0
                  ? { bg: "rgba(253,230,138,0.14)", border: "rgba(253,230,138,0.42)", text: "#FDE68A", icon: faTriangleExclamation }
                  : { bg: "rgba(187,247,208,0.14)", border: "rgba(187,247,208,0.42)", text: "#BBF7D0", icon: faCircleCheck };
              const currentCycleSubtitle = totalSessionTarget > 0
                ? dashboardGroupData.needAttentionCount > 0
                  ? "Several athletes are behind the weekly session target. Open analytics to see who needs a follow-up."
                  : "The squad is tracking well against the weekly session target."
                : "Add athletes to start tracking session completion.";
              const openCalendarShortcut = () => {
                if (!canUseClubCalendar) {
                  notify("Calendar sessions are included with Pro Coach and Club Coaching plans.", "warning");
                  return;
                }
                const athleteIds = uniqueIds((dashboardGroup.workout_group_members || []).map((member) => member.athlete_id || member.profiles?.id));
                setCalendarForm((prev) => ({
                  ...prev,
                  groupId: dashboardGroup.id,
                  inviteAll: true,
                  selectedAthleteIds: athleteIds,
                }));
                setCalendarError("");
                setCalendarEditingSession(null);
                setCalendarFilter("upcoming");
                setCalendarSelectedDateKey(null);
                setCalendarSelectedSessionId(null);
                setCalendarFormOpen(false);
                setToolView("sessions");
                window.requestAnimationFrame(() => {
                  document.getElementById("club-tools")?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              };
              const scrollToClubGroupSection = (id) => {
                window.requestAnimationFrame(() => {
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              };
              const shortcutActions = [
                {
                  key: "calendar",
                  icon: canUseClubCalendar ? faCalendar : faLock,
                  title: "Calendar",
                  sub: canUseClubCalendar ? "Sessions & attendance" : "Preview sessions",
                  onClick: openCalendarShortcut,
                },
                {
                  key: "groupAnalytics",
                  icon: faChartLine,
                  title: "Group analytics",
                  sub: "Performance trends",
                  onClick: () => {
                    setGroupAnalyticsAthleteId("all");
                    setSelectedGroupWorkoutKey("");
                    setSelectedGroupExerciseId("");
                    setGroupAnalyticsModalOpen(true);
                  },
                },
                {
                  key: "athleteAnalytics",
                  icon: faUsers,
                  title: "Athlete analytics",
                  sub: "Tap a member to open",
                  onClick: () => scrollToClubGroupSection("club-workout-group-members"),
                },
                {
                  key: "assignedWorkouts",
                  icon: faDumbbell,
                  title: `Allowed Workouts (${getAllowedWorkoutPairs(dashboardGroup).length})`,
                  sub: "Assigned plans",
                  onClick: () => updateAllowedWorkouts(dashboardGroup),
                },
                {
                  key: "inviteCode",
                  icon: dashboardGroup.join_code ? faCopy : faBolt,
                  title: dashboardGroup.join_code ? "Invite code" : "Create invite",
                  sub: dashboardGroup.join_code || "Generate code",
                  onClick: () => getGroupJoinCode(dashboardGroup),
                },
                {
                  key: "clubDashboard",
                  icon: faBuilding,
                  title: "Club dashboard",
                  sub: "Coaches & club tools",
                  onClick: () => scrollToClubGroupSection("club-tools"),
                },
              ];
              return (
                <>
                  <div style={{ position: "relative", overflow: "hidden", border: "none", background: "linear-gradient(135deg,#0C69C8 0%,#0D275B 100%)", borderRadius: 24, padding: 18, marginBottom: 14 }}>
                    <span aria-hidden="true" style={{ position: "absolute", right: -28, top: 14, width: 180, height: 92, borderRadius: 36, background: "rgba(255,255,255,0.08)", transform: "rotate(-18deg)" }} />
                    <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: "absolute", right: 26, bottom: 18, color: "#FFFFFF", opacity: 0.16, fontSize: 76 }} />
                    <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, alignItems: "center" }}>
                      <div>
                        <div style={{ color: "#22D3EE", fontSize: 13, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Team story</div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10, border: `1px solid ${currentCycleTone.border}`, background: currentCycleTone.bg, color: currentCycleTone.text, borderRadius: 999, padding: "8px 11px", fontSize: 12, lineHeight: 1, fontWeight: 950, maxWidth: "100%", flexWrap: "wrap" }}>
                          <FontAwesomeIcon icon={currentCycleTone.icon} style={{ color: "#fff", fontSize: 11, flexShrink: 0 }} />
                          <span style={{ overflowWrap: "anywhere" }}>{currentCycleHeadline}</span>
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 15, fontWeight: 850, lineHeight: 1.45, marginTop: 10, maxWidth: 620 }}>{currentCycleSubtitle}</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
                          <button type="button" onClick={() => {
                            setGroupAnalyticsAthleteId("all");
                            setSelectedGroupWorkoutKey("");
                            setSelectedGroupExerciseId("");
                            setGroupAnalyticsModalOpen(true);
                          }} style={{ border: "1px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 999, padding: "10px 13px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 950, cursor: "pointer" }}>
                            <FontAwesomeIcon icon={faCalendar} style={{ color: "#fff" }} />
                            Open group analytics
                          </button>
                          {memberCount > 0 && (
                            <button type="button" onClick={() => sendGroupReminder(dashboardGroup, "training")} style={{ border: "1px solid rgba(255,255,255,0.28)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 999, padding: "9px 12px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 950, cursor: "pointer" }}>
                              <FontAwesomeIcon icon={faEnvelope} style={{ color: "#fff" }} />
                              Remind
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.10)", borderRadius: 22, padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                          <div style={{ color: "rgba(255,255,255,0.80)", fontSize: 13, fontWeight: 950, textTransform: "uppercase" }}>Session completion</div>
                          <div style={{ color: "#fff", fontSize: 15, fontWeight: 950 }}>{sessionCompletion}%</div>
                        </div>
                        <div style={{ height: 8, background: "rgba(255,255,255,0.20)", borderRadius: 999, overflow: "hidden", marginTop: 9 }}>
                          <div style={{ height: "100%", width: `${sessionCompletion}%`, background: "#FFFFFF", borderRadius: 999 }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                          <div style={{ color: "#fff", fontSize: 30, fontWeight: 950, lineHeight: 1 }}>{dashboardGroupData.totalSessions.toLocaleString()}</div>
                          <div style={{ color: "#fff", fontSize: 30, fontWeight: 950, lineHeight: 1, textAlign: "right" }}>{totalSessionTarget.toLocaleString()}</div>
                          <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 12, fontWeight: 900 }}>completed</div>
                          <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 12, fontWeight: 900, textAlign: "right" }}>expected</div>
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, fontWeight: 850, lineHeight: 1.35, marginTop: 11 }}>
                          {dashboardGroupData.sessionTarget} each x {memberCount} athletes
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.12)", borderRadius: 18, padding: 14, marginBottom: 16 }}>
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 9 }}>Shortcuts</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                      {shortcutActions.map((action) => (
                        <button
                          key={action.key}
                          type="button"
                          onClick={action.onClick}
                          style={{ minHeight: 68, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", borderRadius: 15, padding: 10, cursor: "pointer", display: "grid", gridTemplateColumns: "34px minmax(0, 1fr)", gap: 9, alignItems: "center", textAlign: "left" }}
                        >
                          <span style={{ width: 34, height: 34, borderRadius: 13, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.20)" }}>
                            <FontAwesomeIcon icon={action.icon} style={{ color: "#fff", fontSize: 14 }} />
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "block", color: "#fff", fontSize: 12, fontWeight: 950, overflowWrap: "anywhere" }}>{action.title}</span>
                            <span style={{ display: "block", color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 800, marginTop: 3, overflowWrap: "anywhere" }}>{action.sub}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}

            <div style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.12)", borderRadius: 18, padding: 14, marginBottom: 16 }}>
              <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 9 }}>Group summary</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                {[
                  { icon: faCalendar, label: "Completed sessions", value: dashboardGroupData.totalSessions.toLocaleString(), color: "#fff", bg: "rgba(255,255,255,0.14)" },
                  { icon: faBolt, label: "Total XP", value: dashboardGroupData.totalGroupXp.toLocaleString(), color: "#fff", bg: "rgba(255,255,255,0.14)" },
                  { icon: faRepeat, label: "Completed reps", value: dashboardGroupData.totalReps.toLocaleString(), color: "#fff", bg: "rgba(255,255,255,0.14)" },
                  { icon: faDumbbell, label: "Completed volume", value: formatGroupVolume(dashboardGroupData.totalVolume), color: "#fff", bg: "rgba(255,255,255,0.14)" },
                ].map((metric) => (
                  <div key={metric.label} style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.10)", borderRadius: 16, padding: 13, display: "grid", gridTemplateColumns: "38px minmax(0, 1fr)", gap: 10, alignItems: "center" }}>
                    <span style={{ width: 38, height: 38, borderRadius: 14, background: metric.bg, color: metric.color, display: "grid", placeItems: "center" }}>
                      <FontAwesomeIcon icon={metric.icon} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4, overflowWrap: "anywhere" }}>{metric.label}</span>
                      <span style={{ display: "block", color: "#fff", fontSize: 22, fontWeight: 950, marginTop: 3, overflowWrap: "anywhere" }}>{metric.value}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const expectedSessionTotal = dashboardGroupData.sessionTarget * dashboardGroupData.members.length;
              const sessionCompletion = expectedSessionTotal > 0
                ? Math.min(100, Math.round((dashboardGroupData.totalSessions / expectedSessionTotal) * 100))
                : 0;
              const attentionRows = dashboardGroupData.athleteRows.filter((row) => row.sessions < Math.ceil(dashboardGroupData.sessionTarget * 0.75));
              return (
                <div id="club-workout-group-performance" style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.12)", borderRadius: 18, padding: 14, marginBottom: 16, scrollMarginTop: 76 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18 }}>Performance Analysis</h3>
                      <div style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, fontWeight: 850, marginTop: 3 }}>{dashboardGroupData.sessionTarget} sessions per athlete</div>
                    </div>
                    <div style={{ color: "#fff", fontSize: 13, fontWeight: 950 }}>{sessionCompletion}% of session target</div>
                  </div>
                  <div style={{ height: 9, background: "rgba(255,255,255,0.24)", borderRadius: 999, overflow: "hidden", marginBottom: 12 }}>
                    <div style={{ height: "100%", width: `${sessionCompletion}%`, background: "#fff", borderRadius: 999 }} />
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.74)", fontSize: 11, fontWeight: 850, marginBottom: 12 }}>
                    {dashboardGroupData.totalSessions.toLocaleString()} / {expectedSessionTotal.toLocaleString()} sessions completed
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
                    {[
                      { label: "On Plan", value: `${dashboardGroupData.onPlanCount}/${dashboardGroupData.members.length}`, detail: "athletes meeting targets" },
                      { label: "XP output", value: `${dashboardGroupData.completion}%`, detail: `${dashboardGroupData.totalGroupXp.toLocaleString()} / ${dashboardGroupData.groupTarget.toLocaleString()} XP` },
                      { label: dashboardGroupData.needAttentionCount > 0 ? "Needs Activity" : "No attention needed", value: dashboardGroupData.needAttentionCount.toLocaleString(), detail: dashboardGroupData.needAttentionCount > 0 ? "" : "all visible athletes on track", avatars: attentionRows.map((row) => row.profile_img || noImage) },
                      { label: "Top Athlete", value: dashboardGroupData.topAthlete?.name || "None", detail: `${Number(dashboardGroupData.topAthlete?.sessions || 0).toLocaleString()}/${dashboardGroupData.sessionTarget} sessions · ${Number(dashboardGroupData.topAthlete?.xp || 0).toLocaleString()} XP`, image: dashboardGroupData.topAthlete?.profile_img || noImage },
                    ].map((item) => (
                      <div key={item.label} style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.09)", borderRadius: 12, padding: "11px 12px", display: item.image ? "grid" : "block", gridTemplateColumns: item.image ? "minmax(0, 1fr) 44px" : undefined, gap: item.image ? 10 : undefined, alignItems: "center" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{item.label}</div>
                          <div style={{ color: "#fff", fontSize: 17, fontWeight: 950, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.value}</div>
                          {item.detail ? <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 11, fontWeight: 800, marginTop: 2 }}>{item.detail}</div> : null}
                          {item.avatars?.length ? (
                            <div style={{ display: "flex", alignItems: "center", marginTop: 8, overflowX: "auto", paddingBottom: 1 }}>
                              {item.avatars.map((src, index) => (
                                <img key={`${item.label}_${index}`} src={src} alt="" style={{ width: 28, height: 28, borderRadius: 999, objectFit: "cover", border: "2px solid rgba(255,255,255,0.28)", marginLeft: index === 0 ? 0 : -7, background: "#FFFFFF", flexShrink: 0 }} loading="lazy" />
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {item.image ? <img src={item.image} alt="" style={{ width: 44, height: 44, borderRadius: 14, objectFit: "cover", border: "1px solid rgba(255,255,255,0.24)" }} loading="lazy" /> : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div id="club-workout-group-members" style={{ scrollMarginTop: 76 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 18 }}>Members</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button title="Send training reminder to all members" onClick={() => sendGroupReminder(dashboardGroup, "training")} style={{ border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 999, padding: "7px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11, fontWeight: 900 }}>Training Reminder</button>
                  {hasNutritionAccess && <button title="Send calorie reminder to all members" onClick={() => sendGroupReminder(dashboardGroup, "calorie")} style={{ border: "1px solid rgba(253,230,138,0.45)", background: "rgba(253,230,138,0.16)", color: "#FDE68A", borderRadius: 999, padding: "7px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11, fontWeight: 900 }}>Calorie Reminder</button>}
                  <span style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, fontWeight: 850 }}>{dashboardGroupData.members.length} total</span>
                </div>
              </div>
              {dashboardGroupData.athleteRows.length === 0 ? (
                <div style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.10)", borderRadius: 14, padding: 16, color: "rgba(255,255,255,0.70)", fontWeight: 850 }}>No members yet.</div>
              ) : (
                <div style={{ borderRadius: 14, overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch" }}>
                  <div style={{ minWidth: 860, display: "grid", gridTemplateColumns: "48px minmax(280px,2.2fr) minmax(130px,.95fr) minmax(128px,.9fr) minmax(120px,.9fr) 74px", gap: 10, padding: "9px 12px", background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.72)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    <div>Rank</div><div>Athlete</div><div style={{ textAlign: "right" }}>Sessions</div><div style={{ textAlign: "right" }}>Nutrition</div><div style={{ textAlign: "right" }}>Status</div><div style={{ textAlign: "right" }}>Action</div>
                  </div>
                  {dashboardGroupData.athleteRows.slice().sort((a, b) => (b.sessions - a.sessions) || (b.xp - a.xp) || a.name.localeCompare(b.name)).map((row, index) => {
                    const sessionPct = dashboardGroupData.sessionTarget > 0 ? Math.min(100, Math.round((row.sessions / dashboardGroupData.sessionTarget) * 100)) : 0;
                    const xpPct = dashboardGroupData.athleteTarget ? Math.min(100, Math.round((row.xp / dashboardGroupData.athleteTarget) * 100)) : 0;
                    const rankLabel = challengePlaceLabel(index + 1);
                    const rankMeta = index === 0
                      ? { bg: "#16A34A", text: "#FFFFFF", border: "rgba(255,255,255,0.34)" }
                      : index === 1
                        ? { bg: "#CBD5E1", text: "#020617", border: "rgba(255,255,255,0.34)" }
                        : index === 2
                          ? { bg: "rgba(180,83,9,0.96)", text: "#FFFFFF", border: "rgba(255,255,255,0.34)" }
                          : { bg: "rgba(255,255,255,0.10)", text: "#FFFFFF", border: "rgba(255,255,255,0.14)" };
                    return (
                    <div
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open athlete analytics for ${row.name}`}
                      onClick={() => openAthleteNotes(row)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openAthleteNotes(row);
                        }
                      }}
                      style={{ minWidth: 860, display: "grid", gridTemplateColumns: "48px minmax(280px,2.2fr) minmax(130px,.95fr) minmax(128px,.9fr) minmax(120px,.9fr) 74px", gap: 12, alignItems: "center", padding: "13px 12px", borderTop: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.055)", cursor: "pointer" }}
                    >
                      <div aria-label={`Rank ${rankLabel}`} style={{ width: 42, minHeight: 30, borderRadius: 999, background: rankMeta.bg, color: rankMeta.text, border: `1px solid ${rankMeta.border}`, display: "inline-grid", placeItems: "center", fontWeight: 950, fontSize: 12, letterSpacing: 0, boxShadow: index < 3 ? "0 8px 18px rgba(0,0,0,0.16)" : "none" }}>{rankLabel}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <img src={row.profile_img || noImage} alt={row.name} style={{ width: 52, height: 52, borderRadius: 14, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(255,255,255,0.24)" }} loading="lazy" />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: "#fff", fontWeight: 950, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
                          {row.username && <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 11, fontWeight: 800, overflowWrap: "anywhere" }}>@{row.username}</div>}
                          <div style={{ display: "grid", gap: 5, marginTop: 7, maxWidth: 220 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(72px, 132px) auto", gap: 8, alignItems: "center" }}>
                              <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.16)", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${xpPct}%`, background: "#FFFFFF", borderRadius: 99 }} />
                              </div>
                              <span style={{ color: "#FFFFFF", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}>{row.xp.toLocaleString()} XP</span>
                            </div>
                            {hasNutritionAccess && (
                              <div style={{ display: "grid", gridTemplateColumns: "minmax(72px, 132px) auto", gap: 8, alignItems: "center" }}>
                                <div style={{ height: 5, borderRadius: 99, background: "rgba(253,230,138,0.16)", overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${row.caloriePct}%`, background: row.caloriePct > 0 ? "#FDE68A" : "transparent", borderRadius: 99 }} />
                                </div>
                                <span style={{ color: row.caloriePct > 0 ? "#FDE68A" : "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}>{Number(row.calories || 0).toLocaleString()} / {Number(dashboardGroupData.calorieTarget || 0).toLocaleString()} kcal</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", justifyItems: "end", gap: 7 }}>
                        <span style={{ color: "#fff", fontSize: 14, lineHeight: 1, fontWeight: 950, whiteSpace: "nowrap" }}>{row.sessions.toLocaleString()}/{dashboardGroupData.sessionTarget} sessions</span>
                        <div style={{ width: "100%", maxWidth: 118 }}>
                          <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.18)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${sessionPct}%`, background: "#FFFFFF", borderRadius: 99 }} />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", justifyItems: "end", gap: 6 }}>
                        <div style={{ color: row.caloriePct > 0 ? "#FDE68A" : "rgba(255,255,255,0.62)", fontSize: 13, fontWeight: 950 }}>{row.caloriePct}%</div>
                        <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 850 }}>{Number(row.calories || 0).toLocaleString()} kcal</div>
                      </div>
                      <div style={{ textAlign: "right" }}><span style={pill(row.status.tone)}>{row.status.label}</span></div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <button title="Send training reminder" onClick={(event) => { event.stopPropagation(); sendGroupReminder(dashboardGroup, "training", row.id); }} style={{ border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 8, padding: "6px 8px", cursor: "pointer", fontSize: 11, fontWeight: 900 }}>
                            <FontAwesomeIcon icon={faEnvelope} />
                          </button>
                          {hasNutritionAccess && (
                            <button title="Send calorie reminder" onClick={(event) => { event.stopPropagation(); sendGroupReminder(dashboardGroup, "calorie", row.id); }} style={{ border: "1px solid rgba(253,230,138,0.45)", background: "rgba(253,230,138,0.16)", color: "#FDE68A", borderRadius: 8, padding: "6px 8px", cursor: "pointer", fontSize: 11, fontWeight: 900 }}>
                              <FontAwesomeIcon icon={faEnvelope} />
                            </button>
                          )}
                        </div>
                        <button onClick={(event) => { event.stopPropagation(); removeGroupMember(dashboardGroup, row); }} style={{ border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 8, padding: "6px 8px", cursor: "pointer", fontSize: 11, fontWeight: 900 }}>
                          Remove
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        <section style={{ background: "#07111F", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 20, marginBottom: 18, boxShadow: "0 22px 70px rgba(10,29,68,0.18)", color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
            <div>
              <div style={{ color: "rgba(255,255,255,0.58)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Coach Insights</div>
              <h2 style={{ margin: "5px 0 5px", fontSize: "clamp(26px,3vw,38px)", lineHeight: 1 }}>Club Insights</h2>
              <p style={{ color: "rgba(255,255,255,0.62)", margin: 0, fontWeight: 760 }}>{analyticsWindow.label}</p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <select value={activeSeasonId} onChange={(event) => selectAnalyticsRange(event.target.value)} style={{ border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, padding: "10px 12px", color: "#fff", fontWeight: 900, background: "#111827" }}>
                {ANALYTICS_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                {seasons.length > 0 && (
                  <optgroup label="Seasons">
                    {seasons.map((season) => <option key={season.id} value={season.id}>{season.name}</option>)}
                  </optgroup>
                )}
              </select>
              {activeSeasonId === "custom" && (
                <input type="number" min="1" max="1095" value={customDays} onChange={(event) => setCustomDays(event.target.value)} style={{ width: 118, border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, padding: "10px 12px", color: "#fff", fontWeight: 900, background: "#111827" }} />
              )}
              {activeSeasonId === "date" && (
                <input aria-label="Club insights review date" type="date" value={analyticsSelectedDate} onChange={(event) => setAnalyticsSelectedDate(event.target.value || localDateInput(new Date()))} style={{ border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, padding: "10px 12px", color: "#fff", fontWeight: 900, background: "#111827" }} />
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Total XP", value: countedSessions.reduce((sum, row) => sum + Number(row.total_xp || 0), 0).toLocaleString(), sub: "club-wide", accent: brandSurface.primary, icon: faBolt },
              { label: "Sessions", value: countedSessions.length.toLocaleString(), sub: "all groups", accent: brandSurface.accent, icon: faDumbbell },
              { label: "Active Athletes", value: `${activeAthleteCount}/${athletes.length}`, sub: "engaged this period", accent: "#22C55E", icon: faUsers },
              { label: "Avg Focus", value: teamAvgFocus ? teamAvgFocus.toFixed(1) : "—", sub: "out of 5.0", accent: "#A78BFA", icon: faBullseye },
              { label: "Avg Rating", value: teamAvgRating ? teamAvgRating.toFixed(1) : "—", sub: "workout enjoyment", accent: "#F59E0B", icon: faStar },
              { label: "Rest Minutes", value: teamRestMinutes.toLocaleString(), sub: "timer usage", accent: "#6EE7B7", icon: faStopwatch },
            ].map((item) => (
              <InsightStatCard key={item.label} {...item} />
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14, marginBottom: 14 }}>
            <div style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14, minHeight: 310 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>Training Trend</h3>
                  <div style={{ color: "rgba(255,255,255,0.52)", fontSize: 12, fontWeight: 800 }}>Daily {activeTrainingTrendMetric.label.toLowerCase()} in the selected range</div>
                </div>
                <div role="group" aria-label="Training trend metric" style={{ display: "inline-flex", gap: 6, padding: 4, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 999 }}>
                  {trainingTrendMetrics.map((metric) => {
                    const selected = trainingTrendMetric === metric.key;
                    return (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => setTrainingTrendMetric(metric.key)}
                        style={{
                          border: 0,
                          borderRadius: 999,
                          padding: "7px 10px",
                          background: selected ? metric.color : "transparent",
                          color: selected ? (isDarkColor(metric.color) ? "#fff" : base.textDark) : "rgba(255,255,255,0.72)",
                          fontSize: 11,
                          fontWeight: 950,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {metric.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {trendData.length === 0 ? (
                <div style={{ height: 240, display: "grid", placeItems: "center", color: "rgba(255,255,255,0.55)", fontWeight: 850 }}>No sessions in this range.</div>
              ) : (
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                      <XAxis dataKey="date" stroke="rgba(255,255,255,0.72)" tick={{ fontSize: 11, fill: "#FFFFFF" }} />
                      <YAxis stroke="rgba(255,255,255,0.72)" tick={{ fontSize: 11, fill: "#FFFFFF" }} />
                      <Tooltip content={<TrainingTrendTooltip metricLabel={activeTrainingTrendMetric.label} metricColor={activeTrainingTrendMetric.color} />} />
                      <Line type="monotone" name={activeTrainingTrendMetric.label} dataKey={activeTrainingTrendMetric.key} stroke={activeTrainingTrendMetric.color} strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Top Performers</h3>
              <div style={{ color: "rgba(255,255,255,0.52)", fontSize: 12, fontWeight: 800, marginTop: 3 }}>Ranked by XP and sessions</div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, color: "#E0F2FE", background: "rgba(56,189,248,0.14)", border: "1px solid rgba(56,189,248,0.30)", borderRadius: 12, padding: "9px 10px", fontSize: 11, fontWeight: 850, lineHeight: 1.45, marginTop: 10 }}>
                <FontAwesomeIcon icon={faBullseye} style={{ color: "#38BDF8", marginTop: 2, flexShrink: 0 }} />
                <span>Focus is the athlete's post-session concentration rating out of 5.0. Use it to separate high output from high-quality training intent.</span>
              </div>
              <div style={{ display: "grid", gap: 9, marginTop: 12 }}>
                {topPerformers.length === 0 ? <div style={{ color: "rgba(255,255,255,0.55)", fontWeight: 850, padding: 14 }}>Add athletes to see performers.</div> : topPerformers.map((athlete, index) => {
                  const focusColor = focusColorForScore(athlete.avgFocus);
                  const focusPct = Math.min(100, Math.max(0, (Number(athlete.avgFocus || 0) / 5) * 100));
                  return (
                    <div key={athlete.id} style={{ display: "grid", gridTemplateColumns: "30px 38px minmax(0,1fr) auto", gap: 10, alignItems: "center", border: `1px solid ${index === 0 ? "rgba(56,189,248,0.28)" : "rgba(255,255,255,0.08)"}`, background: index === 0 ? "rgba(56,189,248,0.12)" : "rgba(255,255,255,0.055)", borderRadius: 14, padding: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 10, background: index === 0 ? "#38BDF8" : "rgba(255,255,255,0.10)", color: index === 0 ? "#07111F" : "#fff", display: "grid", placeItems: "center", fontWeight: 950 }}>{index + 1}</div>
                      <img src={athlete.profile_img || noImage} alt={athlete.name} style={{ width: 38, height: 38, borderRadius: 11, objectFit: "cover", border: "1px solid rgba(255,255,255,0.16)" }} loading="lazy" />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{athlete.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.58)", fontSize: 11, fontWeight: 850, marginTop: 3, flexWrap: "wrap" }}>
                          <span>{athlete.sessions} sessions</span>
                          <span style={{ color: focusColor }}>{focusLabelForScore(athlete.avgFocus)}</span>
                          <span style={{ color: "#fff", fontWeight: 950 }}>{athlete.avgFocus ? athlete.avgFocus.toFixed(1) : "—"}/5</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.12)", overflow: "hidden", marginTop: 7 }}>
                          <div style={{ width: `${focusPct}%`, height: "100%", background: focusColor, borderRadius: 999 }} />
                        </div>
                      </div>
                      <div style={{ color: darkPanelHighlightText, fontWeight: 950, textAlign: "right" }}>{athlete.xp.toLocaleString()} XP</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14 }}>
            <div style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)", borderRadius: 18, padding: 14, minHeight: 300 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>Club Groups</h3>
                  <div style={{ color: "rgba(255,255,255,0.52)", fontSize: 12, fontWeight: 800, marginTop: 3 }}>Compare one group metric at a time</div>
                </div>
                <div role="group" aria-label="Club group chart metric" style={{ display: "inline-flex", gap: 6, padding: 4, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.07)", borderRadius: 999 }}>
                  {clubGroupChartMetrics.map((metric) => {
                    const selected = clubGroupChartMetric === metric.key;
                    return (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => setClubGroupChartMetric(metric.key)}
                        style={{
                          border: 0,
                          borderRadius: 999,
                          padding: "7px 10px",
                          background: selected ? metric.color : "transparent",
                          color: selected ? (isDarkColor(metric.color) ? "#fff" : base.textDark) : "rgba(255,255,255,0.72)",
                          fontSize: 11,
                          fontWeight: 950,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {metric.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {groupChartData.length === 0 ? <div style={{ height: 220, display: "grid", placeItems: "center", color: "rgba(255,255,255,0.55)", fontWeight: 850 }}>No club groups yet.</div> : (
                <div style={{ height: 230, marginTop: 12 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={groupChartData}>
                      <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                      <XAxis dataKey="name" stroke="rgba(255,255,255,0.50)" tick={{ fontSize: 11 }} />
                      <YAxis stroke="rgba(255,255,255,0.50)" tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, color: "#fff" }}
                        labelStyle={{ color: "#fff", fontWeight: 950 }}
                        itemStyle={{ color: "#fff", fontWeight: 850 }}
                      />
                      <Bar dataKey={activeClubGroupChartMetric.key} name={activeClubGroupChartMetric.label} fill={activeClubGroupChartMetric.color} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

          </div>
        </section>

        {hasNutritionAccess && <section className="club-nutrition-dashboard" style={{ background: `radial-gradient(circle at top right, ${alpha(brandSurface.accent, 0.26)}, transparent 34%), linear-gradient(145deg, ${alpha(brandSurface.primary, 0.98)} 0%, ${alpha(brandSurface.secondary, 0.96)} 68%, ${alpha(brandSurface.accent, 0.92)} 135%)`, border: `1px solid ${alpha(brandSurface.accent, 0.40)}`, borderRadius: 24, padding: 24, marginBottom: 16, overflow: "hidden", boxShadow: `0 22px 70px ${alpha(brandSurface.primary, 0.18)}`, color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(255,255,255,0.14)", color: "#fff", display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.22)" }}>
                  <FontAwesomeIcon icon={faUtensils} />
                </div>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.64)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 1 }}>Club Nutrition</div>
                  <h2 style={{ margin: 0, color: "#fff", fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 950, letterSpacing: -0.4 }}>Athlete Nutrition</h2>
                </div>
              </div>
              <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 12, fontWeight: 800, paddingLeft: 46 }}>
                {nutritionWindow.label} · {nutritionWindowDays.toLocaleString()} day window
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <select value={nutritionRangeId} onChange={(event) => selectNutritionRange(event.target.value)} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontWeight: 900, fontSize: 12, outline: "none" }}>
                {ANALYTICS_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                {seasons.length > 0 && (
                  <optgroup label="Seasons">
                    {seasons.map((season) => <option key={season.id} value={season.id}>{season.name}</option>)}
                  </optgroup>
                )}
              </select>
              {nutritionRangeId === "custom" && (
                <input type="number" min="1" max="1095" value={nutritionCustomDays} onChange={(event) => setNutritionCustomDays(event.target.value)} style={{ width: 90, border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontWeight: 900, fontSize: 12 }} />
              )}
              {nutritionRangeId === "date" && (
                <input aria-label="Nutrition review date" type="date" value={nutritionSelectedDate} onChange={(event) => setNutritionSelectedDate(event.target.value || localDateInput(new Date()))} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontWeight: 900, fontSize: 12 }} />
              )}
              <select value={nutritionAthleteFilter} onChange={(event) => setNutritionAthleteFilter(event.target.value)} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: "#fff", borderRadius: 12, padding: "9px 11px", fontWeight: 900, fontSize: 12, outline: "none", maxWidth: 210 }}>
                <option value="all">All athletes</option>
                {nutritionAthleteOptions.map((athlete) => <option key={athlete.id} value={athlete.id}>{athlete.name}</option>)}
              </select>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button onClick={() => setNutritionGroupFilter("all")} style={{ border: `1px solid ${nutritionGroupFilter === "all" ? "rgba(255,255,255,0.48)" : "rgba(255,255,255,0.22)"}`, background: nutritionGroupFilter === "all" ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 999, padding: "6px 12px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>All Groups</button>
                {groups.map((group) => (
                  <button key={group.id} onClick={() => setNutritionGroupFilter(group.id)} style={{ border: `1px solid ${nutritionGroupFilter === group.id ? "rgba(255,255,255,0.48)" : "rgba(255,255,255,0.22)"}`, background: nutritionGroupFilter === group.id ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 999, padding: "6px 12px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>{group.name}</button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setNutritionCollapsed((value) => !value)}
                title={nutritionCollapsed ? "Show athlete nutrition" : "Minimise athlete nutrition"}
                style={{ border: "1px solid rgba(255,255,255,0.24)", background: nutritionCollapsed ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 12, width: 38, height: 38, display: "grid", placeItems: "center", cursor: "pointer", fontWeight: 950 }}
              >
                <FontAwesomeIcon icon={nutritionCollapsed ? faChevronDown : faChevronUp} />
              </button>
            </div>
          </div>

          {!nutritionCollapsed && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(210px,100%),1fr))", gap: 12, marginBottom: 18 }}>
              {nutritionSummaryItems.map((item) => (
                <div key={item.label} style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 14, padding: 16, minHeight: 98, position: "relative", overflow: "hidden", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${item.color}, ${brandSurface.accent})` }} />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, color: base.mutedLight, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.65, marginBottom: 10 }}>
                    <span>{item.label}</span>
                    <span style={{ width: 30, height: 30, borderRadius: 10, display: "grid", placeItems: "center", color: item.color, background: alpha(item.color, 0.12), border: `1px solid ${alpha(item.color, 0.22)}` }}>
                      <FontAwesomeIcon icon={item.icon} />
                    </span>
                  </div>
                  <div style={{ color: base.textDark, fontSize: 26, fontWeight: 950, lineHeight: 1 }}>{item.value}</div>
                  <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850, marginTop: 6 }}>{item.unit}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: nutritionPlanEditorOpen ? "repeat(auto-fit,minmax(min(320px,100%),1fr))" : "1fr", gap: 14, marginBottom: 18 }}>
            {nutritionPlanEditorOpen && (
            <div className="club-nutrition-plan-panel" style={{ order: 2, border: "1px solid rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.12)", borderRadius: 18, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Nutrition Plans</div>
                  <h3 style={{ margin: "2px 0 0", color: "#fff", fontSize: 17, fontWeight: 950 }}>{editingNutritionPlanId ? "Edit Group Plan" : "Create Group Plan"}</h3>
                </div>
                <button
                  type="button"
                  aria-expanded={nutritionPlanEditorOpen}
                  onClick={() => resetNutritionPlanEditor()}
                  style={{ flexShrink: 0, border: "1px solid rgba(255,255,255,0.26)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 12, padding: "9px 12px", fontSize: 11, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}
                >
                  <FontAwesomeIcon icon={faChevronUp} />
                  Close
                </button>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                <NutritionFormField label="Plan title">
                  <input
                    aria-label="Nutrition plan title"
                    value={nutritionPlanForm.title}
                    onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="Monday Match Prep Plan"
                    style={nutritionDarkInput}
                  />
                </NutritionFormField>
                <NutritionFormField label="Workout group">
                  <select
                    aria-label="Nutrition plan workout group"
                    value={nutritionPlanForm.groupId}
                    onChange={(event) => {
                      const group = groups.find((row) => row.id === event.target.value);
                      const athleteIds = getCountingWorkoutMemberIds(group);
                      setNutritionPlanAthleteSearch("");
                      setNutritionPlanForm((prev) => ({
                        ...prev,
                        groupId: event.target.value,
                        coachId: getPreferredNutritionCoachId(group, editingNutritionPlanId ? prev.coachId : ""),
                        selectedAthleteIds: athleteIds,
                      }));
                    }}
                    style={nutritionDarkInput}
                  >
                    {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                  </select>
                </NutritionFormField>
                <NutritionFormField label="Plan coach">
                  <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 9, alignItems: "center" }}>
                    <AthleteAvatar src={selectedNutritionCoachOption?.profile?.profile_img} name={displayProfileName(selectedNutritionCoachOption?.profile, "Coach")} size={38} shape="circle" />
                    <select
                      aria-label="Nutrition plan coach"
                      value={nutritionPlanForm.coachId}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, coachId: event.target.value }))}
                      style={nutritionDarkInput}
                    >
                      {nutritionCoachOptions.length === 0 && <option value="">No active club coaches</option>}
                      {nutritionCoachOptions.map((coach) => (
                        <option key={coach.id} value={coach.id}>
                          {displayProfileName(coach.profile, "Coach")}
                        </option>
                      ))}
                    </select>
                  </div>
                </NutritionFormField>
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 7 }}>
                    <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Plan days</div>
                    <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, fontWeight: 900 }}>{formatNutritionPlanDays(nutritionPlanForm.scheduledDays, "No set days")}</div>
                  </div>
                  <div role="group" aria-label="Nutrition plan assigned days" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(38px,1fr))", gap: 6 }}>
                    {ASSIGNED_WORKOUT_DAYS.map((day) => {
                      const selected = normalizeNutritionPlanDays(nutritionPlanForm.scheduledDays).includes(day.index);
                      return (
                        <button
                          key={day.index}
                          type="button"
                          aria-pressed={selected}
                          aria-label={`${selected ? "Remove" : "Assign"} nutrition plan on ${day.label}`}
                          onClick={() => setNutritionPlanForm((prev) => {
                            const days = normalizeNutritionPlanDays(prev.scheduledDays);
                            const nextDays = days.includes(day.index)
                              ? days.filter((value) => value !== day.index)
                              : [...days, day.index];
                            return { ...prev, scheduledDays: normalizeNutritionPlanDays(nextDays) };
                          })}
                          style={{ minHeight: 38, border: `1px solid ${selected ? "#fff" : "rgba(255,255,255,0.22)"}`, background: selected ? "#fff" : "rgba(255,255,255,0.10)", color: selected ? "#080B12" : "#fff", borderRadius: 10, fontSize: 10, fontWeight: 950, cursor: "pointer" }}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.60)", fontSize: 10, fontWeight: 800, marginTop: 6 }}>Leave empty when a plan can be used any day.</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 8 }}>
                  <NutritionFormField label="Meal section">
                    <select
                      aria-label="Meal section"
                      value={nutritionPlanForm.mealSection}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, mealSection: event.target.value }))}
                      style={nutritionDarkInput}
                    >
                      {["Breakfast", "Lunch", "Dinner", "Snacks"].map((section) => <option key={section} value={section}>{section}</option>)}
                    </select>
                  </NutritionFormField>
                  <NutritionFormField label="Food name">
                    <input
                      aria-label="Food name"
                      value={nutritionPlanForm.foodName}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, foodName: event.target.value }))}
                      placeholder="Greek yoghurt, oats and berries"
                      style={nutritionDarkInput}
                    />
                  </NutritionFormField>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(80px,0.8fr) minmax(70px,0.6fr)", gap: 8 }}>
                  <NutritionFormField label="Brand or source">
                    <input
                      aria-label="Food brand or source"
                      value={nutritionPlanForm.brand}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, brand: event.target.value }))}
                      placeholder="Snows ProAm Coach Sample"
                      style={nutritionDarkInput}
                    />
                  </NutritionFormField>
                  <NutritionFormField label="Portion">
                    <input
                      aria-label="Plan item portion"
                      type="number"
                      min="0"
                      value={nutritionPlanForm.portion}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, portion: event.target.value }))}
                      placeholder="1"
                      style={nutritionDarkInput}
                    />
                  </NutritionFormField>
                  <NutritionFormField label="Unit">
                    <input
                      aria-label="Plan item unit"
                      value={nutritionPlanForm.unit}
                      onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, unit: event.target.value }))}
                      placeholder="bowl"
                      style={nutritionDarkInput}
                    />
                  </NutritionFormField>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(92px,1fr))", gap: 8 }}>
                  {[
                    ["calories", "Calories (kcal)", "Calories kilocalories"],
                    ["protein", "Protein (g)", "Protein grams"],
                    ["carbs", "Carbs (g)", "Carbs grams"],
                    ["fat", "Fat (g)", "Fat grams"],
                  ].map(([key, label]) => (
                    <NutritionFormField key={key} label={label}>
                      <input
                        aria-label={`Plan ${label}`}
                        type="number"
                        min="0"
                        value={nutritionPlanForm[key]}
                        onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, [key]: event.target.value }))}
                        placeholder={label}
                        style={nutritionDarkInput}
                      />
                    </NutritionFormField>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(96px,1fr))", gap: 8 }}>
                  {[
                    ["fiber", "Fiber (g)", "Fiber grams"],
                    ["sugar", "Sugar (g)", "Sugar grams"],
                    ["sodium", "Sodium (mg)", "Sodium milligrams"],
                  ].map(([key, label]) => (
                    <NutritionFormField key={key} label={label}>
                      <input
                        aria-label={`Plan ${label}`}
                        type="number"
                        min="0"
                        value={nutritionPlanForm[key]}
                        onChange={(event) => setNutritionPlanForm((prev) => ({ ...prev, [key]: event.target.value }))}
                        placeholder={label}
                        style={nutritionDarkInput}
                      />
                    </NutritionFormField>
                  ))}
                </div>
                <button type="button" onClick={addNutritionPlanItem} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: "#fff", borderRadius: 12, padding: "11px 12px", fontWeight: 950, cursor: "pointer" }}>
                  Add Meal Item
                </button>
              </div>
              {nutritionPlanForm.items.length > 0 && (
                <div style={{ display: "grid", gap: 7, marginTop: 12 }}>
                  {nutritionPlanForm.items.map((item, index) => {
                    const isEditing = editingNutritionPlanItemIndex === index;
                    return (
                      <div key={`${item.food_name}-${index}`} style={{ display: "grid", gap: 8, background: "rgba(255,255,255,0.08)", border: `1px solid ${isEditing ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.16)"}`, borderRadius: 15, padding: 8 }}>
                        <NutritionPlanMealRow
                          item={item}
                          includeExtras
                          actions={(
                            <>
                              <button type="button" aria-label={`${isEditing ? "Finish editing" : "Edit"} ${item.food_name}`} onClick={() => setEditingNutritionPlanItemIndex(isEditing ? null : index)} style={{ border: `1px solid ${isEditing ? "#BBF7D0" : base.borderLight}`, background: isEditing ? "#F0FDF4" : "#fff", color: isEditing ? "#166534" : base.textDark, borderRadius: 10, width: 32, height: 32, cursor: "pointer" }}>
                                <FontAwesomeIcon icon={isEditing ? faCircleCheck : faPen} />
                              </button>
                              {!isEditing && (
                                <button type="button" aria-label={`Remove ${item.food_name}`} onClick={() => removeNutritionPlanItem(index)} style={{ border: `1px solid ${base.borderLight}`, background: "#fff", color: base.mutedLight, borderRadius: 10, width: 32, height: 32, cursor: "pointer" }}>
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </>
                          )}
                        />
                        {isEditing && (
                          <div style={{ display: "grid", gap: 8, borderTop: "1px solid rgba(255,255,255,0.14)", paddingTop: 8 }}>
                            <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 850 }}>Edits affect this assigned plan item only.</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8 }}>
                              <NutritionFormField label="Food name">
                                <input aria-label="Edit food name" value={item.food_name || ""} onChange={(event) => updateNutritionPlanItem(index, { food_name: event.target.value })} placeholder="Greek yoghurt, oats and berries" style={nutritionDarkInput} />
                              </NutritionFormField>
                              <NutritionFormField label="Brand or source">
                                <input aria-label="Edit brand or source" value={item.brand || ""} onChange={(event) => updateNutritionPlanItem(index, { brand: event.target.value })} placeholder="Snows ProAm Coach Sample" style={nutritionDarkInput} />
                              </NutritionFormField>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(92px,1fr))", gap: 8 }}>
                              <NutritionFormField label="Meal section">
                                <select aria-label="Edit meal section" value={item.meal_section || "Breakfast"} onChange={(event) => updateNutritionPlanItem(index, { meal_section: event.target.value })} style={nutritionDarkInput}>
                                  {["Breakfast", "Lunch", "Dinner", "Snacks"].map((section) => <option key={section} value={section}>{section}</option>)}
                                </select>
                              </NutritionFormField>
                              <NutritionFormField label="Portion">
                                <input aria-label="Edit portion" type="number" min="0" value={item.portion ?? ""} onChange={(event) => updateNutritionPlanItem(index, { portion: event.target.value })} placeholder="1" style={nutritionDarkInput} />
                              </NutritionFormField>
                              <NutritionFormField label="Unit">
                                <input aria-label="Edit unit" value={item.unit || ""} onChange={(event) => updateNutritionPlanItem(index, { unit: event.target.value })} placeholder="bowl" style={nutritionDarkInput} />
                              </NutritionFormField>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(92px,1fr))", gap: 8 }}>
                              {[
                                ["calories", "Calories", "kcal"],
                                ["protein_g", "Protein", "grams"],
                                ["carbs_g", "Carbs", "grams"],
                                ["fat_g", "Fat", "grams"],
                              ].map(([key, label]) => (
                                <NutritionFormField key={key} label={`${label} (${key === "calories" ? "kcal" : "g"})`}>
                                  <input aria-label={`Edit ${label} ${key === "calories" ? "kilocalories" : "grams"}`} type="number" min="0" value={item[key] ?? ""} onChange={(event) => updateNutritionPlanItem(index, { [key]: event.target.value })} placeholder={label} style={nutritionDarkInput} />
                                </NutritionFormField>
                              ))}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(96px,1fr))", gap: 8 }}>
                              {[
                                ["fiber_g", "Fiber (g)", "Fiber grams"],
                                ["sugar_g", "Sugar (g)", "Sugar grams"],
                                ["sodium_mg", "Sodium (mg)", "Sodium milligrams"],
                              ].map(([key, label]) => (
                                <NutritionFormField key={key} label={label}>
                                  <input aria-label={`Edit ${label}`} type="number" min="0" value={item[key] ?? ""} onChange={(event) => updateNutritionPlanItem(index, { [key]: event.target.value })} placeholder={label} style={nutritionDarkInput} />
                                </NutritionFormField>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                              <button type="button" onClick={() => removeNutritionPlanItem(index)} style={{ border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "rgba(255,255,255,0.72)", borderRadius: 10, padding: "8px 10px", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>Remove</button>
                              <button type="button" onClick={() => setEditingNutritionPlanItemIndex(null)} style={{ border: "1px solid rgba(255,255,255,0.26)", background: "rgba(255,255,255,0.16)", color: "#fff", borderRadius: 10, padding: "8px 12px", fontSize: 11, fontWeight: 950, cursor: "pointer" }}>Done</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 7 }}>
                  <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Assign To</div>
                  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 11, fontWeight: 900 }}>{selectedNutritionPlanAthleteCount}/{nutritionPlanAthletes.length} selected</div>
                </div>
                <input
                  aria-label="Search athletes for this nutrition plan"
                  value={nutritionPlanAthleteSearch}
                  onChange={(event) => setNutritionPlanAthleteSearch(event.target.value)}
                  placeholder="Search athletes"
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontWeight: 850, marginBottom: 8 }}
                />
                {nutritionPlanAthletes.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <button type="button" onClick={selectFilteredNutritionPlanAthletes} disabled={!filteredNutritionPlanAthletes.length} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.14)", color: filteredNutritionPlanAthletes.length ? "#fff" : "rgba(255,255,255,0.45)", borderRadius: 12, padding: "9px 10px", fontSize: 11, fontWeight: 950, cursor: filteredNutritionPlanAthletes.length ? "pointer" : "default" }}>
                      Select All
                    </button>
                    <button type="button" onClick={clearFilteredNutritionPlanAthletes} disabled={!filteredNutritionPlanAthletes.length} style={{ border: "1px solid rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.08)", color: filteredNutritionPlanAthletes.length ? "#fff" : "rgba(255,255,255,0.45)", borderRadius: 12, padding: "9px 10px", fontSize: 11, fontWeight: 950, cursor: filteredNutritionPlanAthletes.length ? "pointer" : "default" }}>
                      Deselect All
                    </button>
                  </div>
                )}
                <div style={{ display: "grid", gap: 7, maxHeight: "min(340px, 38vh)", overflow: "auto", paddingRight: 2 }}>
                  {filteredNutritionPlanAthletes.length ? filteredNutritionPlanAthletes.map((athlete) => {
                    const checked = nutritionPlanForm.selectedAthleteIds.includes(athlete.id);
                    return (
                      <label key={athlete.id} style={{ display: "flex", alignItems: "center", gap: 8, background: checked ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 12, padding: "8px 10px", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => setNutritionPlanForm((prev) => ({
                            ...prev,
                            selectedAthleteIds: event.target.checked
                              ? Array.from(new Set([...prev.selectedAthleteIds, athlete.id]))
                              : prev.selectedAthleteIds.filter((id) => id !== athlete.id),
                          }))}
                          style={{ width: 16, height: 16, accentColor: brandSurface.accent }}
                        />
                        <AthleteAvatar src={athlete.profile_img} name={athlete.name} size={30} />
                        <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{athlete.name}</span>
                      </label>
                    );
                  }) : (
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 12, fontWeight: 850 }}>{nutritionPlanAthletes.length ? "No athletes match that search." : "Add athletes to this group before assigning plans."}</div>
                  )}
                </div>
              </div>
              <button type="button" onClick={saveNutritionPlan} disabled={nutritionPlanSaving} style={{ width: "100%", marginTop: 12, border: 0, background: nutritionPlanSaving ? "rgba(255,255,255,0.20)" : "#fff", color: nutritionPlanSaving ? "rgba(255,255,255,0.70)" : "#080B12", borderRadius: 13, padding: "12px 14px", fontWeight: 950, cursor: nutritionPlanSaving ? "wait" : "pointer" }}>
                {nutritionPlanSaving ? "Saving..." : editingNutritionPlanId ? "Update Plan" : "Create and Assign Plan"}
              </button>
            </div>
            )}

            <div style={{ order: 1, gridColumn: nutritionPlanEditorOpen ? "auto" : "1 / -1", border: "1px solid rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.12)", borderRadius: 18, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Assigned Plans</div>
                  <h3 style={{ margin: "2px 0 0", color: "#fff", fontSize: 17, fontWeight: 950 }}>Plan Adoption</h3>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <div style={{ color: "rgba(255,255,255,0.70)", fontSize: 12, fontWeight: 900 }}>{nutritionPlanAdoption.length} active</div>
                  {!nutritionPlanEditorOpen && (
                    <button
                      type="button"
                      onClick={() => setNutritionPlanEditorOpen(true)}
                      style={{ border: "1px solid rgba(255,255,255,0.26)", background: "#fff", color: "#080B12", borderRadius: 12, padding: "9px 12px", fontSize: 11, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      New Plan
                    </button>
                  )}
                </div>
              </div>
              {nutritionPlanAdoption.length === 0 ? (
                <div style={{ border: "1px dashed rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.10)", borderRadius: 14, padding: 16, color: "rgba(255,255,255,0.72)", fontWeight: 850 }}>
                  No group nutrition plans yet.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px" }}>
                    <div style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>
                      Showing {nutritionPlanPageStart}-{nutritionPlanPageEnd} of {nutritionPlanAdoption.length}
                    </div>
                    <div style={{ display: "flex", gap: 7 }}>
                      <button
                        type="button"
                        disabled={boundedNutritionPlanPage === 0}
                        onClick={() => setNutritionPlanPage((page) => Math.max(0, page - 1))}
                        style={{ minHeight: 34, border: "1px solid rgba(255,255,255,0.22)", background: boundedNutritionPlanPage === 0 ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)", color: "#fff", opacity: boundedNutritionPlanPage === 0 ? 0.45 : 1, borderRadius: 10, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: boundedNutritionPlanPage === 0 ? "default" : "pointer" }}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={boundedNutritionPlanPage >= nutritionPlanPageCount - 1}
                        onClick={() => setNutritionPlanPage((page) => Math.min(nutritionPlanPageCount - 1, page + 1))}
                        style={{ minHeight: 34, border: "1px solid rgba(255,255,255,0.22)", background: boundedNutritionPlanPage >= nutritionPlanPageCount - 1 ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)", color: "#fff", opacity: boundedNutritionPlanPage >= nutritionPlanPageCount - 1 ? 0.45 : 1, borderRadius: 10, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: boundedNutritionPlanPage >= nutritionPlanPageCount - 1 ? "default" : "pointer" }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  <div className="club-nutrition-plan-grid">
                  {pagedNutritionPlanAdoption.map((adoption) => {
                    const { plan, group, items, athletes: planAthletes, assignedCount, totalAthletes, loggedCount, completedCount, expectedPlanDayCount, expectedDueDays, calories } = adoption;
                    const completeCoverage = totalAthletes > 0 && assignedCount === totalAthletes;
                    const creatorProfile = plan.owner_profile || (plan.owner_id === profileId ? currentProfile : null);
                    const creatorName = displayProfileName(creatorProfile, "Coach");
                    const athleteRowsExpanded = !!nutritionPlanAthletesExpandedById[plan.id];
                    const hiddenAthleteCount = Math.max(0, planAthletes.length - 5);
                    const visiblePlanAthletes = athleteRowsExpanded ? planAthletes : planAthletes.slice(0, 5);
                    const scheduledDayLabel = formatNutritionPlanDays(plan.scheduled_days);
                    const assignedGroupName = group?.name || plan.workout_groups?.name || "";
                    return (
                      <div key={plan.id} style={{ gridColumn: pagedNutritionPlanAdoption.length === 1 ? "1 / -1" : "auto", background: "#fff", border: `1px solid ${editingNutritionPlanId === plan.id ? brandSurface.accent : base.borderLight}`, borderRadius: 15, padding: 12, boxShadow: editingNutritionPlanId === plan.id ? `0 0 0 2px ${alpha(brandSurface.accent, 0.16)}` : "none" }}>
                        <div style={{ display: "grid", gap: 10 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "start" }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Nutrition plan</div>
                              <div style={{ color: base.textDark, fontWeight: 950, fontSize: 15, lineHeight: 1.25, overflowWrap: "anywhere", marginTop: 3 }}>{plan.title}</div>
                              {assignedGroupName && (
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%", marginTop: 7, border: `1px solid ${base.borderLight}`, background: base.bgAlt, color: base.mutedLight, borderRadius: 999, padding: "5px 8px", fontSize: 10, lineHeight: 1.15, fontWeight: 900 }}>
                                  <FontAwesomeIcon icon={faUsers} style={{ color: brandSurface.primary, fontSize: 10, flexShrink: 0 }} />
                                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{assignedGroupName}</span>
                                </div>
                              )}
                            </div>
                            <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
                            <button type="button" aria-label={`Edit ${plan.title || "nutrition plan"}`} onClick={() => startEditNutritionPlan(plan)} style={{ border: `1px solid ${base.borderLight}`, background: editingNutritionPlanId === plan.id ? "#F8FAFC" : "#fff", color: base.textDark, borderRadius: 9, width: 30, height: 30, cursor: "pointer" }}>
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button type="button" aria-label={`Delete ${plan.title || "nutrition plan"}`} onClick={() => deleteNutritionPlan(plan)} style={{ border: "1px solid #FECACA", background: "#FEF2F2", color: "#B91C1C", borderRadius: 9, width: 30, height: 30, cursor: "pointer" }}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(118px,1fr))", gap: 8 }}>
                            <div className="club-nutrition-plan-metric club-nutrition-plan-metric--neutral" style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, border: `1px solid ${base.borderLight}`, background: base.bgAlt, borderRadius: 12, padding: "9px 10px" }}>
                              <AthleteAvatar src={creatorProfile?.profile_img} name={creatorName} size={38} shape="circle" />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: base.mutedLight, fontSize: 8, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Coach</div>
                                <div style={{ color: base.textDark, fontSize: 12, fontWeight: 950, lineHeight: 1.2, overflowWrap: "anywhere" }}>{creatorName}</div>
                              </div>
                            </div>
                            <div className="club-nutrition-plan-metric club-nutrition-plan-metric--neutral" style={{ display: "grid", gap: 3, border: `1px solid ${base.borderLight}`, background: "#fff", borderRadius: 12, padding: "9px 10px", minWidth: 0 }}>
                              <span style={{ color: base.mutedLight, fontSize: 8, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Items</span>
                              <span style={{ color: base.textDark, fontSize: 18, lineHeight: 1, fontWeight: 950 }}>{items.length}</span>
                            </div>
                            <div className="club-nutrition-plan-metric club-nutrition-plan-metric--energy" style={{ display: "grid", gap: 3, border: `1px solid ${base.borderLight}`, background: "#fff", borderRadius: 12, padding: "9px 10px", minWidth: 0 }}>
                              <span style={{ color: base.mutedLight, fontSize: 8, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Energy</span>
                              <span style={{ color: "#B45309", fontSize: 17, lineHeight: 1, fontWeight: 950, whiteSpace: "nowrap" }}>{calories.toLocaleString()} <span style={{ fontSize: 10, fontWeight: 900 }}>kcal</span></span>
                            </div>
                            <div className="club-nutrition-plan-metric club-nutrition-plan-metric--days" style={{ display: "grid", gap: 3, border: `1px solid ${base.borderLight}`, background: "#EFF6FF", borderRadius: 12, padding: "9px 10px", minWidth: 0 }}>
                              <span style={{ color: base.blue, fontSize: 8, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Days</span>
                              <span style={{ color: base.textDark, fontSize: 12, lineHeight: 1.2, fontWeight: 950, overflowWrap: "anywhere" }}>{scheduledDayLabel}</span>
                            </div>
                            <div className="club-nutrition-plan-metric club-nutrition-plan-metric--assigned" style={{ display: "grid", gap: 3, border: `1px solid ${completeCoverage ? "#BBF7D0" : "#FCD34D"}`, background: completeCoverage ? "#F0FDF4" : "#FFFBEB", borderRadius: 12, padding: "9px 10px", minWidth: 0 }}>
                              <span style={{ color: completeCoverage ? "#166534" : "#92400E", fontSize: 8, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>Assigned</span>
                              <span style={{ color: base.textDark, fontSize: 17, lineHeight: 1, fontWeight: 950 }}>{assignedCount}/{totalAthletes || assignedCount}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "grid", gap: 5, marginTop: 10, paddingTop: 9, borderTop: `1px solid ${base.borderLight}` }}>
                          {items.length ? items.map((item, itemIndex) => (
                            <NutritionPlanMealRow key={`${item.food_name}-${itemIndex}`} item={item} />
                          )) : (
                            <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850 }}>No meal items on this plan.</div>
                          )}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 7, marginTop: 10 }}>
                          {[
                            ["Due", `${expectedDueDays || 1}x`, "per athlete"],
                            ["Started", `${loggedCount}/${expectedPlanDayCount || assignedCount}`, "plan days"],
                            ["Complete", `${completedCount}/${expectedPlanDayCount || assignedCount}`, "plan days"],
                          ].map(([labelText, valueText, subText]) => (
                            <div key={labelText} style={{ border: `1px solid ${base.borderLight}`, borderRadius: 11, padding: "8px 9px", background: "#E2E8F0" }}>
                              <div style={{ color: "#0A1D44", fontWeight: 950, fontSize: 16, lineHeight: 1 }}>{valueText}</div>
                              <div style={{ color: "#334155", fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.35, marginTop: 5 }}>{labelText}</div>
                              <div style={{ color: "#475569", fontSize: 10, fontWeight: 800, marginTop: 2 }}>{subText}</div>
                            </div>
                          ))}
                        </div>
                        <div id={`nutrition-plan-athletes-${plan.id}`} style={{ display: "grid", gap: 6, marginTop: 9 }}>
                          {visiblePlanAthletes.map((athlete) => {
                            const statusStyle = athlete.statusTone === "complete"
                              ? { color: "#166534", background: "#DCFCE7" }
                              : athlete.statusTone === "logged"
                              ? { color: "#92400E", background: "#FEF3C7" }
                              : athlete.statusTone === "missing"
                              ? { color: "#450A0A", background: "#FECACA" }
                              : { color: base.mutedLight, background: "#F8FAFC" };
                            return (
                              <div key={athlete.id} style={{ display: "grid", gap: 6, color: base.mutedLight, fontSize: 11, fontWeight: 850, border: `1px solid ${athlete.statusTone === "complete" ? "#BBF7D0" : base.borderLight}`, background: athlete.statusTone === "complete" ? "#F0FDF4" : "#fff", borderRadius: 12, padding: "8px 9px" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                  <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
                                    <AthleteAvatar src={athlete.profile_img} name={athlete.name} size={30} />
                                    <span style={{ minWidth: 0 }}>
                                      <span style={{ display: "block", color: base.textDark, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{athlete.name}</span>
                                      <span style={{ display: "block", color: base.mutedLight, fontSize: 10, fontWeight: 850, marginTop: 1 }}>
                                        {athlete.hasPlan ? `${athlete.completedPlanDays}/${athlete.expectedPlanDays} due days complete` : "No plan assigned"}
                                      </span>
                                    </span>
                                  </span>
                                  <span style={{ ...statusStyle, borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 950, whiteSpace: "nowrap" }}>{athlete.statusTone === "complete" ? "On target" : athlete.statusLabel}</span>
                                </div>
                                {athlete.hasPlan && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, paddingLeft: 37 }}>
                                    {(athlete.dueDayRows || []).map((day) => (
                                      <span
                                        key={day.key}
                                        title={`${day.label}: ${day.status === "complete" ? "logged" : day.status === "partial" ? "partially logged" : "not logged"}`}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 4,
                                          borderRadius: 999,
                                          padding: "3px 7px",
                                          fontSize: 9,
                                          fontWeight: 950,
                                          color: day.status === "complete" ? "#166534" : day.status === "partial" ? "#92400E" : "#450A0A",
                                          background: day.status === "complete" ? "#DCFCE7" : day.status === "partial" ? "#FEF3C7" : "#FECACA",
                                          border: `1px solid ${day.status === "complete" ? "#86EFAC" : day.status === "partial" ? "#FCD34D" : "#FCA5A5"}`,
                                        }}
                                      >
                                        {day.status === "complete" ? "Logged" : day.status === "partial" ? "Partial" : "Missed"} {day.label}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {hiddenAthleteCount > 0 && (
                            <button
                              type="button"
                              aria-expanded={athleteRowsExpanded}
                              aria-controls={`nutrition-plan-athletes-${plan.id}`}
                              onClick={() => setNutritionPlanAthletesExpandedById((prev) => ({ ...prev, [plan.id]: !prev[plan.id] }))}
                              style={{ width: "100%", border: `1px solid ${base.borderLight}`, background: athleteRowsExpanded ? "#F8FAFC" : "#fff", color: base.textDark, borderRadius: 10, padding: "8px 9px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 11, fontWeight: 950, cursor: "pointer" }}
                            >
                              <span>{athleteRowsExpanded ? "Show fewer athletes" : `Show ${hiddenAthleteCount} more athletes`}</span>
                              <FontAwesomeIcon icon={athleteRowsExpanded ? faChevronUp : faChevronDown} />
                            </button>
                          )}
                          {!planAthletes.length && <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850 }}>No athletes found for this plan group.</div>}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {!nutritionCollapsed && (
            <>
              {displayedNutritionOverview.length === 0 ? (
                <div style={{ border: `1px solid ${base.borderLight}`, background: base.bgAlt, borderRadius: 16, padding: 24, textAlign: "center", color: base.mutedLight, fontWeight: 800 }}>
                  No nutrition logs in this range.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>Player cards</div>
                      <div style={{ color: "rgba(255,255,255,0.78)", fontSize: 12, fontWeight: 850, marginTop: 2 }}>
                        Showing {visibleNutritionOverview.length.toLocaleString()} of {displayedNutritionOverview.length.toLocaleString()} athlete{displayedNutritionOverview.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    {nutritionAthleteFilter === "all" && displayedNutritionOverview.length > NUTRITION_ATHLETE_CARD_PREVIEW_LIMIT && (
                      <button
                        type="button"
                        aria-expanded={nutritionAthleteCardsExpanded}
                        onClick={() => setNutritionAthleteCardsExpanded((expanded) => !expanded)}
                        style={{ minHeight: 38, border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.12)", color: "#fff", borderRadius: 12, padding: "8px 12px", fontSize: 12, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                      >
                        {nutritionAthleteCardsExpanded ? "Show fewer" : `Show all ${displayedNutritionOverview.length.toLocaleString()}`}
                        <FontAwesomeIcon icon={nutritionAthleteCardsExpanded ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                  {visibleNutritionOverview.map((row) => {
                    const calorieStatus =
                      row.pct >= 100 ? { color: "#D97706", bg: "#FEF3C7", border: "#FCD34D", label: "Ahead" }
                      : row.pct >= 90 ? { color: "#16A34A", bg: "#DCFCE7", border: "#86EFAC", label: "On Track" }
                      : row.pct >= 70 ? { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", label: "Building" }
                      : { color: "#DC2626", bg: "#FEE2E2", border: "#FCA5A5", label: "Low" };
                    const maxGramMacro = Math.max(row.protein, row.carbs, row.fat, row.fiber, row.sugar, 1);
                    const maxSodium = Math.max(row.sodium, 1);
                    return (
                  <div key={row.id} style={{ background: "#fff", border: `1.5px solid ${base.borderLight}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 2px 14px rgba(10,29,68,0.06)" }}>
                    <div style={{ background: calorieStatus.bg, borderBottom: `1px solid ${calorieStatus.border}`, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                      <img src={row.profile_img || noImage} alt={row.name} style={{ width: 42, height: 42, borderRadius: 12, objectFit: "cover", border: `1px solid ${calorieStatus.border}`, flexShrink: 0 }} loading="lazy" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#0A1D44", fontWeight: 950, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
                        <div style={{ color: "#334155", fontSize: 11, fontWeight: 800, marginTop: 1 }}>{row.groups.join(", ")} · {row.daysLogged ? `${row.daysLogged} days logged` : row.lastLog ? `Last: ${row.lastLog}` : "Nothing logged"}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: calorieStatus.bg, border: `1px solid ${calorieStatus.border}`, color: calorieStatus.color, borderRadius: 999, padding: "3px 8px", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>
                          <FontAwesomeIcon icon={row.pct >= 90 ? faCircleCheck : faTriangleExclamation} />
                          {calorieStatus.label}
                        </span>
                        <div style={{ color: calorieStatus.color, fontSize: 18, fontWeight: 950, marginTop: 3 }}>{row.pct}%</div>
                      </div>
                    </div>
                    <div style={{ padding: "14px 16px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                        <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Calorie Progress</span>
                        <span style={{ color: base.textDark, fontSize: 11, fontWeight: 950 }}>{row.calories.toLocaleString()} / {row.targetRange.toLocaleString()} kcal</span>
                      </div>
                      <div style={{ height: 10, background: "#F1F5F9", borderRadius: 99, overflow: "hidden", marginBottom: 4 }}>
                        <div style={{ width: `${Math.min(100, row.pct)}%`, height: "100%", background: `linear-gradient(90deg, ${calorieStatus.color}, ${alpha(calorieStatus.color, 0.6)})`, borderRadius: 99 }} />
                      </div>
                      <div style={{ color: base.mutedLight, fontSize: 10, fontWeight: 800 }}>{row.target.toLocaleString()} kcal/day target</div>
                    </div>
                    <div style={{ padding: "12px 16px", display: "grid", gap: 7 }}>
                      <MacroBar label="Protein" value={row.protein} max={maxGramMacro} color="#2563EB" />
                      <MacroBar label="Carbs" value={row.carbs} max={maxGramMacro} color="#D97706" />
                      <MacroBar label="Fat" value={row.fat} max={maxGramMacro} color="#7C3AED" />
                      <MacroBar label="Fiber" value={row.fiber} max={maxGramMacro} color="#0D9488" />
                      <MacroBar label="Sugar" value={row.sugar} max={maxGramMacro} color="#EC4899" />
                      <MacroBar label="Sodium" value={row.sodium} max={maxSodium} color="#64748B" unit="mg" />
                    </div>
                    {row.meals.length > 0 && (
                      <div style={{ margin: "0 16px 14px", background: base.bgAlt, borderRadius: 12, padding: "10px 12px" }}>
                        <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 7 }}>Meal Breakdown</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {row.meals.slice(0, 4).map((meal) => (
                            <div key={meal.section} style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 8, padding: "4px 8px" }}>
                              <span style={{ color: base.textDark, fontSize: 11, fontWeight: 950 }}>{meal.section}</span>
                              <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 800 }}>{Math.round(meal.calories).toLocaleString()} kcal</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                    );
                  })}
                  </div>
                  {hiddenNutritionAthleteCount > 0 && (
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 11, fontWeight: 850, textAlign: "center" }}>
                      {hiddenNutritionAthleteCount.toLocaleString()} more athlete{hiddenNutritionAthleteCount === 1 ? "" : "s"} hidden to keep the section scannable.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>}

        {seasonAnalytics.length > 0 && (() => {
          const selectControlStyle = {
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            border: "1px solid rgba(15,23,42,0.10)",
            background: "#fff",
            color: base.textDark,
            borderRadius: 14,
            padding: "13px 14px",
            fontWeight: 900,
            outline: "none",
            minHeight: 48,
          };
          const selectedMetric = seasonComparisonLab.activeMetric;
          const metricTitle = comparisonOptions.find((option) => option.value === compareMetric)?.label || "XP";
          // The club accent can be very dark (for example, #111827). On the
          // dark comparison hero it needs a high-contrast fallback for Season B.
          const comparisonAccent = isDarkColor(brandSurface.accent) ? "#FBBF24" : brandSurface.accent;
          const seasonLabel = (panel, fallback) => panel.season?.name || fallback;
          const compareValue = (value, suffix = "") => `${compactGroupNumber(value)}${suffix}`;
          const maxAthleteDelta = Math.max(...seasonComparisonLab.athleteDelta.map((athlete) => Math.abs(Number(athlete.delta || 0))), 1);
          const maxDrilldownSessions = Math.max(
            ...seasonComparisonLab.drilldown.workoutsBySessions.map((row) => Number(row.sessions || 0)),
            ...seasonComparisonLab.drilldown.days.map((row) => Number(row.sessions || 0)),
            1
          );
          const maxDrilldownXp = Math.max(...seasonComparisonLab.drilldown.workoutsByXp.map((row) => Number(row.xp || 0)), 1);
          const maxWorkoutComparisonXp = Math.max(...seasonComparisonLab.workoutComparison.map((row) => row.peakXp), 1);
          const maxTeamComparisonValue = Math.max(...seasonComparisonLab.teamComparison.map((row) => row.peak), 1);
          const comparisonFilters = [
            ["Metric", compareMetric, setCompareMetric, comparisonOptions],
            ["Season A", compareSeasonAId, setCompareSeasonAId, seasons.map((season) => ({ value: season.id, label: season.name }))],
            ["Team A", compareGroupAId, setCompareGroupAId, [{ value: "all", label: "All teams" }, ...groups.map((group) => ({ value: group.id, label: group.name }))]],
            ["Athlete A", compareAthleteAId, setCompareAthleteAId, [{ value: "all", label: "All athletes" }, ...comparisonAthleteOptions.map((athlete) => ({ value: athlete.id, label: athlete.name }))]],
            ["Season B", compareSeasonBId, setCompareSeasonBId, seasons.map((season) => ({ value: season.id, label: season.name }))],
            ["Team B", compareGroupBId, setCompareGroupBId, [{ value: "all", label: "All teams" }, ...groups.map((group) => ({ value: group.id, label: group.name }))]],
            ["Athlete B", compareAthleteBId, setCompareAthleteBId, [{ value: "all", label: "All athletes" }, ...comparisonAthleteOptions.map((athlete) => ({ value: athlete.id, label: athlete.name }))]],
          ];
          return (
            <section style={{ background: "#F3F6FA", border: `1px solid ${base.borderLight}`, borderRadius: 32, marginBottom: 24, boxShadow: "0 30px 90px rgba(15,23,42,0.13)", overflow: "hidden" }}>
              <div style={{ background: `radial-gradient(circle at 82% 0%, ${alpha(brandSurface.accent, 0.36)}, transparent 34%), linear-gradient(135deg, ${brandSurface.primary} 0%, ${brandSurface.secondary} 74%, #050B14 140%)`, padding: "34px 34px 38px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 30, flexWrap: "wrap", marginBottom: 30 }}>
                  <div style={{ display: "flex", gap: 18, minWidth: 300, maxWidth: 620 }}>
                    <div style={{ width: 58, height: 58, borderRadius: 20, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.26)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)" }}>
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div>
                      <div style={{ color: "rgba(255,255,255,0.70)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 1 }}>Insight Lab</div>
                      <h2 style={{ margin: "6px 0 0", color: "#fff", fontSize: 34, fontWeight: 950, lineHeight: 1.05 }}>Season & Team Comparisons</h2>
                      <div style={{ color: "rgba(255,255,255,0.74)", fontSize: 14, fontWeight: 780, lineHeight: 1.55, marginTop: 12 }}>Compare performance blocks, teams, athletes, workouts, and nutrition readiness without digging through reports.</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, width: "min(900px,100%)", background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 24, padding: 16, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)" }}>
                    {comparisonFilters.map(([controlLabel, value, setter, options]) => (
                      <label key={controlLabel} style={{ display: "grid", gap: 8, minWidth: 0 }}>
                        <span style={{ color: "rgba(255,255,255,0.72)", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.65 }}>{controlLabel}</span>
                        <select value={value} onChange={(event) => setter(event.target.value)} style={selectControlStyle}>
                          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(360px,100%),1fr))", gap: 20 }}>
                  <div style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.24)", borderRadius: 26, padding: 26, color: "#fff", minHeight: 238, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)" }}>
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.75 }}>Selected Metric</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 18, marginTop: 14 }}>
                      <div style={{ fontSize: 48, fontWeight: 950, lineHeight: 0.98 }}>{metricTitle}</div>
                      <div style={{ color: compareTone(selectedMetric.left, selectedMetric.right), background: "rgba(255,255,255,0.92)", borderRadius: 999, padding: "7px 12px", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}>{selectedMetric.winner}</div>
                    </div>
                    <div style={{ display: "grid", gap: 16, marginTop: 28 }}>
                      {[["A", selectedMetric.left, seasonLabel(seasonComparisonLab.a, "Season A")], ["B", selectedMetric.right, seasonLabel(seasonComparisonLab.b, "Season B")]].map(([side, value, label]) => (
                        <div key={side}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.74)", marginBottom: 8 }}>
                            <span>{side} · {label}</span>
                            <span style={{ color: "#fff" }}>{compareValue(value, selectedMetric.suffix)}</span>
                          </div>
                          <div style={{ height: 12, borderRadius: 999, background: "rgba(255,255,255,0.18)", overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, (Number(value || 0) / selectedMetric.max) * 100)}%`, height: "100%", borderRadius: 999, background: side === "A" ? "#fff" : comparisonAccent }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 22, color: "rgba(255,255,255,0.78)", fontSize: 13, fontWeight: 900 }}>{pctDelta(selectedMetric.left, selectedMetric.right)} vs comparison block</div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
                    {seasonComparisonLab.metrics.map((metric) => {
                      const tone = metric.key === compareMetric ? comparisonAccent : metric.tone;
                      return (
                        <div key={metric.key} style={{ background: "#fff", border: `1px solid ${metric.key === compareMetric ? alpha(comparisonAccent, 0.50) : "rgba(255,255,255,0.72)"}`, borderRadius: 22, padding: 18, boxShadow: metric.key === compareMetric ? `0 16px 38px ${alpha(comparisonAccent, 0.20)}` : "0 12px 30px rgba(15,23,42,0.08)" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                              <span style={{ width: 34, height: 34, borderRadius: 13, display: "grid", placeItems: "center", color: tone, background: alpha(tone, 0.10), border: `1px solid ${alpha(tone, 0.18)}`, flexShrink: 0 }}>
                                <FontAwesomeIcon icon={metric.icon} />
                              </span>
                              <span style={{ color: base.textDark, fontSize: 13, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{metric.label}</span>
                            </div>
                            <span style={{ color: compareTone(metric.left, metric.right), fontSize: 11, fontWeight: 950 }}>{metric.winner}</span>
                          </div>
                          <div style={{ display: "grid", gap: 11, marginTop: 18 }}>
                            {[["A", metric.left, brandSurface.primary], ["B", metric.right, comparisonAccent]].map(([side, value, color]) => (
                              <div key={`${metric.key}-${side}`} style={{ display: "grid", gridTemplateColumns: "22px minmax(0,1fr) auto", alignItems: "center", gap: 9 }}>
                                <span style={{ color: base.mutedLight, fontSize: 11, fontWeight: 950 }}>{side}</span>
                                <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${Math.min(100, (Number(value || 0) / metric.max) * 100)}%`, background: color, borderRadius: 999 }} />
                                </div>
                                <span style={{ color: base.textDark, fontSize: 12, fontWeight: 950 }}>{compareValue(value, metric.suffix)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div style={{ padding: 30, display: "grid", gap: 26 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(420px,100%),1fr))", gap: 22 }}>
                  {[["A", seasonComparisonLab.a, brandSurface.primary], ["B", seasonComparisonLab.b, comparisonAccent]].map(([side, panel, color]) => {
                    return (
                      <div key={side} style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 24, padding: 22, boxShadow: "0 16px 44px rgba(15,23,42,0.07)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.75 }}>Season {side}</div>
                            <h3 style={{ color: base.textDark, margin: "6px 0 0", fontSize: 24, fontWeight: 950, lineHeight: 1.08 }}>{panel.season?.name || "No season"}</h3>
                          </div>
                          <div style={{ color, background: alpha(color, 0.10), border: `1px solid ${alpha(color, 0.18)}`, borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}>{panel.groups.length} team{panel.groups.length === 1 ? "" : "s"}</div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(150px,100%),1fr))", gap: 12, marginBottom: 22 }}>
                          {[["Best day", panel.bestDay?.name || "None"], ["Best team", panel.groupRows[0]?.name || "None"], ["Top workout", panel.topWorkouts[0]?.name || "None"]].map(([labelText, value]) => (
                            <div key={labelText} style={{ background: base.bgAlt, border: `1px solid ${base.borderLight}`, borderRadius: 16, padding: 14, minWidth: 0 }}>
                              <div style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>{labelText}</div>
                              <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, marginTop: 7, lineHeight: 1.25, overflowWrap: "anywhere" }}>{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 26, padding: 24, boxShadow: "0 16px 44px rgba(15,23,42,0.07)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
                    <div>
                      <div style={{ color: brandSurface.primary, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.75 }}>Workout Comparison</div>
                      <h3 style={{ color: base.textDark, margin: "5px 0 0", fontSize: 24, fontWeight: 950, lineHeight: 1.1 }}>Top Workouts A vs B</h3>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", color: base.mutedLight, fontSize: 12, fontWeight: 900 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: brandSurface.primary }} />
                      Season A
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: comparisonAccent, marginLeft: 8 }} />
                      Season B
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 14 }}>
                    {(seasonComparisonLab.workoutComparison.length ? seasonComparisonLab.workoutComparison : [{ name: "No workouts", a: { xp: 0, sessions: 0 }, b: { xp: 0, sessions: 0 }, deltaXp: 0, peakXp: 1 }]).map((workout, index) => {
                      const deltaTone = compareTone(workout.deltaXp, 0);
                      return (
                        <div key={workout.name} style={{ display: "grid", gridTemplateColumns: "40px minmax(0,1fr) 190px 86px", gap: 14, alignItems: "center", background: base.bgAlt, border: `1px solid ${base.borderLight}`, borderRadius: 18, padding: 14 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 12, background: index === 0 ? brandSurface.primary : "#E2E8F0", color: index === 0 ? "#fff" : "#0A1D44", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 950 }}>{index + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: base.textDark, fontSize: 15, fontWeight: 950, lineHeight: 1.25, overflowWrap: "anywhere" }}>{workout.name}</div>
                            <div style={{ display: "grid", gap: 7, marginTop: 10 }}>
                              {[["A", workout.a.xp, brandSurface.primary], ["B", workout.b.xp, comparisonAccent]].map(([side, value, color]) => (
                                <div key={`${workout.name}-${side}`} style={{ display: "grid", gridTemplateColumns: "22px minmax(0,1fr) auto", alignItems: "center", gap: 8 }}>
                                  <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950 }}>{side}</span>
                                  <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                                    <div style={{ width: `${Math.min(100, (Number(value || 0) / maxWorkoutComparisonXp) * 100)}%`, height: "100%", background: color, borderRadius: 999 }} />
                                  </div>
                                  <span style={{ color: base.textDark, fontSize: 12, fontWeight: 950 }}>{compactGroupNumber(value)} XP</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 10, alignItems: "baseline" }}>
                            <div>
                              <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase" }}>A</div>
                              <div style={{ color: base.textDark, fontSize: 22, fontWeight: 950 }}>{compactGroupNumber(workout.a.xp)}</div>
                              <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850 }}>{workout.a.sessions} sessions</div>
                            </div>
                            <div style={{ color: base.mutedLight, fontSize: 12, fontWeight: 950 }}>vs</div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase" }}>B</div>
                              <div style={{ color: base.textDark, fontSize: 22, fontWeight: 950 }}>{compactGroupNumber(workout.b.xp)}</div>
                              <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850 }}>{workout.b.sessions} sessions</div>
                            </div>
                          </div>
                          <div style={{ color: deltaTone, textAlign: "right" }}>
                            <div style={{ fontSize: 20, fontWeight: 950, lineHeight: 1 }}>{workout.deltaXp > 0 ? "+" : ""}{compactGroupNumber(workout.deltaXp)}</div>
                            <div style={{ fontSize: 11, fontWeight: 950, textTransform: "uppercase", marginTop: 5 }}>{workout.deltaXp === 0 ? "Even" : workout.deltaXp > 0 ? "A up" : "B up"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(360px,100%),1fr))", gap: 22 }}>
                  <div style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 24, padding: 22, boxShadow: "0 16px 44px rgba(15,23,42,0.07)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 18 }}>
                      <div>
                        <h3 style={{ margin: 0, color: base.textDark, fontSize: 21, fontWeight: 950 }}>Athlete Movement</h3>
                        <div style={{ color: base.mutedLight, fontSize: 12, fontWeight: 850, marginTop: 5 }}>{metricTitle} change between A and B</div>
                      </div>
                      <span style={{ color: brandSurface.primary, background: alpha(brandSurface.primary, 0.08), border: `1px solid ${alpha(brandSurface.primary, 0.14)}`, borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 950 }}>{seasonComparisonLab.athleteDelta.length} athletes</span>
                    </div>
                    <div style={{ display: "grid", gap: 11 }}>
                      {seasonComparisonLab.athleteDelta.length === 0 ? <div style={{ color: base.mutedLight, fontWeight: 850 }}>No athletes to compare.</div> : seasonComparisonLab.athleteDelta.map((row) => {
                        const deltaTone = compareTone(row.delta, 0);
                        return (
                          <div key={row.id} style={{ display: "grid", gridTemplateColumns: "56px minmax(0,1fr) 92px", gap: 14, alignItems: "center", background: base.bgAlt, border: `1px solid ${base.borderLight}`, borderRadius: 18, padding: 14 }}>
                            <img src={row.profile_img || noImage} alt={row.name} style={{ width: 52, height: 52, borderRadius: 16, objectFit: "cover" }} loading="lazy" />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.textDark, fontSize: 15, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
                              <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.groups || "No team"}</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "baseline", gap: 10, marginTop: 9 }}>
                                <div>
                                  <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>A</div>
                                  <div style={{ color: base.textDark, fontSize: 24, fontWeight: 950, lineHeight: 1 }}>{compareValue(row[compareMetric], compareMetric === "caloriePct" ? "%" : "")}</div>
                                </div>
                                <div style={{ color: base.mutedLight, fontSize: 12, fontWeight: 950 }}>vs</div>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.45 }}>B</div>
                                  <div style={{ color: base.textDark, fontSize: 24, fontWeight: 950, lineHeight: 1 }}>{compareValue(row.previous, compareMetric === "caloriePct" ? "%" : "")}</div>
                                </div>
                              </div>
                              <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
                                <div style={{ width: `${Math.min(100, (Math.abs(Number(row.delta || 0)) / maxAthleteDelta) * 100)}%`, height: "100%", background: deltaTone, borderRadius: 999 }} />
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ color: deltaTone, fontSize: 20, fontWeight: 950, lineHeight: 1 }}>{row.delta > 0 ? "+" : ""}{compareValue(row.delta, compareMetric === "caloriePct" ? "%" : "")}</div>
                              <div style={{ color: deltaTone, fontSize: 11, fontWeight: 950, textTransform: "uppercase", marginTop: 5 }}>{row.delta === 0 ? "Even" : row.delta > 0 ? "Up" : "Down"}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 24, padding: 22, boxShadow: "0 16px 44px rgba(15,23,42,0.07)" }}>
                    <h3 style={{ margin: 0, color: base.textDark, fontSize: 21, fontWeight: 950 }}>Team Comparison</h3>
                    <div style={{ color: base.mutedLight, fontSize: 12, fontWeight: 850, marginTop: 5, marginBottom: 18 }}>{metricTitle} across Season A and Season B.</div>
                    {(seasonComparisonLab.teamComparison.length ? seasonComparisonLab.teamComparison : [{ name: "No teams", a: 0, b: 0, delta: 0, peak: 1 }]).map((group, index) => {
                      const deltaTone = compareTone(group.delta, 0);
                      return (
                        <div key={group.id || group.name} style={{ display: "grid", gridTemplateColumns: "36px minmax(0,1fr) 76px", gap: 12, alignItems: "center", padding: "12px 0", borderTop: index ? `1px solid ${base.borderLight}` : 0 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 10, background: index === 0 ? comparisonAccent : "#E2E8F0", color: index === 0 ? "#fff" : "#0A1D44", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 950 }}>{index + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 8 }}>{group.name}</div>
                            {[["A", group.a, brandSurface.primary], ["B", group.b, comparisonAccent]].map(([side, value, color]) => (
                              <div key={`${group.id || group.name}-${side}`} style={{ display: "grid", gridTemplateColumns: "20px minmax(0,1fr) auto", gap: 8, alignItems: "center", marginTop: 6 }}>
                                <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950 }}>{side}</span>
                                <div style={{ height: 7, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${Math.min(100, (Number(value || 0) / maxTeamComparisonValue) * 100)}%`, background: color, borderRadius: 999 }} />
                                </div>
                                <span style={{ color: base.textDark, fontSize: 12, fontWeight: 950 }}>{compareValue(value, compareMetric === "caloriePct" ? "%" : "")}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ color: deltaTone, textAlign: "right" }}>
                            <div style={{ fontSize: 18, fontWeight: 950, lineHeight: 1 }}>{group.delta > 0 ? "+" : ""}{compareValue(group.delta, compareMetric === "caloriePct" ? "%" : "")}</div>
                            <div style={{ fontSize: 10, fontWeight: 950, textTransform: "uppercase", marginTop: 5 }}>{group.delta === 0 ? "Even" : group.delta > 0 ? "A up" : "B up"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ background: "#fff", border: `1px solid ${base.borderLight}`, borderRadius: 26, padding: 24, boxShadow: "0 16px 44px rgba(15,23,42,0.07)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 22, flexWrap: "wrap", marginBottom: 22 }}>
                    <div>
                      <div style={{ color: brandSurface.primary, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.75 }}>Drilldown</div>
                      <h3 style={{ color: base.textDark, margin: "5px 0 0", fontSize: 24, fontWeight: 950, lineHeight: 1.1 }}>Individual Athlete & Team Workouts</h3>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, width: "min(640px,100%)", background: base.bgAlt, border: `1px solid ${base.borderLight}`, borderRadius: 20, padding: 14 }}>
                      <label style={{ display: "grid", gap: 8, minWidth: 0 }}>
                        <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Team focus</span>
                        <select value={compareDrilldownGroupId} onChange={(event) => setCompareDrilldownGroupId(event.target.value)} style={selectControlStyle}>
                          <option value="all">All teams</option>
                          {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                        </select>
                      </label>
                      <label style={{ display: "grid", gap: 8, minWidth: 0 }}>
                        <span style={{ color: base.mutedLight, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Athlete focus</span>
                        <select value={compareAthleteId} onChange={(event) => setCompareAthleteId(event.target.value)} style={selectControlStyle}>
                          <option value="all">All athletes</option>
                          {comparisonAthleteOptions.map((athlete) => <option key={athlete.id} value={athlete.id}>{athlete.name}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 22 }}>
                    {[
                      ["Focus", seasonComparisonLab.drilldown.label, faUsers, brandSurface.primary],
                      ["Sessions", seasonComparisonLab.drilldown.rows.length.toLocaleString(), faDumbbell, "#16A34A"],
                      ["Calories", compactGroupNumber(seasonComparisonLab.drilldown.calories), faUtensils, "#F59E0B"],
                      ["Protein", `${compactGroupNumber(seasonComparisonLab.drilldown.protein)}g`, faBolt, "#2563EB"],
                    ].map(([title, value, icon, color]) => (
                      <div key={title} style={{ background: base.bgAlt, border: `1px solid ${base.borderLight}`, borderRadius: 18, padding: 16, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color, fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}><FontAwesomeIcon icon={icon} />{title}</div>
                        <div style={{ color: base.textDark, fontSize: 22, fontWeight: 950, marginTop: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))", gap: 18 }}>
                    {[
                      ["Most Trained", seasonComparisonLab.drilldown.workoutsBySessions, "sessions", maxDrilldownSessions, "#16A34A"],
                      ["Highest XP", seasonComparisonLab.drilldown.workoutsByXp, "xp", maxDrilldownXp, brandSurface.primary],
                      ["Active Days", seasonComparisonLab.drilldown.days, "sessions", maxDrilldownSessions, brandSurface.accent],
                    ].map(([title, rows, key, maxValue, color]) => {
                      const displayColor = isDarkColor(color) ? "#FBBF24" : color;
                      return <div key={title} style={{ background: base.bgAlt, borderRadius: 20, padding: 18, border: `1px solid ${base.borderLight}` }}>
                        <h4 style={{ margin: "0 0 14px", color: base.textDark, fontSize: 15, fontWeight: 950 }}>{title}</h4>
                        {(rows.length ? rows : [{ name: "No data yet", sessions: 0, xp: 0 }]).slice(0, 6).map((row, index) => (
                          <div key={`${title}-${row.name}`} style={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) auto", gap: 11, alignItems: "center", padding: "11px 0", borderTop: index ? `1px solid ${base.borderLight}` : 0 }}>
                            <div style={{ width: 30, height: 30, borderRadius: 10, background: index === 0 ? displayColor : "#E2E8F0", color: index === 0 ? "#fff" : "#0A1D44", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 950 }}>{index + 1}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, lineHeight: 1.25, overflowWrap: "anywhere" }}>{row.name}</div>
                              <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
                                <div style={{ width: `${Math.min(100, (Number(row[key] || 0) / maxValue) * 100)}%`, height: "100%", background: displayColor, borderRadius: 999 }} />
                              </div>
                            </div>
                            <div style={{ color: displayColor, background: alpha(displayColor, 0.10), border: `1px solid ${alpha(displayColor, 0.16)}`, borderRadius: 999, padding: "5px 9px", fontSize: 13, fontWeight: 950 }}>{compactGroupNumber(row[key])}</div>
                          </div>
                        ))}
                      </div>
                    })}
                  </div>
                </div>
              </div>
            </section>
          );
        })()}
      </main>

      {notice && (
        <div style={{ position: "fixed", right: 18, bottom: 18, zIndex: 2000, maxWidth: 390, border: `1px solid ${notice.tone === "error" ? "#FCA5A5" : notice.tone === "warning" ? "#FCD34D" : notice.tone === "success" ? "#86EFAC" : "#BFDBFE"}`, background: "#fff", color: base.text, borderRadius: 14, padding: "12px 14px", boxShadow: "0 18px 42px rgba(13,39,91,0.18)", fontSize: 13, fontWeight: 900 }}>
          {notice.message}
        </div>
      )}

      {groupAnalyticsModalOpen && dashboardGroup && dashboardGroupData && (
        <>
          {(() => {
            const memberIds = new Set(getCountingWorkoutMemberIds(dashboardGroup));
            const baseRows = activeSessions
              .filter((row) => memberIds.has(row.profile_id) && sessionCountsForGroup(row, dashboardGroup))
              .sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0));
            const filteredRows = groupAnalyticsAthleteId === "all"
              ? baseRows
              : baseRows.filter((row) => row.profile_id === groupAnalyticsAthleteId);
            const rangeDays = analyticsWindow.days ?? (activeSeason ? inclusiveDays(
              activeSeason.starts_on ? new Date(`${activeSeason.starts_on}T00:00:00`) : new Date(),
              activeSeason.ends_on ? new Date(`${activeSeason.ends_on}T00:00:00`) : new Date()
            ) : 0);
            const totals = filteredRows.reduce((acc, session) => {
              const rowTotals = getWorkoutSessionTotals(session);
              acc.totalXp += rowTotals.totalXp;
              acc.totalSets += rowTotals.totalSets;
              acc.totalReps += rowTotals.totalReps;
              acc.totalVolume += rowTotals.totalVolume;
              acc.totalDuration += rowTotals.durationSeconds;
              acc.totalRest += rowTotals.totalRestSeconds;
              return acc;
            }, { totalXp: 0, totalSets: 0, totalReps: 0, totalVolume: 0, totalDuration: 0, totalRest: 0 });
            const groupTrendOptions = [
              { key: "sessions", label: "Sessions", icon: faCalendar, metric: "sessions", color: "#16A34A", value: filteredRows.length.toLocaleString(), detail: `${analyticsWindow.label} completed sessions` },
              { key: "totalSets", label: "Sets", icon: faDumbbell, metric: "totalSets", color: "#D5A419", value: compactGroupNumber(totals.totalSets), detail: `${analyticsWindow.label} completed sets` },
              { key: "totalReps", label: "Reps", icon: faRepeat, metric: "totalReps", color: brandSurface.secondary, value: compactGroupNumber(totals.totalReps), detail: `${analyticsWindow.label} completed reps` },
              { key: "totalVolume", label: "Volume", icon: faDumbbell, metric: "totalVolume", color: brandSurface.accent, value: formatGroupVolume(totals.totalVolume), detail: `${analyticsWindow.label} completed volume` },
            ];
            const selectedGroupTrend = groupTrendOptions.find((option) => option.key === groupTrendMetric) || groupTrendOptions[0];
            const groupTrendRows = buildWorkoutGroupTrendRows(filteredRows, rangeDays);
            const workoutTrendTargets = buildWorkoutGroupWorkoutTargets(filteredRows);
            const activeWorkoutTarget = workoutTrendTargets.find((workout) => workout.key === selectedGroupWorkoutKey) || workoutTrendTargets[0] || null;
            const activeExerciseTarget = activeWorkoutTarget?.exercises?.find((exercise) => exercise.id === selectedGroupExerciseId) || null;
            const activeWorkoutTrendTarget = activeExerciseTarget || activeWorkoutTarget;
            const activeWorkoutImageUrl = activeExerciseTarget
              ? (activeExerciseTarget.image_url || activeExerciseTarget.gif_url || "")
              : (activeWorkoutTarget?.exercises || []).find((exercise) => exercise.image_url || exercise.gif_url)?.image_url ||
                (activeWorkoutTarget?.exercises || []).find((exercise) => exercise.image_url || exercise.gif_url)?.gif_url ||
                "";
            const activeWorkoutTrendLabel = activeWorkoutTrendTarget?.name || activeWorkoutTrendTarget?.label || "Workout";
            const workoutTrendOptions = [
              { key: "totalSets", label: "Sets", icon: faDumbbell, metric: "totalSets", color: "#D5A419", value: compactGroupNumber(activeWorkoutTrendTarget?.total_sets || 0) },
              { key: "totalReps", label: "Reps", icon: faRepeat, metric: "totalReps", color: brandSurface.secondary, value: compactGroupNumber(activeWorkoutTrendTarget?.total_reps || 0) },
              { key: "totalVolume", label: "Volume", icon: faDumbbell, metric: "totalVolume", color: brandSurface.accent, value: formatGroupVolume(activeWorkoutTrendTarget?.total_volume || 0) },
              { key: "sessions", label: "Sessions", icon: faCalendar, metric: "sessions", color: "#16A34A", value: Number(activeWorkoutTrendTarget?.sessions_count || 0).toLocaleString() },
            ];
            const selectedWorkoutTrend = workoutTrendOptions.find((option) => option.key === groupWorkoutTrendMetric) || workoutTrendOptions[0];
            const activeWorkoutTrendRows = buildWorkoutGroupTrendRows(activeWorkoutTrendTarget?.trendSessions || [], rangeDays);
            const groupTrendHasBreakdown = groupTrendRows.some((row) => Number(row?.[selectedGroupTrend.metric] || 0) > 0);
            const workoutTrendHasBreakdown = activeWorkoutTrendRows.some((row) => Number(row?.[selectedWorkoutTrend.metric] || 0) > 0);
            const groupDurationSeconds = Number(totals.totalDuration || 0);
            const groupRestSeconds = Number(totals.totalRest || 0);
            const groupActiveMinutes = Math.max(1, Math.round(Math.max(0, groupDurationSeconds - groupRestSeconds) / 60));
            const groupWorkDensity = totals.totalVolume > 0 ? Number(totals.totalVolume || 0) / groupActiveMinutes : 0;
            const groupRestRatio = groupDurationSeconds > 0 ? Math.round((groupRestSeconds / groupDurationSeconds) * 100) : 0;
            const topExercises = workoutTrendTargets
              .flatMap((workout) => (workout.exercises || []).map((exercise) => ({ ...exercise, workoutLabel: workout.label })))
              .sort((a, b) => (b.total_xp - a.total_xp) || (b.total_reps - a.total_reps))
              .slice(0, 4);
            const selectedAthlete = dashboardGroupData.athleteRows.find((row) => row.id === groupAnalyticsAthleteId) || null;
            const totalSessionTarget = Math.max(1, dashboardGroupData.sessionTarget * Math.max(1, selectedAthlete ? 1 : dashboardGroupData.members.length));
            const sessionCompletion = Math.min(100, Math.round((filteredRows.length / totalSessionTarget) * 100));
            const attentionSessionCutoff = Math.ceil(dashboardGroupData.sessionTarget * 0.75);
            const onPlanRows = dashboardGroupData.athleteRows.filter((row) => row.sessions >= dashboardGroupData.sessionTarget).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);
            const closeRows = dashboardGroupData.athleteRows.filter((row) => row.sessions >= attentionSessionCutoff && row.sessions < dashboardGroupData.sessionTarget).sort((a, b) => b.sessions - a.sessions || b.xp - a.xp);
            const activityRows = dashboardGroupData.athleteRows.filter((row) => row.sessions < attentionSessionCutoff).sort((a, b) => a.sessions - b.sessions || a.xp - b.xp);
            const sortedAttentionRows = [...activityRows, ...closeRows, ...onPlanRows];
            const attentionPreviewLimit = 6;
            const hiddenAttentionCount = Math.max(0, sortedAttentionRows.length - (showAllGroupAnalyticsAthletes ? sortedAttentionRows.length : Math.min(sortedAttentionRows.length, attentionPreviewLimit)));
            const nutritionStory = dashboardGroupData.nutritionStory || buildDashboardNutritionStory([], dashboardGroupData.members, dashboardGroupData.nutritionTargets, dashboardGroupData.dashboardNutritionDays);
            const nutritionCoverageLabel = nutritionStory.consistency >= 75 ? "Reliable sample" : nutritionStory.consistency >= 40 ? "Partial sample" : "Not enough logs";
            const nutritionCoverageTone = nutritionStory.consistency >= 75 ? base.success : nutritionStory.consistency >= 40 ? "#D97706" : base.danger;
            return (
              <WorkoutGroupAnalyticsModalFrame
                title="Group Analytics"
                subtitle={`${dashboardGroup.name || "Workout group"}${selectedAthlete ? ` · ${selectedAthlete.name}` : ""}`}
                onClose={() => setGroupAnalyticsModalOpen(false)}
              >
                  <div style={{ order: 0 }}>
                    <WorkoutGroupAnalyticsAthleteSelector
                      athletes={dashboardGroupData.athleteRows}
                      selectedId={groupAnalyticsAthleteId}
                      accent={brandSurface.primary}
                      noImage={noImage}
                      onSelectAll={() => { setGroupAnalyticsAthleteId("all"); setSelectedGroupWorkoutKey(""); setSelectedGroupExerciseId(""); }}
                      onSelectAthlete={(athlete) => { setGroupAnalyticsAthleteId(athlete.id); setSelectedGroupWorkoutKey(""); setSelectedGroupExerciseId(""); }}
                    />
                  </div>
                  <div style={{ order: 1 }}>{renderAnalyticsReviewWindow("Group analytics review window")}</div>

                  <div style={{ order: 2 }}>
                    <WorkoutGroupAnalyticsTeamStatusCard
                      selectedAthlete={selectedAthlete}
                      selectedAthleteLabel={selectedAthlete?.name || ""}
                      sessionTarget={dashboardGroupData.sessionTarget}
                      onPlanCount={onPlanRows.length}
                      athleteCount={dashboardGroupData.members.length}
                      activityCount={activityRows.length}
                      closeCount={closeRows.length}
                      completedSessions={dashboardGroupData.totalSessions}
                      sessionCompletion={sessionCompletion}
                      primary="#0C69C8"
                      secondary="#0D275B"
                      onRemind={() => sendGroupReminder(dashboardGroup, "training")}
                      onReviewAthletes={() => setShowAllGroupAnalyticsAthletes(true)}
                    />
                  </div>

                  <div style={{ order: 3, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                    {[
                      { icon: faBolt, label: "Total XP", value: totals.totalXp.toLocaleString(), color: brandSurface.primary, bg: alpha(brandSurface.primary, 0.10) },
                      { icon: faCalendar, label: "Sessions", value: filteredRows.length.toLocaleString(), color: "#16A34A", bg: "#EAFBF0" },
                      { icon: faRepeat, label: "Reps", value: compactGroupNumber(totals.totalReps), color: brandSurface.secondary, bg: alpha(brandSurface.secondary, 0.10) },
                      { icon: faDumbbell, label: "Volume", value: formatGroupVolume(totals.totalVolume), color: brandSurface.accent, bg: "#FFF8E1" },
                    ].map((item) => <WorkoutGroupAnalyticsMetricCard key={item.label} {...item} />)}
                  </div>

                  {!selectedAthlete && (
                    <section style={{ order: 4, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <h3 style={{ margin: 0, color: base.text, fontSize: 18 }}>Athlete Review</h3>
                          <div style={{ color: base.muted, fontSize: 13, fontWeight: 800, lineHeight: 1.4, marginTop: 4 }}>
                            Grouped by session target so the next coaching action is visible first.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {[
                            ["On plan", `${onPlanRows.length}/${dashboardGroupData.members.length}`],
                            ["Close", closeRows.length],
                            ["Needs Activity", activityRows.length],
                          ].map(([label, value]) => (
                            <span key={label} style={{ border: `1px solid ${base.border}`, background: "#F8FAFC", color: base.text, borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950 }}>
                              {value} {label}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                        {[
                          ["Needs Activity", activityRows, { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", muted: "#B45309" }],
                          ["Close", closeRows, { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", muted: "#A16207" }],
                          ...(showAllGroupAnalyticsAthletes ? [["On plan", onPlanRows, { bg: "#ECFDF3", border: "#BBF7D0", text: "#166534", muted: "#15803D" }]] : []),
                        ].map(([title, rows, tone]) => {
                          const visibleRows = showAllGroupAnalyticsAthletes ? rows : rows.slice(0, Math.max(0, attentionPreviewLimit - (title === "Close" ? Math.min(activityRows.length, attentionPreviewLimit) : 0)));
                          if (!visibleRows.length && title !== "Needs Activity") return null;
                          return (
                            <section key={title} style={{ border: `1px solid ${tone.border}`, background: tone.bg, borderRadius: 18, padding: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: visibleRows.length ? 10 : 0 }}>
                                <div>
                                  <h4 style={{ margin: 0, color: tone.text, fontSize: 14, fontWeight: 950 }}>{title}</h4>
                                  <div style={{ color: tone.muted, fontSize: 11, fontWeight: 850, marginTop: 2 }}>{rows.length} athlete{rows.length === 1 ? "" : "s"}</div>
                                </div>
                                <span style={{ width: 34, height: 34, borderRadius: 13, display: "grid", placeItems: "center", background: "#fff", color: tone.text, fontSize: 14, fontWeight: 950 }}>{rows.length}</span>
                              </div>
                              {visibleRows.length ? (
                                <div style={{ display: "grid", gap: 8 }}>
                                  {visibleRows.map((row) => {
                                    const calorieWindowTarget = Number(dashboardGroupData.calorieTarget || 0);
                                    return (
                                      <button key={`${title}_${row.id}`} type="button" onClick={() => openAthleteNotes(row)} style={{ width: "100%", border: `1px solid ${base.border}`, background: "#fff", borderRadius: 16, padding: 12, cursor: "pointer", display: "grid", gridTemplateColumns: "54px minmax(0,1fr) 132px", alignItems: "center", gap: 12, textAlign: "left" }}>
                                        <img src={row.profile_img || noImage} alt="" style={{ width: 54, height: 54, borderRadius: 16, objectFit: "cover" }} loading="lazy" />
                                        <span style={{ minWidth: 0, display: "grid", gap: 7 }}>
                                          <span style={{ display: "block", color: base.text, fontSize: 14, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</span>
                                          <span style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 7 }}>
                                            {[
                                              [`${row.sessions}/${dashboardGroupData.sessionTarget}`, "sessions"],
                                              [`${row.xp.toLocaleString()}/${dashboardGroupData.athleteTarget.toLocaleString()}`, "XP"],
                                              [`${Number(row.calories || 0).toLocaleString()}/${Number(calorieWindowTarget || 0).toLocaleString()}`, "kcal"],
                                            ].map(([value, label]) => (
                                              <span key={label} style={{ minWidth: 0 }}>
                                                <span style={{ display: "block", color: base.text, fontSize: 11, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
                                                <span style={{ display: "block", color: base.muted, fontSize: 9, fontWeight: 950, textTransform: "uppercase", marginTop: 2 }}>{label}</span>
                                              </span>
                                            ))}
                                          </span>
                                        </span>
                                        <span style={{ justifySelf: "end", border: `1px solid ${pill(row.status.tone).borderColor}`, background: pill(row.status.tone).background, color: pill(row.status.tone).color, borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950 }}>{row.status.label}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : <div style={{ color: tone.muted, fontSize: 12, fontWeight: 850 }}>No athletes in this bucket.</div>}
                            </section>
                          );
                        })}
                      </div>
                      {sortedAttentionRows.length > attentionPreviewLimit && (
                        <button type="button" aria-expanded={showAllGroupAnalyticsAthletes} onClick={() => setShowAllGroupAnalyticsAthletes((open) => !open)} style={{ marginTop: 10, width: "100%", minHeight: 42, border: `1px solid ${base.border}`, background: "#F8FAFC", color: base.text, borderRadius: 14, padding: "10px 12px", fontSize: 12, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          {showAllGroupAnalyticsAthletes ? "Show fewer athletes" : `Show all ${sortedAttentionRows.length} athletes`}
                          <FontAwesomeIcon icon={showAllGroupAnalyticsAthletes ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                        </button>
                      )}
                      {!showAllGroupAnalyticsAthletes && hiddenAttentionCount > 0 && (
                        <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 8, textAlign: "center" }}>
                          {hiddenAttentionCount} more athlete{hiddenAttentionCount === 1 ? "" : "s"} hidden to keep this review focused.
                        </div>
                      )}
                    </section>
                  )}

                  <section style={{ order: 7, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                      <div>
                        <div style={{ color: analyticsGold, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Fueling summary</div>
                        <h3 style={{ margin: "4px 0 0", color: base.text, fontSize: 19, lineHeight: 1.15, display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <FontAwesomeIcon icon={hasNutritionAccess ? faFire : faLock} style={{ color: analyticsGold, fontSize: 14 }} />
                          Nutrition readiness
                        </h3>
                      </div>
                      <span style={{ border: "1px solid #FCD34D", background: "#FEF3C7", color: "#92400E", borderRadius: 999, padding: "6px 9px", fontSize: 10, fontWeight: 950 }}>
                        {Number(dashboardGroupData.nutritionTargets?.calories || 2500).toLocaleString()} kcal/day
                      </span>
                    </div>
                    {!hasNutritionAccess ? (
                      <div style={{ border: "1px dashed #FDE68A", background: "#FFFBEB", borderRadius: 16, padding: 12, color: "#92400E", fontSize: 12, fontWeight: 850 }}>
                        Nutrition analytics unlock with the group nutrition dashboard.
                      </div>
                    ) : nutritionStory.loggedDays <= 0 ? (
                      <div style={{ border: "1px dashed #FDE68A", background: "#FFFBEB", borderRadius: 16, padding: 12 }}>
                        <div style={{ color: "#92400E", fontSize: 13, fontWeight: 950 }}>No nutrition logs yet</div>
                        <div style={{ color: "#92400E", fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>Nutrition signals will appear after athletes log meals in this review window.</div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 12 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
                          <div style={{ border: "1px solid #FCD34D", background: "#FEF3C7", borderRadius: 18, padding: 14 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={{ color: "#92400E", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Logging coverage</div>
                                <div style={{ color: base.textDark, fontSize: 29, lineHeight: 1, fontWeight: 950, marginTop: 6 }}>{nutritionStory.loggedDays}/{nutritionStory.expectedDays}</div>
                                <div style={{ color: base.mutedLight, fontSize: 12, fontWeight: 850, marginTop: 5 }}>athlete-days logged</div>
                              </div>
                              <span style={{ color: nutritionCoverageTone, background: alpha(nutritionCoverageTone, 0.12), border: `1px solid ${alpha(nutritionCoverageTone, 0.22)}`, borderRadius: 999, padding: "5px 9px", fontSize: 10, fontWeight: 950 }}>{nutritionCoverageLabel}</span>
                            </div>
                            <div style={{ height: 8, background: "#FDE68A", borderRadius: 999, overflow: "hidden", marginTop: 12 }}>
                              <div style={{ height: "100%", width: `${Math.min(100, Math.round(nutritionStory.consistency))}%`, background: nutritionCoverageTone, borderRadius: 999 }} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8, marginTop: 12 }}>
                              {[
                                ["Calories", compactGroupNumber(nutritionStory.totals.calories), `${Math.round(nutritionStory.consistency)}% consistency`],
                                ["Food entries", nutritionStory.totals.items.toLocaleString(), `${Math.round(nutritionStory.totals.items / Math.max(1, nutritionStory.loggedDays))}/logged day`],
                                ["Avg kcal", Math.round(nutritionStory.macroRows[0]?.value || 0).toLocaleString(), `${dashboardGroupData.nutritionTargets.calories.toLocaleString()} target`],
                              ].map(([metricLabel, value, sub]) => (
                                <div key={metricLabel} style={{ border: "1px solid #FCD34D", background: "#fff", borderRadius: 12, padding: 9 }}>
                                  <div style={{ color: base.textDark, fontSize: 16, fontWeight: 950 }}>{value}</div>
                                  <div style={{ color: base.mutedLight, fontSize: 9, fontWeight: 950, textTransform: "uppercase", marginTop: 4 }}>{metricLabel}</div>
                                  <div style={{ color: base.mutedLight, fontSize: 10, fontWeight: 800, marginTop: 3 }}>{sub}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div style={{ border: `1px solid ${base.border}`, background: base.bgAlt, borderRadius: 18, padding: 14 }}>
                            <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Coach next action</div>
                            <div style={{ display: "grid", gap: 7 }}>
                              {nutritionStory.athleteRows.slice(0, 4).map((athlete) => (
                                <div key={athlete.id || athlete.name} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8, alignItems: "center", border: `1px solid ${base.borderLight}`, background: "#fff", borderRadius: 12, padding: "8px 9px" }}>
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: "block", color: base.textDark, fontSize: 12, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{athlete.name}</span>
                                    <span style={{ display: "block", color: base.mutedLight, fontSize: 10, fontWeight: 800, marginTop: 2 }}>{athlete.loggedDays} logged day{athlete.loggedDays === 1 ? "" : "s"} · {Math.round(athlete.avgCalories).toLocaleString()} avg kcal</span>
                                  </span>
                                  <span style={{ color: athlete.status.color, background: athlete.status.bg, borderRadius: 999, padding: "4px 7px", fontSize: 10, fontWeight: 950 }}>{athlete.status.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
                          <div style={{ border: `1px solid ${base.border}`, background: base.bgAlt, borderRadius: 18, padding: 14 }}>
                            <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Macro targets</div>
                            <div style={{ display: "grid", gap: 8 }}>
                              {nutritionStory.macroRows.map((macro) => (
                                <div key={macro.key} style={{ border: `1px solid ${base.borderLight}`, background: "#fff", borderRadius: 13, padding: 10 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                    <span style={{ color: base.textDark, fontSize: 12, fontWeight: 950 }}>{macro.label}</span>
                                    <span style={{ color: macro.status.color, background: macro.status.bg, borderRadius: 999, padding: "3px 7px", fontSize: 10, fontWeight: 950 }}>{macro.status.label}</span>
                                  </div>
                                  <div style={{ color: base.mutedLight, fontSize: 11, fontWeight: 850, marginTop: 4 }}>{Math.round(macro.value).toLocaleString()}{macro.unit === "kcal" ? " kcal" : macro.unit} / {Number(macro.target || 0).toLocaleString()}{macro.unit === "kcal" ? " kcal" : macro.unit}</div>
                                  <div style={{ height: 6, background: "#E2E8F0", borderRadius: 999, overflow: "hidden", marginTop: 8 }}><div style={{ width: `${Math.min(100, macro.status.pct)}%`, height: "100%", background: macro.status.color, borderRadius: 999 }} /></div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div style={{ border: `1px solid ${base.border}`, background: base.bgAlt, borderRadius: 18, padding: 14 }}>
                            <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950, marginBottom: 10 }}>Meal pattern</div>
                            <div style={{ display: "grid", gap: 8 }}>
                              {nutritionStory.mealRows.map((meal) => (
                                <div key={meal.meal} style={{ border: `1px solid ${meal.meta.border}`, background: meal.count ? meal.meta.bg : "#fff", borderRadius: 13, padding: 10 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                                    <span style={{ color: meal.meta.color, fontSize: 12, fontWeight: 950 }}><FontAwesomeIcon icon={meal.meta.icon} /> {meal.meal}</span>
                                    <span style={{ color: base.textDark, fontSize: 12, fontWeight: 950 }}>{meal.count} logs</span>
                                  </div>
                                  <div style={{ color: base.mutedLight, fontSize: 10, fontWeight: 800, marginTop: 4 }}>{meal.athletes} athletes · avg {Math.round(meal.avgCalories).toLocaleString()} kcal</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div style={{ border: `1px solid ${base.border}`, background: "#fff", borderRadius: 18, padding: 14 }}>
                          <div style={{ color: base.textDark, fontSize: 14, fontWeight: 950 }}>Repeated foods</div>
                          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                            {nutritionStory.topFoods.length ? nutritionStory.topFoods.map((food, index) => (
                              <div key={food.name} style={{ display: "grid", gridTemplateColumns: "30px minmax(0,1fr) auto", gap: 9, alignItems: "center", border: `1px solid ${base.borderLight}`, background: base.bgAlt, borderRadius: 13, padding: "9px 10px" }}>
                                <span style={{ width: 28, height: 28, borderRadius: 10, background: index === 0 ? brandSurface.primary : "#E6F0FF", color: index === 0 ? "#fff" : brandSurface.primary, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 950 }}>{index + 1}</span>
                                <span style={{ minWidth: 0 }}><span style={{ display: "block", color: base.textDark, fontSize: 13, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{food.name}</span><span style={{ display: "block", color: base.mutedLight, fontSize: 10, fontWeight: 800, marginTop: 2 }}>{food.topMeal} · {food.athletes} athlete{food.athletes === 1 ? "" : "s"} · avg {Math.round(food.avgCalories).toLocaleString()} kcal</span></span>
                                <span style={{ color: "#0F766E", background: "#CCFBF1", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 950 }}>{food.count} logs</span>
                              </div>
                            )) : <div style={{ border: "1px dashed #CBD5E1", borderRadius: 14, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850 }}>No food names available in this range.</div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </section>

                  <section style={{ order: 5, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 12, alignItems: "start" }}>
                      <div>
                        <div style={{ color: brandSurface.primary || "#0C69C8", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Team trends</div>
                        <h3 style={{ margin: "4px 0 0", color: base.text, fontSize: 19, lineHeight: 1.15 }}>Completed volume, reps, and sessions</h3>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 96px)", gap: 8 }}>
                        {groupTrendOptions.map((option) => {
                          const active = selectedGroupTrend.key === option.key;
                          return (
                            <button key={option.key} type="button" aria-pressed={active} onClick={() => setGroupTrendMetric(option.key)} style={{ minHeight: 46, border: `1px solid ${active ? option.color : "#E0E7EF"}`, background: active ? option.color : "#F8FAFF", color: active ? "#fff" : base.text, borderRadius: 15, padding: "8px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                              <FontAwesomeIcon icon={option.icon} style={{ color: active ? "#fff" : option.color, fontSize: 13 }} />
                              <span style={{ color: active ? "#fff" : base.text, fontSize: 11, fontWeight: 950 }}>{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ marginTop: 12, border: `1px solid ${base.border}`, background: "#F8FAFC", borderRadius: 18, padding: 13 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Selected signal</div>
                          <div style={{ color: base.text, fontSize: 24, lineHeight: 1.05, fontWeight: 950, marginTop: 4 }}>{selectedGroupTrend.value}</div>
                        </div>
                        <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, textAlign: "right" }}>
                          {groupTrendHasBreakdown ? analyticsWindow.label : `${selectedGroupTrend.value} logged in this window.`}
                        </div>
                      </div>
                      <WGTrendBars rows={groupTrendRows} metric={selectedGroupTrend.metric} color={selectedGroupTrend.color} height={136} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginTop: 10 }}>
                      {[
                        { icon: faClock, label: "Duration", value: formatDuration(groupDurationSeconds) },
                        { icon: faRepeat, label: "Rest", value: formatDuration(groupRestSeconds) },
                        { icon: faChartLine, label: "Rest ratio", value: `${groupRestRatio}%` },
                        { icon: faBolt, label: "Density", value: formatGroupVolume(groupWorkDensity).replace(" kg", "") },
                      ].map((item) => (
                        <div key={item.label} style={{ border: `1px solid ${base.border}`, background: "#FFFFFF", borderRadius: 14, padding: 10, display: "grid", gridTemplateColumns: "30px minmax(0, 1fr)", gap: 8, alignItems: "center", minWidth: 0 }}>
                          <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 12, background: "#E6F0FF", color: brandSurface.primary || "#0C69C8", display: "grid", placeItems: "center" }}>
                            <FontAwesomeIcon icon={item.icon} style={{ fontSize: 12 }} />
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "block", color: base.text, fontSize: 15, lineHeight: 1, fontWeight: 950, overflowWrap: "anywhere" }}>{item.value}</span>
                            <span style={{ display: "block", color: base.muted, fontSize: 9, fontWeight: 950, textTransform: "uppercase", marginTop: 4 }}>{item.label}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section style={{ order: 6, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                      <div>
                        <div style={{ color: brandSurface.primary || "#0C69C8", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Workout and exercise trends</div>
                        <h3 style={{ margin: "4px 0 0", color: base.text, fontSize: 19, lineHeight: 1.15 }}>Pick a workout, then choose an exercise</h3>
                      </div>
                      <WorkoutGroupExerciseHeaderIcon color={brandSurface.primary} />
                    </div>
                    {workoutTrendTargets.length === 0 ? (
                      <div style={{ border: "1px dashed #CBD5E1", borderRadius: 14, padding: 14, color: base.muted, fontSize: 12, fontWeight: 850 }}>No exercise detail in this range yet.</div>
                    ) : (
                      <div style={{ display: "grid", gap: 12 }}>
                        {workoutTrendTargets.length > 1 && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                            {workoutTrendTargets.map((workout) => {
                              const selected = workout.key === activeWorkoutTarget?.key;
                              const workoutImageUrl = (workout.exercises || []).find((exercise) => exercise.image_url || exercise.gif_url)?.image_url ||
                                (workout.exercises || []).find((exercise) => exercise.image_url || exercise.gif_url)?.gif_url ||
                                "";
                              return (
                                <button key={workout.key} type="button" aria-pressed={selected} onClick={() => { setSelectedGroupWorkoutKey(workout.key); setSelectedGroupExerciseId(""); }} style={{ minHeight: 86, border: `1px solid ${selected ? brandSurface.primary : "#E0E7EF"}`, background: selected ? "#EAF3FF" : "#FFFFFF", borderRadius: 16, padding: 10, cursor: "pointer", textAlign: "left", display: "grid", gridTemplateColumns: "44px minmax(0, 1fr)", gap: 10, alignItems: "center" }}>
                                  <span style={{ width: 44, height: 44, borderRadius: 14, background: selected ? "#FFFFFF" : "#EAF3FF", color: brandSurface.primary, display: "grid", placeItems: "center", overflow: "hidden" }}>
                                    {workoutImageUrl ? <img src={workoutImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4, boxSizing: "border-box" }} loading="lazy" /> : <FontAwesomeIcon icon={faDumbbell} />}
                                  </span>
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: "block", color: base.text, fontSize: 12, lineHeight: 1.18, fontWeight: 950, overflowWrap: "anywhere" }}>{workout.label}</span>
                                    <span style={{ display: "block", color: selected ? brandSurface.primary : base.muted, fontSize: 10, fontWeight: 950, marginTop: 5 }}>{workout.exercises.length} exercise{workout.exercises.length === 1 ? "" : "s"} · {compactGroupNumber(workout.sessions_count || 0)} sessions</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {activeWorkoutTarget?.exercises?.length > 1 && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: 8 }}>
                            {activeWorkoutTarget.exercises.slice(0, 8).map((exercise) => {
                              const selected = exercise.id === activeExerciseTarget?.id;
                              const imageUrl = exercise.image_url || exercise.gif_url || "";
                              return (
                                <button key={`${activeWorkoutTarget.key}_${exercise.id}`} type="button" aria-pressed={selected} onClick={() => setSelectedGroupExerciseId((current) => current === exercise.id ? "" : exercise.id)} style={{ minHeight: 128, border: `1px solid ${selected ? brandSurface.primary : "#E0E7EF"}`, background: selected ? "#EAF3FF" : "#FFFFFF", borderRadius: 16, padding: 9, cursor: "pointer", textAlign: "left", display: "grid", gap: 8 }}>
                                  {imageUrl ? (
                                    <img src={imageUrl} alt="" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "contain", borderRadius: 12, background: "#EAF3FF", padding: 6, boxSizing: "border-box" }} loading="lazy" />
                                  ) : (
                                    <span aria-hidden="true" style={{ width: "100%", height: 68, borderRadius: 12, background: "#EAF3FF", color: brandSurface.primary, display: "grid", placeItems: "center" }}><FontAwesomeIcon icon={faDumbbell} /></span>
                                  )}
                                  <span style={{ color: base.text, fontSize: 12, lineHeight: 1.2, fontWeight: 950, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{exercise.name}</span>
                                  <span style={{ color: selected ? brandSurface.primary : base.muted, fontSize: 10, fontWeight: 950 }}>{compactGroupNumber(exercise.total_sets || 0)} sets · {compactGroupNumber(exercise.total_reps)} reps</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {activeWorkoutTrendTarget && (
                          <div style={{ border: `1px solid ${base.border}`, background: "#F8FAFF", borderRadius: 18, padding: 13 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "94px minmax(0, 1fr) auto", gap: 13, alignItems: "center" }}>
                              <div style={{ width: 94, height: 94, borderRadius: 18, background: "#EAF3FF", color: brandSurface.primary, display: "grid", placeItems: "center", overflow: "hidden" }}>
                                {activeWorkoutImageUrl ? (
                                  <img src={activeWorkoutImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 6, boxSizing: "border-box" }} loading="lazy" />
                                ) : (
                                  <FontAwesomeIcon icon={faDumbbell} style={{ fontSize: 22 }} />
                                )}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>
                                  {activeExerciseTarget ? "Selected exercise" : "Selected workout"}
                                </div>
                                <div style={{ color: base.text, fontSize: 18, lineHeight: 1.16, fontWeight: 950, marginTop: 4, overflowWrap: "anywhere" }}>{activeWorkoutTrendLabel}</div>
                                {activeExerciseTarget && activeWorkoutTarget?.label && (
                                  <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 4, overflowWrap: "anywhere" }}>{activeWorkoutTarget.label}</div>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                {workoutTrendOptions.map((option) => {
                                  const active = selectedWorkoutTrend.key === option.key;
                                  return (
                                    <button key={option.key} type="button" onClick={() => setGroupWorkoutTrendMetric(option.key)} style={{ border: `1px solid ${active ? option.color : "#DCE6F3"}`, background: active ? option.color : "#FFFFFF", color: active ? "#fff" : base.text, borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                                      <FontAwesomeIcon icon={option.icon} />
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginTop: 12 }}>
                              {[
                                { label: "Sessions", value: compactGroupNumber(activeWorkoutTrendTarget.sessions_count || 0) },
                                { label: "Sets", value: compactGroupNumber(activeWorkoutTrendTarget.total_sets || 0) },
                                { label: "Reps", value: compactGroupNumber(activeWorkoutTrendTarget.total_reps || 0) },
                                { label: "Volume", value: formatGroupVolume(activeWorkoutTrendTarget.total_volume || 0) },
                              ].map((item) => (
                                <div key={item.label} style={{ border: `1px solid ${base.border}`, background: "#FFFFFF", borderRadius: 14, padding: 10 }}>
                                  <div style={{ color: base.text, fontSize: 15, fontWeight: 950 }}>{item.value}</div>
                                  <div style={{ color: base.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", marginTop: 4 }}>{item.label}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: 12, borderTop: `1px solid ${base.border}`, paddingTop: 12 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                                <span style={{ color: base.muted, fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>{selectedWorkoutTrend.label}</span>
                                <span style={{ color: selectedWorkoutTrend.color, fontSize: 22, lineHeight: 1, fontWeight: 950 }}>{selectedWorkoutTrend.value}</span>
                              </div>
                              {!workoutTrendHasBreakdown && (
                                <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 8 }}>
                                  {selectedWorkoutTrend.value} logged in this window.
                                </div>
                              )}
                              <WGTrendBars rows={activeWorkoutTrendRows} metric={selectedWorkoutTrend.metric} color={selectedWorkoutTrend.color} height={128} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </section>

                  <section style={{ order: 8, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                      <div>
                        <div style={{ color: brandSurface.primary || "#0C69C8", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Exercise leaderboard</div>
                        <h3 style={{ margin: "4px 0 0", color: base.text, fontSize: 19, lineHeight: 1.15 }}>Highest training load</h3>
                      </div>
                      <span style={{ width: 38, height: 38, borderRadius: 14, background: "#E6F0FF", color: brandSurface.primary, display: "grid", placeItems: "center" }}><FontAwesomeIcon icon={faStar} /></span>
                    </div>
                    {topExercises.length === 0 ? (
                      <div style={{ border: "1px dashed #CBD5E1", borderRadius: 14, padding: 14, color: base.muted, fontSize: 12, fontWeight: 850 }}>No exercise detail in this range.</div>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {topExercises.map((exercise, index) => (
                          <div key={`${exercise.name}_${index}`} style={{ border: `1px solid ${index === 0 ? "#CFE2FF" : base.border}`, background: index === 0 ? "#F8FAFF" : "#FFFFFF", borderRadius: 16, padding: 10, display: "grid", gridTemplateColumns: "54px minmax(0,1fr) 106px", gap: 10, alignItems: "center" }}>
                            <div style={{ position: "relative", width: 54, height: 54, borderRadius: 15, background: "#E6F0FF", overflow: "hidden", display: "grid", placeItems: "center", color: brandSurface.primary }}>
                              {exercise.image_url || exercise.gif_url ? (
                                <img src={exercise.image_url || exercise.gif_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4, boxSizing: "border-box" }} loading="lazy" />
                              ) : (
                                <FontAwesomeIcon icon={faDumbbell} />
                              )}
                              <span style={{ position: "absolute", left: 4, top: 4, minWidth: 18, height: 18, borderRadius: 7, background: index === 0 ? brandSurface.primary : "#FFFFFF", color: index === 0 ? "#fff" : brandSurface.primary, display: "grid", placeItems: "center", fontSize: 9, fontWeight: 950, boxShadow: "0 4px 10px rgba(12,105,200,0.18)" }}>{index + 1}</span>
                            </div>
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: "block", color: base.text, fontSize: 13, lineHeight: 1.18, fontWeight: 950, overflowWrap: "anywhere" }}>{exercise.name}</span>
                              <span style={{ display: "block", color: base.muted, fontSize: 10, fontWeight: 800, marginTop: 3 }}>{exercise.workoutLabel}</span>
                              <span style={{ display: "block", color: base.muted, fontSize: 10, fontWeight: 950, marginTop: 5 }}>{compactGroupNumber(exercise.total_sets || 0)} sets · {compactGroupNumber(exercise.total_reps || 0)} reps</span>
                            </span>
                            <span style={{ justifySelf: "end", textAlign: "right" }}>
                              <span style={{ display: "block", color: base.text, fontSize: 13, fontWeight: 950 }}>{formatGroupVolume(exercise.total_volume || 0)}</span>
                              <span style={{ display: "block", color: base.muted, fontSize: 10, fontWeight: 850, marginTop: 3 }}>{compactGroupNumber(exercise.sessions_count || 0)} sessions</span>
                              <span style={{ display: "inline-flex", color: brandSurface.primary, background: "#E6F0FF", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 950, marginTop: 6 }}>{Number(exercise.total_xp || 0).toLocaleString()} XP</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
              </WorkoutGroupAnalyticsModalFrame>
            );
          })()}
        </>
      )}

      {athleteModal && (
        <div role="dialog" aria-modal="true" aria-labelledby="club-athlete-analytics-title" onClick={() => setAthleteModal(null)} style={{ position: "fixed", inset: 0, zIndex: 2050, background: "rgba(10,29,68,0.42)", backdropFilter: "blur(10px)", display: "grid", placeItems: "center", padding: 18 }}>
          <div className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(920px, 100%)", maxHeight: "88vh", overflow: "auto", background: "#F3F7FD", borderRadius: 24, border: "1px solid rgba(255,255,255,0.72)", boxShadow: "0 28px 80px rgba(10,29,68,0.34)" }}>
            <div style={{ position: "relative", overflow: "hidden", background: `linear-gradient(135deg, ${brandSurface.primary}, ${brandSurface.secondary})`, color: "#fff", padding: "28px 26px 52px", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <span aria-hidden="true" style={{ position: "absolute", right: -22, top: -28, width: 150, height: 142, borderRadius: 36, background: "rgba(255,255,255,0.12)", transform: "rotate(-18deg)" }} />
              <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: "absolute", right: 34, bottom: 18, color: "#fff", opacity: 0.16, fontSize: 78 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, position: "relative" }}>
                <img src={athleteModal.athlete.profile_img || noImage} alt="" style={{ width: 58, height: 58, borderRadius: 18, objectFit: "cover", border: "2px solid rgba(255,255,255,0.30)", flexShrink: 0 }} loading="lazy" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6 }}>Athlete Analytics</div>
                  <h2 id="club-athlete-analytics-title" style={{ margin: "5px 0 4px", fontSize: 30, lineHeight: 1.12, color: "#fff", whiteSpace: "normal", overflowWrap: "anywhere" }}>{athleteModal.athlete.name}</h2>
                  <div style={{ color: "rgba(255,255,255,0.76)", fontSize: 13, lineHeight: 1.25, fontWeight: 850 }}>{analyticsWindow.label}</div>
                </div>
              </div>
              <button aria-label="Close athlete analytics" onClick={() => setAthleteModal(null)} style={{ border: 0, background: "#fff", color: base.text, width: 38, height: 38, borderRadius: 19, fontWeight: 950, cursor: "pointer", position: "relative", flexShrink: 0, display: "grid", placeItems: "center" }}>
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
              </button>
            </div>
            <div style={{ padding: 16, display: "grid", gap: 12, marginTop: -44 }}>
              {(() => {
                const athleteSessions = athleteModal.sessions || [];
                const totals = athleteSessions.reduce((acc, session) => {
                  const rowTotals = getWorkoutSessionTotals(session);
                  acc.totalXp += rowTotals.totalXp;
                  acc.totalSets += rowTotals.totalSets;
                  acc.totalReps += rowTotals.totalReps;
                  acc.totalVolume += rowTotals.totalVolume;
                  acc.totalDuration += rowTotals.durationSeconds;
                  acc.totalRest += rowTotals.totalRestSeconds;
                  acc.effortSum += rowTotals.effort;
                  acc.effortCount += rowTotals.effort > 0 ? 1 : 0;
                  return acc;
                }, { totalXp: 0, totalSets: 0, totalReps: 0, totalVolume: 0, totalDuration: 0, totalRest: 0, effortSum: 0, effortCount: 0 });
                const rangeDays = analyticsWindow.days ?? (activeSeason ? inclusiveDays(
                  activeSeason.starts_on ? new Date(`${activeSeason.starts_on}T00:00:00`) : new Date(),
                  activeSeason.ends_on ? new Date(`${activeSeason.ends_on}T00:00:00`) : new Date()
                ) : 0);
                const weeksFactor = targetWeeksForWindow(analyticsWindow);
                const sessionTarget = Number(dashboardGroup?.weekly_session_target || 3) * weeksFactor;
                const completion = sessionTarget > 0 ? Math.min(100, Math.round((athleteSessions.length / sessionTarget) * 100)) : 0;
                const activeDays = new Set(athleteSessions.map(getWorkoutSessionDateKey).filter(Boolean)).size;
                const avgXp = athleteSessions.length ? Math.round(totals.totalXp / athleteSessions.length) : 0;
                const avgEffort = totals.effortCount ? (totals.effortSum / totals.effortCount).toFixed(1) : "0";
                const athleteStatus = getGroupAttentionStatus(totals.totalXp, dashboardGroupData?.athleteTarget || 0, athleteSessions.length, sessionTarget);
                const athletePrompt = athleteSessions.length <= 0
                  ? "No completed assigned-workout sessions are logged in this window."
                  : athleteSessions.length >= sessionTarget
                    ? `${athleteSessions.length}/${sessionTarget} sessions logged. Target reached for this window.`
                    : `${athleteSessions.length}/${sessionTarget} sessions logged. Keep the next session clear and close.`;
                const athleteTrendRows = buildWorkoutGroupTrendRows(athleteSessions, rangeDays);
                const athleteTrendOptions = [
                  { key: "totalVolume", label: "Volume", metric: "totalVolume", icon: faDumbbell, color: brandSurface.accent, value: formatGroupVolume(totals.totalVolume), detail: "Completed load" },
                  { key: "totalReps", label: "Reps", metric: "totalReps", icon: faRepeat, color: brandSurface.secondary, value: compactGroupNumber(totals.totalReps), detail: "Completed reps" },
                  { key: "sessions", label: "Sessions", metric: "sessions", icon: faCalendar, color: "#16A34A", value: athleteSessions.length.toLocaleString(), detail: "Completed sessions" },
                ];
                const selectedAthleteTrend = athleteTrendOptions.find((option) => option.key === athleteTrendMetric) || athleteTrendOptions[0];
                const exerciseMap = new Map();
                athleteSessions.forEach((session) => {
                  (Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : []).forEach((row, index) => {
                    const meta = getWorkoutGroupExerciseMeta(row, index);
                    const key = meta.id || meta.name;
                    const current = exerciseMap.get(key) || { ...meta, reps: 0, volume: 0, xp: 0, sessions: 0, topWeight: 0 };
                    const reps = Number(row.total_reps || 0);
                    const avgWeight = Number(row.avg_weight || 0);
                    current.reps += reps;
                    current.volume += Number(row.total_volume || 0) || (avgWeight * reps);
                    current.xp += Number(row.total_xp || 0);
                    current.sessions += 1;
                    current.topWeight = Math.max(current.topWeight, avgWeight);
                    exerciseMap.set(key, current);
                  });
                });
                const topExercises = Array.from(exerciseMap.values()).sort((a, b) => (b.xp - a.xp) || (b.reps - a.reps)).slice(0, 4);
                const athleteName = displayProfileName(athleteModal.athlete, "this athlete");
                const athleteNutritionTargets = dashboardGroupData?.nutritionTargets || getGroupNutritionTargets(dashboardGroup || {});
                const athleteDailyCalorieTarget = Number(athleteNutritionTargets.calories || dashboardGroup?.daily_calorie_target || DEFAULT_GROUP_NUTRITION_TARGETS.calories);
                const athleteNutritionExpectedDays = Math.max(1, Number(dashboardGroupData?.dashboardNutritionDays || analyticsWindow.days || 1));
                const athleteNutritionRows = (dashboardGroupData?.nutritionRows || nutritionRows || []).filter((row) => String(row.user_id || "") === String(athleteModal.athlete.id || ""));
                const athleteNutritionStory = buildDashboardNutritionStory(
                  athleteNutritionRows,
                  [athleteModal.athlete],
                  athleteNutritionTargets,
                  athleteNutritionExpectedDays
                );
                const athleteNutritionTotals = athleteNutritionStory.totals || {};
                const athleteLoggedDays = Number(athleteNutritionStory.loggedDays || 0);
                const athleteExpectedDays = Number(athleteNutritionStory.expectedDays || athleteNutritionExpectedDays);
                const athleteNutritionHasData = hasNutritionAccess && athleteLoggedDays > 0;
                const athleteNutritionTargetTotal = athleteDailyCalorieTarget * Math.max(1, athleteExpectedDays);
                const athleteCalorieProgressPct = athleteNutritionTargetTotal > 0
                  ? Math.min(100, Math.round((Number(athleteNutritionTotals.calories || 0) / athleteNutritionTargetTotal) * 100))
                  : 0;
                const athleteNutritionAvg = (key) => athleteLoggedDays ? Number(athleteNutritionTotals[key] || 0) / athleteLoggedDays : 0;
                const athleteProteinTarget = Number(athleteNutritionTargets.protein || 0);
                const athleteAverageItems = athleteLoggedDays ? Number(athleteNutritionTotals.items || 0) / athleteLoggedDays : 0;
                const formatNutritionNumber = (value, options = {}) => {
                  const numeric = Number(value || 0);
                  if (options.compact) return compactGroupNumber(numeric);
                  return Math.round(numeric).toLocaleString();
                };
                const athleteNutritionInsight = !athleteNutritionHasData
                  ? `Nutrition signals will appear after ${athleteName} logs meals in this review window.`
                  : athleteCalorieProgressPct < 70
                    ? `${athleteName} is well below the calorie target for this window. Check meal timing and recovery intake.`
                    : athleteProteinTarget > 0 && athleteNutritionAvg("protein_g") < athleteProteinTarget * 0.85
                      ? `${athleteName} is logging consistently, but protein is below the daily target. Prioritise recovery meals.`
                      : athleteCalorieProgressPct > 115
                        ? `${athleteName} is logging above the calorie window. Review matchday and snack intake.`
                        : "Nutrition logging is consistent and the athlete is close to the calorie window.";
                return (
                  <>
                    <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${alpha(brandSurface.accent, 0.35)}`, background: `linear-gradient(135deg, ${brandSurface.primary} 0%, ${brandSurface.secondary} 100%)`, borderRadius: 22, padding: 18, color: "#fff" }}>
                      <span aria-hidden="true" style={{ position: "absolute", right: -18, top: 20, width: 156, height: 82, borderRadius: 28, background: "rgba(255,255,255,0.10)", transform: "rotate(-16deg)" }} />
                      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ color: "#FFFFFF", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Coach priority</div>
                          <h3 style={{ margin: "8px 0 0", color: "#fff", fontSize: 28, lineHeight: 1.12, fontWeight: 950 }}>{athleteStatus.label}</h3>
                          <div style={{ color: "rgba(255,255,255,0.76)", fontSize: 13, fontWeight: 850, lineHeight: 1.45, marginTop: 9 }}>{athletePrompt}</div>
                        </div>
                        <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.10)", borderRadius: 20, padding: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "rgba(255,255,255,0.78)", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>
                            <span>Completion</span>
                            <span style={{ color: "#fff" }}>{completion}%</span>
                          </div>
                          <div style={{ height: 9, background: "rgba(255,255,255,0.20)", borderRadius: 999, overflow: "hidden", marginTop: 10 }}>
                            <div style={{ height: "100%", width: `${completion}%`, background: "#FFFFFF", borderRadius: 999 }} />
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 13 }}>
                            <div style={{ color: "#fff", fontSize: 22, fontWeight: 950 }}>{totals.totalXp.toLocaleString()}</div>
                            <div style={{ color: "#fff", fontSize: 22, fontWeight: 950, textAlign: "right" }}>{avgEffort}</div>
                            <div style={{ color: "rgba(255,255,255,0.66)", fontSize: 10, fontWeight: 900 }}>total XP</div>
                            <div style={{ color: "rgba(255,255,255,0.66)", fontSize: 10, fontWeight: 900, textAlign: "right" }}>avg effort</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                      {[
                        { icon: faCalendar, label: "Sessions", value: athleteSessions.length.toLocaleString(), color: "#16A34A", bg: "#EAFBF0" },
                        { icon: faBolt, label: "Avg XP", value: avgXp.toLocaleString(), color: "#0C69C8", bg: "#E6F0FF" },
                        { icon: faClock, label: "Workout time", value: formatDuration(totals.totalDuration), color: "#7C3AED", bg: "#F3E8FF" },
                        { icon: faFire, label: "Active days", value: activeDays.toLocaleString(), color: "#D5A419", bg: "#FFF8E1" },
                      ].map((item) => (
                        <div key={item.label} style={{ border: `1px solid ${base.border}`, background: "#fff", borderRadius: 16, padding: 13, display: "grid", gridTemplateColumns: "38px minmax(0, 1fr)", gap: 10, alignItems: "center" }}>
                          <span style={{ width: 38, height: 38, borderRadius: 14, background: item.bg, color: item.color, display: "grid", placeItems: "center" }}><FontAwesomeIcon icon={item.icon} /></span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "block", color: base.text, fontSize: 22, fontWeight: 950 }}>{item.value}</span>
                            <span style={{ display: "block", color: base.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{item.label}</span>
                          </span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 12, alignItems: "start" }}>
                      <section style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                          <div>
                            <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Performance Signals</h3>
                            <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>{selectedAthleteTrend.detail}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 6 }}>
                          {athleteTrendOptions.map((option) => {
                            const active = selectedAthleteTrend.key === option.key;
                            return (
                              <button key={option.key} type="button" onClick={() => setAthleteTrendMetric(option.key)} style={{ border: `1px solid ${active ? option.color : "#DCE6F3"}`, background: active ? option.color : "#F8FAFC", color: active ? "#fff" : base.text, borderRadius: 999, padding: "7px 10px", fontSize: 11, fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <FontAwesomeIcon icon={option.icon} />
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ color: base.text, fontSize: 26, fontWeight: 950 }}>{selectedAthleteTrend.value}</div>
                        <WGTrendBars rows={athleteTrendRows} metric={selectedAthleteTrend.metric} color={selectedAthleteTrend.color} height={136} />
                      </section>

                      <section style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                        <h3 style={{ margin: 0, color: base.text, fontSize: 17 }}>Top Exercises</h3>
                        <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>Highest XP from assigned sessions</div>
                        {topExercises.length === 0 ? (
                          <div style={{ border: "1px dashed #CBD5E1", borderRadius: 14, padding: 12, color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 12 }}>No exercise detail in this range.</div>
                        ) : (
                          <div style={{ display: "grid", gap: 9, marginTop: 12 }}>
                            {topExercises.map((exercise) => (
                              <div key={exercise.id} style={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr) auto", gap: 10, alignItems: "center", border: `1px solid ${base.border}`, background: "#F8FAFF", borderRadius: 14, padding: 10 }}>
                                {(exercise.image_url || exercise.gif_url) ? (
                                  <img src={exercise.image_url || exercise.gif_url} alt="" style={{ width: 42, height: 42, borderRadius: 12, objectFit: "contain", background: "#EAF3FF" }} loading="lazy" />
                                ) : (
                                  <span style={{ width: 42, height: 42, borderRadius: 12, background: "#EAF3FF", color: brandSurface.primary, display: "grid", placeItems: "center" }}><FontAwesomeIcon icon={faDumbbell} /></span>
                                )}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ color: base.text, fontSize: 13, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{exercise.name}</div>
                                  <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>{exercise.reps.toLocaleString()} reps · {formatGroupVolume(exercise.volume)}</div>
                                </div>
                                <div style={{ color: brandSurface.primary, fontSize: 12, fontWeight: 950 }}>{exercise.xp.toLocaleString()} XP</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </section>
                    </div>

                    <section style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 20, padding: 14 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                        <div>
                          <h3 style={{ margin: 0, color: base.text, fontSize: 17, display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <FontAwesomeIcon icon={hasNutritionAccess ? faFire : faLock} style={{ color: analyticsGold, fontSize: 14 }} />
                            Nutrition Insights
                          </h3>
                          <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>
                            Calories, macro averages, fibre, and food logging for this athlete.
                          </div>
                        </div>
                        <span style={{ border: "1px solid #FDE68A", background: "#FFFBEB", color: "#92400E", borderRadius: 999, padding: "6px 9px", fontSize: 10, fontWeight: 950 }}>
                          {formatNutritionNumber(athleteDailyCalorieTarget)} kcal/day
                        </span>
                      </div>

                      {!hasNutritionAccess ? (
                        <div style={{ border: "1px solid #FDE68A", background: "#FFFBEB", borderRadius: 16, padding: 12 }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <WorkoutGroupAnalyticsIcon icon={faLock} color={analyticsGold} bg="#FFF8E1" size={36} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ color: "#92400E", fontSize: 13, fontWeight: 950 }}>Nutrition analytics locked</div>
                              <div style={{ color: "#92400E", fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                                Athlete nutrition details stay hidden until this group has Pro Coach or Club Coaching nutrition access.
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : !athleteNutritionHasData ? (
                        <div style={{ border: "1px dashed #FDE68A", background: "#FFFBEB", borderRadius: 16, padding: 12 }}>
                          <div style={{ color: "#92400E", fontSize: 13, fontWeight: 950 }}>No nutrition logs yet</div>
                          <div style={{ color: "#92400E", fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>
                            Nutrition signals will appear after {athleteName} logs meals in this review window.
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 12 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))", gap: 10 }}>
                            <WorkoutGroupNutritionTile
                              emphasis
                              icon={faFire}
                              label="Calories logged"
                              value={formatNutritionNumber(athleteNutritionTotals.calories, { compact: true })}
                              sub={`${athleteCalorieProgressPct}% of ${formatNutritionNumber(athleteNutritionTargetTotal, { compact: true })} kcal target`}
                              color={analyticsGold}
                              bg="#FFF8E1"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faCalendar}
                              label="Logged days"
                              value={`${formatNutritionNumber(athleteLoggedDays)} / ${formatNutritionNumber(athleteExpectedDays)}`}
                              sub={`${Math.round(athleteNutritionStory.consistency || 0)}% logging consistency`}
                              color="#0C69C8"
                              bg="#E6F0FF"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faBolt}
                              label="Average kcal"
                              value={formatNutritionNumber(athleteNutritionAvg("calories"))}
                              sub={`Target ${formatNutritionNumber(athleteDailyCalorieTarget)} kcal/day`}
                              color="#7C3AED"
                              bg="#F3E8FF"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faDumbbell}
                              label="Protein"
                              value={`${formatNutritionNumber(athleteNutritionAvg("protein_g"))}g`}
                              sub="Daily average"
                              color="#16A34A"
                              bg="#EAFBF0"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faClipboardList}
                              label="Carbs"
                              value={`${formatNutritionNumber(athleteNutritionAvg("carbs_g"))}g`}
                              sub="Daily average"
                              color="#22D3EE"
                              bg="#E7FAFF"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faClipboardList}
                              label="Fat"
                              value={`${formatNutritionNumber(athleteNutritionAvg("fat_g"))}g`}
                              sub="Daily average"
                              color={analyticsGold}
                              bg="#FFF8E1"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faClipboardList}
                              label="Fibre"
                              value={`${formatNutritionNumber(athleteNutritionAvg("fiber_g"))}g`}
                              sub="Daily average"
                              color="#0C69C8"
                              bg="#E6F0FF"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faClipboardList}
                              label="Sugar"
                              value={`${formatNutritionNumber(athleteNutritionAvg("sugar_g"))}g`}
                              sub="Daily average"
                              color="#EC4899"
                              bg="#FCE7F3"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faClipboardList}
                              label="Sodium"
                              value={`${formatNutritionNumber(athleteNutritionAvg("sodium_mg"))}mg`}
                              sub="Daily average"
                              color="#64748B"
                              bg="#F1F5F9"
                            />
                            <WorkoutGroupNutritionTile
                              icon={faList}
                              label="Food entries"
                              value={formatNutritionNumber(athleteNutritionTotals.items)}
                              sub={`${formatNutritionNumber(athleteAverageItems)} per logged day`}
                              color="#0F766E"
                              bg="#CCFBF1"
                            />
                          </div>
                          <div style={{ border: "1px solid #FDE68A", background: "#FFFBEB", borderRadius: 16, padding: 12 }}>
                            <div style={{ color: "#92400E", fontSize: 12, fontWeight: 950 }}>Coach nutrition insight</div>
                            <div style={{ color: "#92400E", fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 4 }}>{athleteNutritionInsight}</div>
                          </div>
                        </div>
                      )}
                    </section>
                  </>
                );
              })()}

              <div style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 16, padding: 14 }}>
                <h3 style={{ margin: "0 0 4px", color: base.text }}>Recent Sessions</h3>
                <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginBottom: 10 }}>Open sessions show exercise detail and feedback.</div>
                {athleteModal.sessions.length === 0 ? (
                  <div style={{ color: base.muted, fontWeight: 850 }}>No sessions in this range.</div>
                ) : athleteModal.sessions.map((session) => {
                  const feedback = (session.user_workout_feedback || [])[0];
                  const workoutName = getWorkoutGroupSessionWorkoutMeta(session).label;
                  const durationSeconds = Number(session.duration_seconds || 0);
                  const restSeconds = Number(session.workout_context?.[0]?.rest_timer_usage || 0);
                  const exerciseRows = Array.isArray(session.workout_exercise_summary) ? session.workout_exercise_summary : [];
                  const sessionStartedAt = session.started_at ? new Date(session.started_at).toLocaleString() : "Unknown";
                  const sessionEndedAt = session.ended_at ? new Date(session.ended_at).toLocaleString() : "Unknown";
                  const feedbackCreatedAt = feedback?.created_at ? new Date(feedback.created_at).toLocaleString() : "No feedback submitted";
                  const restLabel = restSeconds > 0 ? formatDuration(restSeconds) : "No rest logged";
                  const restPercent = durationSeconds > 0 ? Math.round((restSeconds / durationSeconds) * 100) : 0;
                  const isExpanded = expandedAthleteSessionId === session.id;
                  return (
                    <div key={session.id} style={{ borderTop: `1px solid ${base.border}`, padding: "11px 0" }}>
                      <button type="button" onClick={() => setExpandedAthleteSessionId(isExpanded ? null : session.id)} aria-expanded={isExpanded} style={{ width: "100%", border: 0, background: "transparent", cursor: "pointer", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, textAlign: "left", padding: 0 }}>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", color: base.text, fontWeight: 950, lineHeight: 1.25, whiteSpace: "normal", overflowWrap: "anywhere" }}>{workoutName === "unnamed workout" ? "Unnamed workout" : workoutName}</span>
                          <span style={{ display: "block", color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 4 }}>{sessionStartedAt} · {formatDuration(session.duration_seconds)}</span>
                          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                            <span style={{ border: "1px solid #CFE2FF", background: "#E6F0FF", color: "#0C69C8", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 950 }}>{Number(session.total_xp || 0).toLocaleString()} XP</span>
                            <span style={{ border: "1px solid #DCE6F3", background: "#F8FAFC", color: "#334155", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 900 }}>{Number(session.total_reps || 0).toLocaleString()} reps</span>
                            <span style={{ border: "1px solid #DCE6F3", background: "#F8FAFC", color: "#334155", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 900 }}>{exerciseRows.length} exercises</span>
                          </span>
                        </span>
                        <span style={{ color: brandSurface.primary, fontSize: 12, fontWeight: 950, display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
                          {isExpanded ? "Hide details" : "View details"}
                          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </span>
                      </button>
                      {isExpanded && <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                        <div style={{ background: "#F8FAFC", border: `1px solid ${base.border}`, borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 950, color: base.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>Workout Start</div>
                          <div style={{ color: base.text, fontWeight: 850, marginTop: 4 }}>{sessionStartedAt}</div>
                        </div>
                        <div style={{ background: "#F8FAFC", border: `1px solid ${base.border}`, borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 950, color: base.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>Workout Finished</div>
                          <div style={{ color: base.text, fontWeight: 850, marginTop: 4 }}>{sessionEndedAt}</div>
                        </div>
                        <div style={{ background: "#F8FAFC", border: `1px solid ${base.border}`, borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 950, color: base.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>Workout Duration</div>
                          <div style={{ color: base.text, fontWeight: 850, marginTop: 4 }}>{formatDuration(session.duration_seconds) || "0s"}</div>
                        </div>
                        <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 950, color: "#9A3412", textTransform: "uppercase", letterSpacing: 0.4 }}>Total Rest Used</div>
                          <div style={{ fontSize: 12, fontWeight: 900, color: "#9A3412", marginTop: 4 }}>{restLabel}{durationSeconds > 0 && restSeconds > 0 ? ` · ${restPercent}% of session time` : ""}</div>
                        </div>
                        <div style={{ background: "#F8FAFC", border: `1px solid ${base.border}`, borderRadius: 12, padding: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 950, color: base.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>Feedback Submitted</div>
                          <div style={{ color: base.text, fontWeight: 850, marginTop: 4 }}>{feedbackCreatedAt}</div>
                        </div>
                        {exerciseRows.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 8 }}>Exercise detail</div>
                            <div style={{ display: "grid", gap: 7 }}>
                              {exerciseRows.slice(0, 8).map((row, index) => {
                                const exercise = getWorkoutGroupExerciseMeta(row, index);
                                const reps = Number(row.total_reps || 0);
                                const avgWeight = Number(row.avg_weight || 0);
                                const volume = Number(row.total_volume || 0) || (avgWeight * reps);
                                return (
                                  <div key={`${session.id}_exercise_${exercise.id}_${index}`} style={{ border: `1px solid ${base.border}`, background: "#fff", borderRadius: 12, padding: 10, display: "grid", gridTemplateColumns: "42px minmax(0, 1fr) auto", gap: 10, alignItems: "center" }}>
                                    {(exercise.image_url || exercise.gif_url) ? (
                                      <img src={exercise.image_url || exercise.gif_url} alt="" style={{ width: 42, height: 42, borderRadius: 12, objectFit: "contain", background: "#EAF3FF" }} loading="lazy" />
                                    ) : (
                                      <span style={{ width: 42, height: 42, borderRadius: 12, background: "#EAF3FF", color: brandSurface.primary, display: "grid", placeItems: "center" }}><FontAwesomeIcon icon={faDumbbell} /></span>
                                    )}
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ color: base.text, fontSize: 12, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{exercise.name}</div>
                                      <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 2 }}>
                                        {reps.toLocaleString()} reps{avgWeight > 0 ? ` · avg ${avgWeight.toLocaleString()} kg` : ""}{volume > 0 ? ` · ${formatGroupVolume(volume)}` : ""}
                                      </div>
                                    </div>
                                    <div style={{ color: brandSurface.primary, fontSize: 12, fontWeight: 950 }}>{Number(row.total_xp || 0).toLocaleString()} XP</div>
                                  </div>
                                );
                              })}
                              {exerciseRows.length > 8 && <div style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>+{exerciseRows.length - 8} more exercises</div>}
                            </div>
                          </div>
                        )}
                      </div>}
                      {isExpanded && (feedback ? (
                        <div style={{ marginTop: 8, background: "#F0F4FA", borderRadius: 16, padding: 12 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
                            <FeedbackEmojiRow label="Enjoyment" value={feedback.rating} options={FEEDBACK_EMOJI_OPTIONS.rating} />
                            <FeedbackEmojiRow label="Difficulty" value={feedback.difficulty} options={FEEDBACK_EMOJI_OPTIONS.difficulty} />
                            <FeedbackEmojiRow label="Target Focus" value={feedback.focus_rating} options={FEEDBACK_EMOJI_OPTIONS.focus} />
                          </div>
                          {feedback.mood && (() => {
                            const mood = MOOD_OPTIONS.find((item) => item.value === feedback.mood);
                            return (
                              <div style={{ marginTop: 12, marginBottom: String(feedback.notes || "").trim() ? 10 : 0 }}>
                                <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 6 }}>Mood</div>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 18, background: base.blue, border: `1px solid ${base.blue}`, color: "#fff", fontSize: 12, fontWeight: 900 }}>
                                  <span>{mood?.emoji || ""}</span>
                                  <span>{mood?.label || feedback.mood}</span>
                                </span>
                              </div>
                            );
                          })()}
                          {String(feedback.notes || "").trim().length > 0 && (
                            <div style={{ marginTop: 2, background: "#FFFFFF", borderRadius: 14, padding: 12, border: `1px solid ${base.border}` }}>
                              <div style={{ fontSize: 11, fontWeight: 950, color: "#0D275B", marginBottom: 5 }}>Athlete Notes</div>
                              <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", lineHeight: 1.5 }}>{String(feedback.notes).trim()}</div>
                            </div>
                          )}
                        </div>
                      ) : <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 6 }}>No feedback for this session.</div>)}
                    </div>
                  );
                })}
              </div>

              <div style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 16, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
                  <h3 style={{ margin: 0, color: base.text }}>Coach Notes</h3>
                  {athleteModal.note?.created_at && <span style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>{new Date(athleteModal.note.created_at).toLocaleDateString()}</span>}
                </div>
                <div style={{ color: base.muted, fontWeight: 800, lineHeight: 1.5, marginBottom: 10 }}>{athleteModal.note?.note || "No athlete notes yet."}</div>
                <label htmlFor="club-athlete-coach-note" style={{ display: "block", color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 7 }}>Coach note</label>
                <textarea id="club-athlete-coach-note" value={athleteNoteText} onChange={(event) => setAthleteNoteText(event.target.value)} placeholder="Write athlete notes..." style={{ width: "100%", minHeight: 86, resize: "vertical", border: `1px solid ${base.border}`, borderRadius: 12, padding: 12, fontWeight: 800, color: base.text, boxSizing: "border-box" }} />
                <button onClick={saveAthleteNote} disabled={athleteNoteSaving || !athleteNoteText.trim()} style={{ marginTop: 10, border: 0, background: athleteNoteText.trim() ? brandSurface.primary : "#A8B4C8", color: "#fff", borderRadius: 12, padding: "10px 14px", fontWeight: 950, cursor: athleteNoteText.trim() ? "pointer" : "default" }}>{athleteNoteSaving ? "Saving..." : "Save Notes"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {joinCodeShare && (
        <div role="presentation" onClick={() => setJoinCodeShare(null)} style={{ position: "fixed", inset: 0, zIndex: 2080, background: "rgba(10,29,68,0.35)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-labelledby="club-join-code-title" className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(480px, 100%)", maxHeight: "90svh", background: "#fff", borderRadius: 20, border: `1px solid ${base.border}`, boxShadow: "0 24px 70px rgba(10,29,68,0.28)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${base.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div id="club-join-code-title" style={{ fontSize: 16, fontWeight: 700, color: base.text }}>Group Join Code</div>
              <button type="button" onClick={() => setJoinCodeShare(null)} aria-label="Close group join code" style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${base.border}`, background: "transparent", color: base.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
              </button>
            </div>
            <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
              <div style={{ display: "grid", gap: 14, justifyItems: "center", textAlign: "center" }}>
                <div style={{ background: "#fff", border: `1px solid ${base.border}`, borderRadius: 16, padding: "16px 24px", color: base.text, fontSize: 28, fontWeight: 950, letterSpacing: 6 }}>
                {joinCodeShare.code}
                </div>
                {joinCodeShare.warning && (
                  <div style={{ background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", borderRadius: 14, padding: 12, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                    {joinCodeShare.warning}
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: "14px 22px", borderTop: `1px solid ${base.border}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setJoinCodeShare(null)} style={{ background: "#fff", border: `1px solid ${base.border}`, color: base.muted, padding: "12px 20px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, minHeight: 44 }}>Close</button>
              <button type="button" onClick={() => copyGroupInviteCode(joinCodeShare.code)} style={{ background: `linear-gradient(135deg, ${brandSurface.primary} 0%, ${brandSurface.secondary} 100%)`, border: "none", color: "#fff", padding: "12px 20px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, minHeight: 44 }}>Copy Code</button>
            </div>
          </div>
        </div>
      )}

      {groupManageModal && (
        <div role="dialog" aria-modal="true" aria-labelledby="club-manage-group-title" onClick={() => setGroupManageModal(null)} style={{ position: "fixed", inset: 0, zIndex: 2070, background: "rgba(10,29,68,0.38)", display: "grid", placeItems: "center", padding: 18 }}>
          {(() => {
            const group = groups.find((item) => item.id === groupManageModal.id) || (selectedGroup?.id === groupManageModal.id ? selectedGroup : null) || groupManageModal;
            const managedGroup = group?.id ? group : groupManageModal;
            const allowedCount = getAllowedWorkoutPairs(managedGroup).length;
            const visibility = getWorkoutGroupMemberAnalyticsVisibility(managedGroup);
            const visibilityShared = visibility === WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group;
            const managedLeadCoach = firstRelationRow(managedGroup.coach) || activeClubCoachOptions.find((coach) => coach.id === managedGroup.coach_id)?.profile || null;
            const runManageAction = (action) => {
              setGroupManageModal(null);
              window.requestAnimationFrame(() => action?.());
            };
            const openCalendar = () => {
              setCalendarForm((prev) => ({
                ...prev,
                groupId: managedGroup.id,
                inviteAll: true,
                selectedAthleteIds: uniqueIds((managedGroup.workout_group_members || []).map((member) => member.athlete_id || member.profiles?.id)),
              }));
              setToolView("sessions");
              document.getElementById("club-tools")?.scrollIntoView({ behavior: "smooth", block: "start" });
            };
            return (
              <div className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(680px, 100%)", maxHeight: "92vh", overflow: "auto", background: "#fff", borderRadius: 20, border: `1px solid ${base.border}`, boxShadow: "0 24px 70px rgba(10,29,68,0.28)", padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <h2 id="club-manage-group-title" style={{ margin: 0, fontSize: 20, color: base.text }}>Manage Group</h2>
                  <button type="button" onClick={() => setGroupManageModal(null)} style={{ ...btn, padding: "8px 11px" }}>Close</button>
                </div>

                <div style={{ position: "relative", overflow: "hidden", borderRadius: 24, padding: 18, color: "#fff", background: `linear-gradient(135deg, ${brandSurface.primary}, ${brandSurface.secondary})` }}>
                  <div style={{ position: "absolute", right: -18, top: 14, width: 148, height: 76, borderRadius: 32, background: "rgba(255,255,255,0.10)", transform: "rotate(-18deg)" }} />
                  <div style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: brandSurface.accent, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.8 }}>Coach Tools</div>
                      <h3 style={{ margin: "5px 0 4px", color: "#fff", fontSize: 26, lineHeight: 1.05 }}>Manage group</h3>
                      <div style={{ color: "rgba(255,255,255,0.76)", fontSize: 13, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{managedGroup.name || "Workout group"}</div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 9, color: "rgba(255,255,255,0.86)", fontSize: 12, fontWeight: 900, maxWidth: "100%" }}>
                        {managedLeadCoach?.profile_img ? (
                          <img src={managedLeadCoach.profile_img} alt="" loading="lazy" style={{ width: 24, height: 24, borderRadius: 999, objectFit: "cover", border: "1px solid rgba(255,255,255,0.24)" }} />
                        ) : (
                          <span style={{ width: 24, height: 24, borderRadius: 999, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.12)" }}><FontAwesomeIcon icon={faUser} style={{ fontSize: 10 }} /></span>
                        )}
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Lead coach: {displayProfileName(managedLeadCoach, "Unassigned")}</span>
                      </div>
                    </div>
                    <div style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.13)", borderRadius: 18, padding: "8px 12px", minWidth: 94, textAlign: "center" }}>
                      <div style={{ color: "#fff", fontSize: 20, fontWeight: 950, lineHeight: 1 }}>{Number(managedGroup.weekly_session_target || 3)}</div>
                      <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 10, fontWeight: 950, marginTop: 2 }}>days/week</div>
                    </div>
                  </div>
                </div>

                <ClubManageLabel>Next actions</ClubManageLabel>
                <div style={{ display: "grid", gap: 10 }}>
                  <ClubManageOption icon={managedGroup.join_code ? faCopy : faBolt} title="Invite code" subtitle="Copy or create the athlete join code" onClick={() => runManageAction(() => getGroupJoinCode(managedGroup))} />
                  <ClubManageOption icon={canUseClubCalendar ? faCalendar : faLock} title="Calendar" subtitle={canUseClubCalendar ? "Plan sessions and attendance" : "Calendar sessions need Pro Coach or Club Coaching"} onClick={() => runManageAction(openCalendar)} muted={!canUseClubCalendar} />
                </div>

                <ClubManageLabel>Reminders</ClubManageLabel>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                  <ClubManageTileButton icon={faEnvelope} title="Workout reminder" onClick={() => runManageAction(() => sendGroupReminder(managedGroup, "training"))} />
                  <ClubManageTileButton icon={hasNutritionAccess ? faBullseye : faLock} title="Calorie reminder" onClick={() => runManageAction(() => sendGroupReminder(managedGroup, "calorie"))} muted={!hasNutritionAccess} />
                </div>

                <ClubManageLabel>Tools</ClubManageLabel>
                <div style={{ display: "grid", gap: 10 }}>
                  <ClubManageOption icon={faDumbbell} title={`Assigned workouts (${allowedCount})`} subtitle="Choose the plans that count here" onClick={() => runManageAction(() => updateAllowedWorkouts(managedGroup))} />
                  <ClubManageOption icon={faChartLine} title="Group analytics" subtitle="Open workout and exercise trends" onClick={() => runManageAction(() => {
                    setGroupAnalyticsAthleteId("all");
                    setSelectedGroupWorkoutKey("");
                    setSelectedGroupExerciseId("");
                    setGroupAnalyticsModalOpen(true);
                  })} />
                  <ClubManageOption icon={faChartLine} title="Club dashboard" subtitle="Coaches, club tools, seasons, and club analytics" onClick={() => runManageAction(() => document.getElementById("club-tools")?.scrollIntoView({ behavior: "smooth", block: "start" }))} />
                </div>

                <ClubManageLabel>Settings</ClubManageLabel>
                <div style={{ border: `1px solid ${base.border}`, background: "#FFFFFF", borderRadius: 18, padding: 14, marginBottom: 10 }}>
                  <label htmlFor="club-manage-lead-coach" style={{ display: "block", color: base.text, fontSize: 14, fontWeight: 950 }}>Lead coach</label>
                  <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>The coach athletes see for this team.</div>
                  <select
                    id="club-manage-lead-coach"
                    value={managedGroup.coach_id || ""}
                    disabled={groupCoachSaving || activeClubCoachOptions.length === 0}
                    onChange={(event) => saveGroupLeadCoach(managedGroup, event.target.value)}
                    style={{ marginTop: 10, width: "100%", border: `1px solid ${base.border}`, background: "#fff", color: base.text, borderRadius: 14, padding: "12px 13px", fontWeight: 900, outline: "none" }}
                  >
                    {activeClubCoachOptions.length === 0 && <option value="">No active club staff</option>}
                    {activeClubCoachOptions.map((coach) => (
                      <option key={coach.id} value={coach.id}>
                        {displayProfileName(coach.profile, "Coach")}{coach.role ? ` - ${formatClubStaffRole(coach.role)}` : ""}
                      </option>
                    ))}
                  </select>
                  {groupCoachSaving && <div style={{ color: base.muted, fontSize: 11, fontWeight: 900, marginTop: 7 }}>Saving lead coach...</div>}
                </div>
                <div style={{ border: `1px solid ${base.border}`, background: "#FFFFFF", borderRadius: 18, padding: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 16, background: brandSurface.secondary, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
                      <FontAwesomeIcon icon={visibilityShared ? faUsers : faLock} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: brandSurface.secondary, fontSize: 15, fontWeight: 950 }}>Athletes see</div>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 850, marginTop: 2 }}>Coach: all</div>
                    </div>
                    {groupVisibilitySaving && <span style={{ color: base.muted, fontSize: 11, fontWeight: 900 }}>Saving...</span>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 13 }}>
                    {[
                      { key: WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.ownOnly, icon: faUser, title: "Private", sub: "Their own" },
                      { key: WORKOUT_GROUP_MEMBER_ANALYTICS_VISIBILITY.group, icon: faUsers, title: "Shared", sub: "Everyone" },
                    ].map((option) => {
                      const selected = visibility === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          disabled={groupVisibilitySaving}
                          onClick={() => saveGroupMemberAnalyticsVisibility(managedGroup, option.key)}
                          style={{ minHeight: 78, border: `1px solid ${selected ? brandSurface.primary : base.border}`, background: selected ? alpha(brandSurface.primary, 0.10) : base.bgAlt, color: selected ? brandSurface.secondary : base.muted, borderRadius: 16, padding: 10, cursor: groupVisibilitySaving ? "wait" : "pointer", display: "grid", placeItems: "center", gap: 4, textAlign: "center" }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: selected ? brandSurface.primary : base.muted, fontSize: 15, fontWeight: 950 }}>
                            <FontAwesomeIcon icon={option.icon} />
                            {option.title}
                          </span>
                          <span style={{ color: selected ? brandSurface.primary : base.muted, fontSize: 11, fontWeight: 850 }}>{option.sub}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 10 }}>
                  <ClubManageTileButton icon={faBolt} title="XP target" subtitle={`${Number(managedGroup.weekly_xp_target || 1000).toLocaleString()} XP / wk`} onClick={() => runManageAction(() => updateGroupNumber(managedGroup, "weekly_xp_target", "Weekly XP target"))} />
                  <ClubManageTileButton icon={faCalendar} title="Session target" subtitle={`${Number(managedGroup.weekly_session_target || 3)} days/week`} onClick={() => runManageAction(() => updateGroupNumber(managedGroup, "weekly_session_target", "Weekly session target"))} />
                  <ClubManageTileButton icon={hasNutritionAccess ? faBullseye : faLock} title="Nutrition target" subtitle={hasNutritionAccess ? formatGroupNutritionTargets(managedGroup) : "Pro Coach or Club"} onClick={() => runManageAction(() => updateGroupNutritionTargets(managedGroup))} muted={!hasNutritionAccess} />
                </div>

                <div style={{ marginTop: 14, borderTop: `1px solid ${base.border}`, paddingTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => runManageAction(() => renameGroup(managedGroup))}
                    style={{ width: "100%", minHeight: 44, border: `1px solid ${base.border}`, background: "#FFFFFF", color: brandSurface.secondary, borderRadius: 14, padding: "10px 12px", fontWeight: 950, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                    Edit name
                  </button>
                  <button
                    type="button"
                    onClick={() => runManageAction(() => deleteGroup(managedGroup))}
                    style={{ width: "100%", minHeight: 44, border: "1px solid #FECACA", background: "#FEF2F2", color: "#991B1B", borderRadius: 14, padding: "10px 12px", fontWeight: 950, cursor: "pointer", marginTop: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete group
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {allowedWorkoutsModalGroup && (
        <div role="dialog" aria-modal="true" aria-label="Allowed workouts" onClick={() => { setAllowedWorkoutsModalGroup(null); setAllowedWorkoutSearch(""); }} style={{ position: "fixed", inset: 0, zIndex: 2075, background: "rgba(10,29,68,0.38)", display: "grid", placeItems: "center", padding: 18 }}>
          <div className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(760px, 100%)", maxHeight: "92vh", overflow: "auto", background: "#fff", borderRadius: 18, border: `1px solid ${base.border}`, boxShadow: "0 24px 70px rgba(10,29,68,0.28)", padding: 18 }}>
            {(() => {
              const group = allowedWorkoutsModalGroup;
              const allowedPairs = getAllowedWorkoutPairs(group);
              const allowedNames = allowedPairs.map((pair) => pair.name);
              const filteredWorkouts = coachWorkouts.filter((workout) => normalizeWorkoutName(workout?.name).includes(normalizeWorkoutName(allowedWorkoutSearch)));
              const isAssigned = (workoutName) => allowedNames.some((name) => normalizeWorkoutName(name) === normalizeWorkoutName(workoutName));
              const limit = activeFeatureAccess?.limits?.assigned_workouts_per_group;
              const limitLabel = typeof limit === "number" ? `${allowedPairs.length} / ${limit}` : `${allowedPairs.length}`;
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: 20, color: base.text }}>Allowed Workouts</h2>
                      <p style={{ margin: "5px 0 0", color: base.muted, fontSize: 13, fontWeight: 800, lineHeight: 1.45 }}>
                        Choose the workout plans athletes can use in {group.name || "this group"}.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAllowedWorkoutsModalGroup(null);
                        setAllowedWorkoutSearch("");
                      }}
                      style={{ ...btn, padding: "8px 11px" }}
                    >
                      Done
                    </button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", border: `1px solid ${base.border}`, background: base.bgAlt, borderRadius: 14, padding: 12, marginTop: 16 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: base.text, fontSize: 14, fontWeight: 950 }}>Assigned workout plans</div>
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>These are the only workouts that count for this group when restrictions are on.</div>
                    </div>
                    <div style={{ display: "grid", placeItems: "center", minWidth: 78, borderRadius: 12, background: "#fff", border: `1px solid ${base.border}`, padding: "8px 10px" }}>
                      <div style={{ color: brandSurface.primary, fontSize: 18, fontWeight: 950, lineHeight: 1 }}>{limitLabel}</div>
                      <div style={{ color: base.muted, fontSize: 10, fontWeight: 900, marginTop: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>assigned</div>
                    </div>
                  </div>

                  <div style={{ border: `1px solid ${base.border}`, borderRadius: 14, background: "#fff", padding: 12, marginTop: 12 }}>
                    <div style={{ color: base.text, fontSize: 12, fontWeight: 950, marginBottom: 10 }}>Selected for this group</div>
                    {allowedPairs.length > 0 ? (
                      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                        {allowedPairs.map((pair) => (
                          <div
                            key={`${pair?.code || pair?.name}_allowed`}
                            style={{ display: "grid", gap: 9, border: `1px solid ${alpha(brandSurface.primary, 0.24)}`, background: "#F8FAFF", color: base.text, borderRadius: 12, padding: 10, fontSize: 12, fontWeight: 900, minWidth: 0, opacity: allowedWorkoutsSaving ? 0.72 : 1 }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, minWidth: 0 }}>
                              <span style={{ minWidth: 0, color: base.text, fontSize: 12, fontWeight: 950, lineHeight: 1.25, whiteSpace: "normal", overflowWrap: "anywhere" }}>{pair?.name}</span>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                {pair?.code && (
                                  <span style={{ border: `1px solid ${canUseWorkoutShareCodes ? alpha(brandSurface.primary, 0.24) : base.border}`, background: "#fff", color: canUseWorkoutShareCodes ? brandSurface.primary : base.muted, borderRadius: 999, padding: "4px 7px", fontSize: 10, fontWeight: 950, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                                    Code {pair.code}
                                  </span>
                                )}
                                <button type="button" onClick={() => removeAllowedWorkoutFromGroup(pair?.name)} disabled={allowedWorkoutsSaving} aria-label={`Remove ${pair?.name} from group`} title="Remove from group" style={{ width: 30, height: 30, border: `1px solid ${base.border}`, background: "#fff", color: base.muted, borderRadius: 10, cursor: allowedWorkoutsSaving ? "wait" : "pointer", display: "grid", placeItems: "center", padding: 0 }}>
                                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                                </button>
                              </span>
                            </div>
                            <div style={{ color: base.muted, fontSize: 11, fontWeight: 850 }}>
                              Schedule: {formatAssignedWorkoutDays(pair)}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {ASSIGNED_WORKOUT_DAYS.map((day) => {
                                const selected = normalizeAssignedWorkoutDays(pair?.scheduled_days || pair?.days).includes(day.index);
                                return (
                                  <button
                                    key={`${pair?.name}-${day.short}`}
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => updateAllowedWorkoutSchedule(pair?.name, day.index)}
                                    disabled={allowedWorkoutsSaving}
                                    style={{ minHeight: 32, minWidth: 42, border: `1px solid ${selected ? brandSurface.primary : base.border}`, background: selected ? brandSurface.primary : "#fff", color: selected ? "#fff" : base.muted, borderRadius: 999, padding: "6px 9px", fontSize: 11, fontWeight: 950, cursor: allowedWorkoutsSaving ? "wait" : "pointer" }}
                                  >
                                    {day.short}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>No workout plans assigned yet.</div>
                    )}
                    {!canUseWorkoutShareCodes && (
                      <div style={{ marginTop: 8, color: base.muted, fontSize: 11, fontWeight: 800, lineHeight: 1.45 }}>
                        Workout share codes are included with Coach Starter and higher plans.
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                    <input
                      autoFocus
                      value={allowedWorkoutSearch}
                      onChange={(event) => setAllowedWorkoutSearch(event.target.value)}
                      placeholder="Search your workout plans"
                      style={{ flex: "1 1 240px", border: `1px solid ${base.border}`, borderRadius: 12, padding: "11px 12px", color: base.text, fontWeight: 850, boxSizing: "border-box" }}
                    />
                    <button type="button" onClick={loadCoachWorkouts} disabled={coachWorkoutsLoading} style={{ ...btn, opacity: coachWorkoutsLoading ? 0.72 : 1 }}>
                      Refresh
                    </button>
                  </div>

                  <div style={{ border: `1px solid ${base.border}`, borderRadius: 14, background: base.bgAlt, overflow: "hidden", marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${base.border}` }}>
                      <div style={{ color: base.text, fontSize: 13, fontWeight: 900 }}>Your Workout Plans</div>
                      <div style={{ color: base.muted, fontSize: 11, fontWeight: 800 }}>{filteredWorkouts.length} shown</div>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: "auto", padding: 10 }}>
                      {coachWorkoutsLoading ? (
                        <div style={{ padding: 18, textAlign: "center", color: base.muted, fontWeight: 800 }}>Loading workout plans...</div>
                      ) : filteredWorkouts.length === 0 ? (
                        <div style={{ padding: 16, background: "#fff", border: `1px solid ${base.border}`, borderRadius: 12 }}>
                          <div style={{ color: base.text, fontSize: 14, fontWeight: 900 }}>No workout plans found</div>
                          <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, lineHeight: 1.5, marginTop: 5 }}>Create workouts first, then assign them to this group here.</div>
                        </div>
                      ) : filteredWorkouts.map((workout) => {
                        const assigned = isAssigned(workout.name);
                        const assignedPair = allowedPairs.find((pair) => normalizeWorkoutName(pair?.name) === normalizeWorkoutName(workout.name));
                        return (
                          <button
                            key={workout.id}
                            type="button"
                            onClick={() => assigned ? notify("Workout already assigned to this group.", "warning") : addAllowedWorkoutFromPlan(workout)}
                            disabled={allowedWorkoutsSaving}
                            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, textAlign: "left", border: `1px solid ${assigned ? alpha(brandSurface.accent, 0.44) : base.border}`, background: "#fff", borderRadius: 14, padding: 12, marginBottom: 8, cursor: allowedWorkoutsSaving ? "wait" : "pointer", opacity: allowedWorkoutsSaving ? 0.72 : 1 }}
                          >
                            <div style={{ width: 38, height: 38, borderRadius: 19, background: assigned ? brandSurface.accent : alpha(brandSurface.accent, 0.12), color: assigned ? "#fff" : brandSurface.accent, display: "grid", placeItems: "center", flexShrink: 0, fontWeight: 950 }}>
                              {assigned ? <FontAwesomeIcon icon={faCircleCheck} /> : <FontAwesomeIcon icon={faDumbbell} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: base.text, fontSize: 14, fontWeight: 900, lineHeight: 1.25, whiteSpace: "normal", overflowWrap: "anywhere" }}>{workout.name}</div>
                              <div style={{ color: base.muted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>
                                {assigned ? "Assigned to this group" : "Add to this group"}
                              </div>
                              {assigned && (
                                <div style={{ color: brandSurface.primary, fontSize: 10, fontWeight: 950, marginTop: 4 }}>
                                  Schedule: {formatAssignedWorkoutDays(assignedPair)}
                                </div>
                              )}
                            </div>
                            {workout.share_code && (
                              <span style={{ border: `1px solid ${canUseWorkoutShareCodes ? alpha(brandSurface.accent, 0.42) : base.border}`, background: canUseWorkoutShareCodes ? alpha(brandSurface.accent, 0.10) : "#fff", color: canUseWorkoutShareCodes ? brandSurface.accent : base.muted, borderRadius: 999, padding: "6px 8px", fontSize: 10, fontWeight: 950, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", flexShrink: 0 }}>
                                Share {workout.share_code}
                              </span>
                            )}
                            <span style={{ background: assigned ? "rgba(22,101,52,0.10)" : base.text, color: assigned ? "#166534" : "#fff", borderRadius: 12, padding: "7px 10px", fontSize: 11, fontWeight: 900, flexShrink: 0 }}>
                              {assigned ? "Added" : "Add"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {groupEditModal && (
        <div role="dialog" aria-modal="true" aria-label={groupEditModal.title} onClick={() => {
          setGroupEditModal(null);
          setGroupEditValue("");
          setGroupNutritionDraft({ calories: "", protein: "", carbs: "", fat: "" });
        }} style={{ position: "fixed", inset: 0, zIndex: 2080, background: "rgba(10,29,68,0.38)", display: "grid", placeItems: "center", padding: 18 }}>
          <div className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(460px, 100%)", background: "#fff", borderRadius: 18, border: `1px solid ${base.border}`, boxShadow: "0 24px 70px rgba(10,29,68,0.28)", padding: 18 }}>
            <h2 style={{ margin: 0, fontSize: 20, color: base.text }}>{groupEditModal.title}</h2>
            {groupEditModal.type === "nutrition_targets" ? (
              <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
                <label style={{ display: "grid", gap: 7 }}>
                  <span style={{ color: base.muted, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>Calories</span>
                  <input
                    autoFocus
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={groupNutritionDraft.calories}
                    onChange={(event) => setGroupNutritionDraft((prev) => ({ ...prev, calories: event.target.value }))}
                    placeholder="kcal per day"
                    aria-label="Daily calorie target"
                    style={{ width: "100%", border: `1px solid ${base.border}`, borderRadius: 12, padding: "11px 12px", color: base.text, fontWeight: 850, boxSizing: "border-box" }}
                  />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}>
                  {[
                    ["protein", "Protein", "Daily protein target in grams"],
                    ["carbs", "Carbs", "Daily carbs target in grams"],
                    ["fat", "Fat", "Daily fat target in grams"],
                  ].map(([key, label, ariaLabel]) => (
                    <label key={key} style={{ display: "grid", gap: 7 }}>
                      <span style={{ color: base.muted, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
                      <input
                        type="number"
                        min="1"
                        inputMode="numeric"
                        value={groupNutritionDraft[key]}
                        onChange={(event) => setGroupNutritionDraft((prev) => ({ ...prev, [key]: event.target.value }))}
                        placeholder="g/day"
                        aria-label={ariaLabel}
                        style={{ width: "100%", border: `1px solid ${base.border}`, borderRadius: 12, padding: "11px 12px", color: base.text, fontWeight: 850, boxSizing: "border-box" }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <label style={{ display: "grid", gap: 7, marginTop: 14 }}>
                <span style={{ color: base.muted, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4 }}>{groupEditModal.label}</span>
                {groupEditModal.type === "textarea" ? (
                  <textarea
                    autoFocus
                    value={groupEditValue}
                    onChange={(event) => setGroupEditValue(event.target.value)}
                    style={{ width: "100%", minHeight: 110, resize: "vertical", border: `1px solid ${base.border}`, borderRadius: 12, padding: 12, color: base.text, fontWeight: 850, boxSizing: "border-box" }}
                  />
                ) : (
                  <input
                    autoFocus
                    type={groupEditModal.type === "number" ? "number" : "text"}
                    min={groupEditModal.field === "daily_calorie_target" ? 0 : 1}
                    value={groupEditValue}
                    onChange={(event) => setGroupEditValue(event.target.value)}
                    style={{ width: "100%", border: `1px solid ${base.border}`, borderRadius: 12, padding: "11px 12px", color: base.text, fontWeight: 850, boxSizing: "border-box" }}
                  />
                )}
              </label>
            )}
            {groupEditModal.helper && <div style={{ color: base.muted, fontSize: 12, fontWeight: 800, lineHeight: 1.45, marginTop: 8 }}>{groupEditModal.helper}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <button
                onClick={() => {
                  setGroupEditModal(null);
                  setGroupEditValue("");
                  setGroupNutritionDraft({ calories: "", protein: "", carbs: "", fat: "" });
                }}
                disabled={groupEditSaving}
                style={btn}
              >
                Cancel
              </button>
              <button onClick={saveGroupEditModal} disabled={groupEditSaving} style={{ ...btn, borderColor: brandSurface.primary, background: brandSurface.primary, color: "#fff", opacity: groupEditSaving ? 0.72 : 1 }}>
                {groupEditSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div role="dialog" aria-modal="true" aria-label={confirmAction.title} onClick={() => setConfirmAction(null)} style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(10,29,68,0.34)", display: "grid", placeItems: "center", padding: 18 }}>
          <div className="club-modal-panel" onClick={(event) => event.stopPropagation()} style={{ width: "min(420px, 100%)", background: "#fff", borderRadius: 18, border: `1px solid ${base.border}`, boxShadow: "0 24px 70px rgba(10,29,68,0.28)", padding: 18 }}>
            <h2 style={{ margin: 0, fontSize: 20, color: base.text }}>{confirmAction.title}</h2>
            <p style={{ margin: "8px 0 16px", color: base.muted, fontWeight: 800, lineHeight: 1.5 }}>{confirmAction.message}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setConfirmAction(null)} style={btn}>Cancel</button>
              <button
                onClick={async () => {
                  const action = confirmAction.onConfirm;
                  setConfirmAction(null);
                  await action?.();
                }}
                style={{ ...btn, borderColor: confirmAction.tone === "danger" ? base.danger : brandSurface.primary, background: confirmAction.tone === "danger" ? base.danger : brandSurface.primary, color: "#fff" }}
              >
                {confirmAction.confirmLabel || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
